'use strict';

import emojiRegex from 'emoji-regex';

/**
 * `Emoji Mix URL Generator` Version 1.0.33
 * 
 * Created by MattFor (Discord: MattFor#9884) on May 30, 2023.
 * Contact: matthew-forester@protonmail.com
 * 
 * Licensed under the MIT License.
 *
 * This module provides functionality for generating URLs for mixed emoji images 
 * using Google's Android Emoji Kitchen. It includes a main function, `getEmojiMixUrl`, 
 * which takes two emoji inputs and generates a URL for fetching a mixed emoji image 
 * based on these inputs. 
 * 
 * The module also includes several helper functions:
 * 
 * `toUnicode`: Converts a string input into a Unicode representation.
 *
 * `getEmojiCombo`: Finds a matching emoji combination from the emojiCompatibilityData object.
 *
 * `checkSupported`: Checks if an emoji is supported.
 *
 * `googleRequestUrl`: Generates a URL for fetching an emoji combination image.
 *
 * `googleRequestUrlEmojiPart`: Transforms an emoji Unicode representation for inclusion in a URL.
 *
 * The module uses data from the `emojiCompatibilityData` and `supportedEmojis` objects to validate emojis and 
 * to find matching emoji combinations. The `baseUrl` string is used as the base URL for fetching 
 * emoji images from Google's Android Emoji Kitchen.
 */

/**
 * `emojiCompatibilityData` is an object where each key is an emoji represented as a string of Unicode code points.
 * Each key's value is an array of objects, where each object represents a "combination" of that emoji with another.
 *
 * For instance, the key '2615' represents the ☕ emoji.
 * The array of objects under '2615' are combinations of the ☕ emoji with various other emojis.
 * 
 * Each object in the array has the following properties:
 * - `leftEmoji`: A string of Unicode code points representing the emoji on the "left" of the combination. 
 * - `rightEmoji`: A string of Unicode code points representing the emoji on the "right" of the combination. In this case, it's always '2615'.
 * - `date`: A string in the format 'YYYYMMDD', representing the date this combination was added or updated.
 */
const emojiCompatibilityData = {
    '2615': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2615', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f972', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f979', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '2615', 'date': '20230301' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '2615',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '2615', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '2615',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f978', 'rightEmoji': '2615', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '2615', 'date': '20220815' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '2615', 'date': '20221101' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '2615', 'date': '20221101' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '2615', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2615', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '2615',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f480', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '2615', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '2615', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2615', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '2615', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '2615', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f332', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '2615', 'date': '20220406' },
      { 'leftEmoji': '26c4', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2615', 'date': '20221107' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2615', 'date': '20221101' },
      { 'leftEmoji': '1f308', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f435', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '2615', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '2615', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '2615', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '2615', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '2615', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '2615', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f989', 'rightEmoji': '2615', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '2615', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '2615', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '2615', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '2615', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '2615', 'date': '20210218' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '2615', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '2615', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '2615', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '2615', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '2615', 'date': '20210521' },
      { 'leftEmoji': '2615', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2615', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '2615', 'date': '20210831' },
      { 'leftEmoji': '2615', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '2615', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '2615', 'date': '20221101' },
      { 'leftEmoji': '2615', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '2615', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '2615', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '2615', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '2615', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f4f0', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2615', 'date': '20201001' }
    ],
    '2648': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2648', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2648', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2648', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2648', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2648', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2648', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2648', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2648', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2648', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2648', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2648', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2648', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2648', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2648', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2648', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2648', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2648', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2648', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2648', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2648', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2648', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2648', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2648', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2648', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2648', 'date': '20230301' }
    ],
    '2649': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2649', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2649', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2649', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2649', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2649', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2649', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2649', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2649', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2649', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2649', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2649', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2649', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2649', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2649', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2649', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2649', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2649', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2649', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2649', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2649', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2649', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2649', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2649', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2649', 'date': '20220506' }
    ],
    '2650': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2650', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2650', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2650', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2650', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2650', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2650', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2650', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2650', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2650', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2650', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2650', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2650', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2650', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2650', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2650', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2650', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2650', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2650', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2650', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2650', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2650', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2650', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2650', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2650', 'date': '20220506' }
    ],
    '2651': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2651', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2651', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2651', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2651', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2651', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2651', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2651', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2651', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2651', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2651', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2651', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2651', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2651', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2651', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2651', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2651', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2651', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2651', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2651', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2651', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2651', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2651', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2651', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2651', 'date': '20220506' }
    ],
    '2652': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2652', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2652', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2652', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2652', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2652', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2652', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2652', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2652', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2652', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2652', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2652', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2652', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2652', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2652', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2652', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2652', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2652', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2652', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2652', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2652', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2652', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2652', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2652', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2652', 'date': '20220506' }
    ],
    '2653': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2653', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2653', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2653', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2653', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2653', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2653', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2653', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2653', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2653', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2653', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2653', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2653', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2653', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2653', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2653', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2653', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2653', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2653', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2653', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2653', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2653', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2653', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2653', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2653', 'date': '20220506' }
    ],
    '2693': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2693', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2693', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2693', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2693', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2693', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2693', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2693', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2693', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2693', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2693', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2693', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2693', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2693', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2693', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2693', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2693', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2693', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2693', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2693', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2693', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2693', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2693', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2693', 'date': '20220506' }
    ],
    '2705': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2705', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2705', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2705', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2705', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2705', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2705', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2705', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2705', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2705', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2705', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2705', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2705', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2705', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2705', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2705', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2705', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2705', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2705', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2705', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2705', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2705', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2705', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2705', 'date': '20220506' }
    ],
    '2753': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2753', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2753', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2753', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2753', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2753', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2753', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2753', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2753', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2753', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2753', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2753', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2753', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2753', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2753', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2753', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2753', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2753', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2753', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2753', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2753', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2753', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2753', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2753', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2753', 'date': '20220506' }
    ],
    '2757': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2757', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2757', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2757', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2757', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2757', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2757', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2757', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2757', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2757', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2757', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2757', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2757', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2757', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2757', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2757', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2757', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2757', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2757', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2757', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2757', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2757', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2757', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2757', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2757', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2757', 'date': '20230301' }
    ],
    '2795': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2795', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2795', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2795', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2795', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2795', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2795', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2795', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2795', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2795', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2795', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2795', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2795', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2795', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2795', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2795', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2795', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2795', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2795', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2795', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2795', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2795', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2795', 'date': '20220506' }
    ],
    '2796': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2796', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2796', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2796', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2796', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2796', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2796', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2796', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2796', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2796', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2796', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2796', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2796', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2796', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2796', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2796', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2796', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2796', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2796', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2796', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2796', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2796', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2796', 'date': '20220506' }
    ],
    '2797': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2797', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2797', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2797', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2797', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2797', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2797', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2797', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2797', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2797', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2797', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2797', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2797', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2797', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2797', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2797', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2797', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2797', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2797', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2797', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2797', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2797', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2797', 'date': '20220506' }
    ],
    '1f600': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f600', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f600', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f603', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f604', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f601', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f606', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f605', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f602', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f923', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f62d', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f61a', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f929', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f600',
        'date': '20210218'
      },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f630', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f628', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f62f', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f632', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f600', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f600', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f600',
        'date': '20210218'
      },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f600',
        'date': '20210218'
      },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f600', 'date': '20220203' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f600', 'date': '20210218' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f600', 'date': '20220406' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f600', 'date': '20220406' },
      { 'leftEmoji': '1f600', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f600', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f600', 'date': '20221101' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f600', 'date': '20220110' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f600', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f600', 'date': '20210831' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f600', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f600', 'date': '20210831' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f600', 'date': '20210831' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f600', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f600', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f600', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f600', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f600', 'date': '20210831' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f600', 'date': '20210218' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f600', 'date': '20210218' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f600', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f600', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f600', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f600', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f600', 'date': '20210831' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f600', 'date': '20220815' },
      { 'leftEmoji': '1f600', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f600', 'date': '20210831' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f600', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f600', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f600', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f603': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f603', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f603', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f603', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f604', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f601', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f606', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f605', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f602', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f923', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f62d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f61a', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f929', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f643', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f603',
        'date': '20210218'
      },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f97a', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f603', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f603', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f603',
        'date': '20210218'
      },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f603',
        'date': '20210218'
      },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f603', 'date': '20220203' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f603', 'date': '20210218' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f603', 'date': '20220406' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f603', 'date': '20220406' },
      { 'leftEmoji': '1f603', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f603', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f603', 'date': '20221101' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f603', 'date': '20220110' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f603', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f603', 'date': '20210831' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f603', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f603', 'date': '20210831' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f603', 'date': '20210831' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f603', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f603', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f603', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f603', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f603', 'date': '20210831' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f603', 'date': '20210218' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f603', 'date': '20210218' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f603', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f603', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f603', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f603', 'date': '20210521' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f603', 'date': '20210831' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f603', 'date': '20220815' },
      { 'leftEmoji': '1f603', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f603', 'date': '20210831' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f603', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f603', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f603', 'date': '20210521' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f604': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f604', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f604', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f604', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f604', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f601', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f606', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f605', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f602', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f923', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f62d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f61a', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f929', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f604',
        'date': '20210218'
      },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f604', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f604', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f604',
        'date': '20210218'
      },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f604',
        'date': '20210218'
      },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f604', 'date': '20220203' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f604', 'date': '20210218' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f604', 'date': '20220406' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f604', 'date': '20220406' },
      { 'leftEmoji': '1f604', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f604', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f604', 'date': '20221101' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f604', 'date': '20220110' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f604', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f604', 'date': '20210831' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f604', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f604', 'date': '20210831' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f604', 'date': '20210831' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f604', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f604', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f604', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f604', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f604', 'date': '20210831' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f604', 'date': '20210218' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f604', 'date': '20210218' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f604', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f604', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f604', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f604', 'date': '20210521' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f604', 'date': '20210831' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f604', 'date': '20220815' },
      { 'leftEmoji': '1f604', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f604', 'date': '20210831' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f604', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f604', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f604', 'date': '20210521' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f601': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f601', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f601', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f601', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f601', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f601', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f606', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f605', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f602', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f923', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f62d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f61a', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f601',
        'date': '20210218'
      },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f601', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f601', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f601',
        'date': '20210218'
      },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f601',
        'date': '20210218'
      },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f601', 'date': '20220203' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f601', 'date': '20210218' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f601', 'date': '20220406' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f601', 'date': '20220406' },
      { 'leftEmoji': '1f601', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f601', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f601', 'date': '20221101' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f601', 'date': '20220110' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f601', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f601', 'date': '20210831' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f601', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f601', 'date': '20210831' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f601', 'date': '20210831' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f601', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f601', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f601', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f601', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f601', 'date': '20210831' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f601', 'date': '20210218' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f601', 'date': '20210218' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f601', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f601', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f601', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f601', 'date': '20210521' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f601', 'date': '20210831' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f601', 'date': '20220815' },
      { 'leftEmoji': '1f601', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f601', 'date': '20210831' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f601', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f601', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f601', 'date': '20210521' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f606': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f606', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f606', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f606', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f606', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f606', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f606', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f606', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f605', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f605', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f602', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f602', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f923', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f923', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f62d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f607', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f60c', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f914', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f928', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f612', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f644', 'date': '20230301' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f606',
        'date': '20210218'
      },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f623', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f606', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f606', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f606',
        'date': '20210218'
      },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f920', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f496', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f49e', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f606',
        'date': '20210218'
      },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f606', 'date': '20220203' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f606', 'date': '20210218' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f606', 'date': '20220406' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f606', 'date': '20220406' },
      { 'leftEmoji': '1f606', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f606', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f606', 'date': '20221101' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f606', 'date': '20220110' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f606', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f606', 'date': '20210831' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f606', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f606', 'date': '20210831' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f606', 'date': '20210831' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f9a5', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f606', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f606', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f606', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f606', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f606', 'date': '20210831' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f606', 'date': '20210218' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f606', 'date': '20210218' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f606', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f606', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f606', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f606', 'date': '20210521' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f606', 'date': '20210831' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f606', 'date': '20220815' },
      { 'leftEmoji': '1f606', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f606', 'date': '20210831' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f606', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f606', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f606', 'date': '20210521' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f605': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f605', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f605', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f605', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f605', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f605', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f605', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f605', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f605', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f602', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f923', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f62d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f61a', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f929', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f643', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f605',
        'date': '20210218'
      },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f605', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f605', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f605',
        'date': '20210218'
      },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f605',
        'date': '20210218'
      },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f605', 'date': '20220203' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f605', 'date': '20210218' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f605', 'date': '20220406' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f605', 'date': '20220406' },
      { 'leftEmoji': '1f605', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f605', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f605', 'date': '20221101' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f605', 'date': '20220110' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f605', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f605', 'date': '20210831' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f605', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f605', 'date': '20210831' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f605', 'date': '20210831' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f605', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f605', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f605', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f605', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f605', 'date': '20210831' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f605', 'date': '20210218' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f605', 'date': '20210218' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f605', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f605', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f605', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f605', 'date': '20210521' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f605', 'date': '20210831' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f605', 'date': '20220815' },
      { 'leftEmoji': '1f605', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f605', 'date': '20210831' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f605', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f605', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f605', 'date': '20210521' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f602': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f602', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f602', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f602', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f602', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f602', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f602', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f602', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f602', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f602', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f923', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f62d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f61a', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f970', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f929', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f643', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f602',
        'date': '20210218'
      },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f92c', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f602', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f602', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f602',
        'date': '20210218'
      },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4a5', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fae7', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f602',
        'date': '20210218'
      },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9e0', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fac0', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fac1', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fa78', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9b7', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9b4', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f602', 'date': '20220203' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f33a', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f602', 'date': '20210218' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3f5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f33b', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f602', 'date': '20220406' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f331', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f33f', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f340', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f334', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f333', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f602', 'date': '20220406' },
      { 'leftEmoji': '1f602', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f300', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f602', 'date': '20221101' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2602-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f602', 'date': '20220110' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f602', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f602', 'date': '20210831' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f428', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f602', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f602', 'date': '20210831' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f602', 'date': '20210831' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f602', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f602', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f602', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f602', 'date': '20221101' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f54a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f602', 'date': '20210831' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f602', 'date': '20210218' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f602', 'date': '20210218' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fab2', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f43e', 'date': '20220815' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f602', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f602', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f602', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f602', 'date': '20210521' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f965', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f360', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9c5', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9c4', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f954', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f602', 'date': '20210831' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f96a', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fad4', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f32e', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f32f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f35d', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f96b', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f963', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f957', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f372', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f35b', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f35c', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f35a', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f359', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f365', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f960', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f602', 'date': '20221101' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f36b', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f375', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f379', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6d1', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6a8', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6df', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2693', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f697', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3ce-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f695', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f68c', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f682', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6f8', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f680', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2708-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3a1', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3aa', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3df-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3e0', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3d5-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f602', 'date': '20210831' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f380', 'date': '20220815' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1faa9', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f947', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f948', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f949', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3c5', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f396-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f602', 'date': '20220406' },
      { 'leftEmoji': '1f602', 'rightEmoji': '26be', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f94e', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3c0', 'date': '20220815' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f602', 'date': '20230126' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3d0', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3c8', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3c9', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3be', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f945', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3f8', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f94d', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3cf', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3d1', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3d2', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f94c', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6f7', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3bf', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '26f8-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6fc', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fa70', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6f9', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '26f3', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3af', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3f9', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f94f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fa83', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fa81', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f93f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3bd', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f94b', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f94a', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3b1', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3d3', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3b3', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '265f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fa80', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9e9', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3ae', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3b2', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3b0', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3b4', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f004', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f0cf', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4f7', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3a8', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f58c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f58d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1faa1', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9f5', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9f6', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3b9', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3b7', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3ba', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3b8', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fa95', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3bb', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fa98', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f941', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fa97', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3a4', 'date': '20220815' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f602', 'date': '20210521' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f399-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4fa', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f39e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3ac', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3ad', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f39f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4f1', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '260e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f50b', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1faab', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4be', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4bf', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4b8', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2696-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9e6', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6e0-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '26d3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f5d1-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f58a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2712-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '270f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4da', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f5c3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4e3', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f50e', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9ff', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f5dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f512', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2648', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2649', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '264a', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '264b', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '264c', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '264d', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '264e', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '264f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2650', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2651', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2652', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2653', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '26ce', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2757', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2753', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2049-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f198', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4f4', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f508', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '267b-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2705', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f195', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f193', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f199', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f197', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f192', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6ae', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '262e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '262f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '267e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2716-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2795', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2796', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2797', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '27b0', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '27bf', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '3030-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2122-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f5ef-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4ac', 'date': '20220815' }
    ],
    '1f923': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f923', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f923', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f923', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f923', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f923', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f923', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f923', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f923', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f923', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f923', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f62d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f61a', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f970', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f929', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f643', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '263a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f611', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f917', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f923',
        'date': '20210218'
      },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f615', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f628', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f62e', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f923', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f923', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f923',
        'date': '20210218'
      },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f974', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f923',
        'date': '20210218'
      },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f923', 'date': '20220203' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f923', 'date': '20210218' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f923', 'date': '20220406' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f923', 'date': '20220406' },
      { 'leftEmoji': '1f923', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f923', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f923', 'date': '20221101' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f923', 'date': '20220110' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f923', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f923', 'date': '20210831' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f428', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f923', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f923', 'date': '20210831' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f923', 'date': '20210831' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f923', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f923', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f923', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f923', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f923', 'date': '20210831' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f923', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f923', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f923', 'date': '20210218' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f923', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f923', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f923', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f923', 'date': '20210521' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f923', 'date': '20210831' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f923', 'date': '20220815' },
      { 'leftEmoji': '1f923', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f923', 'date': '20210831' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f923', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f923', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f923', 'date': '20210521' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f62d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f62d', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f62d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f62d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f62d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f62d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f62d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f62d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f62d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f62d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f62d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f612', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f62d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f623', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f635', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62d', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f62d', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f62d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f920', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f49e', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f62d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f62d', 'date': '20220203' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f62d', 'date': '20210218' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f62d', 'date': '20220406' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f62d', 'date': '20220406' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '26c4', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f62d', 'date': '20221101' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f62d', 'date': '20220110' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f62d', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f62d', 'date': '20210831' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f62d', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f62d', 'date': '20210831' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f62d', 'date': '20210831' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f62d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f62d', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f62d', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f62d', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f62d', 'date': '20210831' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f62d', 'date': '20210218' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f62d', 'date': '20210218' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f62d', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f62d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f62d', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f62d', 'date': '20210521' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f336-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f62d', 'date': '20210831' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f62d', 'date': '20220815' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f62d', 'date': '20210831' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f62d', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f62d', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f62d', 'date': '20210521' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f617': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f617', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f617', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f928', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f612', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f617',
        'date': '20210218'
      },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f623', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f617', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f617', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f617',
        'date': '20210218'
      },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f617',
        'date': '20210218'
      },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f617', 'date': '20220203' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f617', 'date': '20210218' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f617', 'date': '20220406' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f617', 'date': '20220406' },
      { 'leftEmoji': '1f617', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f617', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f617', 'date': '20221101' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f617', 'date': '20220110' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f617', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f617', 'date': '20210831' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f617', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f617', 'date': '20210831' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f617', 'date': '20210831' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f617', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f617', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f617', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f617', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f617', 'date': '20210831' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f617', 'date': '20210218' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f617', 'date': '20210218' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f617', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f617', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f617', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f617', 'date': '20210521' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f617', 'date': '20210831' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f617', 'date': '20220815' },
      { 'leftEmoji': '1f617', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f617', 'date': '20210831' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f617', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f617', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f617', 'date': '20210521' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f619': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f619', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f619', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f914', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f928', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f619',
        'date': '20210218'
      },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f619', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f619', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f619',
        'date': '20210218'
      },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f619',
        'date': '20210218'
      },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f619', 'date': '20220203' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f619', 'date': '20210218' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f619', 'date': '20220406' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f619', 'date': '20220406' },
      { 'leftEmoji': '1f619', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f619', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f619', 'date': '20221101' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f619', 'date': '20220110' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f619', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f619', 'date': '20210831' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f619', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f619', 'date': '20210831' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f619', 'date': '20210831' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f619', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f619', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f619', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f619', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f619', 'date': '20210831' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f619', 'date': '20210218' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f619', 'date': '20210218' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f619', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f619', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f619', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f619', 'date': '20210521' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f619', 'date': '20210831' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f619', 'date': '20220815' },
      { 'leftEmoji': '1f619', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f619', 'date': '20210831' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f619', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f619', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f619', 'date': '20210521' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f61a': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f61a', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f61a', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f61a', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f61a', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f61a', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f61a', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f61a', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f61a', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f61a', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f914', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f928', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f61a',
        'date': '20210218'
      },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f623', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f635', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61a', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f61a', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f61a',
        'date': '20210218'
      },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f61a',
        'date': '20210218'
      },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f61a', 'date': '20220203' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f61a', 'date': '20210218' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f61a', 'date': '20220406' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f61a', 'date': '20220406' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f61a', 'date': '20221101' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f61a', 'date': '20220110' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f61a', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f61a', 'date': '20210831' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f61a', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f61a', 'date': '20210831' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f61a', 'date': '20210831' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f61a', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f61a', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f61a', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f61a', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f61a', 'date': '20210831' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f61a', 'date': '20210218' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f61a', 'date': '20210218' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f61a', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f61a', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f61a', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f61a', 'date': '20210521' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f61a', 'date': '20210831' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f61a', 'date': '20220815' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f61a', 'date': '20210831' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f61a', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f61a', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f61a', 'date': '20210521' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f618': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f618', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f618', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f60c', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f914', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f928', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f612', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f618',
        'date': '20210218'
      },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f623', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f635', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f618', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f618', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f618',
        'date': '20210218'
      },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f920', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4a5', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4a5', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fae7', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fae7', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f618', 'date': '20221101' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f618', 'date': '20221101' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f618', 'date': '20221101' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f618',
        'date': '20210218'
      },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9e0', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fac0', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fac1', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa78', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa78', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9b7', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9b4', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f618', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f33a', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f33a', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f618', 'date': '20210218' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4ae', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3f5-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f618', 'date': '20220406' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f344', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f331', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f340', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fab4', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fab4', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f334', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f333', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f618', 'date': '20220406' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f300', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d6-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f618', 'date': '20221101' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2602-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2602-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26a1', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2604-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa90', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f618', 'date': '20220110' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f618', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f618', 'date': '20210831' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f618', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f618', 'date': '20210831' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f618', 'date': '20210831' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f618', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f618', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f618', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f618', 'date': '20221101' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f54a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f54a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f618', 'date': '20210831' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f99e', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fab8', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f618', 'date': '20210218' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f41a', 'date': '20230301' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f618', 'date': '20210218' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fab2', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fab3', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f41e', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f43e', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f43e', 'date': '20230301' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f618', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f618', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f618', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f96d', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f618', 'date': '20210521' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f348', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f350', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f95d', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad2', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad2', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad0', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad0', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f347', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f965', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f345', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f345', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f955', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f955', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f360', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f360', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c5', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f33d', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f33d', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f966', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f952', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f952', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad1', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c4', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c4', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f954', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad8', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad8', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f330', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f95c', 'date': '20220203' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f618', 'date': '20210831' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad3', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f950', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f96f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f95e', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f95e', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f969', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f356', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f354', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f96a', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f968', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f35f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad4', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad4', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f32e', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f32f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f959', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c6', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f958', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f35d', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f96b', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad5', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f963', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f957', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f372', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f372', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f35b', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f35c', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f35c', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f363', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f364', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f35a', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f371', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f359', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f358', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f365', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f960', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f370', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f618', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f36b', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f369', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f36a', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c2', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f37f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9cb', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9cb', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f375', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f375', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c9', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c9', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f379', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6d1', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6d1', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6a8', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6df', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2693', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f697', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f697', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3ce-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3ce-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f695', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f695', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f68c', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f68c', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6f8', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6f8', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f680', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f680', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2708-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2708-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3a1', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3aa', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3df-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3e0', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d5-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f618', 'date': '20210831' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3dd-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f380', 'date': '20220203' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1faa9', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f947', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f947', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f948', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f948', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f949', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f949', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3c5', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3c5', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f396-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f618', 'date': '20220406' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26bd', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26be', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f94e', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3c0', 'date': '20220203' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f618', 'date': '20230126' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d0', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3c8', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3c9', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3be', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3be', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f945', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3f8', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3f8', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f94d', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f94d', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3cf', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d1', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d1', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d2', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d2', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f94c', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6f7', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6f7', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3bf', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3bf', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26f8-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26f8-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6fc', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6fc', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa70', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6f9', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6f9', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26f3', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3af', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3f9', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f94f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa83', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa83', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa81', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f93f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f93f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3bd', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f94b', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f94a', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b1', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d3', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d3', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b3', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b3', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '265f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa80', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9e9', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3ae', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b2', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b0', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b0', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b4', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f004', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f0cf', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4f7', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3a8', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3a8', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f58c-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f58c-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f58d-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f58d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1faa1', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9f5', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9f6', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9f6', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b9', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b7', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b7', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3ba', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3ba', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b8', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b8', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa95', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa95', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3bb', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3bb', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa98', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa98', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f941', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa97', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3a4', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3a4', 'date': '20230301' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f618', 'date': '20210521' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f399-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4fa', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4fa', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f39e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3ac', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3ad', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3ad', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f39f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4f1', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '260e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f50b', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1faab', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4bf', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4b8', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2696-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2696-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4a1', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9e6', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6e0-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26d3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26d3-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f5d1-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f58a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f58a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2712-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2712-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '270f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '270f-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4da', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4da', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f5c3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f50e', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9ff', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f5dd-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f512', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2648', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2649', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '264a', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '264b', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '264c', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '264d', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '264e', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '264f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2650', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2651', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2652', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2653', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26ce', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2757', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2753', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2049-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f198', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4f4', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f508', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f508', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '267b-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2705', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f195', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f193', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f199', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f197', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f192', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6ae', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '262e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '262f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '267e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2716-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2795', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2796', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2797', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '27b0', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '27bf', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '3030-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '00a9-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '00ae-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2122-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f5ef-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f5ef-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4ac', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4ac', 'date': '20230301' }
    ],
    '1f970': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f970', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f970', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f970', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f970',
        'date': '20210218'
      },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f970', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f31d', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f970', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f970',
        'date': '20210218'
      },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4a5', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fae7', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fae7', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f970', 'date': '20221101' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f970', 'date': '20221101' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f970', 'date': '20221101' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f970',
        'date': '20210218'
      },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9e0', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fac0', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fac1', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fac1', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa78', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa78', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9b7', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9b4', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9b4', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f970', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f33a', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f970', 'date': '20210218' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4ae', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3f5-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f33b', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f970', 'date': '20220406' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f344', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f331', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f33f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f340', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fab4', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f970', 'date': '20220406' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f300', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26c5', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f970', 'date': '20221101' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f329-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2602-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26a1', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26a1', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f970', 'date': '20220110' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f970', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f970', 'date': '20210831' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f970', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f970', 'date': '20210831' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f970', 'date': '20210831' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f970', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f970', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f970', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f970', 'date': '20221101' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f54a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f54a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f970', 'date': '20210831' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f970', 'date': '20210218' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f41a', 'date': '20230301' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f970', 'date': '20210218' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fab2', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fab3', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f41e', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f43e', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f43e', 'date': '20230301' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f970', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f970', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f970', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f96d', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f970', 'date': '20210521' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f348', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f350', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f95d', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fad2', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fad2', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fad0', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f347', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f965', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f345', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f345', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f955', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f955', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f360', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9c5', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f33d', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f966', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f952', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fad1', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9c4', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f954', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fad8', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fad8', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f330', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f95c', 'date': '20220203' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f970', 'date': '20210831' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fad3', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f950', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f96f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f95e', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f95e', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f969', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f356', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f354', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f96a', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f968', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f35f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fad4', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f32e', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f32f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f959', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9c6', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f958', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f35d', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f96b', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fad5', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f963', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f957', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f372', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f35b', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f35c', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f35c', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f363', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f364', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f364', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f35a', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f371', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f359', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f358', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f365', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f960', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f370', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f970', 'date': '20220815' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f36b', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f369', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f36a', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9c2', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f37f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9cb', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f375', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9c9', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f379', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6d1', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6d1', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6a8', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6df', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2693', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f697', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3ce-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3ce-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f695', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f695', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f68c', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6f8', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f680', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f680', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2708-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3a1', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3aa', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3df-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3e0', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d5-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f970', 'date': '20210831' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f380', 'date': '20220203' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1faa9', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f947', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f947', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f948', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f949', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3c5', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f396-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f970', 'date': '20220406' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26bd', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26be', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f94e', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3c0', 'date': '20220203' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f970', 'date': '20230126' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d0', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3c8', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3c9', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3be', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f945', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3f8', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3f8', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f94d', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f94d', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3cf', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3cf', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d1', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d1', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d2', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d2', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f94c', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6f7', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3bf', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3bf', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26f8-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6fc', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa70', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa70', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6f9', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26f3', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3af', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3f9', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f94f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa83', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa81', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f93f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3bd', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f94b', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f94a', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b1', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d3', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d3', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b3', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '265f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa80', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9e9', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3ae', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b2', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b0', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b0', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b4', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f004', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f0cf', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f0cf', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4f7', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3a8', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3a8', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f58c-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f58c-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f58d-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f58d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1faa1', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9f5', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9f6', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9f6', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b9', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b7', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b7', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3ba', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3ba', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b8', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa95', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3bb', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa98', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f941', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa97', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3a4', 'date': '20220203' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f970', 'date': '20210521' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f399-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4fa', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4fa', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f39e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3ac', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3ad', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3ad', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f39f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4f1', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '260e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f50b', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1faab', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4be', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4bf', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4b8', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2696-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4a1', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9e6', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26d3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26d3-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f5d1-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f58a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2712-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2712-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '270f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4da', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f5c3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4e3', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f50e', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f5dd-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f512', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2648', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2649', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '264a', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '264b', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '264c', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '264d', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '264e', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '264f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2650', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2651', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2652', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2653', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26ce', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2757', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2753', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2049-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f198', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4f4', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f508', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '267b-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2705', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f195', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f193', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f199', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f197', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f192', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6ae', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '262e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '262f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '267e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2716-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2795', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2796', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2797', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '27b0', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '27bf', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '3030-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '00a9-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '00ae-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2122-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f5ef-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f5ef-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4ac', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4ac', 'date': '20230301' }
    ],
    '1f60d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f60d', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f929', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f60d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60d', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f60d', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f60d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4a5', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fae7', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f60d', 'date': '20221101' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f60d', 'date': '20221101' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f60d', 'date': '20221101' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f60d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9e0', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fac0', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fac0', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fac1', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fac1', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa78', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9b7', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9b4', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9b4', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f60d', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f33a', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f33a', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f60d', 'date': '20210218' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4ae', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3f5-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f60d', 'date': '20220406' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f344', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f331', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f340', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fab4', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f334', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f333', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f60d', 'date': '20220406' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f300', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f60d', 'date': '20221101' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2602-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26a1', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26a1', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f60d', 'date': '20220110' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f60d', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f60d', 'date': '20210831' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f60d', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f60d', 'date': '20210831' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f60d', 'date': '20210831' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f60d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f60d', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f60d', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f60d', 'date': '20221101' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f54a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f60d', 'date': '20210831' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f60d', 'date': '20210218' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f60d', 'date': '20210218' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fab2', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fab3', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f43e', 'date': '20220203' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f60d', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f60d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f60d', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f96d', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f96d', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f60d', 'date': '20210521' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f348', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f350', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f95d', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f95d', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad2', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad0', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad0', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f347', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f347', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f965', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f965', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f345', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f345', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f955', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f360', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f360', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9c5', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f33d', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f966', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f966', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f952', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f952', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad1', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9c4', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9c4', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f954', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad8', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad8', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f330', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f95c', 'date': '20220203' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f60d', 'date': '20210831' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad3', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f950', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f96f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f95e', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f969', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f969', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f356', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f354', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f96a', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f968', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f35f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad4', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad4', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f32e', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f32f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f32f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f959', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9c6', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f958', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f35d', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f96b', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad5', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f963', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f957', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f372', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f372', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f35b', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f35c', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f35c', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f363', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f364', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f364', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f35a', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f371', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f359', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f358', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f365', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f960', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f370', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f60d', 'date': '20220815' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f36b', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f369', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f36a', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9c2', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f37f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9cb', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f375', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f375', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9c9', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f379', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6d1', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6d1', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6a8', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6df', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2693', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f697', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f697', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3ce-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3ce-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f695', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f695', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f68c', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f68c', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6f8', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f680', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f680', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2708-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2708-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3aa', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3df-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3e0', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d5-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f60d', 'date': '20210831' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f380', 'date': '20220203' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1faa9', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f947', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f948', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f948', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f949', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3c5', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f396-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f396-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f60d', 'date': '20220406' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26bd', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26be', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94e', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94e', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3c0', 'date': '20220203' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f60d', 'date': '20230126' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d0', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3c8', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3c9', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3c9', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3be', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f945', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3f8', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3f8', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94d', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94d', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3cf', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d1', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d1', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d2', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d2', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94c', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6f7', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3bf', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3bf', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26f8-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6fc', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa70', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa70', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6f9', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6f9', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26f3', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26f3', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3af', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3af', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3f9', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3f9', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa83', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa83', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa81', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f93f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3bd', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94b', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94a', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94a', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b1', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d3', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d3', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b3', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b3', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '265f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa80', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9e9', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3ae', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b2', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b0', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b0', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b4', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b4', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f004', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f0cf', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f0cf', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4f7', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3a8', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3a8', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f58c-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f58c-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f58d-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f58d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1faa1', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1faa1', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9f5', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9f6', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9f6', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b9', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b7', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b7', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3ba', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3ba', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b8', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa95', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa95', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3bb', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3bb', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa98', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa98', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f941', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa97', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3a4', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3a4', 'date': '20230301' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f60d', 'date': '20210521' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f399-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4fa', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4fa', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f39e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3ac', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3ad', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f39f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4f1', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '260e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f50b', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1faab', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4bf', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4b8', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2696-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2696-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9fc', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9e6', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26d3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26d3-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f5d1-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f58a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f58a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2712-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2712-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '270f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '270f-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4da', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4da', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f5c3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4e3', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f50e', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f5dd-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f5dd-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f512', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2648', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2649', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '264a', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '264b', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '264c', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '264d', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '264e', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '264f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2650', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2651', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2652', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2653', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26ce', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2757', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2753', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2049-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f198', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4f4', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f508', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f508', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '267b-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2705', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f195', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f193', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f199', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f197', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f192', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6ae', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '262e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '262f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '267e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2716-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2795', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2796', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2797', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '27b0', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '27bf', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '3030-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '3030-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '00a9-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '00ae-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2122-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2660-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f5ef-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f5ef-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4ac', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4ac', 'date': '20230301' }
    ],
    '1f929': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f929', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f929', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f929', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f929', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f929', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f929', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f929', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f929', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f644', 'date': '20230301' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f929',
        'date': '20210218'
      },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f635', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f929', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f929', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f929',
        'date': '20210218'
      },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f929', 'date': '20221101' },
      { 'leftEmoji': '1f929', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f929', 'date': '20221101' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f929', 'date': '20221101' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f49d', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f496', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f493', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f49e', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f929',
        'date': '20210218'
      },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f929', 'date': '20220203' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f929', 'date': '20210218' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f929', 'date': '20220406' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f929', 'date': '20220406' },
      { 'leftEmoji': '1f929', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f929', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f929', 'date': '20221101' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f929', 'date': '20220110' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f929', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f929', 'date': '20210831' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f929', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f929', 'date': '20210831' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f929', 'date': '20210831' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f929', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f929', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f929', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f929', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f929', 'date': '20210831' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f929', 'date': '20210218' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f929', 'date': '20210218' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f929', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f929', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f929', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f929', 'date': '20210521' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f929', 'date': '20210831' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f929', 'date': '20220815' },
      { 'leftEmoji': '1f929', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f929', 'date': '20210831' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f929', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f929', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f929', 'date': '20210521' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f973': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f973', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f973', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f973',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f973', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f973',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f973', 'date': '20221101' },
      { 'leftEmoji': '1f973', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f49b', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f973', 'date': '20221101' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f973', 'date': '20221101' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f973',
        'date': '20210218'
      },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f973', 'date': '20220203' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f973', 'date': '20210218' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f973', 'date': '20220406' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f973', 'date': '20220406' },
      { 'leftEmoji': '1f973', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f973', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f973', 'date': '20221101' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f973', 'date': '20220110' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f973', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f973', 'date': '20210831' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f973', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f973', 'date': '20210831' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f973', 'date': '20210831' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f973', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f973', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f973', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f973', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f973', 'date': '20210831' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f973', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f973', 'date': '20210218' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f973', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f973', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f973', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f973', 'date': '20210521' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f973', 'date': '20210831' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f973', 'date': '20220815' },
      { 'leftEmoji': '1f973', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f973', 'date': '20210831' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f973', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f973', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f973', 'date': '20210521' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f917': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f917', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f917', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f61c', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f60c', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f914', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f928', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f612', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f644', 'date': '20230301' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f917',
        'date': '20210218'
      },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f623', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f635', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f917', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f917', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f917',
        'date': '20210218'
      },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f920', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f49d', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f917',
        'date': '20210218'
      },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f917', 'date': '20220203' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f917', 'date': '20210218' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f917', 'date': '20220406' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f917', 'date': '20220406' },
      { 'leftEmoji': '1f917', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f917', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f917', 'date': '20221101' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f917', 'date': '20220110' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f917', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f917', 'date': '20210831' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f917', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f917', 'date': '20210831' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f917', 'date': '20210831' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f917', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f917', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f917', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f917', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f917', 'date': '20210831' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f419', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f917', 'date': '20210218' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f917', 'date': '20210218' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f917', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f917', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f917', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f917', 'date': '20210521' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f917', 'date': '20210831' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f917', 'date': '20220815' },
      { 'leftEmoji': '1f917', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f917', 'date': '20210831' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f917', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f917', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f917', 'date': '20210521' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f643': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f643', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f643', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f643', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f643', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f643', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f643', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f643', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f643',
        'date': '20210218'
      },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f643', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f643', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f643',
        'date': '20210218'
      },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f643',
        'date': '20210218'
      },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f643', 'date': '20220203' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f643', 'date': '20210218' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f643', 'date': '20220406' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f643', 'date': '20220406' },
      { 'leftEmoji': '1f643', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f643', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f643', 'date': '20221101' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f643', 'date': '20220110' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f643', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f643', 'date': '20210831' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f643', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f643', 'date': '20210831' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f643', 'date': '20210831' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f643', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f643', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f643', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f643', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f643', 'date': '20210831' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f643', 'date': '20210218' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f643', 'date': '20210218' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f643', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f643', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f643', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f643', 'date': '20210521' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f643', 'date': '20210831' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f643', 'date': '20220815' },
      { 'leftEmoji': '1f643', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f643', 'date': '20210831' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f643', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f643', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f643', 'date': '20210521' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f642': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f642', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f643', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f642',
        'date': '20210218'
      },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f642', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f642', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f642',
        'date': '20210218'
      },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f642',
        'date': '20210218'
      },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f642', 'date': '20220203' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f642', 'date': '20210218' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f642', 'date': '20220406' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f642', 'date': '20220406' },
      { 'leftEmoji': '1f642', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f642', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f642', 'date': '20221101' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f642', 'date': '20220110' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f642', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f642', 'date': '20210831' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f642', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f642', 'date': '20210831' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f642', 'date': '20210831' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f642', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f642', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f642', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f642', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f642', 'date': '20210831' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f642', 'date': '20210218' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f642', 'date': '20210218' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f642', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f642', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f642', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f642', 'date': '20210521' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f642', 'date': '20210831' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f642', 'date': '20220815' },
      { 'leftEmoji': '1f642', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f642', 'date': '20210831' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f642', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f642', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f642', 'date': '20210521' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f972': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f972', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f972', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f972',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f972', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f972',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f972', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f972', 'date': '20220815' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f972', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f972',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f972', 'date': '20220203' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f972', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f972', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f972', 'date': '20220406' },
      { 'leftEmoji': '1f972', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f972', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f972', 'date': '20221101' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f972', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f972', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f972', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f972', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f972', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f972', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f972', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f972', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f972', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f972', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f972', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f972', 'date': '20210218' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f972', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f972', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f972', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f972', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f972', 'date': '20210521' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f972', 'date': '20210831' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f972', 'date': '20220815' },
      { 'leftEmoji': '1f972', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f972', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f972', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f972', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f972', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '263a-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '263a-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '263a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '263a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '263a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f60c', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f914', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f928', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f612', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '263a-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f623', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '263a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '263a-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f4af', 'date': '20220815' },
      {
        'leftEmoji': '263a-fe0f',
        'rightEmoji': '1f573-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '263a-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f480', 'date': '20201001' },
      {
        'leftEmoji': '263a-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1fae6', 'rightEmoji': '263a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '263a-fe0f', 'date': '20210218' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '263a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '263a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '26c4', 'date': '20201001' },
      {
        'leftEmoji': '263a-fe0f',
        'rightEmoji': '1f32a-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '263a-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '263a-fe0f', 'date': '20220110' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '263a-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '263a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '263a-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '263a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '263a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '263a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '263a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '263a-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '263a-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '263a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '263a-fe0f', 'date': '20210218' },
      {
        'leftEmoji': '263a-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f40c', 'rightEmoji': '263a-fe0f', 'date': '20210218' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '263a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '263a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '263a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '263a-fe0f', 'date': '20210521' },
      {
        'leftEmoji': '263a-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '263a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '263a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '2615', 'date': '20230301' },
      {
        'leftEmoji': '263a-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '263a-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20230301'
      },
      { 'leftEmoji': '1f307', 'rightEmoji': '263a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '263a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '263a-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '263a-fe0f', 'date': '20210521' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f60a': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f60a', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f970', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f60d', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f929', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f643', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f642', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f60a', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f917', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f60a',
        'date': '20210218'
      },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60a', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f60a', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f60a',
        'date': '20210218'
      },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f60a',
        'date': '20210218'
      },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f60a', 'date': '20220203' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f60a', 'date': '20210218' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f60a', 'date': '20220406' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f60a', 'date': '20220406' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f60a', 'date': '20221101' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f60a', 'date': '20220110' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f60a', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f60a', 'date': '20210831' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f60a', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f60a', 'date': '20210831' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f60a', 'date': '20210831' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f60a', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f60a', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f60a', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f60a', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f60a', 'date': '20210831' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f60a', 'date': '20210218' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f60a', 'date': '20210218' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f60a', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f60a', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f60a', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f60a', 'date': '20210521' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f60a', 'date': '20210831' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f60a', 'date': '20220815' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f60a', 'date': '20210831' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f60a', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f60a', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f60a', 'date': '20210521' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f60f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f60f', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f60f', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f60f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f60f', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f60f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f60f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f60f', 'date': '20220203' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f60f', 'date': '20210218' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f60f', 'date': '20220406' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f60f', 'date': '20220406' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f60f', 'date': '20221101' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f60f', 'date': '20220110' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f60f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f60f', 'date': '20210831' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f60f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f60f', 'date': '20210831' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f60f', 'date': '20210831' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f60f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f60f', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f60f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f60f', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f60f', 'date': '20210831' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f60f', 'date': '20210218' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f60f', 'date': '20210218' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f60f', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f60f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f60f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f60f', 'date': '20210521' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f60f', 'date': '20210831' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f60f', 'date': '20220815' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f60f', 'date': '20210831' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f60f', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f60f', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f60f', 'date': '20210521' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f60c': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f60c', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f60c', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f60c', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f60c', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f60c', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f60c', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f60c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60c', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f60c', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f60c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f60c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f60c', 'date': '20220203' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f60c', 'date': '20210218' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f60c', 'date': '20220406' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f60c', 'date': '20220406' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f60c', 'date': '20221101' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f60c', 'date': '20220110' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f60c', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f60c', 'date': '20210831' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f60c', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f60c', 'date': '20210831' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f60c', 'date': '20210831' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f60c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f60c', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f60c', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f60c', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f60c', 'date': '20210831' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f60c', 'date': '20210218' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f60c', 'date': '20210218' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f60c', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f60c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f60c', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f60c', 'date': '20210521' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f60c', 'date': '20210831' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f60c', 'date': '20220815' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f60c', 'date': '20210831' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f60c', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f60c', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f60c', 'date': '20210521' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f609': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f609', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f609', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f609', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f609',
        'date': '20210218'
      },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f609', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f609', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f609',
        'date': '20210218'
      },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f609',
        'date': '20210218'
      },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f609', 'date': '20220203' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f609', 'date': '20210218' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f609', 'date': '20220406' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f609', 'date': '20220406' },
      { 'leftEmoji': '1f609', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f609', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f609', 'date': '20221101' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f609', 'date': '20220110' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f609', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f609', 'date': '20210831' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f609', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f609', 'date': '20210831' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f609', 'date': '20210831' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f609', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f609', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f609', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f609', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f609', 'date': '20210831' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f609', 'date': '20210218' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f609', 'date': '20210218' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f609', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f609', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f609', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f609', 'date': '20210521' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f609', 'date': '20210831' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f609', 'date': '20220815' },
      { 'leftEmoji': '1f609', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f609', 'date': '20210831' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f609', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f609', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f609', 'date': '20210521' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f92d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f92d', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f92d', 'date': '20230301' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f92d', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f92d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f62f', 'date': '20230301' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92d', 'date': '20230301' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f92d', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f92d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f92d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f92d', 'date': '20220203' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f92d', 'date': '20210218' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f92d', 'date': '20220406' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f92d', 'date': '20220406' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f92d', 'date': '20221101' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f92d', 'date': '20220110' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f92d', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f92d', 'date': '20210831' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f92d', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f92d', 'date': '20210831' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f92d', 'date': '20210831' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f92d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f92d', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f92d', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f92d', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f92d', 'date': '20210831' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f92d', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f92d', 'date': '20230301' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f92d', 'date': '20210218' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f92d', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f92d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f92d', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f92d', 'date': '20210521' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f92d', 'date': '20210831' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f92d', 'date': '20220815' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f92d', 'date': '20210831' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f92d', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f92d', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f92d', 'date': '20210521' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f636': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f636', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f636', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f636',
        'date': '20210218'
      },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f636', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f636', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f636',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f636',
        'date': '20210218'
      },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f636', 'date': '20220203' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f636', 'date': '20210218' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f636', 'date': '20220406' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f636', 'date': '20220406' },
      { 'leftEmoji': '1f636', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f636', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f636', 'date': '20221101' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f636', 'date': '20220110' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f636', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f636', 'date': '20210831' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f636', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f636', 'date': '20210831' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f636', 'date': '20210831' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f636', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f636', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f636', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f636', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f636', 'date': '20210831' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f636', 'date': '20210218' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f636', 'date': '20210218' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f636', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f636', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f636', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f636', 'date': '20210521' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f636', 'date': '20210831' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f636', 'date': '20220815' },
      { 'leftEmoji': '1f636', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f636', 'date': '20210831' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f636', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f636', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f636', 'date': '20210521' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f610': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f610', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f610', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f610',
        'date': '20210218'
      },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f610', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f610', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f610',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f610',
        'date': '20210218'
      },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f610', 'date': '20220203' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f610', 'date': '20210218' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f610', 'date': '20220406' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f610', 'date': '20220406' },
      { 'leftEmoji': '1f610', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f610', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f610', 'date': '20221101' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f610', 'date': '20220110' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f610', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f610', 'date': '20210831' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f610', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f610', 'date': '20210831' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f610', 'date': '20210831' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f610', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f610', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f610', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f610', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f610', 'date': '20210831' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f610', 'date': '20210218' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f610', 'date': '20210218' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f610', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f610', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f610', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f610', 'date': '20210521' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f610', 'date': '20210831' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f610', 'date': '20220815' },
      { 'leftEmoji': '1f610', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f610', 'date': '20210831' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f610', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f610', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f610', 'date': '20210521' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f611': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f611', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f611', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f611', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f611',
        'date': '20210218'
      },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f611', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f611', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f611', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f611',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f611',
        'date': '20210218'
      },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f611', 'date': '20220203' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f611', 'date': '20210218' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f611', 'date': '20220406' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f611', 'date': '20220406' },
      { 'leftEmoji': '1f611', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f611', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f611', 'date': '20221101' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f611', 'date': '20220110' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f611', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f611', 'date': '20210831' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f611', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f611', 'date': '20210831' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f611', 'date': '20210831' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f611', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f611', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f611', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f611', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f611', 'date': '20210831' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f611', 'date': '20210218' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f611', 'date': '20210218' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f611', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f611', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f611', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f611', 'date': '20210521' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f611', 'date': '20210831' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f611', 'date': '20220815' },
      { 'leftEmoji': '1f611', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f611', 'date': '20210831' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f611', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f611', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f611', 'date': '20210521' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f614': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f614', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f614', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f614',
        'date': '20210218'
      },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f62e', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f614', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f614',
        'date': '20210218'
      },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4a5', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fae7', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f614',
        'date': '20210218'
      },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9e0', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fac0', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fac1', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fa78', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9b7', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9b4', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f614', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f33a', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f614', 'date': '20210218' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3f5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f614', 'date': '20220406' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f331', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f33f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f340', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f333', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f614', 'date': '20220406' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f300', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26c5', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f614', 'date': '20221101' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f329-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2602-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f614', 'date': '20220110' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f614', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f614', 'date': '20210831' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f614', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f614', 'date': '20210831' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f614', 'date': '20210831' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f614', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f614', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f614', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f614', 'date': '20221101' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f54a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f614', 'date': '20210831' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f614', 'date': '20210218' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f614', 'date': '20210218' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fab2', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fab3', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f41e', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f43e', 'date': '20220815' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f614', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f614', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f614', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f614', 'date': '20210521' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f965', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f360', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9c5', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9c4', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f954', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f614', 'date': '20210831' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f373', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f96a', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fad4', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f32e', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f32f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f35d', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f96b', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f963', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f957', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f372', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f35b', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f35c', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f35a', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f359', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f365', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f960', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f614', 'date': '20221101' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f36b', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f375', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f379', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f6d1', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f6a8', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f6df', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2693', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f697', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3ce-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f695', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f68c', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f6f8', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f680', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2708-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3aa', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3df-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3e0', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3d5-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f614', 'date': '20210831' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f380', 'date': '20220815' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1faa9', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f947', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f948', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f949', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3c5', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f396-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f614', 'date': '20220406' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26be', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f94e', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3c0', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3d0', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3c8', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3c9', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3be', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f945', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3f8', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f94d', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3cf', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3d1', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3d2', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f94c', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f6f7', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3bf', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26f8-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f6fc', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fa70', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f6f9', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26f3', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3af', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3f9', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f94f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fa83', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fa81', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f93f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3bd', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f94b', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f94a', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3b1', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3d3', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3b3', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '265f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fa80', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9e9', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3ae', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3b2', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3b0', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3b4', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f004', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f0cf', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4f7', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3a8', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f58c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f58d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1faa1', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9f5', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9f6', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3b9', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3b7', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3ba', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3b8', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fa95', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3bb', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fa98', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f941', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fa97', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3a4', 'date': '20220815' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f614', 'date': '20210521' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f399-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4fa', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f39e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3ac', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3ad', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f39f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4f1', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '260e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f50b', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1faab', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4bf', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4b8', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2696-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9fc', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9e6', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26d3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f5d1-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f58a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2712-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '270f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4da', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f5c3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4e3', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f50e', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9ff', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f5dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f512', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2648', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2649', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '264a', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '264b', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '264c', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '264d', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '264e', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '264f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2650', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2651', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2652', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2653', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26ce', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2757', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2753', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2049-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f198', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4f4', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f508', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '267b-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2705', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f195', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f193', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f199', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f197', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f192', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f6ae', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '262e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '262f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '267e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2716-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2795', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2796', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2797', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '27b0', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '27bf', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '3030-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2122-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2660-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f5ef-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4ac', 'date': '20220815' }
    ],
    '1f60b': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f60b', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f60b', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f60b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60b', 'date': '20230301' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f60b', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f60b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f60b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f60b', 'date': '20220203' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f60b', 'date': '20210218' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f60b', 'date': '20220406' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f60b', 'date': '20220406' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f60b', 'date': '20221101' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f60b', 'date': '20220110' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f60b', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f60b', 'date': '20210831' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f60b', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f60b', 'date': '20210831' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f60b', 'date': '20210831' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f60b', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f60b', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f60b', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f60b', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f60b', 'date': '20210831' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f60b', 'date': '20210218' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f60b', 'date': '20210218' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f60b', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f60b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f60b', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f60b', 'date': '20210521' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f60b', 'date': '20210831' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f60b', 'date': '20220815' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f60b', 'date': '20210831' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f60b', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f60b', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f60b', 'date': '20210521' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f61b': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f61b', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f61b', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f61b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61b', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f61b', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f61b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f61b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f61b', 'date': '20220203' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f61b', 'date': '20210218' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f61b', 'date': '20220406' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f61b', 'date': '20220406' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f61b', 'date': '20221101' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f61b', 'date': '20220110' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f61b', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f61b', 'date': '20210831' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f61b', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f61b', 'date': '20210831' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f61b', 'date': '20210831' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f61b', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f61b', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f61b', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f61b', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f61b', 'date': '20210831' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f61b', 'date': '20210218' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f61b', 'date': '20210218' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f61b', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f61b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f61b', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f61b', 'date': '20210521' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f61b', 'date': '20210831' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f61b', 'date': '20220815' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f61b', 'date': '20210831' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f61b', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f61b', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f61b', 'date': '20210521' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f61d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f61d', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f92a', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f61d', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f61d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f632', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61d', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f61d', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f61d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f61d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f61d', 'date': '20220203' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f61d', 'date': '20210218' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f61d', 'date': '20220406' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f61d', 'date': '20220406' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f61d', 'date': '20221101' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f61d', 'date': '20220110' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f61d', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f61d', 'date': '20210831' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f61d', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f61d', 'date': '20210831' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f61d', 'date': '20210831' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f61d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f61d', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f61d', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f61d', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f61d', 'date': '20210831' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f61d', 'date': '20210218' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f61d', 'date': '20210218' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f61d', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f61d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f61d', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f61d', 'date': '20210521' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f61d', 'date': '20210831' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f61d', 'date': '20220815' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f61d', 'date': '20210831' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f61d', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f61d', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f61d', 'date': '20210521' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f61c': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f61c', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f61c', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f61c', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f61c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61c', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f61c', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f61c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f61c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f61c', 'date': '20220203' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f61c', 'date': '20210218' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f61c', 'date': '20220406' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f61c', 'date': '20220406' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f61c', 'date': '20221101' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f61c', 'date': '20220110' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f61c', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f61c', 'date': '20210831' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f61c', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f61c', 'date': '20210831' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f61c', 'date': '20210831' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f61c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f61c', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f61c', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f61c', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f61c', 'date': '20210831' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f61c', 'date': '20210218' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f61c', 'date': '20210218' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f61c', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f61c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f61c', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f61c', 'date': '20210521' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f61c', 'date': '20210831' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f61c', 'date': '20220815' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f61c', 'date': '20210831' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f61c', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f61c', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f61c', 'date': '20210521' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f92a': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f92a', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f92a', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f92a', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f92a',
        'date': '20210218'
      },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92a', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f92a', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f92a',
        'date': '20210218'
      },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f92a',
        'date': '20210218'
      },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f92a', 'date': '20220203' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f92a', 'date': '20210218' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f92a', 'date': '20220406' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f92a', 'date': '20220406' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f92a', 'date': '20221101' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f92a', 'date': '20220110' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f92a', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f92a', 'date': '20210831' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f92a', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f92a', 'date': '20210831' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f92a', 'date': '20210831' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f92a', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f92a', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f92a', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f92a', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f92a', 'date': '20210831' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f92a', 'date': '20210218' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f92a', 'date': '20210218' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f92a', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f92a', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f92a', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f92a', 'date': '20210521' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f92a', 'date': '20210831' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f92a', 'date': '20220815' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f92a', 'date': '20210831' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f92a', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f92a', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f92a', 'date': '20210521' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f914': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f914', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f914', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f914', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f914', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f914', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f914', 'date': '20230301' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f914', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f914', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f914',
        'date': '20210218'
      },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f914', 'date': '20230301' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f914', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f914',
        'date': '20210218'
      },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f914',
        'date': '20210218'
      },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f914', 'date': '20220203' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f914', 'date': '20210218' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f914', 'date': '20220406' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f914', 'date': '20220406' },
      { 'leftEmoji': '1f914', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f914', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f914', 'date': '20221101' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f914', 'date': '20220110' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f914', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f914', 'date': '20210831' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f914', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f914', 'date': '20210831' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f914', 'date': '20210831' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f914', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f914', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f914', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f914', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f914', 'date': '20210831' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f914', 'date': '20210218' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f914', 'date': '20210218' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f914', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f914', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f914', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f914', 'date': '20210521' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f914', 'date': '20210831' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f914', 'date': '20220815' },
      { 'leftEmoji': '1f914', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f914', 'date': '20210831' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f914', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f914', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f914', 'date': '20210521' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f928': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f928', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f928', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f928', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f928', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f928', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f928', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f928', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f928', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f928', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f928',
        'date': '20210218'
      },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f62e', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f928', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f928', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f928',
        'date': '20210218'
      },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f928',
        'date': '20210218'
      },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f928', 'date': '20220203' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f928', 'date': '20210218' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f928', 'date': '20220406' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f928', 'date': '20220406' },
      { 'leftEmoji': '1f928', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f928', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f928', 'date': '20221101' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f928', 'date': '20220110' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f928', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f928', 'date': '20210831' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f928', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f928', 'date': '20210831' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f928', 'date': '20210831' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f928', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f928', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f928', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f928', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f928', 'date': '20210831' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f928', 'date': '20210218' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f928', 'date': '20210218' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f928', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f928', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f928', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f928', 'date': '20210521' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f928', 'date': '20210831' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f928', 'date': '20220815' },
      { 'leftEmoji': '1f928', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f928', 'date': '20210831' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f928', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f928', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f928', 'date': '20210521' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f9d0': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9d0', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f9d0', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f9d0',
        'date': '20210218'
      },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f9d0', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f9d0',
        'date': '20210218'
      },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f9d0',
        'date': '20210218'
      },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f441-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f9d0', 'date': '20220203' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f9d0', 'date': '20210218' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f9d0', 'date': '20220406' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f9d0', 'date': '20220406' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9d0', 'date': '20221101' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f9d0', 'date': '20220110' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f9d0', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f9d0', 'date': '20210831' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f9d0', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f9d0', 'date': '20210831' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f9d0', 'date': '20210831' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f9d0', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f9d0', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f9d0', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f9d0', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f9d0', 'date': '20210831' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f9d0', 'date': '20210218' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f9d0', 'date': '20210218' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f9d0', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f9d0', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f9d0', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f9d0', 'date': '20210521' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f9d0', 'date': '20210831' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f9d0', 'date': '20220815' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f9d0', 'date': '20210831' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f9d0', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f9d0', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f9d0', 'date': '20210521' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f644': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f644', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f644', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f644', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f644', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f60f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f644', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f644', 'date': '20211115' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f644', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f644', 'date': '20211115' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f644', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f644', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f644',
        'date': '20210218'
      },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f644', 'date': '20211115' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f644', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f644', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f644', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f644',
        'date': '20210218'
      },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f644',
        'date': '20210218'
      },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f644', 'date': '20220203' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f644', 'date': '20210218' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f644', 'date': '20220406' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f644', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f644', 'date': '20220406' },
      { 'leftEmoji': '1f644', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f644', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f644', 'date': '20221101' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f644', 'date': '20220110' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f644', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f644', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f644', 'date': '20210831' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f644', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f644', 'date': '20211115' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f644', 'date': '20210831' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f644', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f644', 'date': '20210831' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f644', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f644', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f644', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f644', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f644', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f644', 'date': '20210831' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f644', 'date': '20210218' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f644', 'date': '20210218' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f644', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f644', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f644', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f644', 'date': '20211115' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f644', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f644', 'date': '20210521' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f644', 'date': '20210831' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f644', 'date': '20220815' },
      { 'leftEmoji': '1f644', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f644', 'date': '20210831' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f644', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f644', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f644', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f644', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f644', 'date': '20210521' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f612': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f612', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f612', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f612', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f612', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f612', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f612', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f612', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f612', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f612', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f612',
        'date': '20210218'
      },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f612', 'date': '20230301' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f612', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f612',
        'date': '20210218'
      },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f612',
        'date': '20210218'
      },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f612', 'date': '20220203' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f612', 'date': '20210218' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f612', 'date': '20220406' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f612', 'date': '20220406' },
      { 'leftEmoji': '1f612', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f612', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f612', 'date': '20221101' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f612', 'date': '20220110' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f612', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f612', 'date': '20210831' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f612', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f612', 'date': '20210831' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f612', 'date': '20210831' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f612', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f612', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f612', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f612', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f612', 'date': '20210831' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f612', 'date': '20210218' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f612', 'date': '20210218' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f612', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f612', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f612', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f612', 'date': '20210521' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f612', 'date': '20210831' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f612', 'date': '20220815' },
      { 'leftEmoji': '1f612', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f612', 'date': '20210831' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f612', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f612', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f612', 'date': '20210521' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f624': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f624', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f624', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f624',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f624', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f624', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f624',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f624',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f624', 'date': '20220203' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f624', 'date': '20210218' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f624', 'date': '20220406' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f624', 'date': '20220406' },
      { 'leftEmoji': '1f624', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f624', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f624', 'date': '20221101' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f624', 'date': '20220110' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f624', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f624', 'date': '20210831' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f42d', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f624', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f624', 'date': '20210831' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f624', 'date': '20210831' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f624', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f624', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f624', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f624', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f624', 'date': '20210831' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f624', 'date': '20210218' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f624', 'date': '20210218' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f624', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f624', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f624', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f624', 'date': '20210521' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f624', 'date': '20210831' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f624', 'date': '20220815' },
      { 'leftEmoji': '1f624', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f624', 'date': '20210831' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f624', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f624', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f624', 'date': '20210521' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f620': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f620', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f620', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f620',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f620', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f92c', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f620', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f620', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f620',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f620',
        'date': '20210218'
      },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f620', 'date': '20220203' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f620', 'date': '20210218' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f620', 'date': '20220406' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f620', 'date': '20220406' },
      { 'leftEmoji': '1f620', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f620', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f620', 'date': '20221101' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f620', 'date': '20220110' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f620', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f620', 'date': '20210831' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f620', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f620', 'date': '20210831' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f620', 'date': '20210831' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f620', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f620', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f620', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f620', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f620', 'date': '20210831' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f620', 'date': '20210218' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f620', 'date': '20210218' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f620', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f620', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f620', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f620', 'date': '20210521' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f620', 'date': '20210831' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f620', 'date': '20220815' },
      { 'leftEmoji': '1f620', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f620', 'date': '20210831' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f620', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f620', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f620', 'date': '20210521' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f621': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f621', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f621', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f621',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f621', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f621',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f621',
        'date': '20210218'
      },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f621', 'date': '20220203' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f621', 'date': '20210218' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f621', 'date': '20220406' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f621', 'date': '20220406' },
      { 'leftEmoji': '1f621', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f621', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f621', 'date': '20221101' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f621', 'date': '20220110' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f621', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f621', 'date': '20210831' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f621', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f621', 'date': '20210831' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f621', 'date': '20210831' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f621', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f621', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f621', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f621', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f621', 'date': '20210831' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f621', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f621', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f621', 'date': '20210218' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f621', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f621', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f621', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f621', 'date': '20210521' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f621', 'date': '20210831' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f621', 'date': '20220815' },
      { 'leftEmoji': '1f621', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f621', 'date': '20210831' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f621', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f621', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f621', 'date': '20210521' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f92c': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f92c', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f92c', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f971', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f92c', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f92c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f92c', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f92c', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f634', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f62a', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f31b', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f31c', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f31e', 'date': '20230301' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f92c', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f92c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f92c', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f920', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f916', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f383', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f608', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f573-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f38a', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2665-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f49d', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f496', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f497', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f493', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f49e', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f495', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f48c', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2763-fe0f', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f92c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f494', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f92c', 'date': '20220203' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f339', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f92c', 'date': '20210218' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f92c', 'date': '20220406' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f92c', 'date': '20220406' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '26c4', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f92c', 'date': '20221101' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f92c', 'date': '20220110' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f92c', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f92c', 'date': '20210831' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f428', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f42d', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f92c', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f92c', 'date': '20210831' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f422', 'date': '20230301' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f92c', 'date': '20210831' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f98c', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f999', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f9a5', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f987', 'date': '20230301' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f92c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f92c', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f92c', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f92c', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f92c', 'date': '20210831' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f419', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f92c', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f92c', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f92c', 'date': '20210218' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f41d', 'date': '20230301' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f92c', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f92c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f92c', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f34d', 'date': '20230301' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f92c', 'date': '20210521' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f336-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f951', 'date': '20230301' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f92c', 'date': '20210831' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f9c0', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f32d', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f92c', 'date': '20220815' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f92c', 'date': '20210831' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f388', 'date': '20230301' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f92c', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f92c', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f92c', 'date': '20210521' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f451', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f52e', 'date': '20230301' }
    ],
    '2639-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2639-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '2639-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '2639-fe0f', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '2639-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f4af', 'date': '20201001' },
      {
        'leftEmoji': '2639-fe0f',
        'rightEmoji': '1f573-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '2639-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f480', 'date': '20201001' },
      {
        'leftEmoji': '2639-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1fae6', 'rightEmoji': '2639-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '2639-fe0f', 'date': '20210218' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '2639-fe0f', 'date': '20220406' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '2639-fe0f', 'date': '20220406' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '26c4', 'date': '20201001' },
      {
        'leftEmoji': '2639-fe0f',
        'rightEmoji': '1f32a-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2639-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '2639-fe0f', 'date': '20220110' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '2639-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '2639-fe0f', 'date': '20210831' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '2639-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '2639-fe0f', 'date': '20210831' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '2639-fe0f', 'date': '20210831' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '2639-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '2639-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '2639-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '2639-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '2639-fe0f', 'date': '20210831' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '2639-fe0f', 'date': '20210218' },
      {
        'leftEmoji': '2639-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f40c', 'rightEmoji': '2639-fe0f', 'date': '20210218' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '2639-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '2639-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '2639-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '2639-fe0f', 'date': '20210521' },
      {
        'leftEmoji': '2639-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '2639-fe0f', 'date': '20210831' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '2639-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      {
        'leftEmoji': '2639-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f307', 'rightEmoji': '2639-fe0f', 'date': '20210831' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '2639-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '2639-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '2639-fe0f', 'date': '20210521' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f641': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f641', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f641', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f641',
        'date': '20210218'
      },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f641', 'date': '20230301' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f641', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f641',
        'date': '20210218'
      },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f641',
        'date': '20210218'
      },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f641', 'date': '20220203' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f641', 'date': '20210218' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f641', 'date': '20220406' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f641', 'date': '20220406' },
      { 'leftEmoji': '1f641', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f641', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f641', 'date': '20221101' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f641', 'date': '20220110' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f641', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f641', 'date': '20210831' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f641', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f641', 'date': '20210831' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f641', 'date': '20210831' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f641', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f641', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f641', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f641', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f641', 'date': '20210831' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f641', 'date': '20210218' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f641', 'date': '20210218' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f641', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f641', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f641', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f641', 'date': '20210521' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f641', 'date': '20210831' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f641', 'date': '20220815' },
      { 'leftEmoji': '1f641', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f641', 'date': '20210831' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f641', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f641', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f641', 'date': '20210521' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f615': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f615', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f615', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f615', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f615',
        'date': '20210218'
      },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f615', 'date': '20230301' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f615', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f615',
        'date': '20210218'
      },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f615',
        'date': '20210218'
      },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f615', 'date': '20220203' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f615', 'date': '20210218' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f615', 'date': '20220406' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f615', 'date': '20220406' },
      { 'leftEmoji': '1f615', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f615', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f615', 'date': '20221101' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f615', 'date': '20220110' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f615', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f615', 'date': '20210831' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f615', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f615', 'date': '20210831' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f615', 'date': '20210831' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f615', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f615', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f615', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f615', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f615', 'date': '20210831' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f615', 'date': '20210218' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f615', 'date': '20210218' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f615', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f615', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f615', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f615', 'date': '20210521' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f615', 'date': '20210831' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f615', 'date': '20220815' },
      { 'leftEmoji': '1f615', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f615', 'date': '20210831' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f615', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f615', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f615', 'date': '20210521' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f61f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f61f', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f61f', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f61f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61f', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f61f', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f61f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f61f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f61f', 'date': '20220203' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f61f', 'date': '20210218' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f61f', 'date': '20220406' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f61f', 'date': '20220406' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f61f', 'date': '20221101' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f61f', 'date': '20220110' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f61f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f61f', 'date': '20210831' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f61f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f61f', 'date': '20210831' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f61f', 'date': '20210831' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f61f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f61f', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f61f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f61f', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f61f', 'date': '20210831' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f61f', 'date': '20210218' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f61f', 'date': '20210218' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f61f', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f61f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f61f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f61f', 'date': '20210521' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f61f', 'date': '20210831' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f61f', 'date': '20220815' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f61f', 'date': '20210831' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f61f', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f61f', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f61f', 'date': '20210521' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f97a': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f97a', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f97a', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f611', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f631', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f97a', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f97a',
        'date': '20210218'
      },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f92c', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f97a', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f630', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f628', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f626', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f62e', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f62f', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f632', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f613', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f97a', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f634', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f31a', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f31d', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f97a', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f97a',
        'date': '20210218'
      },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f974', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f912', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4a5', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fae7', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f573-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f97a',
        'date': '20210218'
      },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9e0', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fac0', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fac1', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fa78', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9b7', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9b4', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f97a', 'date': '20220203' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f339', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f33a', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f97a', 'date': '20210218' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3f5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f33b', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f97a', 'date': '20220406' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f331', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f33f', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f340', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f334', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f333', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f97a', 'date': '20220406' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f300', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f97a', 'date': '20221101' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f329-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2602-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f97a', 'date': '20220110' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f97a', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f97a', 'date': '20210831' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f428', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f42d', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f97a', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f97a', 'date': '20210831' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f97a', 'date': '20210831' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f97a', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f97a', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f97a', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f97a', 'date': '20221101' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f54a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f97a', 'date': '20210831' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f99e', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fab8', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f97a', 'date': '20210218' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f97a', 'date': '20210218' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f997', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fab2', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fab3', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f41e', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f43e', 'date': '20220815' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f97a', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f97a', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f97a', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f34d', 'date': '20230301' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f97a', 'date': '20210521' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f965', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f360', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9c5', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9c4', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f954', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f97a', 'date': '20210831' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f373', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f96a', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fad4', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f32e', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f32f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f35d', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f96b', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f963', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f957', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f372', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f35b', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f35c', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f35a', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f359', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f365', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f960', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f368', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f97a', 'date': '20221101' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f36b', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f375', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f379', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f6d1', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f6a8', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f6df', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2693', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f697', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3ce-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f695', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f68c', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f6f8', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f680', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2708-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3aa', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3df-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3e0', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3d5-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f97a', 'date': '20210831' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f380', 'date': '20220815' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1faa9', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f947', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f948', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f949', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3c5', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f396-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f97a', 'date': '20220406' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '26be', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f94e', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3c0', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3d0', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3c8', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3c9', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3be', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f945', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3f8', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f94d', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3cf', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3d1', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3d2', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f94c', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f6f7', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3bf', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '26f8-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f6fc', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fa70', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f6f9', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '26f3', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3af', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3f9', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f94f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fa83', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fa81', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f93f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3bd', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f94b', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f94a', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3b1', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3d3', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3b3', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '265f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fa80', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9e9', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3ae', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3b2', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3b0', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3b4', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f004', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f0cf', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4f7', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3a8', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f58c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f58d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1faa1', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9f5', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9f6', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3b9', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3b7', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3ba', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3b8', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fa95', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3bb', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fa98', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f941', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fa97', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3a4', 'date': '20220815' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f97a', 'date': '20210521' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f399-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4fa', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f39e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3ac', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3ad', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f39f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4f1', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '260e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f50b', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1faab', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4be', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4bf', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4b8', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2696-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9fc', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9e6', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '26d3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f5d1-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f58a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2712-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '270f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4da', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f5c3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4e3', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f50e', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f52e', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9ff', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f5dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f512', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2648', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2649', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '264a', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '264b', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '264c', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '264d', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '264e', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '264f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2650', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2651', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2652', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2653', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '26ce', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2757', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2753', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2049-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f198', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4f4', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f508', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '267b-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2705', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f195', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f193', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f199', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f197', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f192', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f6ae', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '262e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '262f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '267e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2716-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2795', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2796', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2797', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '27b0', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '27bf', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '3030-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2122-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2660-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f5ef-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4ac', 'date': '20220815' }
    ],
    '1f633': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f633', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f633', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f633',
        'date': '20210218'
      },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f633', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f633',
        'date': '20210218'
      },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f633',
        'date': '20210218'
      },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f633', 'date': '20220203' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f633', 'date': '20210218' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f633', 'date': '20220406' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f633', 'date': '20220406' },
      { 'leftEmoji': '1f633', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f633', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f633', 'date': '20221101' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f633', 'date': '20220110' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f633', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f633', 'date': '20210831' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f633', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f633', 'date': '20210831' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f633', 'date': '20210831' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f633', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f633', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f633', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f633', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f633', 'date': '20210831' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f633', 'date': '20210218' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f633', 'date': '20210218' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f633', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f633', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f633', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f633', 'date': '20210521' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f633', 'date': '20210831' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f633', 'date': '20220815' },
      { 'leftEmoji': '1f633', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f633', 'date': '20210831' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f633', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f633', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f633', 'date': '20210521' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f62c': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f62c', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f62c', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f62c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62c', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f62c', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f62c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f62c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f62c', 'date': '20220203' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f62c', 'date': '20210218' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f62c', 'date': '20220406' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f62c', 'date': '20220406' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f62c', 'date': '20221101' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f62c', 'date': '20220110' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f62c', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f62c', 'date': '20210831' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f62c', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f62c', 'date': '20210831' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f62c', 'date': '20210831' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f62c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f62c', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f62c', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f62c', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f62c', 'date': '20210831' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f62c', 'date': '20210218' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f62c', 'date': '20210218' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f62c', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f62c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f62c', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f62c', 'date': '20210521' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f62c', 'date': '20210831' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f62c', 'date': '20220815' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f62c', 'date': '20210831' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f62c', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f62c', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f62c', 'date': '20210521' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f910': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f910', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f910', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f910',
        'date': '20210218'
      },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f910', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f910', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f910',
        'date': '20210218'
      },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f910',
        'date': '20210218'
      },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f910', 'date': '20220203' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f910', 'date': '20210218' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f910', 'date': '20220406' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f910', 'date': '20220406' },
      { 'leftEmoji': '1f910', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f910', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f910', 'date': '20221101' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f910', 'date': '20220110' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f910', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f910', 'date': '20210831' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f910', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f910', 'date': '20210831' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f910', 'date': '20210831' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f910', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f910', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f910', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f910', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f910', 'date': '20210831' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f910', 'date': '20210218' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f910', 'date': '20210218' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f910', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f910', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f910', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f910', 'date': '20210521' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f910', 'date': '20210831' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f910', 'date': '20220815' },
      { 'leftEmoji': '1f910', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f910', 'date': '20210831' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f910', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f910', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f910', 'date': '20210521' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f92b': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f92b', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f92b', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f92b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92b', 'date': '20230301' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f92b', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f92b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f92b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f92b', 'date': '20220203' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f92b', 'date': '20210218' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f92b', 'date': '20220406' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f92b', 'date': '20220406' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f92b', 'date': '20221101' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f92b', 'date': '20220110' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f92b', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f92b', 'date': '20210831' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f92b', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f92b', 'date': '20210831' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f422', 'date': '20230301' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f92b', 'date': '20210831' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f92b', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f92b', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f92b', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f92b', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f92b', 'date': '20210831' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f92b', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f92b', 'date': '20230301' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f92b', 'date': '20210218' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f92b', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f92b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f92b', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f92b', 'date': '20210521' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f92b', 'date': '20210831' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f92b', 'date': '20220815' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f92b', 'date': '20210831' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f92b', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f92b', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f92b', 'date': '20210521' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f630': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f630', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f630', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f630', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f630',
        'date': '20210218'
      },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f630', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f630', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f630', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f630',
        'date': '20210218'
      },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f630',
        'date': '20210218'
      },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f630', 'date': '20220203' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f630', 'date': '20210218' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f630', 'date': '20220406' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f630', 'date': '20220406' },
      { 'leftEmoji': '1f630', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f630', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f630', 'date': '20221101' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f630', 'date': '20220110' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f630', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f630', 'date': '20210831' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f630', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f630', 'date': '20210831' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f630', 'date': '20210831' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f630', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f630', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f630', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f630', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f630', 'date': '20210831' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f630', 'date': '20210218' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f630', 'date': '20210218' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f630', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f630', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f630', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f630', 'date': '20210521' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f630', 'date': '20210831' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f630', 'date': '20220815' },
      { 'leftEmoji': '1f630', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f630', 'date': '20210831' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f630', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f630', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f630', 'date': '20210521' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f628': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f628', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f628', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f628', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f628', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f628',
        'date': '20210218'
      },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f628', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f628', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f628', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f628',
        'date': '20210218'
      },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f628',
        'date': '20210218'
      },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f628', 'date': '20220203' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f628', 'date': '20210218' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f628', 'date': '20220406' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f628', 'date': '20220406' },
      { 'leftEmoji': '1f628', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f628', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f628', 'date': '20221101' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f628', 'date': '20220110' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f628', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f628', 'date': '20210831' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f628', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f628', 'date': '20210831' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f628', 'date': '20210831' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f628', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f628', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f628', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f628', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f628', 'date': '20210831' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f628', 'date': '20210218' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f628', 'date': '20210218' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f628', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f628', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f628', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f628', 'date': '20210521' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f628', 'date': '20210831' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f628', 'date': '20220815' },
      { 'leftEmoji': '1f628', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f628', 'date': '20210831' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f628', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f628', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f628', 'date': '20210521' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f627': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f627', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f627', 'date': '20230301' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f627',
        'date': '20210218'
      },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f627', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f627',
        'date': '20210218'
      },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f627',
        'date': '20210218'
      },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f627', 'date': '20220203' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f627', 'date': '20210218' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f627', 'date': '20220406' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f627', 'date': '20220406' },
      { 'leftEmoji': '1f627', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f627', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f627', 'date': '20221101' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f627', 'date': '20220110' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f627', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f627', 'date': '20210831' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f627', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f627', 'date': '20210831' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f627', 'date': '20210831' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f627', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f627', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f627', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f627', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f627', 'date': '20210831' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f627', 'date': '20210218' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f627', 'date': '20210218' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f627', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f627', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f627', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f627', 'date': '20210521' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f627', 'date': '20210831' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f627', 'date': '20220815' },
      { 'leftEmoji': '1f627', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f627', 'date': '20210831' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f627', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f627', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f627', 'date': '20210521' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f626': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f626', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f626', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f626',
        'date': '20210218'
      },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f626', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f626', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f626', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f626',
        'date': '20210218'
      },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f626',
        'date': '20210218'
      },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f626', 'date': '20220203' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f626', 'date': '20210218' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f626', 'date': '20220406' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f626', 'date': '20220406' },
      { 'leftEmoji': '1f626', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f626', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f626', 'date': '20221101' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f626', 'date': '20220110' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f626', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f626', 'date': '20210831' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f626', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f626', 'date': '20210831' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f626', 'date': '20210831' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f626', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f626', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f626', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f626', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f626', 'date': '20210831' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f626', 'date': '20210218' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f626', 'date': '20210218' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f626', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f626', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f626', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f626', 'date': '20210521' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f626', 'date': '20210831' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f626', 'date': '20220815' },
      { 'leftEmoji': '1f626', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f626', 'date': '20210831' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f626', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f626', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f626', 'date': '20210521' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f62e': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f62e', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f62e', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f62e', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f62e', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f62e', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f62e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f62e', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f62e', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62e', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f62e', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f62e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f62e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f62e', 'date': '20220203' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f62e', 'date': '20210218' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f62e', 'date': '20220406' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f62e', 'date': '20220406' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f62e', 'date': '20221101' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f62e', 'date': '20220110' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f62e', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f62e', 'date': '20210831' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f62e', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f62e', 'date': '20210831' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f62e', 'date': '20210831' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f62e', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f62e', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f62e', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f62e', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f62e', 'date': '20210831' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f62e', 'date': '20210218' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f62e', 'date': '20210218' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f62e', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f62e', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f62e', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f62e', 'date': '20210521' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f62e', 'date': '20210831' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f62e', 'date': '20220815' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f62e', 'date': '20210831' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f62e', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f62e', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f62e', 'date': '20210521' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f62f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f62f', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f62f', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f62f', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f62f', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f62f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f62f', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f62f', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62f', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f62f', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f62f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f62f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f62f', 'date': '20220203' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f62f', 'date': '20210218' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f62f', 'date': '20220406' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f62f', 'date': '20220406' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f62f', 'date': '20221101' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f62f', 'date': '20220110' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f62f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f62f', 'date': '20210831' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f62f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f62f', 'date': '20210831' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f62f', 'date': '20210831' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f62f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f62f', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f62f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f62f', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f62f', 'date': '20210831' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f62f', 'date': '20210218' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f62f', 'date': '20210218' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f62f', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f62f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f62f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f62f', 'date': '20210521' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f62f', 'date': '20210831' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f62f', 'date': '20220815' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f62f', 'date': '20210831' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f62f', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f62f', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f62f', 'date': '20210521' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f632': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f632', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f632', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f632', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f632', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f632',
        'date': '20210218'
      },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f632', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f632', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f632', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f632', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f632',
        'date': '20210218'
      },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f632',
        'date': '20210218'
      },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f632', 'date': '20220203' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f632', 'date': '20210218' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f632', 'date': '20220406' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f632', 'date': '20220406' },
      { 'leftEmoji': '1f632', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f632', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f632', 'date': '20221101' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f632', 'date': '20220110' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f632', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f632', 'date': '20210831' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f632', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f632', 'date': '20210831' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f632', 'date': '20210831' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f632', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f632', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f632', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f632', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f632', 'date': '20210831' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f632', 'date': '20210218' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f632', 'date': '20210218' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f632', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f632', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f632', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f632', 'date': '20210521' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f632', 'date': '20210831' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f632', 'date': '20220815' },
      { 'leftEmoji': '1f632', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f632', 'date': '20210831' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f632', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f632', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f632', 'date': '20210521' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f631': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f631', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f631', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f631',
        'date': '20210218'
      },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f631', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f631', 'date': '20230301' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f631', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f631',
        'date': '20210218'
      },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f49b', 'date': '20230301' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f631',
        'date': '20210218'
      },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f631', 'date': '20220203' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f631', 'date': '20210218' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f631', 'date': '20220406' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f631', 'date': '20220406' },
      { 'leftEmoji': '1f631', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f631', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f631', 'date': '20221101' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f631', 'date': '20220110' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f631', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f631', 'date': '20210831' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f631', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f631', 'date': '20210831' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f631', 'date': '20210831' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f631', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f631', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f631', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f631', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f631', 'date': '20210831' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f631', 'date': '20210218' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f631', 'date': '20210218' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f631', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f631', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f631', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f631', 'date': '20210521' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f631', 'date': '20210831' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f631', 'date': '20220815' },
      { 'leftEmoji': '1f631', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f631', 'date': '20210831' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f631', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f631', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f631', 'date': '20210521' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f92f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f92f', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f92f', 'date': '20230301' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f92f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f92f', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f92f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f92f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f92f', 'date': '20220203' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f92f', 'date': '20210218' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f92f', 'date': '20220406' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f92f', 'date': '20220406' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f92f', 'date': '20221101' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f92f', 'date': '20220110' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f92f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f92f', 'date': '20210831' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f92f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f92f', 'date': '20210831' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f92f', 'date': '20210831' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f92f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f92f', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f92f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f92f', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f92f', 'date': '20210831' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f92f', 'date': '20210218' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f92f', 'date': '20210218' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f92f', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f92f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f92f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f34d', 'date': '20230301' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f92f', 'date': '20210521' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f92f', 'date': '20210831' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f92f', 'date': '20220815' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f92f', 'date': '20210831' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f92f', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f92f', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f92f', 'date': '20210521' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f622': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f622', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f622', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f622',
        'date': '20210218'
      },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f622', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f622', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f622',
        'date': '20210218'
      },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f622',
        'date': '20210218'
      },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f622', 'date': '20220203' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f622', 'date': '20210218' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f622', 'date': '20220406' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f622', 'date': '20220406' },
      { 'leftEmoji': '1f622', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f622', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f622', 'date': '20221101' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f622', 'date': '20220110' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f622', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f622', 'date': '20210831' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f622', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f622', 'date': '20210831' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f622', 'date': '20210831' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f622', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f622', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f622', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f622', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f622', 'date': '20210831' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f622', 'date': '20210218' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f622', 'date': '20210218' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f622', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f622', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f622', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f622', 'date': '20210521' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f622', 'date': '20210831' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f622', 'date': '20220815' },
      { 'leftEmoji': '1f622', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f622', 'date': '20210831' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f622', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f622', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f622', 'date': '20210521' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f625': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f625', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f625', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f625',
        'date': '20210218'
      },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f625', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f625', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f625',
        'date': '20210218'
      },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f625',
        'date': '20210218'
      },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f625', 'date': '20220203' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f625', 'date': '20210218' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f625', 'date': '20220406' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f625', 'date': '20220406' },
      { 'leftEmoji': '1f625', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f625', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f625', 'date': '20221101' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f625', 'date': '20220110' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f625', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f625', 'date': '20210831' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f625', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f625', 'date': '20210831' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f625', 'date': '20210831' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f625', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f625', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f625', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f625', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f625', 'date': '20210831' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f625', 'date': '20210218' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f625', 'date': '20210218' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f625', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f625', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f625', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f625', 'date': '20210521' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f625', 'date': '20210831' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f625', 'date': '20220815' },
      { 'leftEmoji': '1f625', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f625', 'date': '20210831' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f625', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f625', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f625', 'date': '20210521' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f613': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f613', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f613', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f613',
        'date': '20210218'
      },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f613', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f613', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f613', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f613',
        'date': '20210218'
      },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f613',
        'date': '20210218'
      },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f613', 'date': '20220203' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f613', 'date': '20210218' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f613', 'date': '20220406' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f613', 'date': '20220406' },
      { 'leftEmoji': '1f613', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f613', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f613', 'date': '20221101' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f613', 'date': '20220110' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f613', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f613', 'date': '20210831' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f613', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f613', 'date': '20210831' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f613', 'date': '20210831' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f613', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f613', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f613', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f613', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f613', 'date': '20210831' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f613', 'date': '20210218' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f613', 'date': '20210218' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f613', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f613', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f613', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f613', 'date': '20210521' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f613', 'date': '20210831' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f613', 'date': '20220815' },
      { 'leftEmoji': '1f613', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f613', 'date': '20210831' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f613', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f613', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f613', 'date': '20210521' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f61e': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f61e', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f61e', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f61e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61e', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f61e', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f61e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f61e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f61e', 'date': '20220203' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f61e', 'date': '20210218' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f61e', 'date': '20220406' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f61e', 'date': '20220406' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f61e', 'date': '20221101' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f61e', 'date': '20220110' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f61e', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f61e', 'date': '20210831' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f61e', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f61e', 'date': '20210831' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f61e', 'date': '20210831' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f61e', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f61e', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f61e', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f61e', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f61e', 'date': '20210831' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f61e', 'date': '20210218' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f61e', 'date': '20210218' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f61e', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f61e', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f61e', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f61e', 'date': '20210521' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f61e', 'date': '20210831' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f61e', 'date': '20220815' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f61e', 'date': '20210831' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f61e', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f61e', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f61e', 'date': '20210521' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f616': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f616', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f616', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f616',
        'date': '20210218'
      },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f616', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f616', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f616',
        'date': '20210218'
      },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f616',
        'date': '20210218'
      },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f616', 'date': '20220203' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f616', 'date': '20210218' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f616', 'date': '20220406' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f616', 'date': '20220406' },
      { 'leftEmoji': '1f616', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f616', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f616', 'date': '20221101' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f616', 'date': '20220110' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f616', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f616', 'date': '20210831' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f616', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f616', 'date': '20210831' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f616', 'date': '20210831' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f616', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f616', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f616', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f616', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f616', 'date': '20210831' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f616', 'date': '20210218' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f616', 'date': '20210218' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f616', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f616', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f616', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f616', 'date': '20210521' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f616', 'date': '20210831' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f616', 'date': '20220815' },
      { 'leftEmoji': '1f616', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f616', 'date': '20210831' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f616', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f616', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f616', 'date': '20210521' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f623': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f623', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f623', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f623', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f623', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f623', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f623', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f623', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f60c', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f614', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f92b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f92d', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f623', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f623', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f623',
        'date': '20210218'
      },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f97a', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f61f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f625', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f622', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '2639-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f641', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f615', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f910', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f630', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f628', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f627', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f626', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f62e', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f62e', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f62f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f62f', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f632', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f632', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f633', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f92f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f62c', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f613', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f61e', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f616', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f623', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f623', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f623', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f623',
        'date': '20210218'
      },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f623',
        'date': '20210218'
      },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f623', 'date': '20220203' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f623', 'date': '20210218' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f623', 'date': '20220406' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f623', 'date': '20220406' },
      { 'leftEmoji': '1f623', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f623', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f623', 'date': '20221101' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f623', 'date': '20220110' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f623', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f623', 'date': '20210831' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f623', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f623', 'date': '20210831' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f623', 'date': '20210831' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f623', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f623', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f623', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f623', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f623', 'date': '20210831' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f623', 'date': '20210218' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f623', 'date': '20210218' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f623', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f623', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f623', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f623', 'date': '20210521' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f623', 'date': '20210831' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f623', 'date': '20220815' },
      { 'leftEmoji': '1f623', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f623', 'date': '20210831' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f623', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f623', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f623', 'date': '20210521' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f629': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f629', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f629', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f629',
        'date': '20210218'
      },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f629', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f629', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f629', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f629',
        'date': '20210218'
      },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f629',
        'date': '20210218'
      },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f629', 'date': '20220203' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f629', 'date': '20210218' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f629', 'date': '20220406' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f629', 'date': '20220406' },
      { 'leftEmoji': '1f629', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f629', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f629', 'date': '20221101' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f629', 'date': '20220110' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f629', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f629', 'date': '20210831' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f629', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f629', 'date': '20210831' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f629', 'date': '20210831' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f629', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f629', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f629', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f629', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f629', 'date': '20210831' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f629', 'date': '20210218' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f629', 'date': '20210218' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f629', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f629', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f629', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f629', 'date': '20210521' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f629', 'date': '20210831' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f629', 'date': '20220815' },
      { 'leftEmoji': '1f629', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f629', 'date': '20210831' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f629', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f629', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f629', 'date': '20210521' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f62b': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f62b', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f62b', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f62b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f62b', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f62b', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62b', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f62b', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f62b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f62b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f62b', 'date': '20220203' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f62b', 'date': '20210218' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f62b', 'date': '20220406' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f62b', 'date': '20220406' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f62b', 'date': '20221101' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f62b', 'date': '20220110' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f62b', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f62b', 'date': '20210831' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f428', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f62b', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f62b', 'date': '20210831' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f62b', 'date': '20210831' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f62b', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f62b', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f62b', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f62b', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f62b', 'date': '20210831' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f62b', 'date': '20210218' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f62b', 'date': '20210218' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f62b', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f62b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f62b', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f62b', 'date': '20210521' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f62b', 'date': '20210831' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f62b', 'date': '20220815' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f62b', 'date': '20210831' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f62b', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f62b', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f62b', 'date': '20210521' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f924': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f924', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f924', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f924',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f924', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f924', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f924',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f924',
        'date': '20210218'
      },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f924', 'date': '20220203' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f924', 'date': '20210218' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f924', 'date': '20220406' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f924', 'date': '20220406' },
      { 'leftEmoji': '1f924', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f924', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f924', 'date': '20221101' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f924', 'date': '20220110' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f924', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f924', 'date': '20210831' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f924', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f924', 'date': '20210831' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f924', 'date': '20210831' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f924', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f924', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f924', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f924', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f924', 'date': '20210831' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f924', 'date': '20210218' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f924', 'date': '20210218' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f924', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f924', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f924', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f924', 'date': '20210521' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f924', 'date': '20210831' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f924', 'date': '20220815' },
      { 'leftEmoji': '1f924', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f924', 'date': '20210831' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f924', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f924', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f924', 'date': '20210521' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f971': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f971', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f971', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f971',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f971', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f971', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f971', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f971',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f971',
        'date': '20210218'
      },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f971', 'date': '20220203' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f971', 'date': '20210218' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f971', 'date': '20220406' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f971', 'date': '20220406' },
      { 'leftEmoji': '1f971', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f971', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f971', 'date': '20221101' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f971', 'date': '20220110' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f971', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f971', 'date': '20210831' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f971', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f971', 'date': '20210831' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f971', 'date': '20210831' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f971', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f971', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f971', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f971', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f971', 'date': '20210831' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f971', 'date': '20210218' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f971', 'date': '20210218' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f971', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f971', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f971', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f971', 'date': '20210521' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f971', 'date': '20210831' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f971', 'date': '20220815' },
      { 'leftEmoji': '1f971', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f971', 'date': '20210831' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f971', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f971', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f971', 'date': '20210521' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f634': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f634', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f607', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f634', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f634',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f634', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f634', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f634', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f31b', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f31c', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f31e', 'date': '20230301' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f634', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f634',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f383', 'date': '20230301' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f4ab', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f573-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f38a', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '2665-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f49d', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f496', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f493', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f49e', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f634',
        'date': '20210218'
      },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f634', 'date': '20220203' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f634', 'date': '20210218' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f634', 'date': '20220406' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f634', 'date': '20220406' },
      { 'leftEmoji': '1f634', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '26c4', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f634', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f634', 'date': '20221101' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f30d', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f648', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f634', 'date': '20220110' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f634', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f634', 'date': '20210831' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f428', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f42d', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f634', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f634', 'date': '20210831' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f422', 'date': '20230301' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f634', 'date': '20210831' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f98c', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f999', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f9a5', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f987', 'date': '20230301' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f634', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f634', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f634', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f634', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f634', 'date': '20210831' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f419', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f634', 'date': '20210218' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f634', 'date': '20210218' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f41d', 'date': '20230301' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f634', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f634', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f634', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f34d', 'date': '20230301' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f634', 'date': '20210521' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f634', 'date': '20210831' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f9c0', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f32d', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f634', 'date': '20220815' },
      { 'leftEmoji': '1f634', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f634', 'date': '20210831' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f388', 'date': '20230301' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f634', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f634', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f634', 'date': '20210521' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f451', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f52e', 'date': '20230301' }
    ],
    '1f62a': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f62a', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f62a', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f62a',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f62a', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62a', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f62a', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f62a',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f62a',
        'date': '20210218'
      },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f62a', 'date': '20220203' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f62a', 'date': '20210218' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f62a', 'date': '20220406' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f62a', 'date': '20220406' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f62a', 'date': '20221101' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f62a', 'date': '20220110' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f62a', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f62a', 'date': '20210831' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f62a', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f62a', 'date': '20210831' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f62a', 'date': '20210831' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f62a', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f62a', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f62a', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f62a', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f62a', 'date': '20210831' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f62a', 'date': '20210218' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f62a', 'date': '20210218' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f62a', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f62a', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f62a', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f62a', 'date': '20210521' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f62a', 'date': '20210831' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f62a', 'date': '20220815' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f62a', 'date': '20210831' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f62a', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f62a', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f62a', 'date': '20210521' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f31b': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f31b', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f31b', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f31b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f31b', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f31b', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f31b', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f31b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f31b', 'date': '20230301' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f31b', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f31b', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f31b', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f31b', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f31b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f31b', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f31b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f31b', 'date': '20220815' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f31b', 'date': '20210218' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f31b', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f31b', 'date': '20220406' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f31b', 'date': '20221101' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f31b', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f31b', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f31b', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f31b', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f31b', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f31b', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f31b', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f31b', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f31b', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f31b', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f31b', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f31b', 'date': '20210218' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f31b', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f31b', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f31b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f31b', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f31b', 'date': '20210521' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f31b', 'date': '20210831' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f31b', 'date': '20220815' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f31b', 'date': '20210831' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f31b', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f31b', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f31b', 'date': '20210521' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f31c': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f31c', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f31c', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f31c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f31c', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f31c', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f31c', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f31c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f31c', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f31c', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f31c', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f31c', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f31c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f31c', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f31c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f31c', 'date': '20220815' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f31c', 'date': '20210218' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f31c', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f31c', 'date': '20220406' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f31c', 'date': '20221101' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f31c', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f31c', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f31c', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f31c', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f31c', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f31c', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f31c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f31c', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f31c', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f31c', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f31c', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f31c', 'date': '20210218' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f31c', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f31c', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f31c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f31c', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f31c', 'date': '20210521' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f31c', 'date': '20210831' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f31c', 'date': '20220815' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f31c', 'date': '20210831' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f31c', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f31c', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f31c', 'date': '20210521' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f31e': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f31e', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f31e', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f31e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f31e', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f31e', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f31e', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f31e', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f31e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f31e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f31e', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f31e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f31e', 'date': '20220815' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f31e', 'date': '20210218' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f31e', 'date': '20220406' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f31e', 'date': '20220406' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f31e', 'date': '20221101' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f31e', 'date': '20220110' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f31e', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f31e', 'date': '20210831' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f31e', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f31e', 'date': '20210831' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f31e', 'date': '20210831' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f31e', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f31e', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f31e', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f31e', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f31e', 'date': '20210831' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f31e', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f31e', 'date': '20230301' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f31e', 'date': '20210218' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f31e', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f31e', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f31e', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f31e', 'date': '20210521' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f31e', 'date': '20210831' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f31e', 'date': '20220815' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f31e', 'date': '20210831' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f31e', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f31e', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f31e', 'date': '20210521' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f922': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f922', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f922', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f922',
        'date': '20210218'
      },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f922', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f922',
        'date': '20210218'
      },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f922',
        'date': '20210218'
      },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f922', 'date': '20220203' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f922', 'date': '20210218' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f922', 'date': '20220406' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f922', 'date': '20220406' },
      { 'leftEmoji': '1f922', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f922', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f922', 'date': '20221101' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f922', 'date': '20220110' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f922', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f922', 'date': '20210831' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f922', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f922', 'date': '20210831' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f922', 'date': '20210831' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f922', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f922', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f922', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f922', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f922', 'date': '20210831' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f922', 'date': '20210218' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f922', 'date': '20210218' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f922', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f922', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f922', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f922', 'date': '20210521' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f922', 'date': '20210831' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f922', 'date': '20220815' },
      { 'leftEmoji': '1f922', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f922', 'date': '20210831' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f922', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f922', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f922', 'date': '20210521' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f92e': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f92e', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f92e', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f92e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92e', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f92e', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f92e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f92e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f92e', 'date': '20220203' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f92e', 'date': '20210218' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f92e', 'date': '20220406' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f92e', 'date': '20220406' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f92e', 'date': '20221101' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f92e', 'date': '20220110' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f92e', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f92e', 'date': '20210831' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f92e', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f92e', 'date': '20210831' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f92e', 'date': '20210831' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f92e', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f92e', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f92e', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f92e', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f92e', 'date': '20210831' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f92e', 'date': '20210218' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f92e', 'date': '20210218' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f92e', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f92e', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f92e', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f92e', 'date': '20210521' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f92e', 'date': '20210831' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f92e', 'date': '20220815' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f92e', 'date': '20210831' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f92e', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f92e', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f92e', 'date': '20210521' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f927': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f927', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f927', 'date': '20230301' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f927',
        'date': '20210218'
      },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f927', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f927',
        'date': '20210218'
      },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f927',
        'date': '20210218'
      },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f927', 'date': '20220203' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f927', 'date': '20210218' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f927', 'date': '20220406' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f927', 'date': '20220406' },
      { 'leftEmoji': '1f927', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f927', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f927', 'date': '20221101' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f927', 'date': '20220110' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f927', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f927', 'date': '20210831' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f927', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f927', 'date': '20210831' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f927', 'date': '20210831' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f927', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f927', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f927', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f927', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f927', 'date': '20210831' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f927', 'date': '20210218' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f927', 'date': '20210218' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f927', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f927', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f927', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f927', 'date': '20210521' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f927', 'date': '20210831' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f927', 'date': '20220815' },
      { 'leftEmoji': '1f927', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f927', 'date': '20210831' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f927', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f927', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f927', 'date': '20210521' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f912': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f912', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f912', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f912',
        'date': '20210218'
      },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f912', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f912', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f912', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f912',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f912', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f912',
        'date': '20210218'
      },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f912', 'date': '20220203' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f912', 'date': '20210218' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f912', 'date': '20220406' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f912', 'date': '20220406' },
      { 'leftEmoji': '1f912', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f912', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f912', 'date': '20221101' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f912', 'date': '20220110' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f912', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f912', 'date': '20210831' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f912', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f912', 'date': '20210831' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f912', 'date': '20210831' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f912', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f912', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f912', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f912', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f912', 'date': '20210831' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f912', 'date': '20210218' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f912', 'date': '20210218' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f912', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f912', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f912', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f912', 'date': '20210521' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f912', 'date': '20210831' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f912', 'date': '20220815' },
      { 'leftEmoji': '1f912', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f912', 'date': '20210831' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f912', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f912', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f912', 'date': '20210521' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f915': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f915', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f915', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f915',
        'date': '20210218'
      },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f915', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f915', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f915',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f915',
        'date': '20210218'
      },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f915', 'date': '20220203' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f915', 'date': '20210218' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f915', 'date': '20220406' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f915', 'date': '20220406' },
      { 'leftEmoji': '1f915', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f915', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f915', 'date': '20221101' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f915', 'date': '20220110' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f915', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f915', 'date': '20210831' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f915', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f915', 'date': '20210831' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f915', 'date': '20210831' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f915', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f915', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f915', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f915', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f915', 'date': '20210831' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f915', 'date': '20210218' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f915', 'date': '20210218' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f915', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f915', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f915', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f915', 'date': '20210521' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f915', 'date': '20210831' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f915', 'date': '20220815' },
      { 'leftEmoji': '1f915', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f915', 'date': '20210831' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f915', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f915', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f915', 'date': '20210521' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f974': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f974', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f974', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f974', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f974',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f974', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f974', 'date': '20230301' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f974', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f974',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f974',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f974', 'date': '20220203' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f974', 'date': '20210218' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f974', 'date': '20220406' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f974', 'date': '20220406' },
      { 'leftEmoji': '1f974', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f974', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f974', 'date': '20221101' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f974', 'date': '20220110' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f974', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f974', 'date': '20210831' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f974', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f974', 'date': '20210831' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f974', 'date': '20210831' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f974', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f974', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f974', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f974', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f974', 'date': '20210831' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f974', 'date': '20210218' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f974', 'date': '20210218' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f974', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f974', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f974', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f974', 'date': '20210521' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f974', 'date': '20210831' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f974', 'date': '20220815' },
      { 'leftEmoji': '1f974', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f974', 'date': '20210831' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f974', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f974', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f974', 'date': '20210521' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f635': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f635', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f635', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f635', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f635', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f635', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f635', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f631', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f635', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f635',
        'date': '20210218'
      },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f635', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f635', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f635', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f635',
        'date': '20210218'
      },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f922', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f92e', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f927', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f635',
        'date': '20210218'
      },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f635', 'date': '20220203' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f635', 'date': '20210218' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f635', 'date': '20220406' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f635', 'date': '20220406' },
      { 'leftEmoji': '1f635', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f635', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f635', 'date': '20221101' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f635', 'date': '20220110' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f635', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f635', 'date': '20210831' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f635', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f635', 'date': '20210831' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f635', 'date': '20210831' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f635', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f635', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f635', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f635', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f635', 'date': '20210831' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f635', 'date': '20210218' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f635', 'date': '20210218' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f635', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f635', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f635', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f635', 'date': '20210521' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f635', 'date': '20210831' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f635', 'date': '20220815' },
      { 'leftEmoji': '1f635', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f635', 'date': '20210831' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f635', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f635', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f635', 'date': '20210521' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f975': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f975', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f975', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f975',
        'date': '20210218'
      },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f975', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f975',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f975',
        'date': '20210218'
      },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f975', 'date': '20220203' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f975', 'date': '20210218' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f975', 'date': '20220406' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f975', 'date': '20220406' },
      { 'leftEmoji': '1f975', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f975', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f975', 'date': '20221101' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f975', 'date': '20220110' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f975', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f975', 'date': '20210831' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f975', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f975', 'date': '20210831' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f975', 'date': '20210831' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f975', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f975', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f975', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f975', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f975', 'date': '20210831' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f975', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f975', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f975', 'date': '20210218' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f975', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f975', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f975', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f975', 'date': '20210521' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f975', 'date': '20210831' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f975', 'date': '20220815' },
      { 'leftEmoji': '1f975', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f975', 'date': '20210831' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f975', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f975', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f975', 'date': '20210521' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f976': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f976', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f976', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f976',
        'date': '20210218'
      },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f92c', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f976', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f976',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f976', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f38a', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f976',
        'date': '20210218'
      },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f976', 'date': '20220203' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f976', 'date': '20210218' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f976', 'date': '20220406' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f976', 'date': '20220406' },
      { 'leftEmoji': '1f976', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '26c4', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f976', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f976', 'date': '20221101' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f976', 'date': '20220110' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f976', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f976', 'date': '20210831' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f42d', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f976', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f976', 'date': '20210831' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f422', 'date': '20230301' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f976', 'date': '20210831' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f976', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f976', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f976', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f976', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f976', 'date': '20210831' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f976', 'date': '20210218' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f976', 'date': '20210218' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f41d', 'date': '20230301' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f976', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f976', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f976', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f34d', 'date': '20230301' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f976', 'date': '20210521' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f976', 'date': '20210831' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f976', 'date': '20220815' },
      { 'leftEmoji': '1f976', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f976', 'date': '20210831' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f388', 'date': '20230301' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f976', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f976', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f976', 'date': '20210521' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f637': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f637', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f611', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f610', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f636', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f637', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f637',
        'date': '20210218'
      },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f624', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f620', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f621', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f92c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f637', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f637', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f637',
        'date': '20210218'
      },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f974', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f975', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f976', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f912', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f915', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f637', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f637',
        'date': '20210218'
      },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f637', 'date': '20220203' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f637', 'date': '20210218' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f637', 'date': '20220406' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f637', 'date': '20220406' },
      { 'leftEmoji': '1f637', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f637', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f637', 'date': '20221101' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f637', 'date': '20220110' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f637', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f637', 'date': '20210831' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f637', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f637', 'date': '20210831' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f637', 'date': '20210831' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f637', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f637', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f637', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f637', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f637', 'date': '20210831' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f637', 'date': '20210218' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f637', 'date': '20210218' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f637', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f637', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f637', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f637', 'date': '20210521' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f637', 'date': '20210831' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f637', 'date': '20220815' },
      { 'leftEmoji': '1f637', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f637', 'date': '20210831' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f637', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f637', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f637', 'date': '20210521' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f607': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f607', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f607', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f607', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f607',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f607', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f607', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f607', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f607',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f607',
        'date': '20210218'
      },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f607', 'date': '20220203' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f607', 'date': '20210218' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f607', 'date': '20220406' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f607', 'date': '20220406' },
      { 'leftEmoji': '1f607', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f607', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f607', 'date': '20221101' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f607', 'date': '20220110' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f607', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f607', 'date': '20210831' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f607', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f607', 'date': '20210831' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f607', 'date': '20210831' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f607', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f607', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f607', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f607', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f607', 'date': '20210831' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f607', 'date': '20210218' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f607', 'date': '20210218' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f607', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f607', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f607', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f607', 'date': '20210521' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f607', 'date': '20210831' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f607', 'date': '20220815' },
      { 'leftEmoji': '1f607', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f607', 'date': '20210831' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f607', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f607', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f607', 'date': '20210521' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f920': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f920', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f920', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f920', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f920', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f920', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f920', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f920',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f920', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f920', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f920', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f920',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f920', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f920', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f920', 'date': '20221101' },
      { 'leftEmoji': '1f920', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f920',
        'date': '20210218'
      },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f920', 'date': '20220203' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f920', 'date': '20210218' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f920', 'date': '20220406' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f920', 'date': '20220406' },
      { 'leftEmoji': '1f920', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f920', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f920', 'date': '20221101' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f920', 'date': '20220110' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f920', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f920', 'date': '20210831' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f428', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f920', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f920', 'date': '20210831' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f920', 'date': '20210831' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f920', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f920', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f920', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f920', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f920', 'date': '20210831' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f920', 'date': '20210218' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f920', 'date': '20210218' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f920', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f920', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f920', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f920', 'date': '20210521' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f920', 'date': '20210831' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f920', 'date': '20220815' },
      { 'leftEmoji': '1f920', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f920', 'date': '20210831' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f920', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f920', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f920', 'date': '20210521' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f911': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f911', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f911', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f911',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f911', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f911',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f911',
        'date': '20210218'
      },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f911', 'date': '20220203' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f911', 'date': '20210218' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f911', 'date': '20220406' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f911', 'date': '20220406' },
      { 'leftEmoji': '1f911', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f911', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f911', 'date': '20221101' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f911', 'date': '20220110' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f911', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f911', 'date': '20210831' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f911', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f911', 'date': '20210831' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f911', 'date': '20210831' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f911', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f911', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f911', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f911', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f911', 'date': '20210831' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f911', 'date': '20210218' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f911', 'date': '20210218' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f911', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f911', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f911', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f911', 'date': '20210521' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f911', 'date': '20210831' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f911', 'date': '20220815' },
      { 'leftEmoji': '1f911', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f911', 'date': '20210831' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f911', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f911', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f911', 'date': '20210521' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f60e': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f60e', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f60e', 'date': '20230301' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f60e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f31a', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f31d', 'date': '20220815' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f60e', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f60e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4a5', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fae7', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f60e', 'date': '20221101' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f60e', 'date': '20221101' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f60e', 'date': '20221101' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f60e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9e0', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fac0', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fac1', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fa78', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9b7', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9b4', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f60e', 'date': '20220203' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f33a', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f60e', 'date': '20210218' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3f5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f33b', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f60e', 'date': '20220406' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f331', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f33f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f343', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f340', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f334', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f333', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fab9', 'date': '20220815' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f60e', 'date': '20220406' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f30a', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f32c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f300', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f30b', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3d6-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '26c5', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f60e', 'date': '20221101' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f327-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f329-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4a7', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2602-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2604-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fa90', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f60e', 'date': '20220110' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f60e', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f60e', 'date': '20210831' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f60e', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f60e', 'date': '20210831' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f60e', 'date': '20210831' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f60e', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f60e', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f60e', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f60e', 'date': '20221101' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f54a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9a9', 'date': '20220815' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f60e', 'date': '20210831' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f99e', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f980', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fab8', 'date': '20220815' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f60e', 'date': '20210218' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f41a', 'date': '20220815' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f60e', 'date': '20210218' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f997', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fab2', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fab3', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f41e', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f98b', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f43e', 'date': '20220815' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f60e', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f60e', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f60e', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f60e', 'date': '20210521' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f965', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f360', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9c5', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9c4', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f954', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f60e', 'date': '20210831' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f373', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f96a', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fad4', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f32e', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f32f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f35d', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f96b', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f963', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f957', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f372', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f35b', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f35c', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f35a', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f359', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f365', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f960', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f367', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f368', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f366', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f60e', 'date': '20221101' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f36b', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f37c', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f375', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f379', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f6d1', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f6a8', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f6df', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2693', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f697', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3ce-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f695', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f68c', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f682', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f6f8', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f680', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2708-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3a2', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3a1', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3aa', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3db-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3df-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3e0', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3d5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f60e', 'date': '20210831' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f380', 'date': '20220815' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1faa9', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f947', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f948', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f949', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3c5', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f396-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f60e', 'date': '20220406' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '26be', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f94e', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3c0', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3d0', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3c8', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3c9', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3be', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f945', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3f8', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f94d', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3cf', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3d1', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3d2', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f94c', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f6f7', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3bf', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '26f8-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f6fc', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fa70', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f6f9', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '26f3', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3af', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3f9', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f94f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fa83', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fa81', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f93f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3bd', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f94b', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f94a', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3b1', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3d3', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3b3', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '265f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fa80', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9e9', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3ae', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3b2', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3b0', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3b4', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f004', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f0cf', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4f7', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3a8', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f58c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f58d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1faa1', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9f5', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9f6', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3b9', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3b7', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3ba', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3b8', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fa95', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3bb', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fa98', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f941', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fa97', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3a4', 'date': '20220815' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f60e', 'date': '20210521' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f399-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4fa', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f39e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3ac', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3ad', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f39f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4f1', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '260e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f50b', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1faab', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4be', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4bf', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4b8', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2696-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4a1', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9fc', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9e6', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f6e0-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '26d3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f5d1-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f58a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2712-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '270f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4da', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f5c3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4e3', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f50e', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9ff', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f5dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f512', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2648', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2649', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '264a', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '264b', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '264c', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '264d', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '264e', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '264f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2650', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2651', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2652', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2653', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '26ce', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2757', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2753', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2049-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f198', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4f4', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f508', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '26a0-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '267b-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2705', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f195', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f193', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f199', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f197', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f192', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f6ae', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '262e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '262f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '267e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2716-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2795', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2796', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2797', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '27b0', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '27bf', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '3030-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2122-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2660-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f5ef-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4ac', 'date': '20220815' }
    ],
    '1f913': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f913', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f913', 'date': '20230301' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f913',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f31b', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f913', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f913',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f916', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f573-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f913', 'date': '20221101' },
      { 'leftEmoji': '1f913', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f49b', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f913', 'date': '20221101' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f913', 'date': '20221101' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f5a4', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f913',
        'date': '20210218'
      },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f441-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f913', 'date': '20220203' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f339', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f913', 'date': '20210218' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f913', 'date': '20220406' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f913', 'date': '20220406' },
      { 'leftEmoji': '1f913', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '26c4', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f913', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f913', 'date': '20221101' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f913', 'date': '20220110' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f913', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f913', 'date': '20210831' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f428', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f42d', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f913', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f913', 'date': '20210831' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f913', 'date': '20210831' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f9a5', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f913', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f913', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f913', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f913', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f913', 'date': '20210831' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f913', 'date': '20210218' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f913', 'date': '20210218' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f913', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f913', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f913', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f34d', 'date': '20230301' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f913', 'date': '20210521' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f913', 'date': '20210831' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f913', 'date': '20220815' },
      { 'leftEmoji': '1f913', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f913', 'date': '20210831' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f913', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f913', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f913', 'date': '20210521' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f978': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f978', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f972', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f978', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f978',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f978', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f978',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f978', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f978', 'date': '20220815' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f978', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f978',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f9a0', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f978', 'date': '20220203' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f978', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f978', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f332', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f978', 'date': '20220406' },
      { 'leftEmoji': '1f978', 'rightEmoji': '26c4', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f978', 'date': '20221101' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f648', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f978', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f978', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f978', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f978', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f978', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f978', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f978', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f978', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f978', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f978', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f978', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f978', 'date': '20210218' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f978', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f978', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f978', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f978', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f978', 'date': '20210521' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f336-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f951', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f978', 'date': '20210831' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f9c0', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f382', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f978', 'date': '20220815' },
      { 'leftEmoji': '1f978', 'rightEmoji': '2615', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f978', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f978', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f978', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f978', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f4f0', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f52e', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f925': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f925', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f925', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f925',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f925', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f925',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f925',
        'date': '20210218'
      },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f925', 'date': '20220203' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f925', 'date': '20210218' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f925', 'date': '20220406' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f925', 'date': '20220406' },
      { 'leftEmoji': '1f925', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f925', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f925', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f925', 'date': '20221101' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f925', 'date': '20220110' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f925', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f925', 'date': '20210831' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f925', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f925', 'date': '20210831' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f925', 'date': '20210831' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f925', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f925', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f925', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f925', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f925', 'date': '20210831' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f419', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f925', 'date': '20210218' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f925', 'date': '20210218' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f925', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f925', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f925', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f925', 'date': '20210521' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f925', 'date': '20210831' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f925', 'date': '20220815' },
      { 'leftEmoji': '1f925', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f925', 'date': '20210831' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f925', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f925', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f925', 'date': '20210521' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f921': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f921', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f921', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f921',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f921', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f921',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f921', 'date': '20221101' },
      { 'leftEmoji': '1f921', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f49b', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f921', 'date': '20221101' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f921', 'date': '20221101' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f5a4', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f921',
        'date': '20210218'
      },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f921', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f921', 'date': '20220815' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f921', 'date': '20210218' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f921', 'date': '20220406' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f921', 'date': '20220406' },
      { 'leftEmoji': '1f921', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f921', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f921', 'date': '20221101' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f921', 'date': '20220110' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f921', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f921', 'date': '20210831' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f428', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f42d', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f921', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f921', 'date': '20210831' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f422', 'date': '20230301' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f921', 'date': '20210831' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f921', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f921', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f921', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f921', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f921', 'date': '20210831' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f921', 'date': '20210218' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f921', 'date': '20210218' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f921', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f921', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f921', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f921', 'date': '20210521' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f921', 'date': '20210831' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f921', 'date': '20220815' },
      { 'leftEmoji': '1f921', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f921', 'date': '20210831' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f388', 'date': '20230301' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f921', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f921', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f921', 'date': '20210521' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f47b': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f47b', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f47b', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f47b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f31a', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f31d', 'date': '20220815' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f47b', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f47b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f47b', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4a5', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fae7', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f47b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9e0', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fa78', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9b7', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9b4', 'date': '20220815' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f47b', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f47b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f47b', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f33a', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f47b', 'date': '20210218' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3f5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f47b', 'date': '20220406' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f340', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f333', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f47b', 'date': '20220406' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f30a', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f300', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f47b', 'date': '20221101' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2602-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f47b', 'date': '20220110' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f47b', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f47b', 'date': '20210831' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f47b', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f47b', 'date': '20210831' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f47b', 'date': '20210831' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f47b', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f47b', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f47b', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f47b', 'date': '20221101' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f54a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f47b', 'date': '20210831' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f47b', 'date': '20210218' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f47b', 'date': '20210218' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f43e', 'date': '20220815' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f47b', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f47b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f47b', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f47b', 'date': '20210521' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f965', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f360', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9c5', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9c4', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f954', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f47b', 'date': '20210831' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f96a', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fad4', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f32e', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f32f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f35d', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f96b', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f963', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f957', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f372', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f35b', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f35c', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f35a', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f359', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f365', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f960', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f47b', 'date': '20221101' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f36b', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f37c', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f375', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f6d1', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f6a8', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f6df', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2693', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3ce-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f682', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f6f8', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f680', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2708-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3a1', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3aa', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3e0', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f47b', 'date': '20210831' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f380', 'date': '20220815' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1faa9', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f947', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f948', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f949', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3c5', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f396-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f47b', 'date': '20220406' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '26be', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f94e', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3c0', 'date': '20220815' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f47b', 'date': '20230126' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3d0', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3c8', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3c9', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3be', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f945', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3f8', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f94d', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3cf', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3d1', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3d2', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f94c', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f6f7', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3bf', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '26f8-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f6fc', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fa70', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f6f9', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '26f3', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3af', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3f9', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f94f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fa83', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fa81', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f93f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3bd', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f94b', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f94a', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3b1', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3d3', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3b3', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '265f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fa80', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9e9', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3ae', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3b2', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3b0', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3b4', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f004', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f0cf', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4f7', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3a8', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f58c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f58d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1faa1', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9f5', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9f6', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3b9', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3b7', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3ba', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3b8', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fa95', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3bb', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fa98', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f941', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fa97', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3a4', 'date': '20220815' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f47b', 'date': '20210521' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f399-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4fa', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f39e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3ac', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3ad', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f39f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4f1', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '260e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f50b', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1faab', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4bf', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4b8', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2696-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9e6', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '26d3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f58a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2712-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '270f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4da', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f5c3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4e3', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f50e', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f5dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f512', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2648', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2649', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '264a', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '264b', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '264c', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '264d', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '264e', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '264f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2650', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2651', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2652', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2653', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '26ce', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2757', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2753', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2049-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f198', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4f4', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f508', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '267b-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2705', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f195', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f193', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f199', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f197', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f192', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f6ae', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '262e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '262f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '267e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2716-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2795', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2796', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2797', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '27b0', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '27bf', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '3030-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2122-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2660-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f5ef-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4ac', 'date': '20220815' }
    ],
    '1f4a9': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4a9', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f4a9', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f4a9',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f4a9', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f4a9',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f4a9',
        'date': '20210218'
      },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f4a9', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f4a9', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4a9', 'date': '20220815' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f4a9', 'date': '20210218' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f4a9', 'date': '20220406' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f4a9', 'date': '20220406' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4a9', 'date': '20221101' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f4a9', 'date': '20220110' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f4a9', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f4a9', 'date': '20210831' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f4a9', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f4a9', 'date': '20210831' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f4a9', 'date': '20210831' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f4a9', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f4a9', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f4a9', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f4a9', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f4a9', 'date': '20210831' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f4a9', 'date': '20210218' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f4a9', 'date': '20210218' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f4a9', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f4a9', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f4a9', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f4a9', 'date': '20210521' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f4a9', 'date': '20210831' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f4a9', 'date': '20220815' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f4a9', 'date': '20210831' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f4a9', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f4a9', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f4a9', 'date': '20210521' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f47d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f47d', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f47d', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f47d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f47d', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f47d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f47d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f47d', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f47d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f47d', 'date': '20220815' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f47d', 'date': '20210218' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f47d', 'date': '20220406' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f47d', 'date': '20220406' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f47d', 'date': '20221101' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f47d', 'date': '20220110' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f47d', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f47d', 'date': '20210831' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f47d', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f47d', 'date': '20210831' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f47d', 'date': '20210831' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f47d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f47d', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f47d', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f47d', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f47d', 'date': '20210831' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f47d', 'date': '20210218' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f47d', 'date': '20210218' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f47d', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f47d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f47d', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f47d', 'date': '20210521' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f47d', 'date': '20210831' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f47d', 'date': '20220815' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f47d', 'date': '20210831' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f47d', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f47d', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f47d', 'date': '20210521' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f916': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f916', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f916', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f916',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f916', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f916', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f916', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f916',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f916', 'date': '20230301' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f38a', 'date': '20230301' },
      { 'leftEmoji': '1f916', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f916',
        'date': '20210218'
      },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f916', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f916', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f916', 'date': '20220815' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f916', 'date': '20210218' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f916', 'date': '20220406' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f916', 'date': '20220406' },
      { 'leftEmoji': '1f916', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f916', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f916', 'date': '20221101' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f916', 'date': '20220110' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f916', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f916', 'date': '20210831' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f916', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f916', 'date': '20210831' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f916', 'date': '20210831' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f916', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f916', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f916', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f916', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f916', 'date': '20210831' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f916', 'date': '20210218' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f916', 'date': '20210218' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f916', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f916', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f916', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f916', 'date': '20210521' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f916', 'date': '20210831' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f916', 'date': '20220815' },
      { 'leftEmoji': '1f916', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f916', 'date': '20210831' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f916', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f916', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f916', 'date': '20210521' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f383': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f383', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f383', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f383',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f383', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f383', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f383', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f383',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f383', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f383',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f383', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f383', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f383', 'date': '20220815' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f383', 'date': '20210218' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f383', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f383', 'date': '20220406' },
      { 'leftEmoji': '1f383', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f383', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f383', 'date': '20221101' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f383', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f383', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f383', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f383', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f383', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f383', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f383', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f383', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f383', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f383', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f383', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f383', 'date': '20210218' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f383', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f383', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f383', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f383', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f383', 'date': '20210521' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f383', 'date': '20210831' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f383', 'date': '20220815' },
      { 'leftEmoji': '1f383', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f383', 'date': '20210831' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f383', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f383', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f383', 'date': '20210521' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f608': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f608', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f608', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f608',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f608', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f608', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f608',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f608',
        'date': '20210218'
      },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f608', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f608', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f608', 'date': '20220815' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f608', 'date': '20210218' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f608', 'date': '20220406' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f608', 'date': '20220406' },
      { 'leftEmoji': '1f608', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f608', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f608', 'date': '20221101' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f608', 'date': '20220110' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f608', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f608', 'date': '20210831' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f608', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f608', 'date': '20210831' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f608', 'date': '20210831' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f608', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f608', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f608', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f608', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f608', 'date': '20210831' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f608', 'date': '20210218' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f608', 'date': '20210218' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f608', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f608', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f608', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f608', 'date': '20210521' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f608', 'date': '20210831' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f608', 'date': '20220815' },
      { 'leftEmoji': '1f608', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f608', 'date': '20210831' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f608', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f608', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f608', 'date': '20210521' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f47f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f47f', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f973', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f607', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f971', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f47f', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f47f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f634', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f62a', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f924', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f31e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f47f', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f47f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f920', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f911', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f60e', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f913', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f925', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f921', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f4a9', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f608', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f47f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f47f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f47f', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f47f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f47f', 'date': '20220815' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f47f', 'date': '20210218' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f47f', 'date': '20220406' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f47f', 'date': '20220406' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f47f', 'date': '20221101' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f47f', 'date': '20220110' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f47f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f47f', 'date': '20210831' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f47f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f47f', 'date': '20210831' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f47f', 'date': '20210831' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f47f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f47f', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f47f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f47f', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f47f', 'date': '20210831' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f47f', 'date': '20210218' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f47f', 'date': '20210218' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f47f', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f47f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f47f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f47f', 'date': '20210521' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f47f', 'date': '20210831' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f47f', 'date': '20220815' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f47f', 'date': '20210831' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f47f', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f47f', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f47f', 'date': '20210521' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f573-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f573-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '263a-fe0f',
        'rightEmoji': '1f573-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f573-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f573-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f573-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2639-fe0f',
        'rightEmoji': '1f573-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f573-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f573-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f573-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f573-fe0f',
        'rightEmoji': '1f573-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f573-fe0f', 'date': '20221101' },
      {
        'leftEmoji': '2764-fe0f',
        'rightEmoji': '1f573-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f573-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f573-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f573-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f573-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f573-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f573-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f573-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f573-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f573-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f573-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '26c4', 'date': '20201001' },
      {
        'leftEmoji': '1f32a-fe0f',
        'rightEmoji': '1f573-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '1f573-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20221107'
      },
      {
        'leftEmoji': '1f573-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f573-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f648', 'date': '20230301' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f573-fe0f', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f573-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f573-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f573-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f573-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f573-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f573-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f573-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f573-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f573-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f573-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f573-fe0f', 'date': '20210218' },
      {
        'leftEmoji': '1f573-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '1f573-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20230301'
      },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f573-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f573-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f573-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f573-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f573-fe0f', 'date': '20210521' },
      {
        'leftEmoji': '1f573-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f573-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f573-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      {
        'leftEmoji': '1f573-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '1f573-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20230301'
      },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f573-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f573-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f573-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f573-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f38a': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f38a', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f38a', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f38a',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f38a', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f38a', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f38a', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f38a',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f38a',
        'date': '20230301'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f38a', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f38a', 'date': '20230301' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4a5', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f38a', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fae7', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f38a', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f38a', 'date': '20230301' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f38a', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f38a', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f38a', 'date': '20230301' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f38a', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f38a', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f38a',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9e0', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fac0', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fac1', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fa78', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9b7', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9b4', 'date': '20220815' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f38a', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f38a', 'date': '20221101' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f38a', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f33a', 'date': '20220815' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f38a', 'date': '20210218' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3f5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f38a', 'date': '20220406' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f340', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f38a', 'date': '20220406' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f300', 'date': '20220815' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f38a', 'date': '20221101' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2602-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f38a', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f38a', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f38a', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f38a', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f38a', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f38a', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f38a', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f38a', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f38a', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f38a', 'date': '20221101' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f54a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f38a', 'date': '20210831' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f38a', 'date': '20210218' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f38a', 'date': '20210218' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f43e', 'date': '20220815' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f38a', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f38a', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f38a', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f38a', 'date': '20210521' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f965', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f360', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9c5', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9c4', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f954', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f38a', 'date': '20210831' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f96a', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fad4', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f32e', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f32f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f35d', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f96b', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f963', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f957', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f372', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f35b', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f35c', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f35a', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f359', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f365', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f960', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f38a', 'date': '20221101' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f36b', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f375', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f6d1', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f6a8', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f6df', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2693', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f697', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3ce-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f695', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f68c', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f6f8', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f680', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2708-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3aa', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3e0', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f38a', 'date': '20210831' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f380', 'date': '20220815' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1faa9', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f947', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f948', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f949', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3c5', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f396-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f38a', 'date': '20220406' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '26be', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f94e', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3c0', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3d0', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3c8', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3c9', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3be', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f945', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3f8', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f94d', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3cf', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3d1', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3d2', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f94c', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f6f7', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3bf', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '26f8-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f6fc', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fa70', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f6f9', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '26f3', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3af', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3f9', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f94f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fa83', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fa81', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f93f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3bd', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f94b', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f94a', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3b1', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3d3', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3b3', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '265f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fa80', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9e9', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3ae', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3b2', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3b0', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3b4', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f004', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f0cf', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4f7', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3a8', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f58c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f58d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1faa1', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9f5', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9f6', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3b9', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3b7', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3ba', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3b8', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fa95', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3bb', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fa98', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f941', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fa97', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3a4', 'date': '20220815' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f38a', 'date': '20210521' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f399-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4fa', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f39e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3ac', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3ad', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f39f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4f1', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '260e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f50b', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1faab', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4bf', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4b8', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2696-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9e6', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '26d3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f58a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2712-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '270f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4da', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f5c3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4e3', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f50e', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f5dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f512', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2648', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2649', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '264a', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '264b', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '264c', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '264d', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '264e', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '264f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2650', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2651', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2652', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2653', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '26ce', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2757', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2753', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2049-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f198', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4f4', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f508', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '267b-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2705', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f195', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f193', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f199', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f197', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f192', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f6ae', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '262e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '262f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '267e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2716-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2795', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2796', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2797', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '27b0', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '27bf', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '3030-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2122-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f5ef-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4ac', 'date': '20220815' }
    ],
    '2665-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2665-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '2665-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2665-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '2665-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '2665-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4a5', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fae7', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f573-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '2665-fe0f', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '2665-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '2665-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f499', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '2665-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2665-fe0f', 'date': '20220823' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '2665-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9e0', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fac0', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fac1', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fa78', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9b7', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9b4', 'date': '20220506' },
      { 'leftEmoji': '1f480', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1fae6', 'rightEmoji': '2665-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2665-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f33a', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '2665-fe0f', 'date': '20210218' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4ae', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f3f5-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '2665-fe0f', 'date': '20220406' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f340', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fab4', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '2665-fe0f', 'date': '20220406' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f30a', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f32c-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f300', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f32a-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f30b', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f3d6-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2665-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f327-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f329-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2602-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26a1', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '2665-fe0f', 'date': '20220110' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '2665-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '2665-fe0f', 'date': '20210831' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '2665-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '2665-fe0f', 'date': '20210831' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '2665-fe0f', 'date': '20210831' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '2665-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '2665-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '2665-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '2665-fe0f', 'date': '20221101' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f54a-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f427', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '2665-fe0f', 'date': '20210831' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f982', 'rightEmoji': '2665-fe0f', 'date': '20210218' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f41a', 'date': '20230301' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '2665-fe0f', 'date': '20210218' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f43e', 'date': '20220506' },
      { 'leftEmoji': '1f353', 'rightEmoji': '2665-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '2665-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '2665-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f96d', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '2665-fe0f', 'date': '20210521' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f348', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f350', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f95d', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fad2', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fad0', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f347', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f965', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f345', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f955', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f360', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9c5', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f33d', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f966', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f952', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fad1', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9c4', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f954', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fad8', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f330', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f95c', 'date': '20220506' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '2665-fe0f', 'date': '20210831' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fad3', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f950', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f96f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f95e', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f969', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f356', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f354', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f96a', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f968', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f35f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fad4', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f32e', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f32f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f959', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9c6', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f958', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f35d', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f96b', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fad5', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f963', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f957', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f372', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f35b', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f35c', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f363', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f364', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f35a', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f371', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f359', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f358', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f365', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f960', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f370', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '2665-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f36b', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f369', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f36a', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9c2', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f37f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9cb', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f375', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9c9', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f379', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f6d1', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f6a8', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f6df', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2693', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f697', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f3ce-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f695', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f68c', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f6f8', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f680', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2708-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3aa', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f3db-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f3df-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3e0', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f3d5-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f307', 'rightEmoji': '2665-fe0f', 'date': '20210831' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f3dd-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f380', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1faa9', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f947', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f947', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f948', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f948', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f949', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f949', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3c5', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3c5', 'date': '20230301' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f396-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '2665-fe0f', 'date': '20220406' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26be', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26be', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f94e', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f94e', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3c0', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3c0', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3d0', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3d0', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3c8', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3c8', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3c9', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3c9', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3be', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3be', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f945', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3f8', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f94d', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3cf', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3d1', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3d2', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f94c', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f6f7', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3bf', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26f8-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f6fc', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fa70', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f6f9', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26f3', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3af', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3af', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3f9', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f94f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fa83', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fa81', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f93f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3bd', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f94b', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f94a', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b1', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b1', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3d3', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b3', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '265f-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fa80', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9e9', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3ae', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b2', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b2', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b0', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b4', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f004', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f0cf', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4f7', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3a8', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f58c-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f58d-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1faa1', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9f5', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9f6', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b9', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b7', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3ba', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b8', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fa95', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3bb', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fa98', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f941', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fa97', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3a4', 'date': '20220506' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '2665-fe0f', 'date': '20210521' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f399-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4fa', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f39e-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3ac', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3ad', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f39f-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4f1', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '260e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f50b', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1faab', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4bf', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4b8', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2696-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9e6', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f48e', 'date': '20201001' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f6e0-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26d3-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f5d1-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f58a-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2712-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '270f-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4da', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f5c3-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4e3', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f50e', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9ff', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f5dd-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f5dd-fe0f',
        'date': '20230301'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f512', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f512', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2648', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2648', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2649', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2649', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264a', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264a', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264b', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264b', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264c', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264c', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264d', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264d', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264e', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264e', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264f', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2650', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2650', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2651', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2651', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2652', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2652', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2653', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2653', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26ce', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26ce', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2757', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2753', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2049-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f198', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f198', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4f4', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f508', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '267b-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2705', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f195', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f195', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f193', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f193', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f199', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f199', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f197', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f197', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f192', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f192', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f6ae', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f6ae', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '262e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '262e-fe0f', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '262f-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '262f-fe0f', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '267e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '267e-fe0f', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2716-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2795', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2796', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2797', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '27b0', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '27bf', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '3030-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2122-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f5ef-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f5ef-fe0f',
        'date': '20230301'
      },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4ac', 'date': '20220506' }
    ],
    '1f498': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f498', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f498', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f498',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f498', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f498',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4a5', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fae7', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f498', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f498', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f498', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f498',
        'date': '20210218'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9e0', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fac0', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fac1', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fa78', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9b7', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9b4', 'date': '20220203' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f498', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f498', 'date': '20220815' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f33a', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f498', 'date': '20210218' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4ae', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3f5-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f498', 'date': '20220406' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f344', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f340', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fab4', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f498', 'date': '20220406' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f300', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f498', 'date': '20221101' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2602-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26a1', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f498', 'date': '20220110' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f498', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f498', 'date': '20210831' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f498', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f498', 'date': '20210831' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f498', 'date': '20210831' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f498', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f498', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f498', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f498', 'date': '20221101' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f54a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f498', 'date': '20210831' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f498', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f498', 'date': '20210218' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f43e', 'date': '20220203' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f498', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f498', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f498', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f96d', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f498', 'date': '20210521' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f348', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f350', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f95d', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fad2', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fad0', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f347', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f965', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f345', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f955', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f360', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9c5', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f33d', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f966', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f952', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fad1', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9c4', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f954', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fad8', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f330', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f95c', 'date': '20220203' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f498', 'date': '20210831' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fad3', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f950', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f96f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f95e', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f969', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f356', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f354', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f96a', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f968', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f35f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fad4', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f32e', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f32f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f959', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9c6', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f958', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f35d', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f96b', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fad5', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f963', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f957', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f372', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f35b', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f35c', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f363', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f364', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f35a', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f371', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f359', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f358', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f365', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f960', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f370', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f498', 'date': '20221101' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f36b', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f369', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f36a', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9c2', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f37f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9cb', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f375', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9c9', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f6d1', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f6a8', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f6df', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2693', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f697', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3ce-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f695', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f68c', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f6f8', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f680', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2708-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3aa', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3e0', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f498', 'date': '20210831' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f380', 'date': '20220203' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1faa9', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f947', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f948', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f949', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3c5', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f396-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f498', 'date': '20220406' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26bd', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26be', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f94e', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3c0', 'date': '20220203' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f498', 'date': '20230126' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3d0', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3c8', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3c9', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3be', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f945', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3f8', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f94d', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3cf', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3d1', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3d2', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f94c', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f6f7', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3bf', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26f8-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f6fc', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fa70', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f6f9', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26f3', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3af', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3f9', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f94f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fa83', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fa81', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f93f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3bd', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f94b', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f94a', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3b1', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3d3', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3b3', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '265f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fa80', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9e9', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3ae', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3b2', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3b0', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3b4', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f004', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f0cf', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4f7', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3a8', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f58c-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f58d-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1faa1', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9f5', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9f6', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3b9', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3b7', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3ba', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3b8', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fa95', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3bb', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fa98', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f941', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fa97', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3a4', 'date': '20220203' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f498', 'date': '20210521' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f399-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4fa', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f39e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3ac', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3ad', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f39f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4f1', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '260e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f50b', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1faab', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4bf', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4b8', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2696-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9e6', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26d3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f58a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2712-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '270f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4da', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f5c3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4e3', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f50e', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f5dd-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f512', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2648', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2649', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '264a', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '264b', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '264c', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '264d', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '264e', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '264f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2650', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2651', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2652', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2653', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26ce', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2757', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2753', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2049-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f198', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4f4', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f508', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '267b-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2705', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f195', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f193', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f199', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f197', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f192', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f6ae', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '262e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '262f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '267e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2716-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2795', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2796', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2797', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '27b0', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '27bf', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '3030-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '00a9-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '00ae-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2122-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f5ef-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4ac', 'date': '20220203' }
    ],
    '1f49d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f49d', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f49d', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f49d', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f49d', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f49d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f49d', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f49d', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f49d', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f49d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f49d', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f49d', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f49d', 'date': '20230301' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f49d', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f49d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f49d', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f49d', 'date': '20220815' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f49d', 'date': '20210218' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f49d', 'date': '20220406' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f49d', 'date': '20220406' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f49d', 'date': '20221101' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f49d', 'date': '20220110' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f49d', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f49d', 'date': '20210831' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f49d', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f49d', 'date': '20210831' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f49d', 'date': '20210831' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f49d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f49d', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f49d', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f49d', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f49d', 'date': '20210831' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f49d', 'date': '20210218' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f49d', 'date': '20210218' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f49d', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f49d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f49d', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f49d', 'date': '20210521' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f49d', 'date': '20210831' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f49d', 'date': '20220815' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f49d', 'date': '20210831' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f49d', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f49d', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f49d', 'date': '20210521' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f496': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f496', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f496', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f496', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f496', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f496',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f496', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f496', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f496', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f496',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f496', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f496', 'date': '20230301' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f496', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f496', 'date': '20230301' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f496', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f496', 'date': '20230301' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f496',
        'date': '20210218'
      },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f496', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f496', 'date': '20220815' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f496', 'date': '20210218' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f496', 'date': '20220406' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f496', 'date': '20220406' },
      { 'leftEmoji': '1f496', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f496', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f496', 'date': '20221101' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f496', 'date': '20220110' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f496', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f496', 'date': '20210831' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f496', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f496', 'date': '20210831' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f496', 'date': '20210831' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f496', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f496', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f496', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f496', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f496', 'date': '20210831' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f496', 'date': '20210218' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f496', 'date': '20210218' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f496', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f496', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f496', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f496', 'date': '20210521' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f496', 'date': '20210831' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f496', 'date': '20220815' },
      { 'leftEmoji': '1f496', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f496', 'date': '20210831' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f496', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f496', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f496', 'date': '20210521' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f497': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f497', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f497', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f497',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f497', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f497', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f497',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '2b50', 'date': '20230405' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f497', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f497', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f497', 'date': '20230301' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f497', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f497',
        'date': '20210218'
      },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f497', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f497', 'date': '20220815' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f497', 'date': '20210218' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f497', 'date': '20220406' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f497', 'date': '20220406' },
      { 'leftEmoji': '1f497', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f497', 'date': '20221101' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f435', 'date': '20230405' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f497', 'date': '20220110' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f497', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f497', 'date': '20210831' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f497', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f497', 'date': '20210831' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f422', 'date': '20230405' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f497', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f497', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f497', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f497', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f497', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f497', 'date': '20210831' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f497', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f497', 'date': '20230301' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f497', 'date': '20210218' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f497', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f497', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f497', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f497', 'date': '20210521' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f497', 'date': '20210831' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f497', 'date': '20220815' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f497', 'date': '20210831' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f497', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f497', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f497', 'date': '20210521' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f493': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f493', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f493', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f493', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f493',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f493', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f493', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f493', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f493', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f493',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4a5', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fae7', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f493', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f493', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f493', 'date': '20230301' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f493', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f493', 'date': '20230301' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f493',
        'date': '20210218'
      },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9e0', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fac0', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fac1', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fa78', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9b7', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9b4', 'date': '20220203' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f493', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f493', 'date': '20220815' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f33a', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f493', 'date': '20210218' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4ae', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3f5-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f493', 'date': '20220406' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f344', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f340', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fab4', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f493', 'date': '20220406' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f300', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f493', 'date': '20221101' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2602-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26a1', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f493', 'date': '20220110' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f493', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f493', 'date': '20210831' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f493', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f493', 'date': '20210831' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f493', 'date': '20210831' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f493', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f493', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f493', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f493', 'date': '20221101' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f54a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f493', 'date': '20210831' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f493', 'date': '20210218' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f493', 'date': '20210218' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f43e', 'date': '20220203' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f493', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f493', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f493', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f96d', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f493', 'date': '20210521' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f348', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f350', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f95d', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fad2', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fad0', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f347', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f965', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f345', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f955', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f360', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9c5', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f33d', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f966', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f952', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fad1', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9c4', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f954', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fad8', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f330', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f95c', 'date': '20220203' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f493', 'date': '20210831' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fad3', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f950', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f96f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f95e', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f969', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f356', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f354', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f96a', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f968', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f35f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fad4', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f32e', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f32f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f959', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9c6', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f958', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f35d', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f96b', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fad5', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f963', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f957', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f372', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f35b', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f35c', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f363', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f364', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f35a', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f371', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f359', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f358', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f365', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f960', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f370', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f493', 'date': '20221101' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f36b', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f369', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f36a', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9c2', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f37f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9cb', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f375', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9c9', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f6d1', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f6a8', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f6df', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2693', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f697', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3ce-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f695', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f68c', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f6f8', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f680', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2708-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3aa', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3e0', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f493', 'date': '20210831' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f380', 'date': '20220203' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1faa9', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f947', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f948', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f949', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3c5', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f396-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f493', 'date': '20220406' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26bd', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26be', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f94e', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3c0', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3d0', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3c8', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3c9', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3be', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f945', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3f8', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f94d', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3cf', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3d1', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3d2', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f94c', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f6f7', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3bf', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26f8-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f6fc', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fa70', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f6f9', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26f3', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3af', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3f9', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f94f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fa83', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fa81', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f93f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3bd', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f94b', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f94a', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3b1', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3d3', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3b3', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '265f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fa80', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9e9', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3ae', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3b2', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3b0', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3b4', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f004', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f0cf', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4f7', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3a8', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f58c-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f58d-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1faa1', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9f5', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9f6', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3b9', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3b7', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3ba', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3b8', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fa95', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3bb', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fa98', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f941', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fa97', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3a4', 'date': '20220203' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f493', 'date': '20210521' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f399-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4fa', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f39e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3ac', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3ad', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f39f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4f1', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '260e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f50b', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1faab', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4bf', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4b8', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2696-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9e6', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26d3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f58a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2712-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '270f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4da', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f5c3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4e3', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f50e', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f5dd-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f512', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2648', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2649', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '264a', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '264b', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '264c', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '264d', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '264e', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '264f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2650', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2651', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2652', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2653', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26ce', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2757', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2753', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2049-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f198', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4f4', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f508', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '267b-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2705', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f195', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f193', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f199', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f197', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f192', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f6ae', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '262e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '262f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '267e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2716-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2795', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2796', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2797', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '27b0', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '27bf', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '3030-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '00a9-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '00ae-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2122-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f5ef-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4ac', 'date': '20220203' }
    ],
    '1f49e': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f49e', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f49e', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f49e', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f49e', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f49e', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f49e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f49e', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f49e', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f49e', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f49e', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f49e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f49e', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f49e', 'date': '20230301' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f49e', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f49e', 'date': '20230301' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f49e', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f49e', 'date': '20230301' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f49e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f49e', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f49e', 'date': '20220815' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f49e', 'date': '20210218' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f49e', 'date': '20220406' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f49e', 'date': '20220406' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f49e', 'date': '20221101' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f49e', 'date': '20220110' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f49e', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f49e', 'date': '20210831' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f49e', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f49e', 'date': '20210831' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f49e', 'date': '20210831' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f49e', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f49e', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f49e', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f49e', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f49e', 'date': '20210831' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f49e', 'date': '20210218' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f49e', 'date': '20210218' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f49e', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f49e', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f49e', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f49e', 'date': '20210521' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f49e', 'date': '20210831' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f49e', 'date': '20220815' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f49e', 'date': '20210831' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f49e', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f49e', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f49e', 'date': '20210521' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f495': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f495', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f495', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f495',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f495', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f495', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f495', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f495',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4a5', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fae7', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f495', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f495', 'date': '20230301' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f495', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f495', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f495', 'date': '20230301' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f495',
        'date': '20210218'
      },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9e0', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fac0', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fac1', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fa78', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9b7', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9b4', 'date': '20220203' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f495', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f33a', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f495', 'date': '20210218' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4ae', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3f5-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f495', 'date': '20220406' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f344', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f340', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fab4', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f495', 'date': '20220406' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f300', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2602-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26a1', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f495', 'date': '20220110' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f495', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f495', 'date': '20210831' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f495', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f495', 'date': '20210831' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f495', 'date': '20210831' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f495', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f495', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f495', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f495', 'date': '20221101' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f54a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f495', 'date': '20210831' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f495', 'date': '20210218' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f495', 'date': '20210218' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f43e', 'date': '20220203' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f495', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f495', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f495', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f96d', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f495', 'date': '20210521' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f348', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f350', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f95d', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fad2', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fad0', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f347', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f965', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f345', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f955', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f360', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9c5', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f33d', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f966', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f952', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fad1', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9c4', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f954', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fad8', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f330', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f95c', 'date': '20220203' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f495', 'date': '20210831' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fad3', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f950', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f96f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f95e', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f969', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f356', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f354', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f96a', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f968', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f35f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fad4', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f32e', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f32f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f959', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9c6', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f958', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f35d', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f96b', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fad5', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f963', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f957', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f372', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f35b', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f35c', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f363', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f364', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f35a', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f371', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f359', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f358', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f365', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f960', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f370', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f495', 'date': '20221101' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f36b', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f369', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f36a', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9c2', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f37f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9cb', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f375', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9c9', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f6d1', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f6a8', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f6df', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2693', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f697', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3ce-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f695', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f68c', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f6f8', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f680', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2708-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3aa', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3e0', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f495', 'date': '20210831' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f380', 'date': '20220203' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1faa9', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f947', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f948', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f949', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3c5', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f396-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f495', 'date': '20220406' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26bd', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26be', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f94e', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3c0', 'date': '20220203' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f495', 'date': '20230126' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3d0', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3c8', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3c9', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3be', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f945', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3f8', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f94d', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3cf', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3d1', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3d2', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f94c', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f6f7', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3bf', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26f8-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f6fc', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fa70', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f6f9', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26f3', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3af', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3f9', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f94f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fa83', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fa81', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f93f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3bd', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f94b', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f94a', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3b1', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3d3', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3b3', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '265f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fa80', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9e9', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3ae', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3b2', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3b0', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3b4', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f004', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f0cf', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4f7', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3a8', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f58c-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f58d-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1faa1', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9f5', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9f6', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3b9', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3b7', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3ba', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3b8', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fa95', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3bb', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fa98', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f941', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fa97', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3a4', 'date': '20220203' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f495', 'date': '20210521' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f399-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4fa', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f39e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3ac', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3ad', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f39f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4f1', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '260e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f50b', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1faab', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4bf', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4b8', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2696-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9e6', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26d3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f58a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2712-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '270f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4da', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f5c3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4e3', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f50e', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f5dd-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f512', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2648', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2649', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '264a', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '264b', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '264c', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '264d', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '264e', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '264f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2650', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2651', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2652', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2653', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26ce', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2757', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2753', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2049-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f198', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4f4', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f508', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '267b-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2705', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f195', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f193', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f199', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f197', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f192', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f6ae', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '262e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '262f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '267e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2716-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2795', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2796', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2797', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '27b0', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '27bf', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '3030-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '00a9-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '00ae-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2122-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f5ef-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4ac', 'date': '20220203' }
    ],
    '1f48c': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f48c', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f48c', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f48c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f48c', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f48c', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f48c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f48c', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f48c', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f48c', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f48c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f48c', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f48c', 'date': '20220815' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f48c', 'date': '20210218' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f48c', 'date': '20220406' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f48c', 'date': '20220406' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f48c', 'date': '20221101' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f48c', 'date': '20220110' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f48c', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f48c', 'date': '20210831' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f48c', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f48c', 'date': '20210831' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f48c', 'date': '20210831' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f48c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f48c', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f48c', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f48c', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f48c', 'date': '20210831' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f48c', 'date': '20210218' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f48c', 'date': '20210218' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f48c', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f48c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f48c', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f48c', 'date': '20210521' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f48c', 'date': '20210831' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f48c', 'date': '20220815' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f48c', 'date': '20210831' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f48c', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f48c', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f48c', 'date': '20210521' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f49f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f49f', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f49f', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f49f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f49f', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f49f', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f49f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f49f', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f49f', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f49f', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f49f', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f49f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f49f', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f49f', 'date': '20221101' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f49f', 'date': '20220815' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f49f', 'date': '20210218' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f49f', 'date': '20220406' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f49f', 'date': '20220406' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f49f', 'date': '20221101' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f49f', 'date': '20220110' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f49f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f49f', 'date': '20210831' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f49f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f49f', 'date': '20210831' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f49f', 'date': '20210831' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f49f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f49f', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f49f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f49f', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f49f', 'date': '20210831' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f49f', 'date': '20210218' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f49f', 'date': '20210218' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f49f', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f49f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f49f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f49f', 'date': '20210521' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f49f', 'date': '20210831' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f49f', 'date': '20220815' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f49f', 'date': '20210831' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f397-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f49f', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f49f', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f49f', 'date': '20210521' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '2763-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2763-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '2763-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2763-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '2763-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f383', 'date': '20230301' },
      { 'leftEmoji': '1f608', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4a5', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fae7', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f573-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '2763-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '2763-fe0f', 'date': '20230301' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '2763-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '2763-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '2763-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f49f', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2763-fe0f', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '2763-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f494', 'date': '20230301' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9e0', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fac0', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fac1', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fa78', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9b7', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9b4', 'date': '20220203' },
      { 'leftEmoji': '1f480', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20230301'
      },
      { 'leftEmoji': '1fae6', 'rightEmoji': '2763-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '2763-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2763-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f339', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f33a', 'date': '20220203' },
      { 'leftEmoji': '1f337', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '2763-fe0f', 'date': '20210218' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4ae', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f3f5-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '2763-fe0f', 'date': '20220406' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f344', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f340', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fab4', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f332', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '2763-fe0f', 'date': '20220406' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26c4', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f30a', 'date': '20220506' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f32c-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f300', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f32a-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f30b', 'date': '20220506' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f3d6-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2763-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f327-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f329-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2602-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26a1', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f30d', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '2763-fe0f', 'date': '20220110' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '2763-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '2763-fe0f', 'date': '20210831' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '2763-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '2763-fe0f', 'date': '20210831' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '2763-fe0f', 'date': '20210831' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '2763-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '2763-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '2763-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '2763-fe0f', 'date': '20221101' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f54a-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f427', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '2763-fe0f', 'date': '20210831' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f982', 'rightEmoji': '2763-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '2763-fe0f', 'date': '20210218' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f43e', 'date': '20220203' },
      { 'leftEmoji': '1f353', 'rightEmoji': '2763-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '2763-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '2763-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f96d', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f96d', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f34d', 'date': '20230301' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '2763-fe0f', 'date': '20210521' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f348', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f350', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f95d', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fad2', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fad0', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f347', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f965', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f345', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20230301'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f955', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f360', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9c5', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f33d', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f966', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f952', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fad1', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f951', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9c4', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f954', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fad8', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f330', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f95c', 'date': '20220203' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '2763-fe0f', 'date': '20210831' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fad3', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f950', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f96f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f95e', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9c0', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f969', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f356', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f354', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f96a', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f968', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f968', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f35f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fad4', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f32e', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f32f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f959', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9c6', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f958', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f35d', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f96b', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fad5', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f963', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f957', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f372', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f35b', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f35c', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f363', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f364', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f35a', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f371', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f359', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f358', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f365', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f960', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f370', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '2763-fe0f', 'date': '20221101' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f36b', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f369', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f369', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f36a', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f36a', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9c2', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f37f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9cb', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f375', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9c9', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f379', 'date': '20220506' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20230301'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f6d1', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f6a8', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f6df', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2693', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f697', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f3ce-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f695', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f68c', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f6f8', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f6f8', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f680', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f680', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2708-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3aa', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f3db-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f3df-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3e0', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f3d5-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f307', 'rightEmoji': '2763-fe0f', 'date': '20210831' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f3dd-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f380', 'date': '20220203' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1faa9', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f947', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f948', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f949', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3c5', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f396-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '2763-fe0f', 'date': '20220406' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26bd', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26be', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f94e', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3c0', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3d0', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3c8', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3c9', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3be', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f945', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3f8', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f94d', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3cf', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3d1', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3d2', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f94c', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f6f7', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3bf', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26f8-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f6fc', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fa70', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f6f9', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26f3', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3af', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3af', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3f9', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f94f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fa83', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fa81', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f93f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3bd', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f94b', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f94a', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3b1', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3d3', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3b3', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '265f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fa80', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9e9', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3ae', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3b2', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3b0', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3b4', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f004', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f0cf', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4f7', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3a8', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f58c-fe0f',
        'date': '20220203'
      },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f58d-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1faa1', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9f5', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9f6', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3b9', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3b7', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3ba', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3b8', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fa95', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3bb', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fa98', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f941', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fa97', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3a4', 'date': '20220203' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '2763-fe0f', 'date': '20210521' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f399-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4fa', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f39e-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3ac', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3ad', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f39f-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4f1', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '260e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f50b', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1faab', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4bf', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4b8', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2696-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9e6', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f451', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f48e', 'date': '20201001' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f6e0-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26d3-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f5d1-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f58a-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2712-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2712-fe0f', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '270f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4da', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f5c3-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4e3', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f50e', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9ff', 'date': '20220506' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f5dd-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f512', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2648', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2649', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '264a', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '264b', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '264c', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '264d', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '264e', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '264f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2650', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2651', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2652', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2653', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26ce', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2757', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2753', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2049-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f198', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4f4', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f508', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '267b-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2705', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f195', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f193', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f199', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f197', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f192', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f6ae', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '262e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '262f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '267e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2716-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2795', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2796', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2797', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '27b0', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '27bf', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '3030-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '00a9-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '00ae-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2122-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f5ef-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4ac', 'date': '20220203' }
    ],
    '1f494': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f494', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f494', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f494',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f494', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f494', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f494',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f494', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f494', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f494', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f494', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f494',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f494', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f494', 'date': '20220815' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f494', 'date': '20210218' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f494', 'date': '20220406' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f494', 'date': '20220406' },
      { 'leftEmoji': '1f494', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f494', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f494', 'date': '20221101' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f494', 'date': '20220110' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f494', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f494', 'date': '20210831' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f494', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f494', 'date': '20210831' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f494', 'date': '20210831' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f494', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f494', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f494', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f494', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f494', 'date': '20210831' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f494', 'date': '20210218' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f494', 'date': '20210218' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f494', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f494', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f494', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f494', 'date': '20210521' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f494', 'date': '20210831' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f494', 'date': '20221101' },
      { 'leftEmoji': '1f494', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f494', 'date': '20210831' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f494', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f494', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f494', 'date': '20210521' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f48b': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f48b', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f48b', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f48b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f31a', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f31d', 'date': '20220815' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f48b', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f48b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4a5', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fae7', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f48b', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f48b', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f48b', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f48b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9e0', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fac0', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fac1', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fa78', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9b7', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9b4', 'date': '20220815' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f48b', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f48b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f48b', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f33a', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f48b', 'date': '20210218' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3f5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f33b', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f48b', 'date': '20220406' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f331', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f33f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f343', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f340', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f334', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f333', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fab9', 'date': '20220815' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f48b', 'date': '20220406' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f30a', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f32c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f300', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f30b', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3d6-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '26c5', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f48b', 'date': '20221101' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f327-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f329-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4a7', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2602-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2604-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fa90', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f48b', 'date': '20220110' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f48b', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f48b', 'date': '20210831' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f48b', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f48b', 'date': '20210831' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f48b', 'date': '20210831' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f48b', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f48b', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f48b', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f48b', 'date': '20221101' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f54a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9a9', 'date': '20220815' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f48b', 'date': '20210831' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f99e', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f980', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fab8', 'date': '20220815' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f48b', 'date': '20210218' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f41a', 'date': '20220815' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f48b', 'date': '20210218' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f997', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fab2', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fab3', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f41e', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f98b', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f43e', 'date': '20220815' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f48b', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f48b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f48b', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f48b', 'date': '20210521' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f965', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f360', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9c5', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9c4', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f954', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f48b', 'date': '20210831' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f373', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f96a', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fad4', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f32e', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f32f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f35d', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f96b', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f963', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f957', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f372', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f35b', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f35c', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f35a', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f359', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f365', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f960', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f367', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f368', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f366', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f48b', 'date': '20221101' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f36b', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f37c', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f375', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f379', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f6d1', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f6a8', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f6df', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2693', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f697', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3ce-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f695', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f68c', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f682', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f6f8', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f680', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2708-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3a2', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3a1', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3aa', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3db-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3df-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3e0', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3d5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f48b', 'date': '20210831' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f380', 'date': '20220815' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1faa9', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f947', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f948', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f949', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3c5', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f396-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f48b', 'date': '20220406' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '26be', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f94e', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3c0', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3d0', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3c8', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3c9', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3be', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f945', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3f8', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f94d', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3cf', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3d1', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3d2', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f94c', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f6f7', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3bf', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '26f8-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f6fc', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fa70', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f6f9', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '26f3', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3af', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3f9', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f94f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fa83', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fa81', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f93f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3bd', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f94b', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f94a', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3b1', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3d3', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3b3', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '265f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fa80', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9e9', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3ae', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3b2', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3b0', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3b4', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f004', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f0cf', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4f7', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3a8', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f58c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f58d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1faa1', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9f5', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9f6', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3b9', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3b7', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3ba', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3b8', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fa95', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3bb', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fa98', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f941', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fa97', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3a4', 'date': '20220815' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f48b', 'date': '20210521' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f399-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4fa', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f39e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3ac', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3ad', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f39f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4f1', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '260e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f50b', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1faab', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4be', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4bf', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4b8', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2696-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4a1', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9fc', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9e6', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f6e0-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '26d3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f5d1-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f58a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2712-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '270f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4da', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f5c3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4e3', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f50e', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9ff', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f5dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f512', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2648', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2649', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '264a', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '264b', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '264c', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '264d', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '264e', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '264f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2650', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2651', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2652', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2653', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '26ce', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2757', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2753', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2049-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f198', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4f4', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f508', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '26a0-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '267b-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2705', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f195', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f193', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f199', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f197', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f192', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f6ae', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '262e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '262f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '267e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2716-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2795', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2796', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2797', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '27b0', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '27bf', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '3030-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2122-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2660-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f5ef-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4ac', 'date': '20220815' }
    ],
    '1f9a0': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9a0', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f9a0', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f9a0',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f9a0', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f9a0',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f9a0', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f9a0', 'date': '20220815' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f9a0',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f9a0', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f9a0', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9a0', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f9a0', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f9a0', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f9a0', 'date': '20220406' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '26c4', 'date': '20230301' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9a0', 'date': '20221101' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f9a0', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f9a0', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f9a0', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f9a0', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f9a0', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f9a0', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f9a0', 'date': '20230301' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f9a0', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f9a0', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f9a0', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f9a0', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f9a0', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f9a0', 'date': '20210218' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f9a0', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f9a0', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f9a0', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f9a0', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f9a0', 'date': '20210521' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f9a0', 'date': '20210831' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f9a0', 'date': '20221101' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f9a0', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f9a0', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f9a0', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f9a0', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f480': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f480', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f480', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f480',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f480', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f480',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f47b', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f47d', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f916', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f480',
        'date': '20210218'
      },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f480', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f480', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f480', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f480', 'date': '20220815' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f480', 'date': '20210218' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f480', 'date': '20220406' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f480', 'date': '20220406' },
      { 'leftEmoji': '1f480', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f480', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f480', 'date': '20221101' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f480', 'date': '20220110' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f480', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f480', 'date': '20210831' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f480', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f480', 'date': '20210831' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f480', 'date': '20210831' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f480', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f480', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f480', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f480', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f480', 'date': '20210831' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f480', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f480', 'date': '20210218' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f480', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f480', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f480', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f480', 'date': '20210521' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f480', 'date': '20210831' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f480', 'date': '20220815' },
      { 'leftEmoji': '1f480', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f480', 'date': '20210831' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f480', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f480', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f480', 'date': '20210521' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f441-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f441-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '263a-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f441-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f441-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2639-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f441-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f573-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f441-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f441-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f441-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f441-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f441-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20230301'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f441-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f441-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f441-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f441-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f441-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f441-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f441-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '26c4', 'date': '20201001' },
      {
        'leftEmoji': '1f32a-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '1f441-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20221107'
      },
      {
        'leftEmoji': '1f441-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f441-fe0f', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f441-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f441-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f441-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f441-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f441-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f441-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f441-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f441-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f441-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f441-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f441-fe0f', 'date': '20210218' },
      {
        'leftEmoji': '1f441-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f441-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f441-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f441-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f441-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f441-fe0f', 'date': '20210521' },
      {
        'leftEmoji': '1f441-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f441-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f441-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      {
        'leftEmoji': '1f37d-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f441-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f441-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f441-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f441-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f490': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f490', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f490', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f490',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f490', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f490',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f490', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f490', 'date': '20220815' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f490', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f490', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f490', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f490', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f490',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f490', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f490', 'date': '20221101' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f490', 'date': '20220823' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f490', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f490', 'date': '20210218' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f490', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f490', 'date': '20220406' },
      { 'leftEmoji': '1f490', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f490', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f490', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f490', 'date': '20221101' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f490', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f490', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f490', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f490', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f490', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f490', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f490', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f490', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f490', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f490', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f490', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f490', 'date': '20210218' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f490', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f490', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f490', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f490', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f490', 'date': '20210521' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f490', 'date': '20210831' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f490', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f490', 'date': '20210831' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f490', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f490', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f490', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f339': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f339', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f339', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f339',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f339', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f339', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f339', 'date': '20230301' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f339', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f339', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f339', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f339', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f339',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f339', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f339', 'date': '20220815' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f339', 'date': '20210218' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f339', 'date': '20220406' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f339', 'date': '20220406' },
      { 'leftEmoji': '1f339', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f339', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f339', 'date': '20221101' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f339', 'date': '20220110' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f339', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f339', 'date': '20210831' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f339', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f339', 'date': '20210831' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f339', 'date': '20210831' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f339', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f339', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f339', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f339', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f339', 'date': '20210831' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f339', 'date': '20210218' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f339', 'date': '20210218' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f339', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f339', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f339', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f339', 'date': '20210521' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f339', 'date': '20210831' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f339', 'date': '20220815' },
      { 'leftEmoji': '1f339', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f339', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f339', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f339', 'date': '20210521' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f337': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f337', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f337', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f337',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f337', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f337',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f337', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f337', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f337', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f337',
        'date': '20210218'
      },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f337', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f337', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f337', 'date': '20220815' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f337', 'date': '20210218' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f337', 'date': '20220406' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f337', 'date': '20220406' },
      { 'leftEmoji': '1f337', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f337', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f337', 'date': '20221101' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f337', 'date': '20220110' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f337', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f337', 'date': '20210831' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f337', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f337', 'date': '20210831' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f337', 'date': '20210831' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f337', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f337', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f337', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f337', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f337', 'date': '20210831' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f337', 'date': '20210218' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f337', 'date': '20210218' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f337', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f337', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f337', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f337', 'date': '20210521' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f337', 'date': '20210831' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f337', 'date': '20220815' },
      { 'leftEmoji': '1f337', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f337', 'date': '20210831' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f337', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f337', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f337', 'date': '20210521' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f33c': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f33c', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f33c', 'date': '20230301' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f33c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f33c', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f33c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f33c', 'date': '20220815' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f33c', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f33c', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f33c', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f33c', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f33c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f33c', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f33c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f33c', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f33c', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f33c', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f33c', 'date': '20220406' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f33c', 'date': '20221101' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f33c', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f33c', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f33c', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f33c', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f33c', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f33c', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f33c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f33c', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f33c', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f33c', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f33c', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f33c', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f33c', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f33c', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f33c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f33c', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f33c', 'date': '20210521' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f33c', 'date': '20210831' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f33c', 'date': '20220815' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f33c', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f33c', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f33c', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f33c', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f335': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f335', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f335', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f335',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f335', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f335',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f335', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f335',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f335', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f335', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f335', 'date': '20220815' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f335', 'date': '20210218' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f335', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f335', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f335', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f335', 'date': '20221101' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f335', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f335', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f335', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f335', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f335', 'date': '20210831' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f335', 'date': '20210831' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f335', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f335', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f335', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f335', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f335', 'date': '20210831' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f335', 'date': '20210218' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f335', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f335', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f335', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f335', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f335', 'date': '20210521' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f335', 'date': '20210831' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f335', 'date': '20220815' },
      { 'leftEmoji': '1f335', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f335', 'date': '20210831' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f335', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f335', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f335', 'date': '20210521' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f332': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f332', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f332', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f332',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f332', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f332',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f332', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f332', 'date': '20230301' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f332', 'date': '20220815' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f332', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f332',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f332', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f332', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f332', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f332', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f332', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f332', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f332', 'date': '20220406' },
      { 'leftEmoji': '1f332', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f332', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f332', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f332', 'date': '20221101' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f332', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f332', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f332', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f332', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f332', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f332', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f332', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f332', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f332', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f332', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f332', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f332', 'date': '20210218' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f332', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f332', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f332', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f332', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f332', 'date': '20210521' },
      { 'leftEmoji': '1f332', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f332', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f332', 'date': '20210831' },
      { 'leftEmoji': '1f332', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f332', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f332', 'date': '20221101' },
      { 'leftEmoji': '1f332', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f332', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f332', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f332', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f332', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f332', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f332', 'date': '20201001' }
    ],
    '26c4': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '26c4', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '26c4', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '26c4', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '26c4',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '26c4', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '26c4', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '26c4', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '26c4',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '26c4', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '26c4', 'date': '20230301' },
      { 'leftEmoji': '1f978', 'rightEmoji': '26c4', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '26c4', 'date': '20220815' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26c4', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '26c4',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '26c4', 'date': '20230301' },
      { 'leftEmoji': '1f480', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '26c4', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '26c4', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '26c4', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '26c4', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '26c4', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f332', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '26c4', 'date': '20220406' },
      { 'leftEmoji': '26c4', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '26c4', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '26c4', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '26c4', 'date': '20221101' },
      { 'leftEmoji': '1f308', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '26c4', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '26c4', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '26c4', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '26c4', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '26c4', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '26c4', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '26c4', 'date': '20230301' },
      { 'leftEmoji': '1f989', 'rightEmoji': '26c4', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '26c4', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '26c4', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '26c4', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '26c4', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '26c4', 'date': '20210218' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '26c4', 'date': '20230301' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '26c4', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '26c4', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '26c4', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '26c4', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '26c4', 'date': '20210521' },
      { 'leftEmoji': '26c4', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '26c4', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '26c4', 'date': '20210831' },
      { 'leftEmoji': '26c4', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '26c4', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '26c4', 'date': '20221101' },
      { 'leftEmoji': '26c4', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '26c4', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '26c4', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '26c4', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '26c4', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '26c4', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26c4', 'date': '20201001' }
    ],
    '1f32a-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f32a-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '263a-fe0f',
        'rightEmoji': '1f32a-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f32a-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2639-fe0f',
        'rightEmoji': '1f32a-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f32a-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f32a-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '1f32a-fe0f',
        'rightEmoji': '1f573-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f38a', 'date': '20201001' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f32a-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f32a-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f32a-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f32a-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f32a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f32a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f32a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f32a-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f32a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f32a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '26c4', 'date': '20201001' },
      {
        'leftEmoji': '1f32a-fe0f',
        'rightEmoji': '1f32a-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '1f32a-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20221107'
      },
      {
        'leftEmoji': '1f32a-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f32a-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f32a-fe0f', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f32a-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f32a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f32a-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f32a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f32a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f32a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f32a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f32a-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f32a-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f32a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f32a-fe0f', 'date': '20210218' },
      {
        'leftEmoji': '1f32a-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f32a-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f32a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f32a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f32a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f32a-fe0f', 'date': '20210521' },
      {
        'leftEmoji': '1f32a-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f32a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f32a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      {
        'leftEmoji': '1f32a-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f32a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f32a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f32a-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f32a-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f525': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f525', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f525', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f525',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f525', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f525',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f525', 'date': '20220815' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f525', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f525', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f525', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f525', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f525',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f525', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f525', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f525', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f525', 'date': '20210218' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f525', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f332', 'date': '20230301' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f525', 'date': '20220406' },
      { 'leftEmoji': '1f525', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f525', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f525', 'date': '20221101' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f648', 'date': '20230301' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f525', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f525', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f525', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f525', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f525', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f525', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f525', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f525', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f525', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f525', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f525', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f525', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f525', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f525', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f525', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f525', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f525', 'date': '20210521' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f525', 'date': '20210831' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f525', 'date': '20220815' },
      { 'leftEmoji': '1f525', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f525', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f525', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f525', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f525', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '2601-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2601-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f600', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f603', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f604', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f601', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f606', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f605', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f923', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f609', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f617', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f619', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f929', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f973', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f643', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f642', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f972', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f979', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f607', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f611', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f610', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f636', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f914', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f971', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f917', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f631', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f928', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f612', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f644', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '2601-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f624', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f620', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f621', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f625', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f622', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f641', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f615', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f910', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f630', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f628', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f627', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f626', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f632', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f633', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f613', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f616', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f623', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f629', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f635', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f634', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f924', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f974', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f975', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f976', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f922', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f927', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f912', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f915', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f637', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f920', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f911', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f913', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f925', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f921', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f916', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f383', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f608', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f525', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '2b50', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4a5', 'date': '20220203' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '2601-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fae7', 'date': '20220203' },
      {
        'leftEmoji': '1f573-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20221107'
      },
      {
        'leftEmoji': '1f573-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f496', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '2601-fe0f',
        'date': '20221107'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '2601-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f494', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9e0', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fac0', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fac1', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fa78', 'date': '20220203' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9b7', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9b4', 'date': '20220203' },
      { 'leftEmoji': '1f480', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f480', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f441-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20221107'
      },
      {
        'leftEmoji': '1f441-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1fae6', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '2601-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '2601-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2601-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2601-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f490', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f490', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f339', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f33a', 'date': '20220203' },
      { 'leftEmoji': '1f337', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f337', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f338', 'rightEmoji': '2601-fe0f', 'date': '20210218' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4ae', 'date': '20220203' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f3f5-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f341', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f344', 'rightEmoji': '2601-fe0f', 'date': '20220406' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f344', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f340', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fab4', 'date': '20220203' },
      { 'leftEmoji': '1f335', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f335', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f332', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f332', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '2601-fe0f', 'date': '20220406' },
      { 'leftEmoji': '26c4', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '26c4', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f30a', 'date': '20220506' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f32c-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f300', 'date': '20220203' },
      {
        'leftEmoji': '1f32a-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20221107'
      },
      {
        'leftEmoji': '1f32a-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f30b', 'date': '20220506' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f3d6-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f327-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f329-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2602-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '26a1', 'date': '20220203' },
      { 'leftEmoji': '1f308', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f308', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f648', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f435', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f981', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '2601-fe0f', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '2601-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '2601-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f428', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f430', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '2601-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f437', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '2601-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f422', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '2601-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f999', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f994', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f987', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '2601-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f426', 'rightEmoji': '2601-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '2601-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '2601-fe0f', 'date': '20221101' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f54a-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f427', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f427', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '2601-fe0f', 'date': '20210831' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f419', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f419', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f982', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f982', 'rightEmoji': '2601-fe0f', 'date': '20210218' },
      {
        'leftEmoji': '1f577-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20221107'
      },
      {
        'leftEmoji': '1f577-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '2601-fe0f', 'date': '20210218' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f43e', 'date': '20220203' },
      { 'leftEmoji': '1f353', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f353', 'rightEmoji': '2601-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f352', 'rightEmoji': '2601-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f349', 'rightEmoji': '2601-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f96d', 'date': '20220203' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '2601-fe0f', 'date': '20210521' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f348', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f350', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f95d', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fad2', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fad0', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f347', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f965', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f345', 'date': '20220203' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20221107'
      },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f955', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f360', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9c5', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f33d', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f966', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f952', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fad1', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f951', 'date': '20221107' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9c4', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f954', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fad8', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f330', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f95c', 'date': '20220203' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '2601-fe0f', 'date': '20210831' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fad3', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f950', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f96f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f95e', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9c0', 'date': '20221107' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f969', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f356', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f354', 'date': '20220203' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f96a', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f968', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f35f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fad4', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f32e', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f32f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f959', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9c6', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f958', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f35d', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f96b', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fad5', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f963', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f957', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f372', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f35b', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f35c', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f363', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f364', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f35a', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f371', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f359', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f358', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f365', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f960', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f370', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f382', 'date': '20221107' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '2601-fe0f', 'date': '20221101' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f36b', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f369', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f36a', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9c2', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f37f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9cb', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f375', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2615', 'date': '20221107' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9c9', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f379', 'date': '20220506' },
      {
        'leftEmoji': '1f37d-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20221107'
      },
      {
        'leftEmoji': '1f37d-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f6d1', 'date': '20221107' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f6d1', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f6a8', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f6df', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2693', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f697', 'date': '20220203' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f3ce-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f695', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f68c', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f6f8', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f680', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2708-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3aa', 'date': '20220203' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f3db-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f3df-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3e0', 'date': '20220203' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f3d5-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f307', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f307', 'rightEmoji': '2601-fe0f', 'date': '20210831' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f3dd-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f388', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f388', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f380', 'date': '20220203' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1faa9', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f947', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f948', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f949', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3c5', 'date': '20220203' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f396-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '26bd', 'rightEmoji': '2601-fe0f', 'date': '20220406' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '26bd', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '26be', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f94e', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3c0', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3d0', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3c8', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3c9', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3be', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f945', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3f8', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f94d', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3cf', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3d1', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3d2', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f94c', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f6f7', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3bf', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '26f8-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f6fc', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fa70', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f6f9', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '26f3', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3af', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3f9', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f94f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fa83', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fa81', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f93f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3bd', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f94b', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f94a', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3b1', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3d3', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3b3', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '265f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fa80', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9e9', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3ae', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3b2', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3b0', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3b4', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f004', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f0cf', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4f7', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3a8', 'date': '20220203' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f58c-fe0f',
        'date': '20220203'
      },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f58d-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1faa1', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9f5', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9f6', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3b9', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3b7', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3ba', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3b8', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fa95', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3bb', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fa98', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f941', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fa97', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3a4', 'date': '20220203' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '2601-fe0f', 'date': '20210521' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f399-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4fa', 'date': '20221107' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4fa', 'date': '20220203' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f39e-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3ac', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3ad', 'date': '20220203' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f39f-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4f1', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '260e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f50b', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1faab', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4bf', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4b8', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2696-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9e6', 'date': '20220203' },
      { 'leftEmoji': '1f451', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f451', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f6e0-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '26d3-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f5d1-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f58a-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2712-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '270f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4da', 'date': '20220203' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f5c3-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4f0', 'date': '20221107' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4e3', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f50e', 'date': '20220203' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9ff', 'date': '20220506' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f5dd-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f512', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2648', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2649', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '264a', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '264b', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '264c', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '264d', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '264e', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '264f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2650', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2651', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2652', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2653', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '26ce', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2757', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2753', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2049-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f198', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4f4', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f508', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '267b-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2705', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f195', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f193', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f199', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f197', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f192', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f6ae', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '262e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '262f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '267e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2716-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2795', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2796', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2797', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '27b0', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '27bf', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '3030-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '00a9-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '00ae-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2122-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f5ef-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4ac', 'date': '20220203' }
    ],
    '1f308': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f308', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f308', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f308',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f308', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f308',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f308', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f308', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f308', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f308', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f308',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f308', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f308', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f308', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f308', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f308', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f308', 'date': '20220406' },
      { 'leftEmoji': '1f308', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f308', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f308', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f308', 'date': '20221101' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f308', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f308', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f308', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f308', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f308', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f308', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f308', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f308', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f308', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f308', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f308', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f308', 'date': '20210218' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f308', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f308', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f308', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f308', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f308', 'date': '20210521' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f308', 'date': '20210831' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f308', 'date': '20220815' },
      { 'leftEmoji': '1f308', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f308', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f397-fe0f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f308', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f308', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f308', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '2b50': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2b50', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '2b50', 'date': '20230301' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '2b50',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '2b50', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '2b50',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '2b50', 'date': '20220815' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '2b50', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '2b50', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '2b50', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '2b50', 'date': '20230405' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2b50', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '2b50',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '2b50', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '2b50', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2b50', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '2b50', 'date': '20210218' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '2b50', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '2b50', 'date': '20220406' },
      { 'leftEmoji': '2b50', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '2b50', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2b50', 'date': '20221101' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '2b50', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '2b50', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '2b50', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '2b50', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '2b50', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '2b50', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f989', 'rightEmoji': '2b50', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '2b50', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '2b50', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '2b50', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '2b50', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '2b50', 'date': '20210218' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '2b50', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '2b50', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '2b50', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '2b50', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '2b50', 'date': '20210521' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '2b50', 'date': '20210831' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '2b50', 'date': '20220815' },
      { 'leftEmoji': '2b50', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '2b50', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '2b50', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '2b50', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '2b50', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f31f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f31f', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f31f', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f31f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f31f', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f31f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4a5', 'date': '20220203' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f31f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fae7', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f31f', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f31f', 'date': '20230301' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f31f', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f31f', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f31f', 'date': '20230301' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f31f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f31f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9e0', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fac0', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fac1', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fa78', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9b7', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9b4', 'date': '20220203' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f31f', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f31f', 'date': '20221101' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f31f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f33a', 'date': '20220203' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f31f', 'date': '20210218' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4ae', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3f5-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f31f', 'date': '20220406' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f344', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f340', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fab4', 'date': '20220203' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f31f', 'date': '20220406' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f300', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f31f', 'date': '20221101' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2602-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26a1', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f31f', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f31f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f31f', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f31f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f31f', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f31f', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f31f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f31f', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f31f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f31f', 'date': '20221101' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f54a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f31f', 'date': '20210831' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f31f', 'date': '20210218' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f31f', 'date': '20210218' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f43e', 'date': '20220203' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f31f', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f31f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f31f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f96d', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f31f', 'date': '20210521' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f348', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f350', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f95d', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fad2', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fad0', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f347', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f965', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f345', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f955', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f360', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9c5', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f33d', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f966', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f952', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fad1', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9c4', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f954', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fad8', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f330', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f95c', 'date': '20220203' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f31f', 'date': '20210831' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fad3', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f950', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f96f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f95e', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f969', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f356', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f354', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f96a', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f968', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f35f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fad4', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f32e', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f32f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f959', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9c6', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f958', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f35d', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f96b', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fad5', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f963', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f957', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f372', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f35b', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f35c', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f363', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f364', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f35a', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f371', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f359', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f358', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f365', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f960', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f370', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f31f', 'date': '20221101' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f36b', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f369', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f36a', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9c2', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f37f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9cb', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f375', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9c9', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f6d1', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f6a8', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f6df', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2693', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f697', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3ce-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f695', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f68c', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f6f8', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f680', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2708-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3aa', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3e0', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f31f', 'date': '20210831' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f380', 'date': '20220203' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1faa9', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f947', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f948', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f949', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3c5', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f396-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f31f', 'date': '20220406' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26bd', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26be', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f94e', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3c0', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3d0', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3c8', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3c9', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3be', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f945', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3f8', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f94d', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3cf', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3d1', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3d2', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f94c', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f6f7', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3bf', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26f8-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f6fc', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fa70', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f6f9', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26f3', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3af', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3f9', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f94f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fa83', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fa81', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f93f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3bd', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f94b', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f94a', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3b1', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3d3', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3b3', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '265f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fa80', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9e9', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3ae', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3b2', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3b0', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3b4', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f004', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f0cf', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4f7', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3a8', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f58c-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f58d-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1faa1', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9f5', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9f6', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3b9', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3b7', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3ba', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3b8', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fa95', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3bb', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fa98', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f941', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fa97', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3a4', 'date': '20220203' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f31f', 'date': '20210521' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f399-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4fa', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f39e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3ac', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3ad', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f39f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4f1', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '260e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f50b', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1faab', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4bf', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4b8', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2696-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9e6', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26d3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f58a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2712-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '270f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4da', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f5c3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4e3', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f50e', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f5dd-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f512', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2648', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2649', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '264a', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '264b', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '264c', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '264d', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '264e', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '264f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2650', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2651', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2652', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2653', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26ce', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2757', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2753', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2049-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f198', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4f4', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f508', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '267b-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2705', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f195', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f193', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f199', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f197', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f192', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f6ae', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '262e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '262f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '267e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2716-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2795', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2796', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2797', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '27b0', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '27bf', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '3030-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '00a9-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '00ae-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2122-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f5ef-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4ac', 'date': '20220203' }
    ],
    '1f4ab': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4ab', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f4ab', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f4ab',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f4ab', 'date': '20230301' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f4ab', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f4ab', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f4ab',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f4af', 'date': '20220203' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f4ab', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f4ab', 'date': '20230301' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f4ab', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f4ab', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f4ab', 'date': '20230301' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f4ab',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f4ab', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f4ab', 'date': '20221101' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f4ab', 'date': '20210218' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f4ab', 'date': '20220406' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f4ab', 'date': '20220406' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4ab', 'date': '20221101' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f4ab', 'date': '20220110' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f4ab', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f4ab', 'date': '20210831' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f4ab', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f4ab', 'date': '20210831' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f4ab', 'date': '20210831' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f4ab', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f4ab', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f4ab', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f4ab', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f4ab', 'date': '20210831' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f4ab', 'date': '20210218' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f4ab', 'date': '20210218' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f4ab', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f4ab', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f4ab', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f4ab', 'date': '20210521' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f4ab', 'date': '20210831' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f4ab', 'date': '20220815' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f4ab', 'date': '20210831' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f4ab', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f4ab', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f4ab', 'date': '20210521' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f30d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f30d', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f30d', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f30d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f30d', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f30d', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f30d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f30d', 'date': '20220815' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f30d', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f30d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f30d', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f30d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f30d', 'date': '20220815' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f30d', 'date': '20210218' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f30d', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f30d', 'date': '20220406' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f30d', 'date': '20221101' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f30d', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f30d', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f30d', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f30d', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f30d', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f30d', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f30d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f30d', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f30d', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f30d', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f30d', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f30d', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f30d', 'date': '20230301' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f30d', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f30d', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f30d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f30d', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f30d', 'date': '20210521' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f30d', 'date': '20210831' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f30d', 'date': '20220815' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f30d', 'date': '20210831' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f30d', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f30d', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f30d', 'date': '20210521' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f648': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f648', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f648', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f648',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f648', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f648', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f648',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f648', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f648', 'date': '20230301' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f648', 'date': '20220815' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f648', 'date': '20230301' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f648', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f648',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f648', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f648', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f648', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f648', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f648', 'date': '20220406' },
      { 'leftEmoji': '1f648', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f648', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f648', 'date': '20221101' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f648', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f648', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f648', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f648', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f648', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f648', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f648', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f648', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f648', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f648', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f648', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f648', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f648', 'date': '20230301' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f648', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f648', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f648', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f648', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f648', 'date': '20210521' },
      { 'leftEmoji': '1f648', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f648', 'date': '20210831' },
      { 'leftEmoji': '1f648', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f648', 'date': '20221101' },
      { 'leftEmoji': '1f648', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f648', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f648', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f648', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f648', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f435': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f435', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f435', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f435',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f435', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f435',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f435', 'date': '20220815' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f435', 'date': '20230405' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f435', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f435',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f435', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f435', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f435', 'date': '20220815' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f435', 'date': '20210218' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f435', 'date': '20220406' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f435', 'date': '20220406' },
      { 'leftEmoji': '1f435', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f435', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f435', 'date': '20221101' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f435', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f435', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f435', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f435', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f435', 'date': '20210831' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f435', 'date': '20210831' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f435', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f435', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f435', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f435', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f435', 'date': '20210831' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f435', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f435', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f435', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f435', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f435', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f435', 'date': '20210521' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f435', 'date': '20210831' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f435', 'date': '20220815' },
      { 'leftEmoji': '1f435', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f435', 'date': '20210831' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f435', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f435', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f435', 'date': '20210521' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f981': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f981', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f981', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f981',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f981', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f981',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f981', 'date': '20220815' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f981', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f981',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f981', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f981', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f981', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f981', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f981', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f981', 'date': '20220406' },
      { 'leftEmoji': '1f981', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f981', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f981', 'date': '20221101' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f981', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f981', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f981', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f981', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f981', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f981', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f981', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f981', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f981', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f981', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f981', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f981', 'date': '20210218' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f981', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f981', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f981', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f981', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f981', 'date': '20210521' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f981', 'date': '20210831' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f981', 'date': '20221101' },
      { 'leftEmoji': '1f981', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f981', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f981', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f981', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f981', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f431': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f431', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f431', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f431',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f431', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f431',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f431', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f431', 'date': '20220815' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f431', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f431',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f431', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f431', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f431', 'date': '20220815' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f431', 'date': '20210218' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f431', 'date': '20220406' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f431', 'date': '20220406' },
      { 'leftEmoji': '1f431', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f431', 'date': '20221101' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f431', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f431', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f431', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f431', 'date': '20210831' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f431', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f431', 'date': '20210831' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f431', 'date': '20210831' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f431', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f431', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f431', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f431', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f431', 'date': '20210831' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f431', 'date': '20210218' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f431', 'date': '20210218' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f431', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f431', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f431', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f431', 'date': '20210521' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f431', 'date': '20210831' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f431', 'date': '20220815' },
      { 'leftEmoji': '1f431', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f431', 'date': '20210831' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f431', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f431', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f431', 'date': '20210521' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f428': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f428', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f428', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f428', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f428', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f428',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f428', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f428', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f428', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f428', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f428', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f428',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f428', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f428', 'date': '20230301' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f428', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f428', 'date': '20220815' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f428', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f428',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f428', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f428', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f428', 'date': '20220815' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f428', 'date': '20210218' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f428', 'date': '20220406' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f428', 'date': '20220406' },
      { 'leftEmoji': '1f428', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f428', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f428', 'date': '20221101' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f428', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f428', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f428', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f428', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f428', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f428', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f428', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f428', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f428', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f428', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f428', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f428', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f428', 'date': '20210218' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f428', 'date': '20210218' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f428', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f428', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f428', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f428', 'date': '20210521' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f428', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f428', 'date': '20220815' },
      { 'leftEmoji': '1f428', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f428', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f428', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f428', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f428', 'date': '20210521' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f43c': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f43c', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f43c', 'date': '20230301' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f43c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f43c', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f43c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f43c', 'date': '20220815' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f43c', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f43c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f43c', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f43c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f43c', 'date': '20220815' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f43c', 'date': '20210218' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f43c', 'date': '20220406' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f43c', 'date': '20220406' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f43c', 'date': '20221101' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f43c', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f43c', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f43c', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f43c', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f43c', 'date': '20210831' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f43c', 'date': '20210831' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f43c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f43c', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f43c', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f43c', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f43c', 'date': '20210831' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f43c', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f43c', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f43c', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f43c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f43c', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f43c', 'date': '20210521' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f43c', 'date': '20210831' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f43c', 'date': '20220815' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f43c', 'date': '20210831' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f43c', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f43c', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f43c', 'date': '20210521' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f42d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f42d', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f42d', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f42d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f42d', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f42d', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f42d', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f42d', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f42d', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f42d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f42d',
        'date': '20230301'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f42d', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f42d', 'date': '20230301' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f42d', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f42d', 'date': '20220815' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f42d', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f42d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f42d', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f42d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f42d', 'date': '20220815' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f42d', 'date': '20210218' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f42d', 'date': '20220406' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f42d', 'date': '20220406' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f42d', 'date': '20221101' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f42d', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f42d', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f42d', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f42d', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f42d', 'date': '20210831' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f42d', 'date': '20210831' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f42d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f42d', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f42d', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f42d', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f42d', 'date': '20210831' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f42d', 'date': '20210218' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f42d', 'date': '20210218' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f42d', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f42d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f42d', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f42d', 'date': '20210521' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f42d', 'date': '20210831' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f42d', 'date': '20220815' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f42d', 'date': '20210831' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f42d', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f42d', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f42d', 'date': '20210521' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f430': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f430', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f430', 'date': '20230301' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f430',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f430', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f430',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f430', 'date': '20220815' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f430', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f430',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f430', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f430', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f430', 'date': '20220815' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f430', 'date': '20210218' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f430', 'date': '20220406' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f430', 'date': '20220406' },
      { 'leftEmoji': '1f430', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f430', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f430', 'date': '20221101' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f430', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f430', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f430', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f430', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f430', 'date': '20210831' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f430', 'date': '20210831' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f430', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f430', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f430', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f430', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f430', 'date': '20210831' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f430', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f430', 'date': '20210218' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f430', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f430', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f430', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f430', 'date': '20210521' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f430', 'date': '20210831' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f430', 'date': '20220815' },
      { 'leftEmoji': '1f430', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f430', 'date': '20210831' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f430', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f430', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f430', 'date': '20210521' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f437': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f437', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f437', 'date': '20230301' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f437',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f437', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f437',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f437', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f437', 'date': '20220815' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f437', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f437',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f437', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f437', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f437', 'date': '20220815' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f437', 'date': '20210218' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f437', 'date': '20220406' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f437', 'date': '20220406' },
      { 'leftEmoji': '1f437', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f437', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f437', 'date': '20221101' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f437', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f437', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f437', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f437', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f437', 'date': '20210831' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f437', 'date': '20210831' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f437', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f437', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f437', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f437', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f437', 'date': '20210831' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f437', 'date': '20210218' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f437', 'date': '20210218' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f437', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f437', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f437', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f437', 'date': '20210521' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f437', 'date': '20210831' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f437', 'date': '20220815' },
      { 'leftEmoji': '1f437', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f437', 'date': '20210831' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f437', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f437', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f437', 'date': '20210521' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f422': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f422', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f422', 'date': '20230301' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f422', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f422',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f422', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f422', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f422', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f422',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f422', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f422', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f422', 'date': '20220815' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f422', 'date': '20230405' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f422', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f422',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f422', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f422', 'date': '20220823' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f422', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f422', 'date': '20220815' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f422', 'date': '20210218' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f422', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f422', 'date': '20220406' },
      { 'leftEmoji': '1f422', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f422', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f422', 'date': '20221101' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f422', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f422', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f422', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f422', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f422', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f422', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f422', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f422', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f422', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f422', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f422', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f422', 'date': '20210218' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f422', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f422', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f422', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f422', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f422', 'date': '20210521' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f422', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f422', 'date': '20220815' },
      { 'leftEmoji': '1f422', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f422', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f422', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f422', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f422', 'date': '20210521' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f98c': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f98c', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f98c', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f98c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f98c', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f98c', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f98c', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f98c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f98c', 'date': '20220815' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f98c', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f98c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f98c', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f98c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f98c', 'date': '20220815' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f98c', 'date': '20210218' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f98c', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f98c', 'date': '20220406' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f98c', 'date': '20221101' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f98c', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f98c', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f98c', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f98c', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f98c', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f98c', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f98c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f98c', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f98c', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f98c', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f98c', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f98c', 'date': '20220203' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f98c', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f98c', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f98c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f98c', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f98c', 'date': '20210521' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f336-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f98c', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f98c', 'date': '20220815' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f98c', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f98c', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f98c', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f98c', 'date': '20210521' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f999': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f999', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f999', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f999',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f999', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f999', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f999', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f999',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f999', 'date': '20220815' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f999', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f999',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f9a0', 'date': '20230301' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f999', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f999', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f999', 'date': '20220815' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f999', 'date': '20210218' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f999', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f999', 'date': '20220406' },
      { 'leftEmoji': '1f999', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f32a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f999', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f999', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f999', 'date': '20221101' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f999', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f999', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f999', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f999', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f999', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f999', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f999', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f999', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f999', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f999', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f999', 'date': '20210831' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f999', 'date': '20210218' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f999', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f999', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f999', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f999', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f999', 'date': '20210521' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f999', 'date': '20210831' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f999', 'date': '20220815' },
      { 'leftEmoji': '1f999', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f999', 'date': '20210831' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f999', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f999', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f999', 'date': '20210521' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f9a5': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9a5', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f9a5', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f9a5', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f9a5',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f9a5', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f9a5', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f9a5', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f9a5',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f9a5', 'date': '20230301' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f9a5', 'date': '20220815' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f9a5',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f9a5', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f9a5', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9a5', 'date': '20220815' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f9a5', 'date': '20210218' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f9a5', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f9a5', 'date': '20220406' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9a5', 'date': '20221101' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f9a5', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f9a5', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f9a5', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f9a5', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f9a5', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f9a5', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f9a5', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f9a5', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f9a5', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f9a5', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f9a5', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f9a5', 'date': '20210218' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f9a5', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f9a5', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f9a5', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f9a5', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f9a5', 'date': '20210521' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f9a5', 'date': '20210831' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f9a5', 'date': '20220815' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f9a5', 'date': '20210831' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f9a5', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f9a5', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f9a5', 'date': '20210521' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f994': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f994', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f994', 'date': '20230301' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f994',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f994', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f994',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f994', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f994', 'date': '20220815' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f994', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f994',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f994', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f994', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f994', 'date': '20220815' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f994', 'date': '20210218' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f994', 'date': '20220406' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f994', 'date': '20220406' },
      { 'leftEmoji': '1f994', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f994', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f994', 'date': '20221101' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f994', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f994', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f994', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f42d', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f430', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f994', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f437', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f994', 'date': '20210831' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f994', 'date': '20210831' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f994', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f994', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f994', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f994', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f994', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f994', 'date': '20210831' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f994', 'date': '20210218' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f994', 'date': '20210218' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f994', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f994', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f994', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f994', 'date': '20210521' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f994', 'date': '20210831' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f994', 'date': '20220815' },
      { 'leftEmoji': '1f994', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f994', 'date': '20210831' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f994', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f994', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f994', 'date': '20210521' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f987': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f987', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f987', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f987',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f987', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f987', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f987', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f987',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f987', 'date': '20220815' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f987', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f987',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f987', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f987', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f987', 'date': '20220815' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f987', 'date': '20210218' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f987', 'date': '20220406' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f987', 'date': '20220406' },
      { 'leftEmoji': '1f987', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '26c4', 'date': '20230301' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f987', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f987', 'date': '20221101' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f987', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f987', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f987', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f987', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f987', 'date': '20210831' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f987', 'date': '20210831' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f987', 'date': '20230301' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f987', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f987', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f987', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f987', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f987', 'date': '20210831' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f987', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f987', 'date': '20230301' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f987', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f987', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f987', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f987', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f987', 'date': '20210521' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f336-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f951', 'date': '20230301' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f987', 'date': '20210831' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f9c0', 'date': '20230301' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f987', 'date': '20220815' },
      { 'leftEmoji': '1f987', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f987', 'date': '20210831' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f987', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f987', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f987', 'date': '20210521' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f419': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f419', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f419', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f419', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f419',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f419', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f419', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f419', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f419',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f419', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f419', 'date': '20220815' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f419', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f419',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f419', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f419', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f419', 'date': '20220815' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f419', 'date': '20210218' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f419', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f419', 'date': '20220406' },
      { 'leftEmoji': '1f419', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f419', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f419', 'date': '20221101' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f419', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f419', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f419', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f419', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f419', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f419', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f419', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f419', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f419', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f419', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f419', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f419', 'date': '20210218' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f419', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f419', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f419', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f419', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f419', 'date': '20210521' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f419', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f419', 'date': '20220815' },
      { 'leftEmoji': '1f419', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f419', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f419', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f419', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f419', 'date': '20210521' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f577-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f577-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '263a-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f577-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2639-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f577-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '1f573-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '1f573-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20230301'
      },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f577-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f441-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f577-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f577-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f577-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f577-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f577-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f577-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '26c4', 'date': '20230301' },
      {
        'leftEmoji': '1f32a-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '1f577-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20221107'
      },
      {
        'leftEmoji': '1f577-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f577-fe0f', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f577-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f577-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f577-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f577-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f577-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f577-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f577-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f577-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f577-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f577-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f577-fe0f', 'date': '20210218' },
      {
        'leftEmoji': '1f577-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f577-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f577-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f577-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f577-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f577-fe0f', 'date': '20210521' },
      {
        'leftEmoji': '1f577-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f577-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f577-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      {
        'leftEmoji': '1f37d-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f577-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f577-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f577-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f577-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f41d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f41d', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f41d', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f41d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f41d', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f41d', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f41d', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f41d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f41d', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f41d', 'date': '20220815' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f41d', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f41d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f41d', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f41d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f41d', 'date': '20220815' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f41d', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f41d', 'date': '20220406' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f41d', 'date': '20220406' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f32a-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f41d', 'date': '20221101' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f435', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f41d', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f41d', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f41d', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f43c', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f41d', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f41d', 'date': '20210831' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f422', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f41d', 'date': '20210831' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f98c', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f999', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f9a5', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f987', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f41d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f41d', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f41d', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f41d', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f41d', 'date': '20210831' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f419', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f41d', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f41d', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f41d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f41d', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f41d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f41d', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f41d', 'date': '20210521' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f41d', 'date': '20210831' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f41d', 'date': '20220815' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f41d', 'date': '20210831' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f41d', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f41d', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f41d', 'date': '20210521' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f34d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f34d', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f34d', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f34d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f34d', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f34d', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f34d', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f34d', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f34d', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f34d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f34d', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f34d', 'date': '20230301' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f34d', 'date': '20220815' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f34d', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f34d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f34d', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f34d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f34d', 'date': '20220815' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f34d', 'date': '20210218' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f34d', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f34d', 'date': '20220406' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f34d', 'date': '20221101' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f34d', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f34d', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f34d', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f34d', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f34d', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f34d', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f34d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f34d', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f34d', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f34d', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f34d', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f34d', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f34d', 'date': '20230301' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f34d', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f34d', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f34d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f34d', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f34d', 'date': '20210521' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f34d', 'date': '20210831' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f34d', 'date': '20220815' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f34d', 'date': '20210831' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f34d', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f34d', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f34d', 'date': '20210521' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f336-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f336-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f336-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '263a-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f336-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f336-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2639-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f336-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f336-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '1f573-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20230301'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f336-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f441-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f336-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f336-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f336-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f336-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f336-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f332', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f336-fe0f', 'date': '20220406' },
      { 'leftEmoji': '26c4', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f32a-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20221107'
      },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f336-fe0f', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f336-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f336-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f336-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f336-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f336-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f336-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f336-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f336-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f336-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f336-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f336-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f336-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f336-fe0f', 'date': '20210218' },
      {
        'leftEmoji': '1f577-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f336-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f336-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f336-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f336-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f336-fe0f', 'date': '20210521' },
      {
        'leftEmoji': '1f336-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f336-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f336-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f9c0', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f382', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f336-fe0f', 'date': '20221101' },
      { 'leftEmoji': '2615', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f37d-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f336-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f336-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f336-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f336-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4f0', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f336-fe0f', 'date': '20201001' }
    ],
    '1f951': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f951', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f951', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f951',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f951', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f951', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f951',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f951', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f951', 'date': '20220815' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f951', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f951',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f951', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f951', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f951', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f951', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f951', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f332', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f951', 'date': '20220406' },
      { 'leftEmoji': '26c4', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f951', 'date': '20221107' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f951', 'date': '20221101' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f951', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f951', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f951', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f951', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f951', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f951', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f951', 'date': '20230301' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f951', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f951', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f951', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f951', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f951', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f951', 'date': '20210218' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f951', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f951', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f951', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f951', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f951', 'date': '20210521' },
      { 'leftEmoji': '1f336-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f951', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f951', 'date': '20210831' },
      { 'leftEmoji': '1f9c0', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f382', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f951', 'date': '20221101' },
      { 'leftEmoji': '2615', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f951', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f951', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f951', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f951', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f4f0', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f951', 'date': '20201001' }
    ],
    '1f9c0': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9c0', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f9c0', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f9c0',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f9c0', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f9c0', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f9c0', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f9c0',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f9c0', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9c0', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f9c0',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f9c0', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f9c0', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9c0', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f9c0', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f9c0', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f332', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f9c0', 'date': '20220406' },
      { 'leftEmoji': '26c4', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9c0', 'date': '20221107' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9c0', 'date': '20221101' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f9c0', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f9c0', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f9c0', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f9c0', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f9c0', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f9c0', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f9c0', 'date': '20230301' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f9c0', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f9c0', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f9c0', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f9c0', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f9c0', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f9c0', 'date': '20210218' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f9c0', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f9c0', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f9c0', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f9c0', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f9c0', 'date': '20210521' },
      { 'leftEmoji': '1f9c0', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9c0', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f9c0', 'date': '20210831' },
      { 'leftEmoji': '1f9c0', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f382', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f9c0', 'date': '20221101' },
      { 'leftEmoji': '2615', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f9c0', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f9c0', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f9c0', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f9c0', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f4f0', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9c0', 'date': '20201001' }
    ],
    '1f32d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f32d', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f32d', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f32d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f32d', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f32d', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f32d', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f32d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f32d', 'date': '20220815' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f32d', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f32d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f32d', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f32d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f32d', 'date': '20220815' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f32d', 'date': '20210218' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f32d', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f32d', 'date': '20220406' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f32d', 'date': '20221101' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f30d', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f32d', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f32d', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f32d', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f32d', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f32d', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f32d', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f32d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f32d', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f32d', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f32d', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f32d', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f32d', 'date': '20210218' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f32d', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f32d', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f32d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f32d', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f34d', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f32d', 'date': '20210521' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f32d', 'date': '20210831' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f32d', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f32d', 'date': '20220815' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f32d', 'date': '20210831' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f32d', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f32d', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f32d', 'date': '20210521' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f382': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f382', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f382', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f382',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f382', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f382',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f382', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f382', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f382', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f382', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f382', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f382',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f382', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f382', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f382', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f382', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f382', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f332', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f382', 'date': '20220406' },
      { 'leftEmoji': '26c4', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f382', 'date': '20221107' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f382', 'date': '20221101' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f382', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f382', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f382', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f382', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f382', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f382', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f382', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f382', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f382', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f382', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f382', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f382', 'date': '20210218' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f382', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f382', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f382', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f382', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f382', 'date': '20210521' },
      { 'leftEmoji': '1f382', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f382', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f382', 'date': '20210831' },
      { 'leftEmoji': '1f382', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f382', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f382', 'date': '20221101' },
      { 'leftEmoji': '2615', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f382', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f382', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f382', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f382', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f4f0', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f382', 'date': '20201001' }
    ],
    '1f9c1': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9c1', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f9c1', 'date': '20230301' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f9c1',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f9c1', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f9c1',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f9c1', 'date': '20220815' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f9c1', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f9c1', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f9c1', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9c1', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f9c1',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f9c1', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f9c1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9c1', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f9c1', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f9c1', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f9c1', 'date': '20220406' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9c1', 'date': '20221101' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f981', 'date': '20230301' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f9c1', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f9c1', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f9c1', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f9c1', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f9c1', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f9c1', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f9c1', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f9c1', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f9c1', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f9c1', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f9c1', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f9c1', 'date': '20210218' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f577-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f9c1', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f9c1', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f9c1', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f9c1', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f9c1', 'date': '20210521' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f9c1', 'date': '20210831' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f9c1', 'date': '20221101' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f9c1', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f9c1', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f9c1', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f9c1', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f37d-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f37d-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '263a-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '263a-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20230301'
      },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f37d-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2639-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f573-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '1f573-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20230301'
      },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20230301'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f37d-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '1f37d-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f37d-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f37d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f37d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f37d-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f37d-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f37d-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '26c4', 'date': '20201001' },
      {
        'leftEmoji': '1f32a-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '1f37d-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20221107'
      },
      {
        'leftEmoji': '1f37d-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20201001'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f37d-fe0f', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f37d-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f37d-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f37d-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f37d-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f37d-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f37d-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f37d-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f37d-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f37d-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f37d-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f37d-fe0f', 'date': '20210218' },
      {
        'leftEmoji': '1f37d-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f37d-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f37d-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f37d-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f37d-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f37d-fe0f', 'date': '20210521' },
      {
        'leftEmoji': '1f37d-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f37d-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f37d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f37d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '2615', 'date': '20201001' },
      {
        'leftEmoji': '1f37d-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f37d-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f37d-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f37d-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f37d-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f388': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f388', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f388', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f388',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f388', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f388', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f388', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f388',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f388', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f388', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f388', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f388', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f388', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f388', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f388', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f388',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f388', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f388', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f388', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f388', 'date': '20210218' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f388', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f388', 'date': '20220406' },
      { 'leftEmoji': '1f388', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f388', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f388', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f388', 'date': '20221101' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f388', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f388', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f388', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f388', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f388', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f388', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f388', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f388', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f388', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f388', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f388', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f388', 'date': '20210218' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f388', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f388', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f388', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f388', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f388', 'date': '20210521' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f388', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f388', 'date': '20220815' },
      { 'leftEmoji': '1f388', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f388', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f388', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f388', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f388', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f451': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f451', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f451', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f451',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f451', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f451', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f451', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f451',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f451', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f451', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f451', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f451', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f451', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f451',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f451', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f451', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f451', 'date': '20220815' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f451', 'date': '20210218' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f451', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f451', 'date': '20220406' },
      { 'leftEmoji': '1f451', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f451', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f451', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f451', 'date': '20221101' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f451', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f451', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f451', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f451', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f451', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f451', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f451', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f451', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f451', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f451', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f451', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f451', 'date': '20210218' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f451', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f451', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f451', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f451', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f451', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f451', 'date': '20210831' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f451', 'date': '20220815' },
      { 'leftEmoji': '1f451', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f451', 'date': '20210831' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f451', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f451', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f451', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f48e': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f48e', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f972', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f48e', 'date': '20230301' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f48e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f48e', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f48e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f978', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f48e', 'date': '20220815' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f48e', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f48e', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f48e', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f48e', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f48e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f9a0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f48e', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f48e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f48e', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f48e', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f48e', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f48e', 'date': '20220406' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f48e', 'date': '20221101' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f648', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f981', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f48e', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f48e', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f48e', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f48e', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f48e', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f48e', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f48e', 'date': '20230301' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f48e', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f48e', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f48e', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f48e', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f48e', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f48e', 'date': '20210218' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f577-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f48e', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f48e', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f48e', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f48e', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f48e', 'date': '20210521' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f48e', 'date': '20210831' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f48e', 'date': '20220815' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f48e', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f48e', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f48e', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f48e', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f48e', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f52e', 'date': '20201001' }
    ],
    '1f4f0': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4f0', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f4f0', 'date': '20230301' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f4f0',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f4f0', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f4f0',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f4f0', 'date': '20230301' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f4f0', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f4f0', 'date': '20220815' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4f0', 'date': '20230301' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f4f0',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f4f0', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f4f0', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4f0', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f4f0', 'date': '20210218' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f4f0', 'date': '20220406' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f332', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f4f0', 'date': '20220406' },
      { 'leftEmoji': '26c4', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4f0', 'date': '20221107' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4f0', 'date': '20221101' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f4f0', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f4f0', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f4f0', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f4f0', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f4f0', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f4f0', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f4f0', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f4f0', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f4f0', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f4f0', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f4f0', 'date': '20210831' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f4f0', 'date': '20210218' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f4f0', 'date': '20210218' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f4f0', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f4f0', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f4f0', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f4f0', 'date': '20210521' },
      { 'leftEmoji': '1f4f0', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4f0', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f4f0', 'date': '20210831' },
      { 'leftEmoji': '1f4f0', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f4f0', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f4f0', 'date': '20220815' },
      { 'leftEmoji': '1f4f0', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f4f0', 'date': '20210831' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f4f0', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f4f0', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f4f0', 'date': '20210521' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f4f0', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4f0', 'date': '20201001' }
    ],
    '1f52e': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f52e', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f972', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f52e', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f52e', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f52e', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f52e', 'date': '20211115' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f52e', 'date': '20201001' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f52e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f52e', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f52e', 'date': '20230301' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f52e', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f52e', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f52e', 'date': '20230301' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f31b', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f31c', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f52e', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f52e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f52e', 'date': '20221107' },
      { 'leftEmoji': '1f978', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f383', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f525', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '2b50', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4a5', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4a5', 'date': '20230301' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f52e', 'date': '20220815' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fae7', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fae7', 'date': '20230301' },
      { 'leftEmoji': '1f573-fe0f', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f52e', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f52e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9e0', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fac0', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fac0', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fac1', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fac1', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa78', 'date': '20220506' },
      { 'leftEmoji': '1f9a0', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9b7', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9b4', 'date': '20220506' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f441-fe0f', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f52e', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f52e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f52e', 'date': '20220815' },
      { 'leftEmoji': '1f490', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f339', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f33a', 'date': '20220506' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f52e', 'date': '20210218' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4ae', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3f5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f33c', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f52e', 'date': '20220406' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f340', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fab4', 'date': '20220506' },
      { 'leftEmoji': '1f335', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f332', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f52e', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f52e', 'date': '20220406' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26c4', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f300', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f300', 'date': '20230301' },
      { 'leftEmoji': '1f32a-fe0f', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2601-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f52e', 'date': '20221101' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2602-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26a1', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26a1', 'date': '20230301' },
      { 'leftEmoji': '1f308', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f30d', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f648', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f981', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f52e', 'date': '20220110' },
      { 'leftEmoji': '1f431', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f52e', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f52e', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f52e', 'date': '20210831' },
      { 'leftEmoji': '1f428', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f43c', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f42d', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f430', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f52e', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f52e', 'date': '20211115' },
      { 'leftEmoji': '1f437', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f52e', 'date': '20210831' },
      { 'leftEmoji': '1f422', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f52e', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f52e', 'date': '20210831' },
      { 'leftEmoji': '1f98c', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f999', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f9a5', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f994', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f987', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f52e', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f52e', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f52e', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f52e', 'date': '20221101' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f54a-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f54a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9a9', 'date': '20230301' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f52e', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f52e', 'date': '20210831' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f99e', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f980', 'date': '20230301' },
      { 'leftEmoji': '1f419', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fab8', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f52e', 'date': '20210218' },
      { 'leftEmoji': '1f577-fe0f', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f41a', 'date': '20230301' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f52e', 'date': '20210218' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f997', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fab2', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f41d', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f43e', 'date': '20220506' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f52e', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f52e', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f52e', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f52e', 'date': '20211115' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f96d', 'date': '20220506' },
      { 'leftEmoji': '1f34d', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f52e', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f52e', 'date': '20210521' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f348', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f350', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f350', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f95d', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad2', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad0', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f347', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f347', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f965', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f965', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f345', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f336-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f955', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f360', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f360', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9c5', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f33d', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f966', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f952', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad1', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad1', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f951', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9c4', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f954', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f954', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad8', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f330', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f95c', 'date': '20220506' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f52e', 'date': '20210831' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad3', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f950', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f950', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f96f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f95e', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f373', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f969', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f969', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f356', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f354', 'date': '20220506' },
      { 'leftEmoji': '1f32d', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f96a', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f968', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f968', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f35f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad4', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad4', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f32e', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f32f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f959', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9c6', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f958', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f35d', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f96b', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad5', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad5', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f963', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f957', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f372', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f35b', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f35c', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f363', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f364', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f364', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f35a', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f371', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f359', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f358', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f365', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f365', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f960', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f960', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f370', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9c1', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f52e', 'date': '20221101' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f36b', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f36b', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f369', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f369', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f36a', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9c2', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f37f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9cb', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f375', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f375', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9c9', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9c9', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f37d-fe0f', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6d1', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6a8', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6df', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2693', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f697', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f697', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3ce-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3ce-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f695', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f695', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f68c', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f68c', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6f8', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f680', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2708-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3aa', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3e0', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f52e', 'date': '20210831' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f388', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f380', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f380', 'date': '20230301' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f52e', 'date': '20211115' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1faa9', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f947', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f948', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f949', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3c5', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f396-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f52e', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f52e', 'date': '20220406' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26be', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f94e', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3c0', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3d0', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3c8', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3c9', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3be', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f945', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3f8', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f94d', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f94d', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3cf', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3d1', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3d2', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f94c', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6f7', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6f7', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3bf', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3bf', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26f8-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6fc', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa70', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6f9', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26f3', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3af', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3f9', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3f9', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f94f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa83', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa81', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f93f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3bd', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f94b', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f94a', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f94a', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b1', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3d3', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b3', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b3', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '265f-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa80', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa80', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9e9', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3ae', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b2', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b0', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b0', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b4', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f004', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f0cf', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4f7', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3a8', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f58c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f58d-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f58d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1faa1', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9f5', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9f6', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b9', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b7', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3ba', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b8', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa95', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa95', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3bb', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa98', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f941', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa97', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa97', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3a4', 'date': '20220506' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f52e', 'date': '20210521' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f399-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4fa', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f39e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f39e-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3ac', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3ad', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f39f-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4f1', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '260e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f50b', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1faab', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4bf', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4b8', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2696-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4a1', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9e6', 'date': '20220506' },
      { 'leftEmoji': '1f451', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f48e', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26d3-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26d3-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f58a-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2712-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '270f-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4da', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f5c3-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4f0', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4e3', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f50e', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f50e', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f52e', 'date': '20201001' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f5dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f5dd-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f512', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f512', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2648', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2648', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2649', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '264a', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '264b', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '264b', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '264c', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '264d', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '264d', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '264e', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '264e', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '264f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2650', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2651', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2652', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2653', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26ce', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2757', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2757', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2753', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2049-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f198', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4f4', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f508', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f508', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '267b-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2705', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f195', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f193', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f199', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f197', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f197', 'date': '20230301' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f192', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6ae', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '262e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '262f-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '267e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2716-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2795', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2796', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2797', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '27b0', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '27bf', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '3030-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2122-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f5ef-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4ac', 'date': '20220506' }
    ],
    '1f4af': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4af', 'date': '20210521' },
      { 'leftEmoji': '1f600', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f603', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f604', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f601', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f606', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f605', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f923', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f62d', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f609', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f617', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f619', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f61a', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f643', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f642', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f972', 'date': '20220815' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f60b', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f61b', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f61d', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f61c', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f92a', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f607', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f60a', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '263a-fe0f', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f60f', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f60c', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f611', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f610', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f636', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f914', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f92b', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f92d', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f971', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f917', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f928', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f9d0', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f612', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f644', 'rightEmoji': '1f4af', 'date': '20220815' },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f4af',
        'date': '20210218'
      },
      { 'leftEmoji': '1f624', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f620', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f621', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f92c', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f61f', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f625', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f622', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '2639-fe0f', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f641', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f615', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f910', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f630', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f628', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f627', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f626', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f62e', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f62f', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f632', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f633', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f92f', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f62c', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f613', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f61e', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f616', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f623', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f629', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f62b', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f635', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f634', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f62a', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f924', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f31e', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f4af', 'date': '20211115' },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f4af',
        'date': '20210218'
      },
      { 'leftEmoji': '1f974', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f975', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f976', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f922', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f92e', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f927', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f912', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f915', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f637', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f920', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f911', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f978', 'date': '20220815' },
      { 'leftEmoji': '1f925', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f4a9', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f47d', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f916', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f383', 'date': '20201001' },
      { 'leftEmoji': '1f608', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f47f', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f525', 'date': '20220815' },
      { 'leftEmoji': '1f4ab', 'rightEmoji': '1f4af', 'date': '20220203' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '2b50', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f31f', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f38a', 'date': '20220815' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f4af', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f4af', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f4af', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f49d', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f496', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f497', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f49e', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f48c', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4af', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f4af',
        'date': '20210218'
      },
      { 'leftEmoji': '1f494', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f9a0', 'date': '20220815' },
      { 'leftEmoji': '1f480', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f4af', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f490', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f337', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f4af', 'date': '20210218' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f33c', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f4af', 'date': '20220406' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f335', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f334', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f332', 'date': '20220815' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f4af', 'date': '20220406' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '26c4', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f32a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '2601-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4af', 'date': '20221101' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f30d', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f648', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f435', 'date': '20220815' },
      { 'leftEmoji': '1f435', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f981', 'date': '20220815' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f4af', 'date': '20220110' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f431', 'date': '20220815' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f4af', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f4af', 'date': '20210831' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f428', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f43c', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f42d', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f430', 'date': '20220815' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f4af', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f437', 'date': '20220815' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f4af', 'date': '20210831' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f422', 'date': '20220815' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f4af', 'date': '20210831' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f98c', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f999', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f9a5', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f994', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f987', 'date': '20220815' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f4af', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f4af', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f4af', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f4af', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f4af', 'date': '20210831' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f419', 'date': '20220815' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f4af', 'date': '20210218' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f577-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f41a', 'date': '20220815' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f4af', 'date': '20220203' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f41d', 'date': '20220815' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f4af', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f4af', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f4af', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f34d', 'date': '20220815' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f4af', 'date': '20210521' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f336-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f951', 'date': '20220815' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f4af', 'date': '20210831' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f9c0', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f32d', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f9c1', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f4af', 'date': '20221101' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '2615', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f37d-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f4af', 'date': '20210831' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f4af', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f4af', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f4af', 'date': '20210521' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f48e', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f4f0', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f52e', 'date': '20220815' }
    ],
    '2764-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2764-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f4af', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f',
        'rightEmoji': '1f573-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '2764-fe0f', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '2764-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '2764-fe0f', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '2764-fe0f', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '2764-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f440', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f440', 'date': '20230301' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '2764-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2764-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '2764-fe0f', 'date': '20210218' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '2764-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f429', 'rightEmoji': '2764-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '2764-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f982', 'rightEmoji': '2764-fe0f', 'date': '20210218' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '2764-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2764-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '2764-fe0f',
        'rightEmoji': '1f397-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f48e', 'date': '20201001' }
    ],
    '1f9e1': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9e1', 'date': '20210521' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f9e1', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f9e1', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f9e1', 'date': '20221101' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f9e1', 'date': '20221101' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f9e1',
        'date': '20210218'
      },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f440', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f440', 'date': '20230301' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f9e1', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9e1', 'date': '20220815' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f9e1', 'date': '20210218' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f9e1', 'date': '20210831' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f9e1', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f9e1', 'date': '20210831' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f9e1', 'date': '20210218' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f9e1', 'date': '20220815' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9e1', 'date': '20211115' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f397-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f48e', 'date': '20201001' }
    ],
    '1f49b': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f49b', 'date': '20210521' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f49b', 'date': '20230301' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f49b', 'date': '20230301' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f49b', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f49b', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f49b', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f49b', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f49b', 'date': '20221101' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f49b', 'date': '20221101' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f49b',
        'date': '20210218'
      },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f440', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f49b', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f49b', 'date': '20220815' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f49b', 'date': '20210218' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f49b', 'date': '20210831' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f49b', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f49b', 'date': '20210831' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f49b', 'date': '20210218' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f49b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f49b', 'date': '20220406' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f49b', 'date': '20220815' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f49b', 'date': '20211115' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f397-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f48e', 'date': '20201001' }
    ],
    '1f49a': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f49a', 'date': '20210521' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f49a', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f49a', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f49a', 'date': '20221101' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f49a', 'date': '20221101' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f49a',
        'date': '20210218'
      },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f440', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f49a', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f49a', 'date': '20220815' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f49a', 'date': '20210218' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f49a', 'date': '20210831' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f49a', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f49a', 'date': '20210831' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f49a', 'date': '20210218' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f49a', 'date': '20210521' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f49a', 'date': '20220815' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f49a', 'date': '20211115' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f397-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f48e', 'date': '20201001' }
    ],
    '1f499': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f499', 'date': '20210521' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f499', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f499', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f499', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f499', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f499',
        'date': '20210218'
      },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f440', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f499', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f499', 'date': '20220815' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f499', 'date': '20210218' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f499', 'date': '20210831' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f499', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f499', 'date': '20210831' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f499', 'date': '20210218' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f499', 'date': '20220815' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f499', 'date': '20211115' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f397-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f48e', 'date': '20201001' }
    ],
    '1f49c': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f49c', 'date': '20210521' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f49c', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f49c', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f49c', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f49c', 'date': '20221101' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f49c',
        'date': '20210218'
      },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f440', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f49c', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f49c', 'date': '20220815' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f49c', 'date': '20210218' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f49c', 'date': '20210831' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f49c', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f49c', 'date': '20210831' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f49c', 'date': '20210218' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f49c', 'date': '20220815' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f49c', 'date': '20211115' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f397-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f48e', 'date': '20201001' }
    ],
    '1f90e': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f90e', 'date': '20210521' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f90e', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f90e', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f90e', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f90e', 'date': '20221101' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f90e',
        'date': '20210218'
      },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f440', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f440', 'date': '20230301' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f90e', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f90e', 'date': '20220815' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f90e', 'date': '20210218' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f90e', 'date': '20220406' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f90e', 'date': '20210831' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f90e', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f90e', 'date': '20210831' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f90e', 'date': '20210218' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f90e', 'date': '20220815' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f90e', 'date': '20211115' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f397-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f90e', 'date': '20211115' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f48e', 'date': '20201001' }
    ],
    '1f5a4': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f5a4', 'date': '20210521' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f5a4', 'date': '20230301' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f5a4', 'date': '20230301' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f5a4', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f5a4', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f5a4', 'date': '20230301' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f5a4', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f5a4', 'date': '20230301' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f5a4',
        'date': '20210218'
      },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f440', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f440', 'date': '20230301' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f5a4', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f5a4', 'date': '20220815' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f5a4', 'date': '20210218' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f5a4', 'date': '20210831' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f5a4', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f5a4', 'date': '20210831' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f5a4', 'date': '20210218' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f382', 'date': '20230301' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f5a4', 'date': '20220815' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '2615', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '2615', 'date': '20230301' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f5a4', 'date': '20211115' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f397-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f5a4', 'date': '20211115' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f48e', 'date': '20201001' }
    ],
    '1f90d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f90d', 'date': '20210521' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f929', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f973', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f631', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f31b', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f31c', 'date': '20201001' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f913', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f921', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f525', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f4ab', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '2b50', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '2b50', 'date': '20230301' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f31f', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f4af', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f573-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f38a', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f38a', 'date': '20230301' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f90d', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f90d', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f90d', 'date': '20221101' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f5a4', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f90d', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '2665-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f498', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f49d', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f496', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f497', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f493', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f493', 'date': '20230301' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f49e', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f495', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f48c', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f49f', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '2763-fe0f', 'date': '20201001' },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f90d',
        'date': '20210218'
      },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f494', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f48b', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f440', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f440', 'date': '20230301' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f441-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f90d', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f90d', 'date': '20220815' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f490', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f339', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f337', 'date': '20201001' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f90d', 'date': '20210218' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f33c', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f33c', 'date': '20230301' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f308', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f308', 'date': '20230301' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f90d', 'date': '20220110' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f90d', 'date': '20210831' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f90d', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f90d', 'date': '20210831' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f90d', 'date': '20210218' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f382', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f9c1', 'date': '20201001' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f90d', 'date': '20220815' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f388', 'date': '20201001' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f90d', 'date': '20211115' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f397-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f90d', 'date': '20211115' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f451', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f48e', 'date': '20201001' }
    ],
    '1f440': [
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f440', 'date': '20221101' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f440', 'date': '20201001' },
      { 'leftEmoji': '2764-fe0f', 'rightEmoji': '1f440', 'date': '20230301' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f440', 'date': '20201001' },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f440', 'date': '20230301' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f440', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f440', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f440', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f440', 'date': '20230301' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f440', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f440', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f440', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f440', 'date': '20230301' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f440', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f440', 'date': '20201001' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f440', 'date': '20230301' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f440', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f440', 'date': '20230301' }
    ],
    '1f397-fe0f': [
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f397-fe0f', 'date': '20221101' },
      {
        'leftEmoji': '2764-fe0f',
        'rightEmoji': '1f397-fe0f',
        'date': '20201001'
      },
      { 'leftEmoji': '1f9e1', 'rightEmoji': '1f397-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49b', 'rightEmoji': '1f397-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49a', 'rightEmoji': '1f397-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f397-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f499', 'rightEmoji': '1f397-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49c', 'rightEmoji': '1f397-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f90e', 'rightEmoji': '1f397-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f397-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f5a4', 'rightEmoji': '1f397-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f90d', 'rightEmoji': '1f397-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f49f', 'rightEmoji': '1f397-fe0f', 'date': '20201001' },
      { 'leftEmoji': '1f397-fe0f', 'rightEmoji': '1f308', 'date': '20201001' },
      {
        'leftEmoji': '1f397-fe0f',
        'rightEmoji': '1f397-fe0f',
        'date': '20201001'
      }
    ],
    '1f62e-200d-1f4a8': [
      {
        'leftEmoji': '1fa84',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210521'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f600',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f603',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f604',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f601',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f606',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f605',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f602',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f923',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f62d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f609',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f617',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f619',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f61a',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f618',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f970',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f60d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f929',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f973',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f643',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f642',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f972',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f979',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f60b',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f61b',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f61d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f61c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f92a',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f607',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f60a',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '263a-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f60f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f60c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f614',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f611',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f610',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f636',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae1',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f914',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f92b',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae2',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f92d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f971',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f917',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae3',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f631',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f928',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f9d0',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f612',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f644',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f624',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f620',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f621',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f92c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f97a',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f61f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f625',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f622',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '2639-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f641',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae4',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f615',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f910',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f630',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f628',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f627',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f626',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f62e',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f62f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f632',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f633',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f92f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f62c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f613',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f61e',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f616',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f623',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f629',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f62b',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f635',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae5',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      {
        'leftEmoji': '1fae5',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20230301'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f634',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f62a',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f924',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f31b',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f31c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f31e',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae0',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f974',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f975',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f976',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f922',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f92e',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f927',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f912',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f915',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f637',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f920',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f911',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f60e',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f913',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f978',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f925',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f921',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f47b',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f4a9',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f47d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f916',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f383',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f608',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f47f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f525',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f4ab',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '2b50',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f31f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f4af',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f573-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f38a',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '2665-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f498',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f49d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f496',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f497',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f493',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f49e',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f495',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f48c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f49f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '2763-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f494',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f48b',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f9a0',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f480',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f441-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae6',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20220203'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f490',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f339',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f337',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f338',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f33c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f344',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20220406'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f335',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f332',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fab5',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      {
        'leftEmoji': '1faa8',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20220406'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '26c4',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f32a-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '2601-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f308',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f30d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f648',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f435',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f981',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f42f',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20220110'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f431',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f436',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f43a',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f43b',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f428',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f43c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f42d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f430',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f98a',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f99d',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f437',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f984',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f422',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f429',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f410',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f98c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f999',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f9a5',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f994',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f987',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f989',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f426',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f414',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20230126'
      },
      {
        'leftEmoji': '1fabf',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f427',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f41f',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f419',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f982',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f577-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f40c',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f41d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f353',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f352',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20220406'
      },
      {
        'leftEmoji': '1f349',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20220406'
      },
      {
        'leftEmoji': '1f34a',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f34d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f34c',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f34b',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210521'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f336-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f951',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f35e',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f9c0',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f32d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f382',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f9c1',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f36c',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20220815'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '2615',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f37d-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f307',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f388',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f381',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f3c6',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      {
        'leftEmoji': '26bd',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20220406'
      },
      {
        'leftEmoji': '1f3c0',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20230126'
      },
      {
        'leftEmoji': '1f3a7',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210521'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f451',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f48e',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f4f0',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f62e-200d-1f4a8',
        'rightEmoji': '1f52e',
        'date': '20210218'
      }
    ],
    '1f636-200d-1f32b-fe0f': [
      {
        'leftEmoji': '1fa84',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210521'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f600',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f603',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f604',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f601',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f606',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f605',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f602',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f923',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f62d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f609',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f617',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f619',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f61a',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f618',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f970',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f60d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f929',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f973',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f643',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f642',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f972',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f979',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f60b',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f61b',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f61d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f61c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f92a',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f607',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f60a',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '263a-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f60f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f60c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f614',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f611',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f610',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f636',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae1',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f914',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f92b',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae2',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f92d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f971',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f917',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae3',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f631',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f928',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f9d0',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f612',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f644',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f624',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f620',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f621',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f92c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f97a',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f61f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f625',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f622',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '2639-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f641',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae4',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f615',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f910',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f630',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f628',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f627',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f626',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f62e',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f62f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f632',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f633',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f92f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f62c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f613',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f61e',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f616',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f623',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f629',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f62b',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f635',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae5',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      {
        'leftEmoji': '1fae5',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20230301'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f634',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f62a',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f924',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f31b',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f31c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f31e',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae0',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f974',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f975',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f976',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f922',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f92e',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f927',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f912',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f915',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f637',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f920',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f911',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f60e',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f913',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f978',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f925',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f921',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f47b',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f4a9',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f47d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f916',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f383',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f608',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f47f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f525',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f4ab',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '2b50',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f31f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f4af',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f573-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f38a',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f38a',
        'date': '20230301'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '2665-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f498',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f49d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f496',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f497',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f493',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f49e',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f495',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f48c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f49f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '2763-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f494',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f48b',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f9a0',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f480',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae6',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20220203'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f490',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f337',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f338',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f33c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f344',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20220406'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f335',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f332',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fab5',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      {
        'leftEmoji': '1faa8',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20220406'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '26c4',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f32a-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '2601-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f308',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f30d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f648',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f435',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f981',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f42f',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20220110'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f431',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f436',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f43a',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f43b',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f428',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f43c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f42d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f42d',
        'date': '20230301'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f430',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f98a',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f99d',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f437',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f984',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f422',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f429',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f410',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f98c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f999',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f9a5',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f994',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f987',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f989',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f426',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f414',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20230126'
      },
      {
        'leftEmoji': '1fabf',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f427',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f41f',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f419',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f982',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f40c',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f41d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f353',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f352',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20220406'
      },
      {
        'leftEmoji': '1f349',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20220406'
      },
      {
        'leftEmoji': '1f34a',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f34d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f34c',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f34b',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210521'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f951',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f35e',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f9c0',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f32d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f382',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f9c1',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f36c',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20220815'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '2615',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f307',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f388',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f381',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f3c6',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      {
        'leftEmoji': '26bd',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20220406'
      },
      {
        'leftEmoji': '1f3c0',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20230126'
      },
      {
        'leftEmoji': '1f3a7',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210521'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f451',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f48e',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f4f0',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f636-200d-1f32b-fe0f',
        'rightEmoji': '1f52e',
        'date': '20210218'
      }
    ],
    '2764-fe0f-200d-1fa79': [
      {
        'leftEmoji': '1fa84',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210521'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f600',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f603',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f604',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f601',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f606',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f605',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f602',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f923',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f62d',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f609',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f617',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f619',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f61a',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f618',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f970',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f60d',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f929',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f973',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f643',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f642',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f972',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f979',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f60b',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f61b',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f61d',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f61c',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f92a',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f607',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f60a',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '263a-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f60f',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f60c',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f614',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f611',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f610',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f636',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae1',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f914',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f92b',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae2',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f92d',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f971',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f917',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae3',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f631',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f928',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f9d0',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f612',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f644',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f624',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f620',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f621',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f92c',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f97a',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f61f',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f625',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f622',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '2639-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f641',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae4',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f615',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f910',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f630',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f628',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f627',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f626',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f62e',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f62f',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f632',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f633',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f92f',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f62c',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f613',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f61e',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f616',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f623',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f629',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f62b',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f635',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae5',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f634',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f62a',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f924',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f31b',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f31c',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f31e',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae0',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f974',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f975',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f976',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f922',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f92e',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f927',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f912',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f915',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f637',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f920',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f911',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f60e',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f913',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f978',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f925',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f921',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f47b',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f4a9',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f47d',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f916',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f383',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f608',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f47f',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f525',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f4ab',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '2b50',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f31f',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f4af',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f573-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f38a',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fa77',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20221101'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '2764-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f9e1',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f49b',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f49a',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fa75',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20221101'
      },
      {
        'leftEmoji': '1fa75',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20230301'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f499',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f49c',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f90e',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fa76',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20221101'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f5a4',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f90d',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '2665-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f498',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f49d',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f496',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f497',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f493',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f49e',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f495',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f48c',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f49f',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '2763-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f494',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f48b',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f9a0',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f480',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f441-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fae6',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20220203'
      },
      {
        'leftEmoji': '1f937',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20220815'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f490',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f339',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f337',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f338',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f33c',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f344',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20220406'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f335',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f332',
        'date': '20210218'
      },
      {
        'leftEmoji': '1fab5',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      {
        'leftEmoji': '1faa8',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20220406'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '26c4',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f32a-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '2601-fe0f',
        'date': '20221107'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '2601-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20221101'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f308',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f30d',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f648',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f435',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f981',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f42f',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20220110'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f431',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f436',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f43a',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f43b',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210831'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f428',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f43c',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f42d',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f430',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f98a',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f99d',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f437',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f984',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210831'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f422',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f429',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f410',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210831'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f98c',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f999',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f9a5',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f994',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f987',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f989',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f426',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f414',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20230126'
      },
      {
        'leftEmoji': '1fabf',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f427',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f41f',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210831'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f419',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f982',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f982',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20230301'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f577-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f40c',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f41d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f353',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210831'
      },
      {
        'leftEmoji': '1f352',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20220406'
      },
      {
        'leftEmoji': '1f349',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20220406'
      },
      {
        'leftEmoji': '1f34a',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f34d',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f34c',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f34b',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210521'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f336-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f951',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f35e',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210831'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f9c0',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f32d',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f382',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f9c1',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f36c',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20221101'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '2615',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f37d-fe0f',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f307',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210831'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f388',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f381',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      {
        'leftEmoji': '1f3c6',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      {
        'leftEmoji': '26bd',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20220406'
      },
      {
        'leftEmoji': '1f3c0',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20230126'
      },
      {
        'leftEmoji': '1f3a7',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210521'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f451',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f48e',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f4f0',
        'date': '20210218'
      },
      {
        'leftEmoji': '2764-fe0f-200d-1fa79',
        'rightEmoji': '1f52e',
        'date': '20210218'
      }
    ],
    '1f338': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f338', 'date': '20210521' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f600', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f603', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f604', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f601', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f606', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f605', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f602', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f923', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f62d', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f609', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f617', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f619', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f61a', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f618', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f970', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f60d', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f929', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f973', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f643', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f642', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f972', 'date': '20210218' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f60b', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f61b', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f61d', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f61c', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f92a', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f607', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f60a', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '263a-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f60f', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f60c', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f614', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f611', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f610', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f636', 'date': '20210218' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f914', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f92b', 'date': '20210218' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f92d', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f971', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f917', 'date': '20210218' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f631', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f928', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f9d0', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f612', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f644', 'date': '20210218' },
      {
        'leftEmoji': '1f338',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210218'
      },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f624', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f620', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f621', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f92c', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f97a', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f61f', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f625', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f622', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '2639-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f641', 'date': '20210218' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f615', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f910', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f630', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f628', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f627', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f626', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f62e', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f62f', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f632', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f633', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f92f', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f62c', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f613', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f61e', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f616', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f623', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f629', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f62b', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f635', 'date': '20210218' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f634', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f62a', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f924', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f31b', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f31c', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f31e', 'date': '20210218' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f338', 'date': '20211115' },
      {
        'leftEmoji': '1f338',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f974', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f975', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f976', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f922', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f92e', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f927', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f912', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f915', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f637', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f920', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f911', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f60e', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f913', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f978', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f925', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f921', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f47b', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f4a9', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f47d', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f916', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f383', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f608', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f47f', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f525', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f4ab', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '2b50', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f31f', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f4af', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f573-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f38a', 'date': '20210218' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f338', 'date': '20221101' },
      { 'leftEmoji': '1f338', 'rightEmoji': '2764-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f9e1', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f49b', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f49a', 'date': '20210218' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f338', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f338', 'date': '20230301' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f499', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f49c', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f90e', 'date': '20210218' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f338', 'date': '20221101' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f5a4', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f90d', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '2665-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f498', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f49d', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f496', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f497', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f493', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f49e', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f495', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f48c', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f49f', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '2763-fe0f', 'date': '20210218' },
      {
        'leftEmoji': '1f338',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210218'
      },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f494', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f48b', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f9a0', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f480', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f441-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f338', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f338', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f338', 'date': '20220815' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f490', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f339', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f337', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f338', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f33c', 'date': '20210218' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f338', 'date': '20220406' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f335', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f332', 'date': '20210218' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f338', 'date': '20220406' },
      { 'leftEmoji': '1f338', 'rightEmoji': '26c4', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f32a-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f338', 'rightEmoji': '2601-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f338', 'date': '20221101' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f308', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f30d', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f648', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f435', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f981', 'date': '20210218' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f338', 'date': '20220110' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f431', 'date': '20210218' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f338', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f338', 'date': '20210831' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f428', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f43c', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f42d', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f430', 'date': '20210218' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f338', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f437', 'date': '20210218' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f338', 'date': '20210831' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f422', 'date': '20210218' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f338', 'date': '20210831' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f98c', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f999', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f9a5', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f994', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f987', 'date': '20210218' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f338', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f338', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f338', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f338', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f338', 'date': '20210831' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f419', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f338', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f577-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f338', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f41d', 'date': '20210218' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f338', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f338', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f338', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f34d', 'date': '20210218' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f338', 'date': '20210521' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f336-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f951', 'date': '20210218' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f338', 'date': '20210831' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f9c0', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f32d', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f382', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f9c1', 'date': '20210218' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f338', 'date': '20221101' },
      { 'leftEmoji': '1f338', 'rightEmoji': '2615', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f37d-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f338', 'date': '20210831' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f388', 'date': '20210218' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f338', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f338', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f338', 'date': '20210521' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f451', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f48e', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f4f0', 'date': '20210218' },
      { 'leftEmoji': '1f338', 'rightEmoji': '1f52e', 'date': '20210218' }
    ],
    '1f982': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f982', 'date': '20210521' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f600', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f603', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f604', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f601', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f606', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f605', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f602', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f923', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f923', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f62d', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f609', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f617', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f619', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f61a', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f618', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f970', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f60d', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f929', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f973', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f643', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f642', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f972', 'date': '20210218' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f60b', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f61b', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f61d', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f61c', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f92a', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f607', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f60a', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '263a-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f60f', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f60c', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f614', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f611', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f610', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f636', 'date': '20210218' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f914', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f92b', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f92b', 'date': '20230301' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f92d', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f92d', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f971', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f917', 'date': '20210218' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f631', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f928', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f9d0', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f612', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f644', 'date': '20210218' },
      {
        'leftEmoji': '1f982',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210218'
      },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f624', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f620', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f621', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f621', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f92c', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f92c', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f97a', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f61f', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f625', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f622', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '2639-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f641', 'date': '20210218' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f615', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f910', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f630', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f628', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f627', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f626', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f62e', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f62f', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f632', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f633', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f92f', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f62c', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f613', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f61e', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f616', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f623', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f629', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f62b', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f635', 'date': '20210218' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f634', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f62a', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f924', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f31b', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f31c', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f31e', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f31e', 'date': '20230301' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f982', 'date': '20211115' },
      {
        'leftEmoji': '1f982',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f974', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f975', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f975', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f976', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f922', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f92e', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f927', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f912', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f915', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f637', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f920', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f911', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f60e', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f913', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f978', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f925', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f921', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f47b', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f4a9', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f47d', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f916', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f383', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f608', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f47f', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f525', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f4ab', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '2b50', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f31f', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f4af', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f573-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f38a', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '2764-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f9e1', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f49b', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f49a', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f499', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f49c', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f90e', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f5a4', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f90d', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '2665-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f498', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f49d', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f496', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f497', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f497', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f493', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f49e', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f495', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f48c', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f49f', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '2763-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1f982',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210218'
      },
      {
        'leftEmoji': '1f982',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20230301'
      },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f494', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f48b', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f9a0', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f480', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f441-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f982', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f982', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f982', 'date': '20220815' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f490', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f339', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f337', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f338', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f33c', 'date': '20210218' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f982', 'date': '20220406' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f335', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f332', 'date': '20210218' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f982', 'date': '20220406' },
      { 'leftEmoji': '1f982', 'rightEmoji': '26c4', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f32a-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f982', 'rightEmoji': '2601-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f982', 'date': '20221101' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f308', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f30d', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f30d', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f648', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f648', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f435', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f435', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f981', 'date': '20210218' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f982', 'date': '20220110' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f431', 'date': '20210218' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f982', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f982', 'date': '20210831' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f428', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f43c', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f43c', 'date': '20230301' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f42d', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f430', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f430', 'date': '20230301' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f982', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f437', 'date': '20210218' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f982', 'date': '20210831' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f422', 'date': '20210218' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f982', 'date': '20210831' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f98c', 'date': '20220203' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f999', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f9a5', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f994', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f987', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f987', 'date': '20230301' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f982', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f982', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f982', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f982', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f982', 'date': '20210831' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f419', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f982', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f577-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f982', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f41d', 'date': '20210218' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f982', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f982', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f982', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f34d', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f34d', 'date': '20230301' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f982', 'date': '20210521' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f336-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f951', 'date': '20210218' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f982', 'date': '20210831' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f9c0', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f32d', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f382', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f9c1', 'date': '20210218' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f982', 'date': '20221101' },
      { 'leftEmoji': '1f982', 'rightEmoji': '2615', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f37d-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f982', 'date': '20210831' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f388', 'date': '20210218' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f982', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f982', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f982', 'date': '20210521' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f451', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f48e', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f4f0', 'date': '20210218' },
      { 'leftEmoji': '1f982', 'rightEmoji': '1f52e', 'date': '20210218' }
    ],
    '1f40c': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f40c', 'date': '20210521' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f600', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f603', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f604', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f601', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f606', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f605', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f602', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f923', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f62d', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f609', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f617', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f619', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f61a', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f618', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f970', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f60d', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f929', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f973', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f643', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f642', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f972', 'date': '20210218' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f60b', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f61b', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f61d', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f61c', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f92a', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f607', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f60a', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '263a-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f60f', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f60c', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f614', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f611', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f610', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f636', 'date': '20210218' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f914', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f92b', 'date': '20210218' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f92d', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f971', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f917', 'date': '20210218' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f631', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f928', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f9d0', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f612', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f644', 'date': '20210218' },
      {
        'leftEmoji': '1f40c',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210218'
      },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f624', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f620', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f621', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f92c', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f97a', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f61f', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f625', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f622', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '2639-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f641', 'date': '20210218' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f615', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f910', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f630', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f628', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f627', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f626', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f62e', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f62f', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f632', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f633', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f92f', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f62c', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f613', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f61e', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f616', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f623', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f629', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f62b', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f635', 'date': '20210218' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f634', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f62a', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f924', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f31b', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f31c', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f31e', 'date': '20210218' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f40c', 'date': '20211115' },
      {
        'leftEmoji': '1f40c',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210218'
      },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f974', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f975', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f976', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f922', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f92e', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f927', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f912', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f915', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f637', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f920', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f911', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f60e', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f913', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f978', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f925', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f921', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f47b', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f4a9', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f47d', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f916', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f383', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f608', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f47f', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f525', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f4ab', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '2b50', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f31f', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f4af', 'date': '20220203' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f573-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f38a', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '2665-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f498', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f49d', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f496', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f497', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f493', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f49e', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f495', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f48c', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f49f', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '2763-fe0f', 'date': '20210218' },
      {
        'leftEmoji': '1f40c',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210218'
      },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f494', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f48b', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f9a0', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f480', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f441-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f40c', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f40c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f40c', 'date': '20220815' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f490', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f339', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f337', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f338', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f33c', 'date': '20210218' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f40c', 'date': '20220406' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f335', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f332', 'date': '20210218' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f40c', 'date': '20220406' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '26c4', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f32a-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '2601-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f40c', 'date': '20221101' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f308', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f30d', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f648', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f435', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f981', 'date': '20210218' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f40c', 'date': '20220110' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f431', 'date': '20210218' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f40c', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f40c', 'date': '20210831' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f428', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f43c', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f42d', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f430', 'date': '20210218' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f40c', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f437', 'date': '20210218' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f40c', 'date': '20210831' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f422', 'date': '20210218' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f40c', 'date': '20210831' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f98c', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f999', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f9a5', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f994', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f987', 'date': '20210218' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f40c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f40c', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f40c', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f40c', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f40c', 'date': '20210831' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f419', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f982', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f577-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f40c', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f41d', 'date': '20210218' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f40c', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f40c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f40c', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f34d', 'date': '20210218' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f40c', 'date': '20210521' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f336-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f951', 'date': '20210218' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f40c', 'date': '20210831' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f9c0', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f32d', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f382', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f9c1', 'date': '20210218' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f40c', 'date': '20220815' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '2615', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f37d-fe0f', 'date': '20210218' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f40c', 'date': '20210831' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f388', 'date': '20210218' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f40c', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f40c', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f40c', 'date': '20210521' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f451', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f48e', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f4f0', 'date': '20210218' },
      { 'leftEmoji': '1f40c', 'rightEmoji': '1f52e', 'date': '20210218' }
    ],
    '1f34b': [
      { 'leftEmoji': '1f34b', 'rightEmoji': '1fa84', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f600', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f603', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f604', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f601', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f606', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f605', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f602', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f923', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f62d', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f609', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f617', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f619', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f61a', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f618', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f970', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f60d', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f929', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f973', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f643', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f642', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f972', 'date': '20210521' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f60b', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f61b', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f61d', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f61c', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f92a', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f607', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f60a', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '263a-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f60f', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f60c', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f614', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f611', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f610', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f636', 'date': '20210521' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f914', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f92b', 'date': '20210521' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f92d', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f971', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f917', 'date': '20210521' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f631', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f928', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f9d0', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f612', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f644', 'date': '20210521' },
      {
        'leftEmoji': '1f34b',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210521'
      },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f624', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f620', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f621', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f92c', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f97a', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f61f', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f625', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f622', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '2639-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f641', 'date': '20210521' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f615', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f910', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f630', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f628', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f627', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f626', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f62e', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f62f', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f632', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f633', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f92f', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f62c', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f613', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f61e', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f616', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f623', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f629', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f62b', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f635', 'date': '20210521' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f634', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f62a', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f924', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f31b', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f31c', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f31e', 'date': '20210521' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f34b', 'date': '20211115' },
      {
        'leftEmoji': '1f34b',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210521'
      },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f974', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f975', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f976', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f922', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f92e', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f927', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f912', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f915', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f637', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f920', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f911', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f60e', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f913', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f978', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f925', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f921', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f47b', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f4a9', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f47d', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f916', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f383', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f608', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f47f', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f525', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f4ab', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '2b50', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f31f', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f4af', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f573-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f38a', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f49a', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '2665-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f498', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f49d', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f496', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f497', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f493', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f49e', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f495', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f48c', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f49f', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '2763-fe0f', 'date': '20210521' },
      {
        'leftEmoji': '1f34b',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210521'
      },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f494', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f48b', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f9a0', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f480', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f441-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f34b', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f34b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f34b', 'date': '20220815' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f490', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f339', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f337', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f338', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f33c', 'date': '20210521' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f34b', 'date': '20220406' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f335', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f332', 'date': '20210521' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f34b', 'date': '20220406' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '26c4', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f32a-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '2601-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f34b', 'date': '20221101' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f308', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f30d', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f648', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f435', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f981', 'date': '20210521' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f34b', 'date': '20220110' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f431', 'date': '20210521' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f34b', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f34b', 'date': '20210831' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f428', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f43c', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f42d', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f430', 'date': '20210521' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f34b', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f437', 'date': '20210521' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f34b', 'date': '20210831' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f422', 'date': '20210521' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f34b', 'date': '20210831' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f98c', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f999', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f9a5', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f994', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f987', 'date': '20210521' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f34b', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f34b', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f34b', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f34b', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f34b', 'date': '20210831' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f419', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f982', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f577-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f40c', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f41d', 'date': '20210521' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f34b', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f34b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f34b', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f34d', 'date': '20210521' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f34b', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f336-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f951', 'date': '20210521' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f34b', 'date': '20210831' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f9c0', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f32d', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f382', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f9c1', 'date': '20210521' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f34b', 'date': '20221101' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '2615', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f37d-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f34b', 'date': '20210831' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f388', 'date': '20210521' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f34b', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f34b', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f34b', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f451', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f48e', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f4f0', 'date': '20210521' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1f52e', 'date': '20210521' }
    ],
    '1fa84': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa84', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f600', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f603', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f604', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f601', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f606', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f605', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f602', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f923', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f62d', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f609', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f617', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f619', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f61a', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f618', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f970', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f60d', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f929', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f973', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f643', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f642', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f972', 'date': '20210521' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f60b', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f61b', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f61d', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f61c', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f92a', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f607', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f60a', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '263a-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f60f', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f60c', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f614', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f611', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f610', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f636', 'date': '20210521' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f914', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f92b', 'date': '20210521' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f92d', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f92d', 'date': '20230301' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f971', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f917', 'date': '20210521' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f631', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f928', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9d0', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f612', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f644', 'date': '20210521' },
      {
        'leftEmoji': '1fa84',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210521'
      },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f624', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f620', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f621', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f92c', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f97a', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f61f', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f625', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f622', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2639-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f641', 'date': '20210521' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f615', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f910', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f630', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f628', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f627', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f626', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f62e', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f62f', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f632', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f633', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f92f', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f62c', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f613', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f61e', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f616', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f623', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f629', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f62b', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f635', 'date': '20210521' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1fa84', 'date': '20230301' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f634', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f62a', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f924', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f31b', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f31c', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f31e', 'date': '20210521' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1fa84', 'date': '20211115' },
      {
        'leftEmoji': '1fa84',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210521'
      },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f974', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f975', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f976', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f922', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f92e', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f927', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f912', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f915', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f637', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f920', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f911', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f60e', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f913', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f978', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f925', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f921', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f47b', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4a9', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f47d', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f916', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f383', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f608', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f47f', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f525', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4ab', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2b50', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f31f', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4a5', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4af', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fae7', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f573-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f38a', 'date': '20210521' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1fa84', 'date': '20221101' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2764-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9e1', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f49b', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f49a', 'date': '20210521' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1fa84', 'date': '20221101' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f499', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f49c', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f90e', 'date': '20210521' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1fa84', 'date': '20221101' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f5a4', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f90d', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2665-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f498', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f49d', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f496', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f497', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f493', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f49e', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f495', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f48c', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f49f', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2763-fe0f', 'date': '20210521' },
      {
        'leftEmoji': '1fa84',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210521'
      },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f494', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f48b', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9e0', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fac0', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fac1', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa78', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9a0', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9b7', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9b4', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f480', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f441-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1fa84', 'date': '20220203' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa84', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f490', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f339', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f33a', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f337', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f338', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3f5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f33c', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1fa84', 'date': '20220406' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f340', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f335', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f334', 'date': '20230301' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f332', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1fa84', 'date': '20220406' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '26c4', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f300', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f32a-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '26c5', 'date': '20230301' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2601-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa84', 'date': '20221101' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2602-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f308', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f30d', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f648', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f435', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f981', 'date': '20210521' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1fa84', 'date': '20220110' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f431', 'date': '20210521' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1fa84', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1fa84', 'date': '20210831' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f428', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f43c', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f42d', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f430', 'date': '20210521' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1fa84', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f437', 'date': '20210521' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1fa84', 'date': '20210831' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f422', 'date': '20210521' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1fa84', 'date': '20210831' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f98c', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f999', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9a5', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f994', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f987', 'date': '20210521' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1fa84', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1fa84', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fa84', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fa84', 'date': '20221101' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f54a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1fa84', 'date': '20210831' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f419', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f982', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f577-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f40c', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f41d', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f41e', 'date': '20230301' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f43e', 'date': '20220815' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1fa84', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1fa84', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1fa84', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f34d', 'date': '20210521' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1f34b', 'rightEmoji': '1fa84', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f965', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f336-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f360', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9c5', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f951', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9c4', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f954', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1fa84', 'date': '20210831' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9c0', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f32d', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f96a', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fad4', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f32e', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f32f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f35d', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f96b', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f963', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f957', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f372', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f35b', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f35c', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f35a', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f359', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f365', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f960', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f367', 'date': '20230301' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f368', 'date': '20230301' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f366', 'date': '20230301' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f382', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9c1', 'date': '20210521' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1fa84', 'date': '20221101' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f36b', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f375', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2615', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f37d-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f6d1', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f6a8', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f6df', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2693', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f697', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3ce-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f695', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f68c', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f6f8', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f680', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2708-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3aa', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3e0', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1fa84', 'date': '20210831' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f388', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f380', 'date': '20220815' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1faa9', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f947', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f948', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f949', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3c5', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f396-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1fa84', 'date': '20220406' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '26be', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f94e', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3c0', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3d0', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3c8', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3c9', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3be', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f945', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3f8', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f94d', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3cf', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3d1', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3d2', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f94c', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f6f7', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3bf', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '26f8-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f6fc', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa70', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f6f9', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '26f3', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3af', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3f9', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f94f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa83', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa81', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f93f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3bd', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f94b', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f94a', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3b1', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3d3', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3b3', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '265f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa80', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9e9', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3ae', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3b2', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3b0', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3b4', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f004', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f0cf', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4f7', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3a8', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f58c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f58d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1faa1', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9f5', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9f6', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3b9', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3b7', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3ba', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3b8', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa95', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3bb', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa98', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f941', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa97', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3a4', 'date': '20220815' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1fa84', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f399-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4fa', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f39e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3ac', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3ad', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f39f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4f1', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '260e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f50b', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1faab', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4bf', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4b8', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2696-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9e6', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f451', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f48e', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '26d3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f58a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2712-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '270f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4da', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f5c3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4f0', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4e3', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f50e', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f52e', 'date': '20210521' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9ff', 'date': '20230301' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f5dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f512', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2648', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2649', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '264a', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '264b', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '264c', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '264d', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '264e', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '264f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2650', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2651', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2652', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2653', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '26ce', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2757', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2757', 'date': '20230301' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2753', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2753', 'date': '20230301' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2049-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f198', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4f4', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f508', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '267b-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2705', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f195', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f193', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f199', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f197', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f192', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f6ae', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '262e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '262f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '267e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2716-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2795', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2796', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2797', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '27b0', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '27bf', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '3030-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2122-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f5ef-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4ac', 'date': '20220815' }
    ],
    '1f3a7': [
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1fa84', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f600', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f603', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f604', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f601', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f606', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f605', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f602', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f923', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f62d', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f609', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f617', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f619', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f61a', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f618', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f970', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f60d', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f929', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f973', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f643', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f642', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f972', 'date': '20210521' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f60b', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f61b', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f61d', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f61c', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f92a', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f607', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f60a', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '263a-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f60f', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f60c', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f614', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f611', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f610', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f636', 'date': '20210521' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f914', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f92b', 'date': '20210521' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f92d', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f971', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f917', 'date': '20210521' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f631', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f928', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f9d0', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f612', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f644', 'date': '20210521' },
      {
        'leftEmoji': '1f3a7',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210521'
      },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f624', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f620', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f621', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f92c', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f97a', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f61f', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f625', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f622', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '2639-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f641', 'date': '20210521' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f615', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f910', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f630', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f628', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f627', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f626', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f62e', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f62f', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f632', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f633', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f92f', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f62c', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f613', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f61e', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f616', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f623', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f629', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f62b', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f635', 'date': '20210521' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f3a7', 'date': '20230301' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f634', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f62a', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f924', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f31b', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f31c', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f31e', 'date': '20210521' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f3a7', 'date': '20211115' },
      {
        'leftEmoji': '1f3a7',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210521'
      },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f974', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f975', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f976', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f922', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f92e', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f927', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f912', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f915', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f637', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f920', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f911', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f60e', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f913', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f978', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f925', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f921', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f47b', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f4a9', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f47d', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f916', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f383', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f608', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f47f', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f525', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f4ab', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '2b50', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f31f', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f4af', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f573-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f38a', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '2665-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f498', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f49d', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f496', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f497', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f493', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f49e', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f495', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f48c', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f49f', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '2763-fe0f', 'date': '20210521' },
      {
        'leftEmoji': '1f3a7',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210521'
      },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f494', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f48b', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f9a0', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f480', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f441-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f3a7', 'date': '20220203' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f490', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f339', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f337', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f338', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f33c', 'date': '20210521' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f3a7', 'date': '20220406' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f335', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f332', 'date': '20210521' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f3a7', 'date': '20220406' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '26c4', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f32a-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '2601-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3a7', 'date': '20221101' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f308', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f30d', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f648', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f435', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f981', 'date': '20210521' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f3a7', 'date': '20220110' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f431', 'date': '20210521' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f3a7', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f3a7', 'date': '20210831' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f428', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f43c', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f42d', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f430', 'date': '20210521' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f3a7', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f437', 'date': '20210521' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f3a7', 'date': '20210831' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f422', 'date': '20210521' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f3a7', 'date': '20210831' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f98c', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f999', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f9a5', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f994', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f987', 'date': '20210521' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f3a7', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f3a7', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f3a7', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f3a7', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f3a7', 'date': '20210831' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f419', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f982', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f577-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f40c', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f41d', 'date': '20210521' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f3a7', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f3a7', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f3a7', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f34d', 'date': '20210521' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f34b', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f336-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f951', 'date': '20210521' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f3a7', 'date': '20210831' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f9c0', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f32d', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f382', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f9c1', 'date': '20210521' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f3a7', 'date': '20221101' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '2615', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f37d-fe0f', 'date': '20210521' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f3a7', 'date': '20210831' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f388', 'date': '20210521' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f3a7', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f3a7', 'date': '20230126' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f3a7', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f451', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f48e', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f4f0', 'date': '20210521' },
      { 'leftEmoji': '1f3a7', 'rightEmoji': '1f52e', 'date': '20210521' }
    ],
    '1f43b': [
      { 'leftEmoji': '1f43b', 'rightEmoji': '1fa84', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f600', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f603', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f604', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f601', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f606', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f605', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f602', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f923', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f62d', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f609', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f617', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f619', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f61a', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f618', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f970', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f60d', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f929', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f973', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f643', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f642', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f972', 'date': '20210831' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f60b', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f61b', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f61d', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f61c', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f92a', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f607', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f60a', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '263a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f60f', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f60c', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f614', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f611', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f610', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f636', 'date': '20210831' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f914', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f92b', 'date': '20210831' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f92d', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f971', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f917', 'date': '20210831' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f631', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f928', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f9d0', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f612', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f644', 'date': '20210831' },
      {
        'leftEmoji': '1f43b',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210831'
      },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f624', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f620', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f621', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f92c', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f97a', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f61f', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f625', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f622', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '2639-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f641', 'date': '20210831' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f615', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f910', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f630', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f628', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f627', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f626', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f62e', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f62f', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f632', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f633', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f92f', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f62c', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f613', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f61e', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f616', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f623', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f629', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f62b', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f635', 'date': '20210831' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f634', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f62a', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f924', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f31b', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f31c', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f31e', 'date': '20210831' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f43b', 'date': '20211115' },
      {
        'leftEmoji': '1f43b',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210831'
      },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f974', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f975', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f976', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f922', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f92e', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f927', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f912', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f915', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f637', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f920', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f911', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f60e', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f913', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f978', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f925', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f921', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f47b', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f4a9', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f47d', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f916', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f383', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f608', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f47f', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f525', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f4ab', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '2b50', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f31f', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f4af', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f573-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f38a', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '2665-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f498', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f49d', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f496', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f497', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f493', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f49e', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f495', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f48c', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f49f', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '2763-fe0f', 'date': '20210831' },
      {
        'leftEmoji': '1f43b',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210831'
      },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f494', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f48b', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f9a0', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f480', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f441-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f43b', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f43b', 'date': '20220815' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f490', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f339', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f337', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f338', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f33c', 'date': '20210831' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f43b', 'date': '20220406' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f335', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f332', 'date': '20210831' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f43b', 'date': '20220406' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '26c4', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f32a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '2601-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f43b', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f308', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f30d', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f648', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f435', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f981', 'date': '20210831' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f43b', 'date': '20220110' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f431', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f436', 'date': '20210831' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f43b', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f43b', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f428', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f43c', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f42d', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f430', 'date': '20210831' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f43b', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f437', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f984', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f422', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f429', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f410', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f98c', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f999', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f9a5', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f994', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f987', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f989', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f426', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f43b', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f43b', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f41f', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f419', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f982', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f577-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f40c', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f41d', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f353', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f43b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f43b', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f34d', 'date': '20210831' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f34b', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f336-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f951', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f43b', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f9c0', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f32d', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f382', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f9c1', 'date': '20210831' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f43b', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '2615', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f37d-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f307', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f388', 'date': '20210831' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f43b', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f43b', 'date': '20230126' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f3a7', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f451', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f48e', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f4f0', 'date': '20210831' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f52e', 'date': '20210831' }
    ],
    '1f436': [
      { 'leftEmoji': '1f436', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f644', 'date': '20211115' },
      {
        'leftEmoji': '1f436',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f436', 'date': '20211115' },
      {
        'leftEmoji': '1f436',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f436',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f436', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f436', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f436', 'date': '20220815' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f436', 'date': '20220406' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f436', 'date': '20220406' },
      { 'leftEmoji': '1f436', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f436', 'date': '20221101' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f436', 'date': '20220110' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f436', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f436', 'date': '20210831' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f436', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f436', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f436', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f436', 'date': '20210831' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f436', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f436', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f436', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f436', 'date': '20210831' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f436', 'date': '20221101' },
      { 'leftEmoji': '1f436', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f436', 'date': '20210831' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f436', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f436', 'date': '20230126' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f52e', 'date': '20211115' }
    ],
    '1f984': [
      { 'leftEmoji': '1f984', 'rightEmoji': '1fa84', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f600', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f603', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f604', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f601', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f606', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f605', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f602', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f923', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f62d', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f609', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f617', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f619', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f61a', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f618', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f970', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f60d', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f929', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f973', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f643', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f642', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f972', 'date': '20210831' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f60b', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f61b', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f61d', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f61c', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f92a', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f607', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f60a', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '263a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f60f', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f60c', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f614', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f611', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f610', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f636', 'date': '20210831' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f914', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f92b', 'date': '20210831' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f92d', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f971', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f917', 'date': '20210831' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f631', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f928', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f9d0', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f612', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f644', 'date': '20210831' },
      {
        'leftEmoji': '1f984',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210831'
      },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f624', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f620', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f621', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f92c', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f97a', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f61f', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f625', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f622', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '2639-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f641', 'date': '20210831' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f615', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f910', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f630', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f628', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f627', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f626', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f62e', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f62f', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f632', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f633', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f92f', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f62c', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f613', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f61e', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f616', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f623', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f629', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f62b', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f635', 'date': '20210831' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f634', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f62a', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f924', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f31b', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f31c', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f31e', 'date': '20210831' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f984', 'date': '20211115' },
      {
        'leftEmoji': '1f984',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210831'
      },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f974', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f975', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f976', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f922', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f92e', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f927', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f912', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f915', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f637', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f920', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f911', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f60e', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f913', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f978', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f925', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f921', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f47b', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f4a9', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f47d', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f916', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f383', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f608', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f47f', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f525', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f4ab', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '2b50', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f31f', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f4af', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f573-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f38a', 'date': '20210831' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f984', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f984', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '2764-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f9e1', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f49b', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f49a', 'date': '20210831' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f984', 'date': '20221101' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f499', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f49c', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f90e', 'date': '20210831' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f984', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f984', 'date': '20230301' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f5a4', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f90d', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '2665-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f498', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f49d', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f496', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f497', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f493', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f49e', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f495', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f48c', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f49f', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '2763-fe0f', 'date': '20210831' },
      {
        'leftEmoji': '1f984',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210831'
      },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f494', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f48b', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f9a0', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f480', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f441-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f984', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f984', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f984', 'date': '20220815' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f490', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f339', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f337', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f338', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f33c', 'date': '20210831' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f984', 'date': '20220406' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f335', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f332', 'date': '20210831' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f984', 'date': '20220406' },
      { 'leftEmoji': '1f984', 'rightEmoji': '26c4', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f32a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '2601-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f984', 'date': '20221101' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f308', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f30d', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f648', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f435', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f981', 'date': '20210831' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f984', 'date': '20220110' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f431', 'date': '20210831' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f984', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f984', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f428', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f43c', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f42d', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f430', 'date': '20210831' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f984', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f437', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f984', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f422', 'date': '20210831' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f410', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f98c', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f999', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f9a5', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f994', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f987', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f984', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f984', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f984', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f984', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f984', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f419', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f982', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f577-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f40c', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f41d', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f984', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f984', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f984', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f34d', 'date': '20210831' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f34b', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f336-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f951', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f984', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f9c0', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f32d', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f382', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f9c1', 'date': '20210831' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f984', 'date': '20221101' },
      { 'leftEmoji': '1f984', 'rightEmoji': '2615', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f37d-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f984', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f388', 'date': '20210831' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f984', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f984', 'date': '20230126' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f3a7', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f451', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f48e', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f4f0', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f52e', 'date': '20210831' }
    ],
    '1f429': [
      { 'leftEmoji': '1f429', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f644', 'date': '20211115' },
      {
        'leftEmoji': '1f429',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f429', 'date': '20211115' },
      {
        'leftEmoji': '1f429',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f429', 'date': '20221101' },
      { 'leftEmoji': '1f429', 'rightEmoji': '2764-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f9e1', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f49b', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f49a', 'date': '20211115' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f429', 'date': '20221101' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f499', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f49c', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f90e', 'date': '20211115' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f429', 'date': '20221101' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f5a4', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f90d', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f429',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f429', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f429', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f429', 'date': '20220815' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f429', 'date': '20220406' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f429', 'date': '20220406' },
      { 'leftEmoji': '1f429', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f429', 'date': '20221101' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f429', 'date': '20220110' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f429', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f429', 'date': '20210831' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f429', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f429', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f429', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f429', 'date': '20210831' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f429', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f429', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f429', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f429', 'date': '20210831' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f429', 'date': '20221101' },
      { 'leftEmoji': '1f429', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f429', 'date': '20210831' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f429', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f429', 'date': '20230126' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f52e', 'date': '20211115' }
    ],
    '1f410': [
      { 'leftEmoji': '1f410', 'rightEmoji': '1fa84', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f600', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f603', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f604', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f601', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f606', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f605', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f602', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f923', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f62d', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f609', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f617', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f619', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f61a', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f618', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f970', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f60d', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f929', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f973', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f643', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f642', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f972', 'date': '20210831' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f60b', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f61b', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f61d', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f61c', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f92a', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f607', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f60a', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '263a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f60f', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f60c', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f614', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f611', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f610', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f636', 'date': '20210831' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f914', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f92b', 'date': '20210831' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f92d', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f971', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f917', 'date': '20210831' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f631', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f928', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f9d0', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f612', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f644', 'date': '20210831' },
      {
        'leftEmoji': '1f410',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210831'
      },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f624', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f620', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f621', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f92c', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f97a', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f61f', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f625', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f622', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '2639-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f641', 'date': '20210831' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f615', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f910', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f630', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f628', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f627', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f626', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f62e', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f62f', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f632', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f633', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f92f', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f62c', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f613', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f61e', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f616', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f623', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f629', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f62b', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f635', 'date': '20210831' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f634', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f62a', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f924', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f31b', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f31c', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f31e', 'date': '20210831' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f410', 'date': '20211115' },
      {
        'leftEmoji': '1f410',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210831'
      },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f974', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f975', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f976', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f922', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f92e', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f927', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f912', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f915', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f637', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f920', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f911', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f60e', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f913', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f978', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f925', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f921', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f47b', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f4a9', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f47d', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f916', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f383', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f608', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f47f', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f525', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f4ab', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '2b50', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f31f', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f4af', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f573-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f38a', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '2665-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f498', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f49d', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f496', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f497', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f493', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f49e', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f495', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f48c', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f49f', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '2763-fe0f', 'date': '20210831' },
      {
        'leftEmoji': '1f410',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210831'
      },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f494', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f48b', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f9a0', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f480', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f441-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f410', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f410', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f410', 'date': '20220815' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f490', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f339', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f337', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f338', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f33c', 'date': '20210831' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f410', 'date': '20220406' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f335', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f332', 'date': '20210831' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f410', 'date': '20220406' },
      { 'leftEmoji': '1f410', 'rightEmoji': '26c4', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f32a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '2601-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f410', 'date': '20221101' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f308', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f30d', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f648', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f435', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f981', 'date': '20210831' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f410', 'date': '20220110' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f431', 'date': '20210831' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f410', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f410', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f428', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f43c', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f42d', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f430', 'date': '20210831' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f410', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f437', 'date': '20210831' },
      { 'leftEmoji': '1f984', 'rightEmoji': '1f410', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f422', 'date': '20210831' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f410', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f98c', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f999', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f9a5', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f994', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f987', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f410', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f410', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f410', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f410', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f410', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f419', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f982', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f577-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f40c', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f41d', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f410', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f410', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f410', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f34d', 'date': '20210831' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f34b', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f336-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f951', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f410', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f9c0', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f32d', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f382', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f9c1', 'date': '20210831' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f410', 'date': '20221101' },
      { 'leftEmoji': '1f410', 'rightEmoji': '2615', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f37d-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f410', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f388', 'date': '20210831' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f410', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f410', 'date': '20230126' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f3a7', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f451', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f48e', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f4f0', 'date': '20210831' },
      { 'leftEmoji': '1f410', 'rightEmoji': '1f52e', 'date': '20210831' }
    ],
    '1f426': [
      { 'leftEmoji': '1f426', 'rightEmoji': '1fa84', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f600', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f603', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f604', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f601', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f606', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f605', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f602', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f923', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f62d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f609', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f617', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f619', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f61a', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f618', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f970', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f60d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f929', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f973', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f643', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f642', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f972', 'date': '20210831' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f60b', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f61b', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f61d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f61c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f92a', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f607', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f60a', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '263a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f60f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f60c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f614', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f611', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f610', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f636', 'date': '20210831' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f914', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f92b', 'date': '20210831' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f92d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f971', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f917', 'date': '20210831' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f631', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f928', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f9d0', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f612', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f644', 'date': '20210831' },
      {
        'leftEmoji': '1f426',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210831'
      },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f624', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f620', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f621', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f92c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f97a', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f61f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f625', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f622', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '2639-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f641', 'date': '20210831' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f615', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f910', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f630', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f628', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f627', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f626', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f62e', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f62f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f632', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f633', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f92f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f62c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f613', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f61e', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f616', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f623', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f629', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f62b', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f635', 'date': '20210831' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f634', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f62a', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f924', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f31b', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f31c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f31e', 'date': '20210831' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f426', 'date': '20211115' },
      {
        'leftEmoji': '1f426',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210831'
      },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f974', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f975', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f976', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f922', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f92e', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f927', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f912', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f915', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f637', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f920', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f911', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f60e', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f913', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f978', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f925', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f921', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f47b', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f4a9', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f47d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f916', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f383', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f608', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f47f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f525', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f4ab', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '2b50', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f31f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f4af', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f573-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f38a', 'date': '20210831' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f426', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f426', 'date': '20230301' },
      { 'leftEmoji': '1f426', 'rightEmoji': '2764-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f9e1', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f49b', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f49a', 'date': '20210831' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f426', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f426', 'date': '20230301' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f499', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f49c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f90e', 'date': '20210831' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f426', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f426', 'date': '20230301' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f5a4', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f90d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '2665-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f498', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f49d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f496', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f497', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f493', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f49e', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f495', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f48c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f49f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '2763-fe0f', 'date': '20210831' },
      {
        'leftEmoji': '1f426',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210831'
      },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f494', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f48b', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f9a0', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f480', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f441-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f426', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f426', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f426', 'date': '20220815' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f490', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f339', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f337', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f338', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f33c', 'date': '20210831' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f426', 'date': '20220406' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f335', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f332', 'date': '20210831' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f426', 'date': '20220406' },
      { 'leftEmoji': '1f426', 'rightEmoji': '26c4', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f32a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f426', 'rightEmoji': '2601-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f426', 'date': '20221101' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f308', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f30d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f648', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f435', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f981', 'date': '20210831' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f426', 'date': '20220110' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f431', 'date': '20210831' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f426', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f426', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f428', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f43c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f42d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f430', 'date': '20210831' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f426', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f437', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f984', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f422', 'date': '20210831' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f410', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f98c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f999', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f9a5', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f994', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f987', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f989', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f426', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f426', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f426', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f426', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f419', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f982', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f577-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f40c', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f41d', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f426', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f426', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f426', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f34d', 'date': '20210831' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f34b', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f336-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f951', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f426', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f9c0', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f32d', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f382', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f9c1', 'date': '20210831' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f426', 'date': '20221101' },
      { 'leftEmoji': '1f426', 'rightEmoji': '2615', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f37d-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f426', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f388', 'date': '20210831' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f426', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f426', 'date': '20230126' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f3a7', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f451', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f48e', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f4f0', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f52e', 'date': '20210831' }
    ],
    '1f989': [
      { 'leftEmoji': '1f989', 'rightEmoji': '1fa84', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f600', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f603', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f604', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f601', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f606', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f605', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f602', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f923', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f62d', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f609', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f617', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f619', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f61a', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f618', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f970', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f60d', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f929', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f973', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f643', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f642', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f972', 'date': '20210831' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f60b', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f61b', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f61d', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f61c', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f92a', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f607', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f60a', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '263a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f60f', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f60c', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f614', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f611', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f610', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f636', 'date': '20210831' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f914', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f92b', 'date': '20210831' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f92d', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f971', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f917', 'date': '20210831' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f631', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f928', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f9d0', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f612', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f644', 'date': '20210831' },
      {
        'leftEmoji': '1f989',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210831'
      },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f624', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f620', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f621', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f92c', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f97a', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f61f', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f625', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f622', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '2639-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f641', 'date': '20210831' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f615', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f910', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f630', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f628', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f627', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f626', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f62e', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f62f', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f632', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f633', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f92f', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f62c', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f613', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f61e', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f616', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f623', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f629', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f62b', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f635', 'date': '20210831' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f634', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f62a', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f924', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f31b', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f31c', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f31e', 'date': '20210831' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f989', 'date': '20211115' },
      {
        'leftEmoji': '1f989',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210831'
      },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f974', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f975', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f976', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f922', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f92e', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f927', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f912', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f915', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f637', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f920', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f911', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f60e', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f913', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f978', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f925', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f921', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f47b', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f4a9', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f47d', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f916', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f383', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f608', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f47f', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f525', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f4ab', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '2b50', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f31f', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f4af', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f573-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f38a', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '2665-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f498', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f49d', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f496', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f497', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f493', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f49e', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f495', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f48c', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f49f', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '2763-fe0f', 'date': '20210831' },
      {
        'leftEmoji': '1f989',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210831'
      },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f494', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f48b', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f9a0', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f480', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f441-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f989', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f989', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f989', 'date': '20220815' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f490', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f339', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f337', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f338', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f33c', 'date': '20210831' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f989', 'date': '20220406' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f335', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f332', 'date': '20210831' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f989', 'date': '20220406' },
      { 'leftEmoji': '1f989', 'rightEmoji': '26c4', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f32a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '2601-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f989', 'date': '20221101' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f308', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f30d', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f648', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f435', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f981', 'date': '20210831' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f989', 'date': '20220110' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f431', 'date': '20210831' },
      { 'leftEmoji': '1f436', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f989', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f989', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f428', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f43c', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f42d', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f430', 'date': '20210831' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f989', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f437', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f984', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f422', 'date': '20210831' },
      { 'leftEmoji': '1f429', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f410', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f98c', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f999', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f9a5', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f994', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f987', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f989', 'date': '20210831' },
      { 'leftEmoji': '1f426', 'rightEmoji': '1f989', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f989', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f989', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f989', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f419', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f982', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f577-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f40c', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f41d', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f989', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f989', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f989', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f34d', 'date': '20210831' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f34b', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f336-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f951', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f989', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f9c0', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f32d', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f382', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f9c1', 'date': '20210831' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f989', 'date': '20221101' },
      { 'leftEmoji': '1f989', 'rightEmoji': '2615', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f37d-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f989', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f388', 'date': '20210831' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f989', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f989', 'date': '20230126' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f3a7', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f451', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f48e', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f4f0', 'date': '20210831' },
      { 'leftEmoji': '1f989', 'rightEmoji': '1f52e', 'date': '20210831' }
    ],
    '1f41f': [
      { 'leftEmoji': '1f41f', 'rightEmoji': '1fa84', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f600', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f603', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f604', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f601', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f606', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f605', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f602', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f923', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f62d', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f609', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f617', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f619', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f61a', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f618', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f970', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f60d', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f929', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f973', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f643', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f642', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f972', 'date': '20210831' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f60b', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f61b', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f61d', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f61c', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f92a', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f607', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f60a', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '263a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f60f', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f60c', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f614', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f611', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f610', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f636', 'date': '20210831' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f914', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f92b', 'date': '20210831' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f92d', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f971', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f917', 'date': '20210831' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f631', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f928', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f9d0', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f612', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f644', 'date': '20210831' },
      {
        'leftEmoji': '1f41f',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210831'
      },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f624', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f620', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f621', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f92c', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f97a', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f61f', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f625', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f622', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '2639-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f641', 'date': '20210831' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f615', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f910', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f630', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f628', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f627', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f626', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f62e', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f62f', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f632', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f633', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f92f', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f62c', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f613', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f61e', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f616', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f623', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f629', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f62b', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f635', 'date': '20210831' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f634', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f62a', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f924', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f31b', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f31c', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f31e', 'date': '20210831' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f41f', 'date': '20211115' },
      {
        'leftEmoji': '1f41f',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210831'
      },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f974', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f975', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f976', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f922', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f92e', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f927', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f912', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f915', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f637', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f920', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f911', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f60e', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f913', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f978', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f925', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f921', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f47b', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f4a9', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f47d', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f916', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f383', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f608', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f47f', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f525', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f4ab', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '2b50', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f31f', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f4af', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f573-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f38a', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '2665-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f498', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f49d', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f496', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f497', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f493', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f49e', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f495', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f48c', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f49f', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '2763-fe0f', 'date': '20210831' },
      {
        'leftEmoji': '1f41f',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210831'
      },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f494', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f48b', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f9a0', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f480', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f441-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f41f', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f41f', 'date': '20220815' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f490', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f339', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f337', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f338', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f33c', 'date': '20210831' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f41f', 'date': '20220406' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f335', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f332', 'date': '20210831' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f41f', 'date': '20220406' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '26c4', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f32a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '2601-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f41f', 'date': '20221101' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f308', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f30d', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f648', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f435', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f981', 'date': '20210831' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f41f', 'date': '20220110' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f431', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f436', 'date': '20210831' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f41f', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f41f', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f428', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f43c', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f42d', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f430', 'date': '20210831' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f41f', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f437', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f984', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f422', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f429', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f410', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f98c', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f999', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f9a5', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f994', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f987', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f989', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f426', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f41f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f41f', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f41f', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f419', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f982', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f577-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f40c', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f41d', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f353', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f41f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f41f', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f34d', 'date': '20210831' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f34b', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f336-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f951', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f41f', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f9c0', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f32d', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f382', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f9c1', 'date': '20210831' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f41f', 'date': '20221101' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '2615', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f37d-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f307', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f388', 'date': '20210831' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f41f', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f41f', 'date': '20230126' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f3a7', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f451', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f48e', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f4f0', 'date': '20210831' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f52e', 'date': '20210831' }
    ],
    '1f353': [
      { 'leftEmoji': '1f353', 'rightEmoji': '1fa84', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f600', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f603', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f604', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f601', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f606', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f605', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f602', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f923', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f62d', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f609', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f617', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f619', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f61a', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f618', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f970', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f60d', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f929', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f973', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f643', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f642', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f972', 'date': '20210831' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f60b', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f61b', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f61d', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f61c', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f92a', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f607', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f60a', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '263a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f60f', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f60c', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f614', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f611', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f610', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f636', 'date': '20210831' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f914', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f92b', 'date': '20210831' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f92d', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f971', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f917', 'date': '20210831' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f631', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f928', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f9d0', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f612', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f644', 'date': '20210831' },
      {
        'leftEmoji': '1f353',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210831'
      },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f624', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f620', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f621', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f92c', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f97a', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f61f', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f625', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f622', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '2639-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f641', 'date': '20210831' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f615', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f910', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f630', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f628', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f627', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f626', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f62e', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f62f', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f632', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f633', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f92f', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f62c', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f613', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f61e', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f616', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f623', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f629', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f62b', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f635', 'date': '20210831' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f634', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f62a', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f924', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f31b', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f31c', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f31e', 'date': '20210831' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f353', 'date': '20211115' },
      {
        'leftEmoji': '1f353',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210831'
      },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f974', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f975', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f976', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f922', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f92e', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f927', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f912', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f915', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f637', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f920', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f911', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f60e', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f913', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f978', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f925', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f921', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f47b', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f4a9', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f47d', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f916', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f383', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f608', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f47f', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f525', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f4ab', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '2b50', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f31f', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f4af', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f573-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f38a', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '2665-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f498', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f49d', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f496', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f497', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f493', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f49e', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f495', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f48c', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f49f', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '2763-fe0f', 'date': '20210831' },
      {
        'leftEmoji': '1f353',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210831'
      },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f494', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f48b', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f9a0', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f480', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f441-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f353', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f353', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f353', 'date': '20220815' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f490', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f339', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f337', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f338', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f33c', 'date': '20210831' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f353', 'date': '20220406' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f335', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f332', 'date': '20210831' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f353', 'date': '20220406' },
      { 'leftEmoji': '1f353', 'rightEmoji': '26c4', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f32a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f353', 'rightEmoji': '2601-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f353', 'date': '20221101' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f308', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f30d', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f648', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f435', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f981', 'date': '20210831' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f353', 'date': '20220110' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f431', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f436', 'date': '20210831' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f353', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f353', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f428', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f43c', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f42d', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f430', 'date': '20210831' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f353', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f437', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f984', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f422', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f429', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f410', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f98c', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f999', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f9a5', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f994', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f987', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f989', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f426', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f353', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f353', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f353', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f419', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f982', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f577-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f40c', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f41d', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f353', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f353', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f353', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f34d', 'date': '20210831' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f34b', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f336-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f951', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f353', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f9c0', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f32d', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f382', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f9c1', 'date': '20210831' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f353', 'date': '20221101' },
      { 'leftEmoji': '1f353', 'rightEmoji': '2615', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f37d-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f353', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f388', 'date': '20210831' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f353', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f353', 'date': '20230126' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f3a7', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f451', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f48e', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f4f0', 'date': '20210831' },
      { 'leftEmoji': '1f353', 'rightEmoji': '1f52e', 'date': '20210831' }
    ],
    '1f307': [
      { 'leftEmoji': '1f307', 'rightEmoji': '1fa84', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f600', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f603', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f604', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f601', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f606', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f605', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f602', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f923', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f62d', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f609', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f617', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f619', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f61a', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f618', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f970', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f60d', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f929', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f973', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f643', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f642', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f972', 'date': '20210831' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f60b', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f61b', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f61d', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f61c', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f92a', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f607', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f60a', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '263a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f60f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f60c', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f614', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f611', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f610', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f636', 'date': '20210831' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f914', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f92b', 'date': '20210831' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f92d', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f971', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f917', 'date': '20210831' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f631', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f928', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f9d0', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f612', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f644', 'date': '20210831' },
      {
        'leftEmoji': '1f307',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210831'
      },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f624', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f620', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f621', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f92c', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f97a', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f61f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f625', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f622', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '2639-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f641', 'date': '20210831' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f615', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f910', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f630', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f628', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f627', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f626', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f62e', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f62f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f632', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f633', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f92f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f62c', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f613', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f61e', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f616', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f623', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f629', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f62b', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f635', 'date': '20210831' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f634', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f62a', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f924', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f31b', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f31c', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f31e', 'date': '20210831' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f307', 'date': '20211115' },
      {
        'leftEmoji': '1f307',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210831'
      },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f974', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f975', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f976', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f922', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f92e', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f927', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f912', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f915', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f637', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f920', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f911', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f60e', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f913', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f978', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f925', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f921', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f47b', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f4a9', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f47d', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f916', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f383', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f608', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f47f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f525', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f4ab', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '2b50', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f31f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f4af', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f573-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f38a', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '2665-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f498', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f49d', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f496', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f497', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f493', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f49e', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f495', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f48c', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f49f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '2763-fe0f', 'date': '20210831' },
      {
        'leftEmoji': '1f307',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210831'
      },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f494', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f48b', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f9a0', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f480', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f441-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f307', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f307', 'date': '20220815' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f490', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f337', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f338', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f33c', 'date': '20210831' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f307', 'date': '20220406' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f335', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f332', 'date': '20210831' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f307', 'date': '20220406' },
      { 'leftEmoji': '1f307', 'rightEmoji': '26c4', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f32a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f307', 'rightEmoji': '2601-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f307', 'date': '20221101' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f308', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f30d', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f648', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f435', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f981', 'date': '20210831' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f307', 'date': '20220110' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f431', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f436', 'date': '20210831' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f307', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f307', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f428', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f43c', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f42d', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f430', 'date': '20210831' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f307', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f437', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f984', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f422', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f429', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f410', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f98c', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f999', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f9a5', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f994', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f987', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f989', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f426', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f307', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f307', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f307', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f419', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f982', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f577-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f40c', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f41d', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f353', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f307', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f307', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f34d', 'date': '20210831' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f34b', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f336-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f951', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f307', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f9c0', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f32d', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f382', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f9c1', 'date': '20210831' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f307', 'date': '20221101' },
      { 'leftEmoji': '1f307', 'rightEmoji': '2615', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f37d-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f307', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f388', 'date': '20210831' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f307', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f307', 'date': '20230126' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f3a7', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f451', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f48e', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f4f0', 'date': '20210831' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f52e', 'date': '20210831' }
    ],
    '1f35e': [
      { 'leftEmoji': '1f35e', 'rightEmoji': '1fa84', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f600', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f603', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f604', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f601', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f606', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f605', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f602', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f923', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f62d', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f609', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f617', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f619', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f61a', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f618', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f970', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f60d', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f929', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f973', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f643', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f642', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f972', 'date': '20210831' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f60b', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f61b', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f61d', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f61c', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f92a', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f607', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f60a', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '263a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f60f', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f60c', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f614', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f611', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f610', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f636', 'date': '20210831' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f914', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f92b', 'date': '20210831' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f92d', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f971', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f917', 'date': '20210831' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f631', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f928', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f9d0', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f612', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f644', 'date': '20210831' },
      {
        'leftEmoji': '1f35e',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20210831'
      },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f624', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f620', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f621', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f92c', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f97a', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f61f', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f625', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f622', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '2639-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f641', 'date': '20210831' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f615', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f910', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f630', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f628', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f627', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f626', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f62e', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f62f', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f632', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f633', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f92f', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f62c', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f613', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f61e', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f616', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f623', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f629', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f62b', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f635', 'date': '20210831' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f634', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f62a', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f924', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f31b', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f31c', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f31e', 'date': '20210831' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f35e', 'date': '20211115' },
      {
        'leftEmoji': '1f35e',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20210831'
      },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f974', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f975', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f976', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f922', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f92e', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f927', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f912', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f915', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f637', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f920', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f911', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f60e', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f913', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f978', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f925', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f921', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f47b', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f4a9', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f47d', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f916', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f383', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f608', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f47f', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f525', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f4ab', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '2b50', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f31f', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f4af', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f573-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f38a', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '2665-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f498', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f49d', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f496', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f497', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f493', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f49e', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f495', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f48c', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f49f', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '2763-fe0f', 'date': '20210831' },
      {
        'leftEmoji': '1f35e',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20210831'
      },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f494', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f48b', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f9a0', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f480', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f441-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f35e', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f35e', 'date': '20220815' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f490', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f339', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f337', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f338', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f33c', 'date': '20210831' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f35e', 'date': '20220406' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f335', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f332', 'date': '20210831' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f35e', 'date': '20220406' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '26c4', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f32a-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '2601-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f35e', 'date': '20221101' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f308', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f30d', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f648', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f435', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f981', 'date': '20210831' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f35e', 'date': '20220110' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f431', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f436', 'date': '20210831' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f35e', 'date': '20221101' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f43b', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f428', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f43c', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f42d', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f430', 'date': '20210831' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f35e', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f437', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f984', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f422', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f429', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f410', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f98c', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f999', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f9a5', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f994', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f987', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f989', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f426', 'date': '20210831' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f35e', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f35e', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f41f', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f419', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f982', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f577-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f40c', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f41d', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f353', 'date': '20210831' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f35e', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f35e', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f34d', 'date': '20210831' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f34b', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f336-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f951', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f35e', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f9c0', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f32d', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f382', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f9c1', 'date': '20210831' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f35e', 'date': '20221101' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '2615', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f37d-fe0f', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f307', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f388', 'date': '20210831' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f35e', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f35e', 'date': '20230126' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f3a7', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f451', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f48e', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f4f0', 'date': '20210831' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f52e', 'date': '20210831' }
    ],
    '1f979': [
      { 'leftEmoji': '1f979', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f979', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1fae2', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1fae3', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f644', 'date': '20211115' },
      {
        'leftEmoji': '1f979',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1fae4', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1fae5', 'date': '20230301' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1fae0', 'date': '20211115' },
      {
        'leftEmoji': '1f979',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f979',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f979', 'date': '20220203' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f979', 'date': '20220406' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f979', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f979', 'date': '20220406' },
      { 'leftEmoji': '1f979', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f979', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f979', 'date': '20221101' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f979', 'date': '20220110' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f979', 'date': '20221101' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f979', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f979', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f979', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f979', 'date': '20221101' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f979', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f979', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f979', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f979', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f979', 'date': '20220815' },
      { 'leftEmoji': '1f979', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f979', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f979', 'date': '20230126' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f52e', 'date': '20211115' }
    ],
    '1fae2': [
      { 'leftEmoji': '1fae2', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1fae2', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1fae2', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1fae2', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f644', 'date': '20211115' },
      {
        'leftEmoji': '1fae2',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1fae2', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1fae5', 'date': '20230301' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1fae0', 'date': '20211115' },
      {
        'leftEmoji': '1fae2',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1fae2',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1fae2', 'date': '20220203' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1fae2', 'date': '20220406' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1fae2', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1fae2', 'date': '20220406' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fae2', 'date': '20221101' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1fae2', 'date': '20220110' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1fae2', 'date': '20221101' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1fae2', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1fae2', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fae2', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fae2', 'date': '20221101' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1fae2', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1fae2', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1fae2', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1fae2', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1fae2', 'date': '20220815' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1fae2', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1fae2', 'date': '20230126' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f52e', 'date': '20211115' }
    ],
    '1fae1': [
      { 'leftEmoji': '1fae1', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1fae3', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f644', 'date': '20211115' },
      {
        'leftEmoji': '1fae1',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1fae1', 'date': '20230301' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1fae1', 'date': '20211115' },
      {
        'leftEmoji': '1fae1',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1fae1',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1fae1', 'date': '20220203' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1fae1', 'date': '20220406' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1fae1', 'date': '20220406' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fae1', 'date': '20221101' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1fae1', 'date': '20220110' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1fae1', 'date': '20221101' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1fae1', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fae1', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fae1', 'date': '20221101' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1fae1', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1fae1', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1fae1', 'date': '20220815' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1fae1', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1fae1', 'date': '20230126' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f52e', 'date': '20211115' }
    ],
    '1fae4': [
      { 'leftEmoji': '1fae4', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1fae4', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1fae2', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1fae3', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f644', 'date': '20211115' },
      {
        'leftEmoji': '1fae4',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1fae4', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1fae5', 'date': '20230301' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1fae0', 'date': '20211115' },
      {
        'leftEmoji': '1fae4',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1fae4',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1fae4', 'date': '20220203' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1fae4', 'date': '20220406' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1fae4', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1fae4', 'date': '20220406' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fae4', 'date': '20221101' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1fae4', 'date': '20220110' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1fae4', 'date': '20221101' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1fae4', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1fae4', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fae4', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fae4', 'date': '20221101' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1fae4', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1fae4', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1fae4', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1fae4', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1fae4', 'date': '20220815' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1fae4', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1fae4', 'date': '20230126' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f52e', 'date': '20211115' }
    ],
    '1fae3': [
      { 'leftEmoji': '1fae3', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1fae3', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1fae3', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1fae2', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1fae3', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f644', 'date': '20211115' },
      {
        'leftEmoji': '1fae3',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1fae3', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1fae5', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1fae0', 'date': '20211115' },
      {
        'leftEmoji': '1fae3',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1fae3',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1fae3', 'date': '20220203' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1fae3', 'date': '20220406' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1fae3', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1fae3', 'date': '20220406' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fae3', 'date': '20221101' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1fae3', 'date': '20220110' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1fae3', 'date': '20221101' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1fae3', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1fae3', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fae3', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fae3', 'date': '20221101' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1fae3', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1fae3', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1fae3', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1fae3', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1fae3', 'date': '20220815' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1fae3', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1fae3', 'date': '20230126' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f52e', 'date': '20211115' }
    ],
    '1fae0': [
      { 'leftEmoji': '1fae0', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1fae0', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1fae0', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1fae0', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f644', 'date': '20211115' },
      {
        'leftEmoji': '1fae0',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1fae0', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1fae5', 'date': '20230301' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1fae0', 'date': '20211115' },
      {
        'leftEmoji': '1fae0',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1fae0',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1fae0', 'date': '20220203' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1fae0', 'date': '20220406' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1fae0', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1fae0', 'date': '20220406' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fae0', 'date': '20221101' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1fae0', 'date': '20220110' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1fae0', 'date': '20221101' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1fae0', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1fae0', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fae0', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fae0', 'date': '20221101' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1fae0', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1fae0', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1fae0', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1fae0', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1fae0', 'date': '20220815' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1fae0', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1fae0', 'date': '20230126' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f52e', 'date': '20211115' }
    ],
    '1fae5': [
      { 'leftEmoji': '1fae5', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1fa84', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f600', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f603', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f604', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f601', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f606', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f605', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f602', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f923', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62d', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f609', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f617', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f619', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61a', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f618', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f970', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60d', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f929', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f973', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f643', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f642', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f972', 'date': '20230301' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1fae5', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60b', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61b', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61d', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61c', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92a', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f607', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60a', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '263a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60f', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60c', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f614', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f611', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f610', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f636', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1fae1', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f914', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92b', 'date': '20230301' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1fae5', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92d', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f971', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f917', 'date': '20230301' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1fae5', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f631', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f928', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f9d0', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f612', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f644', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f644', 'date': '20230301' },
      {
        'leftEmoji': '1fae5',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      {
        'leftEmoji': '1fae5',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20230301'
      },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f624', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f620', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f97a', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61f', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f625', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f622', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '2639-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f641', 'date': '20230301' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1fae5', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f615', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f910', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f630', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f628', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f627', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f626', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62e', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62f', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f632', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f633', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92f', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62c', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f613', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f61e', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f616', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f623', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f629', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62b', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f635', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1fae5', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f634', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f62a', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f924', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f31e', 'date': '20230301' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1fae5', 'date': '20230301' },
      {
        'leftEmoji': '1fae5',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      {
        'leftEmoji': '1fae5',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20230301'
      },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f974', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f92e', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f927', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f912', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f915', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f637', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f920', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f911', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f60e', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f913', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f978', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f925', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f921', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f47d', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f916', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f4ab', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f493', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f49e', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f495', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f49f', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1fae5',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f480', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1fae5', 'date': '20220203' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f337', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1fae5', 'date': '20220406' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1fae5', 'date': '20220406' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fae5', 'date': '20221101' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1fae5', 'date': '20220110' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1fae5', 'date': '20221101' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1fae5', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fae5', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fae5', 'date': '20221101' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1fae5', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1fae5', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1fae5', 'date': '20220815' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1fae5', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1fae5', 'date': '20230126' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f3a7', 'date': '20230301' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f52e', 'date': '20211115' }
    ],
    '1f427': [
      { 'leftEmoji': '1f427', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f644', 'date': '20211115' },
      {
        'leftEmoji': '1f427',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f427', 'date': '20211115' },
      {
        'leftEmoji': '1f427',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f427',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f427', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f427', 'date': '20220815' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f427', 'date': '20220406' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f427', 'date': '20220406' },
      { 'leftEmoji': '1f427', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f427', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f427', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f427', 'date': '20220110' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f427', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f427', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f427', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f427', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f427', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f427', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f427', 'date': '20221101' },
      { 'leftEmoji': '1f427', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f427', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f427', 'date': '20230126' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f427', 'rightEmoji': '1f52e', 'date': '20211115' }
    ],
    '1f381': [
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f644', 'date': '20211115' },
      {
        'leftEmoji': '1f381',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f381', 'date': '20211115' },
      {
        'leftEmoji': '1f381',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4a5', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fae7', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f381', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2764-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9e1', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f49b', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f49a', 'date': '20211115' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f381', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f381', 'date': '20230301' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f499', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f49c', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f90e', 'date': '20211115' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f381', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f5a4', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f90d', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f381',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9e0', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fac0', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fac1', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa78', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9b7', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9b4', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f381', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f381', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f33a', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4ae', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3f5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f381', 'date': '20220406' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f340', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fab4', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f381', 'date': '20220406' },
      { 'leftEmoji': '1f381', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f300', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f381', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2602-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '26a1', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f381', 'date': '20220110' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f381', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f381', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f381', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f381', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f54a-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f43e', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f381', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f381', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f96d', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f348', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f350', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f95d', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fad2', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fad0', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f347', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f965', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f345', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f955', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f360', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9c5', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f33d', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f966', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f952', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fad1', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9c4', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f954', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fad8', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f330', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f95c', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fad3', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f950', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f96f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f95e', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f969', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f356', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f354', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f96a', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f968', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f35f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fad4', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f32e', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f32f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f959', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9c6', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f958', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f35d', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f96b', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fad5', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f963', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f957', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f372', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f35b', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f35c', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f363', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f364', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f35a', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f371', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f359', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f358', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f365', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f960', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f370', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f381', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f36b', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f369', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f36a', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9c2', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f37f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9cb', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f375', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9c9', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f6d1', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f6a8', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f6df', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2693', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f697', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3ce-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f695', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f68c', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f6f8', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f680', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2708-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3aa', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3e0', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f380', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1faa9', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f947', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f948', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f949', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3c5', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f396-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f381', 'date': '20220406' },
      { 'leftEmoji': '1f381', 'rightEmoji': '26be', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f94e', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3c0', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3d0', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3c8', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3c9', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3be', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f945', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3f8', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f94d', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3cf', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3d1', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3d2', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f94c', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f6f7', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3bf', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '26f8-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f6fc', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa70', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f6f9', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '26f3', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3af', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3f9', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f94f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa83', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa81', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f93f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3bd', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f94b', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f94a', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3b1', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3d3', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3b3', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '265f-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa80', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9e9', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3ae', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3b2', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3b0', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3b4', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f004', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f0cf', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4f7', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3a8', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f58c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f58d-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1faa1', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9f5', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9f6', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3b9', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3b7', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3ba', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3b8', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa95', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3bb', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa98', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f941', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa97', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3a4', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f399-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4fa', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f39e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3ac', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3ad', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f39f-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4f1', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '260e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f50b', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1faab', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4bf', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4b8', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2696-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9e6', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '26d3-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f58a-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2712-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '270f-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4da', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f5c3-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4e3', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f50e', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f52e', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f5dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f512', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2648', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2649', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '264a', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '264b', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '264c', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '264d', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '264e', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '264f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2650', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2651', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2652', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2653', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '26ce', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2757', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2753', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2049-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f198', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4f4', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f508', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '267b-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2705', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f195', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f193', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f199', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f197', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f192', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f6ae', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '262e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '262f-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '267e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2716-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2795', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2796', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2797', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '27b0', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '27bf', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '3030-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2122-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f5ef-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4ac', 'date': '20220506' }
    ],
    '1f3c6': [
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f979', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1fae1', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1fae2', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1fae3', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f644', 'date': '20211115' },
      {
        'leftEmoji': '1f3c6',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1fae4', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1fae5', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1fae0', 'rightEmoji': '1f3c6', 'date': '20211115' },
      {
        'leftEmoji': '1f3c6',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f90e', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f5a4', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f90d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f3c6',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f3c6', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f3c6', 'date': '20220815' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f3c6', 'date': '20220406' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f3c6', 'date': '20220406' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3c6', 'date': '20221101' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f3c6', 'date': '20220110' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f3c6', 'date': '20221101' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f3c6', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f3c6', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f3c6', 'date': '20221101' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f3c6', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f3c6', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f3c6', 'date': '20221101' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f3c6', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f3c6', 'date': '20230126' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f3c6', 'rightEmoji': '1f52e', 'date': '20211115' }
    ],
    '1fab5': [
      { 'leftEmoji': '1fab5', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f979', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1fae2', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1fae3', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f644', 'date': '20211115' },
      {
        'leftEmoji': '1fab5',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1fae4', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1fae0', 'date': '20211115' },
      {
        'leftEmoji': '1fab5',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1fab5',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1fab5', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fab5', 'date': '20220815' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f937', 'date': '20230301' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1fab5', 'date': '20220406' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1fab5', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1fab5', 'date': '20220406' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fab5', 'date': '20221101' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1fab5', 'date': '20220110' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1fab5', 'date': '20221101' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1fab5', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1fab5', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fab5', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fab5', 'date': '20221101' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1fab5', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1fab5', 'date': '20220406' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f34a', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f34c', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1fab5', 'date': '20221101' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1fab5', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1fab5', 'date': '20230126' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f52e', 'date': '20211115' }
    ],
    '1f34a': [
      { 'leftEmoji': '1f34a', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f979', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1fae2', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1fae3', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f644', 'date': '20211115' },
      {
        'leftEmoji': '1f34a',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1fae4', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1fae0', 'date': '20211115' },
      {
        'leftEmoji': '1f34a',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f34a',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f34a', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f34a', 'date': '20220815' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f34a', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f34a', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f34a', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f34a', 'date': '20221101' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f34a', 'date': '20220110' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f34a', 'date': '20221101' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f34a', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f34a', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f34a', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f34a', 'date': '20221101' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f34a', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f34a', 'date': '20220406' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f34a', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f34a', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f34a', 'date': '20221101' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f34a', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f34a', 'date': '20230126' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f34a', 'rightEmoji': '1f52e', 'date': '20211115' }
    ],
    '1f34c': [
      { 'leftEmoji': '1f34c', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f979', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1fae2', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1fae3', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f644', 'date': '20211115' },
      {
        'leftEmoji': '1f34c',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1fae4', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1fae0', 'date': '20211115' },
      {
        'leftEmoji': '1f34c',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f34c',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f34c', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f34c', 'date': '20220815' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f34c', 'date': '20220406' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f34c', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f34c', 'date': '20220406' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f34c', 'date': '20221101' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f34c', 'date': '20220110' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f34c', 'date': '20221101' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f34c', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f34c', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f34c', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f34c', 'date': '20221101' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f34c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f34c', 'date': '20220406' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f34a', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f34c', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f34c', 'date': '20221101' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f34c', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f34c', 'date': '20230126' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f34c', 'rightEmoji': '1f52e', 'date': '20211115' }
    ],
    '1f99d': [
      { 'leftEmoji': '1f99d', 'rightEmoji': '1fa84', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f600', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f603', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f604', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f601', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f606', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f605', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f602', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f923', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f62d', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f609', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f617', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f619', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f61a', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f618', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f970', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f60d', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f929', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f973', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f643', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f642', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f972', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f979', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f60b', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f61b', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f61d', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f61c', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f92a', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f607', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f60a', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '263a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f60f', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f60c', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f614', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f611', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f610', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f636', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1fae1', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f914', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f92b', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1fae2', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f92d', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f971', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f917', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1fae3', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f631', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f928', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f9d0', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f612', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f644', 'date': '20211115' },
      {
        'leftEmoji': '1f99d',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20211115'
      },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f624', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f620', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f621', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f92c', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f97a', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f61f', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f625', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f622', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '2639-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f641', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1fae4', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f615', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f910', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f630', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f628', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f627', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f626', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f62e', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f62f', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f632', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f633', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f92f', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f62c', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f613', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f61e', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f616', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f623', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f629', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f62b', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f635', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1fae5', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f634', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f62a', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f924', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f31b', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f31c', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f31e', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1fae0', 'date': '20211115' },
      {
        'leftEmoji': '1f99d',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20211115'
      },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f974', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f975', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f976', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f922', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f92e', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f927', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f912', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f915', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f637', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f920', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f911', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f60e', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f913', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f978', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f925', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f921', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f47b', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f4a9', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f47d', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f916', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f383', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f608', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f47f', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f525', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f4ab', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '2b50', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f31f', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f4af', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f573-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f38a', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '2665-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f498', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f49d', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f496', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f497', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f493', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f49e', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f495', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f48c', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f49f', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '2763-fe0f', 'date': '20211115' },
      {
        'leftEmoji': '1f99d',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20211115'
      },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f494', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f48b', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f9a0', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f480', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f441-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f99d', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f99d', 'date': '20220815' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f937', 'date': '20230301' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f490', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f339', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f337', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f338', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f33c', 'date': '20211115' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f99d', 'date': '20220406' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f335', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f332', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1fab5', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1faa8', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '26c4', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f32a-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '2601-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f99d', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f308', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f30d', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f648', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f435', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f981', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f431', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f436', 'date': '20211115' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f99d', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f43b', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f428', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f43c', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f42d', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f430', 'date': '20211115' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f99d', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f99d', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f437', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f984', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f422', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f429', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f410', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f98c', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f999', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f9a5', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f994', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f987', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f989', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f426', 'date': '20211115' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f99d', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f99d', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f427', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f41f', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f419', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f982', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f577-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f40c', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f41d', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f353', 'date': '20211115' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f99d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f99d', 'date': '20220406' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f34a', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f34d', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f34c', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f34b', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f336-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f951', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f35e', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f9c0', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f32d', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f382', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f9c1', 'date': '20211115' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f99d', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '2615', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f37d-fe0f', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f307', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f388', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f381', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f3c6', 'date': '20211115' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f99d', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f99d', 'date': '20230126' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f3a7', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f451', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f48e', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f4f0', 'date': '20211115' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f52e', 'date': '20211115' }
    ],
    '1faa8': [
      { 'leftEmoji': '1faa8', 'rightEmoji': '1fa84', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f600', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f603', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f604', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f601', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f606', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f605', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f602', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f923', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f62d', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f609', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f617', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f619', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f61a', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f618', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f970', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f60d', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f929', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f973', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f643', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f642', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f972', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f979', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f60b', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f61b', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f61d', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f61c', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f92a', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f607', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f60a', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '263a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f60f', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f60c', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f614', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f611', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f610', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f636', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1fae1', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f914', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f92b', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1fae2', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f92d', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f971', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f917', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1fae3', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f631', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f928', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f9d0', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f612', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f644', 'date': '20220406' },
      {
        'leftEmoji': '1faa8',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20220406'
      },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f624', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f620', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f621', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f92c', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f97a', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f61f', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f625', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f622', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '2639-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f641', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1fae4', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f615', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f910', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f630', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f628', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f627', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f626', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f62e', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f62f', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f632', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f633', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f92f', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f62c', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f613', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f61e', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f616', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f623', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f629', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f62b', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f635', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1fae5', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f634', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f62a', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f924', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f31b', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f31c', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f31e', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1fae0', 'date': '20220406' },
      {
        'leftEmoji': '1faa8',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20220406'
      },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f974', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f975', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f976', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f922', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f92e', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f927', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f912', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f915', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f637', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f920', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f911', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f60e', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f913', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f978', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f925', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f921', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f47b', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f4a9', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f47d', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f916', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f383', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f608', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f47f', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f525', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f4ab', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '2b50', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f31f', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f4af', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f573-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f38a', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '2665-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f498', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f49d', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f496', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f497', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f493', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f49e', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f495', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f48c', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f49f', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '2763-fe0f', 'date': '20220406' },
      {
        'leftEmoji': '1faa8',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20220406'
      },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f494', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f48b', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f9a0', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f480', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f441-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1faa8', 'date': '20220406' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1faa8', 'date': '20220815' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f490', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f339', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f337', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f338', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f33c', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1faa8', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f335', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f332', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1fab5', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1faa8', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '26c4', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f32a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '2601-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1faa8', 'date': '20221101' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f308', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f30d', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f648', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f435', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f981', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f431', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f436', 'date': '20220406' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1faa8', 'date': '20221101' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f43b', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f428', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f43c', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f42d', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f430', 'date': '20220406' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1faa8', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1faa8', 'date': '20211115' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f437', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f984', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f422', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f429', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f410', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f98c', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f999', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f9a5', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f994', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f987', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f989', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f426', 'date': '20220406' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1faa8', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1faa8', 'date': '20221101' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f427', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f41f', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f419', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f982', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f577-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f40c', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f41d', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f353', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1faa8', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1faa8', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f34a', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f34d', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f34c', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f34b', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f336-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f951', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f35e', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f9c0', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f32d', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f382', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f9c1', 'date': '20220406' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1faa8', 'date': '20221101' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '2615', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f37d-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f307', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f388', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f381', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f3c6', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1faa8', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1faa8', 'date': '20230126' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f3a7', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f451', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f48e', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f4f0', 'date': '20220406' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f52e', 'date': '20220406' }
    ],
    '1f42f': [
      { 'leftEmoji': '1f42f', 'rightEmoji': '1fa84', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f600', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f603', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f604', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f601', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f606', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f605', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f602', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f923', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f62d', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f609', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f617', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f619', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f61a', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f618', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f970', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f60d', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f929', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f973', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f643', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f642', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f972', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f979', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f60b', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f61b', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f61d', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f61c', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f92a', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f607', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f60a', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '263a-fe0f', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f60f', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f60c', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f614', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f611', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f610', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f636', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1fae1', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f914', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f92b', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1fae2', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f92d', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f971', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f917', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1fae3', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f631', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f928', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f9d0', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f612', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f644', 'date': '20220110' },
      {
        'leftEmoji': '1f42f',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20220110'
      },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f624', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f620', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f621', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f92c', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f97a', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f61f', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f625', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f622', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '2639-fe0f', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f641', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1fae4', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f615', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f910', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f630', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f628', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f627', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f626', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f62e', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f62f', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f632', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f633', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f92f', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f62c', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f613', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f61e', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f616', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f623', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f629', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f62b', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f635', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1fae5', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f634', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f62a', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f924', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f31b', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f31c', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f31e', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1fae0', 'date': '20220110' },
      {
        'leftEmoji': '1f42f',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20220110'
      },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f974', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f975', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f976', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f922', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f92e', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f927', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f912', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f915', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f637', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f920', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f911', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f60e', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f913', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f978', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f925', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f921', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f47b', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f4a9', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f47d', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f916', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f383', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f608', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f47f', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f525', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f4ab', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '2b50', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f31f', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f4af', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f573-fe0f', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f38a', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f90d', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '2665-fe0f', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f498', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f49d', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f496', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f497', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f493', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f49e', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f495', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f48c', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f49f', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '2763-fe0f', 'date': '20220110' },
      {
        'leftEmoji': '1f42f',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20220110'
      },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f494', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f48b', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f9a0', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f480', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f441-fe0f', 'date': '20220110' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f42f', 'date': '20220203' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f42f', 'date': '20220815' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f490', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f339', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f337', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f338', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f33c', 'date': '20220110' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f42f', 'date': '20220406' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f335', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f332', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1fab5', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '26c4', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f32a-fe0f', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '2601-fe0f', 'date': '20220110' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f42f', 'date': '20221101' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f308', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f30d', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f648', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f435', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f981', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f42f', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f431', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f436', 'date': '20220110' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f42f', 'date': '20221101' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f43b', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f428', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f43c', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f42d', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f430', 'date': '20220110' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f42f', 'date': '20221101' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f437', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f984', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f422', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f429', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f410', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f98c', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f999', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f9a5', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f994', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f987', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f989', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f426', 'date': '20220110' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f42f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f42f', 'date': '20221101' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f427', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f41f', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f419', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f982', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f577-fe0f', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f40c', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f41d', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f353', 'date': '20220110' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f42f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f42f', 'date': '20220406' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f34a', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f34d', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f34c', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f34b', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f336-fe0f', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f951', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f35e', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f9c0', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f32d', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f382', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f9c1', 'date': '20220110' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f42f', 'date': '20221101' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '2615', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f37d-fe0f', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f307', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f388', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f381', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f3c6', 'date': '20220110' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f42f', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f42f', 'date': '20230126' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f3a7', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f451', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f48e', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f4f0', 'date': '20220110' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f52e', 'date': '20220110' }
    ],
    '1f4a5': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4a5', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4a5', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4a5', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4a5', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4a5', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4a5', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4a5', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4a5', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4a5', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4a5', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4a5', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4a5', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4a5', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4a5', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4a5', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4a5', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4a5', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4a5', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4a5', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4a5', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4a5', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4a5', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4a5', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4a5', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4a5', 'date': '20230301' }
    ],
    '1fae7': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fae7', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fae7', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fae7', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fae7', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fae7', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fae7', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fae7', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fae7', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fae7', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fae7', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fae7', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fae7', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fae7', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fae7', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fae7', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fae7', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fae7', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fae7', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fae7', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fae7', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fae7', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fae7', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fae7', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fae7', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fae7', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fae7', 'date': '20230301' }
    ],
    '1f9e0': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9e0', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9e0', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9e0', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9e0', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9e0', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9e0', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9e0', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9e0', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9e0', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9e0', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9e0', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9e0', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9e0', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9e0', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9e0', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9e0', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9e0', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9e0', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9e0', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9e0', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9e0', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9e0', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9e0', 'date': '20220506' }
    ],
    '1fac0': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fac0', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fac0', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fac0', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fac0', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fac0', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fac0', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fac0', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fac0', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fac0', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fac0', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fac0', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fac0', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fac0', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fac0', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fac0', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fac0', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fac0', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fac0', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fac0', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fac0', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fac0', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fac0', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fac0', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fac0', 'date': '20230301' }
    ],
    '1fac1': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fac1', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fac1', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fac1', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fac1', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fac1', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fac1', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fac1', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fac1', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fac1', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fac1', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fac1', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fac1', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fac1', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fac1', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fac1', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fac1', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fac1', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fac1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fac1', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fac1', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fac1', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fac1', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fac1', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fac1', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fac1', 'date': '20230301' }
    ],
    '1fa78': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa78', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fa78', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa78', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa78', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa78', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa78', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa78', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fa78', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fa78', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fa78', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fa78', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fa78', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fa78', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fa78', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fa78', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fa78', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fa78', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fa78', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fa78', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa78', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fa78', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa78', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa78', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fa78', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa78', 'date': '20220506' }
    ],
    '1f9b7': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9b7', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9b7', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9b7', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9b7', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9b7', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9b7', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9b7', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9b7', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9b7', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9b7', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9b7', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9b7', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9b7', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9b7', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9b7', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9b7', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9b7', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9b7', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9b7', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9b7', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9b7', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9b7', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9b7', 'date': '20220506' }
    ],
    '1f9b4': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9b4', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9b4', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9b4', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9b4', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9b4', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9b4', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9b4', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9b4', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9b4', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9b4', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9b4', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9b4', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9b4', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9b4', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9b4', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9b4', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9b4', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9b4', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9b4', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9b4', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9b4', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9b4', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9b4', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9b4', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9b4', 'date': '20220506' }
    ],
    '1f33a': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f33a', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f33a', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f33a', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f33a', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f33a', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f33a', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f33a', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f33a', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f33a', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f33a', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f33a', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f33a', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f33a', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f33a', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f33a', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f33a', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f33a', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f33a', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f33a', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f33a', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f33a', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f33a', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f33a', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f33a', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f33a', 'date': '20220506' }
    ],
    '1f4ae': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4ae', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4ae', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4ae', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4ae', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4ae', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4ae', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4ae', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4ae', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4ae', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4ae', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4ae', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4ae', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4ae', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4ae', 'date': '20220506' }
    ],
    '1f3f5-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3f5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3f5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3f5-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3f5-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3f5-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3f5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3f5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3f5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3f5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3f5-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3f5-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f3f5-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3f5-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3f5-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3f5-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f3f5-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3f5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3f5-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f3f5-fe0f',
        'date': '20220203'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f3f5-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3f5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3f5-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3f5-fe0f', 'date': '20220506' }
    ],
    '1f344': [
      { 'leftEmoji': '1f344', 'rightEmoji': '1fa84', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f600', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f603', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f604', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f601', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f606', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f605', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f602', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f923', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f62d', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f609', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f617', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f619', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f61a', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f618', 'date': '20220406' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f344', 'date': '20220203' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f970', 'date': '20220406' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f344', 'date': '20220203' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f60d', 'date': '20220406' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f344', 'date': '20220203' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f929', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f973', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f643', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f642', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f972', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f979', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f60b', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f61b', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f61d', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f61c', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f92a', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f607', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f60a', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '263a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f60f', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f60c', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f614', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f611', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f610', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f636', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1fae1', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f914', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f92b', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1fae2', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f92d', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f971', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f917', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1fae3', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f631', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f928', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f9d0', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f612', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f644', 'date': '20220406' },
      {
        'leftEmoji': '1f344',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20220406'
      },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f624', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f620', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f621', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f92c', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f97a', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f61f', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f625', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f622', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '2639-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f641', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1fae4', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f615', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f910', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f630', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f628', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f627', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f626', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f62e', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f62f', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f632', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f633', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f92f', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f62c', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f613', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f61e', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f616', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f623', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f629', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f62b', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f635', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1fae5', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f634', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f62a', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f924', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f31b', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f31c', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f31e', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1fae0', 'date': '20220406' },
      {
        'leftEmoji': '1f344',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20220406'
      },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f974', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f975', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f976', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f922', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f92e', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f927', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f912', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f915', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f637', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f920', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f911', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f60e', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f913', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f978', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f925', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f921', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f47b', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f4a9', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f47d', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f916', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f383', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f608', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f47f', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f525', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f4ab', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '2b50', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f31f', 'date': '20220406' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f344', 'date': '20220203' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f4af', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f573-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f38a', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f90e', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '2665-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f498', 'date': '20220406' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f344', 'date': '20220203' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f49d', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f496', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f497', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f493', 'date': '20220406' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f344', 'date': '20220203' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f49e', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f495', 'date': '20220406' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f344', 'date': '20220203' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f48c', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f49f', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '2763-fe0f', 'date': '20220406' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f344', 'date': '20220203' },
      {
        'leftEmoji': '1f344',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20220406'
      },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f494', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f48b', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f9a0', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f480', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f441-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1fae6', 'date': '20220406' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f344', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f490', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f339', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f337', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f338', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f33c', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f344', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f335', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f332', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1fab5', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1faa8', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '26c4', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f32a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f344', 'rightEmoji': '2601-fe0f', 'date': '20220406' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f344', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f344', 'date': '20221101' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f308', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f30d', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f648', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f435', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f981', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f42f', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f431', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f436', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f43b', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f428', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f43c', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f42d', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f430', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f99d', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f437', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f984', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f422', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f429', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f410', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f98c', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f999', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f9a5', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f994', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f987', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f989', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f426', 'date': '20220406' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f344', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f344', 'date': '20221101' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f427', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f41f', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f419', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f982', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f577-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f40c', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f41d', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f353', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f352', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f349', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f34a', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f34d', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f34c', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f34b', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f336-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f951', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f35e', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f9c0', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f32d', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f382', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f9c1', 'date': '20220406' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f344', 'date': '20221101' },
      { 'leftEmoji': '1f344', 'rightEmoji': '2615', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f37d-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f307', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f388', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f381', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f3c6', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '26bd', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f344', 'date': '20230126' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f3a7', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f451', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f48e', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f4f0', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f52e', 'date': '20220406' }
    ],
    '1f340': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f340', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f340', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f340', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f340', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f340', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f340', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f340', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f340', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f340', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f340', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f340', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f340', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f340', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f340', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f340', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f340', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f340', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f340', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f340', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f340', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f340', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f340', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f340', 'date': '20220506' }
    ],
    '1fab4': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fab4', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fab4', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fab4', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fab4', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fab4', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fab4', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fab4', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fab4', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fab4', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fab4', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fab4', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fab4', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fab4', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fab4', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fab4', 'date': '20220506' }
    ],
    '1f300': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f300', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f300', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f300', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f300', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f300', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f300', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f300', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f300', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f300', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f300', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f300', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f300', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f300', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f300', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f300', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f300', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f300', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f300', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f300', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f300', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f300', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f300', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f300', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f300', 'date': '20230301' }
    ],
    '26a1': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26a1', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26a1', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26a1', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26a1', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26a1', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26a1', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26a1', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26a1', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26a1', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26a1', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26a1', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '26a1', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '26a1', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '26a1', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '26a1', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26a1', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26a1', 'date': '20230301' }
    ],
    '1f43e': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f43e', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f43e', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f43e', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f43e', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f43e', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f43e', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f43e', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f43e', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f43e', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f43e', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f43e', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f43e', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f43e', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f43e', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f43e', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f43e', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f43e', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f43e', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f43e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f43e', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f43e', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f43e', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f43e', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f43e', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f43e', 'date': '20220506' }
    ],
    '1f96d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f96d', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f96d', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f96d', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f96d', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f96d', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f96d', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f96d', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f96d', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f96d', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f96d', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f96d', 'date': '20230301' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f96d', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f96d', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f96d', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f96d', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f96d', 'date': '20220506' }
    ],
    '1f348': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f348', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f348', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f348', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f348', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f348', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f348', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f348', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f348', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f348', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f348', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f348', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f348', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f348', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f348', 'date': '20220506' }
    ],
    '1f350': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f350', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f350', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f350', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f350', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f350', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f350', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f350', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f350', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f350', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f350', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f350', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f350', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f350', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f350', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f350', 'date': '20230301' }
    ],
    '1f95d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f95d', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f95d', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f95d', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f95d', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f95d', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f95d', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f95d', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f95d', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f95d', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f95d', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f95d', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f95d', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f95d', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f95d', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f95d', 'date': '20220506' }
    ],
    '1fad2': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad2', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad2', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fad2', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fad2', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad2', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fad2', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fad2', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fad2', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fad2', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fad2', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fad2', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fad2', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fad2', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fad2', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fad2', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad2', 'date': '20220506' }
    ],
    '1fad0': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad0', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad0', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fad0', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad0', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad0', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fad0', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fad0', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fad0', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fad0', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fad0', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fad0', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fad0', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fad0', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fad0', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fad0', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad0', 'date': '20220506' }
    ],
    '1f347': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f347', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f347', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f347', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f347', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f347', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f347', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f347', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f347', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f347', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f347', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f347', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f347', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f347', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f347', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f347', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f347', 'date': '20230301' }
    ],
    '1f965': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f965', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f965', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f965', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f965', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f965', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f965', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f965', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f965', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f965', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f965', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f965', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f965', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f965', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f965', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f965', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f965', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f965', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f965', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f965', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f965', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f965', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f965', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f965', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f965', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f965', 'date': '20230301' }
    ],
    '1f345': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f345', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f345', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f345', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f345', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f345', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f345', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f345', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f345', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f345', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f345', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f345', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f345', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f345', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f345', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f345', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f345', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f345', 'date': '20220506' }
    ],
    '1f955': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f955', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f955', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f955', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f955', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f955', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f955', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f955', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f955', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f955', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f955', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f955', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f955', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f955', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f955', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f955', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f955', 'date': '20220506' }
    ],
    '1f360': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f360', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f360', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f360', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f360', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f360', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f360', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f360', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f360', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f360', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f360', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f360', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f360', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f360', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f360', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f360', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f360', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f360', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f360', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f360', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f360', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f360', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f360', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f360', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f360', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f360', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f360', 'date': '20230301' }
    ],
    '1f9c5': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9c5', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9c5', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c5', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9c5', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9c5', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9c5', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9c5', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9c5', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9c5', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9c5', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9c5', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9c5', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9c5', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9c5', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9c5', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9c5', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9c5', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9c5', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9c5', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9c5', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9c5', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9c5', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9c5', 'date': '20220506' }
    ],
    '1f33d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f33d', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f33d', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f33d', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f33d', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f33d', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f33d', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f33d', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f33d', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f33d', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f33d', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f33d', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f33d', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f33d', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f33d', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f33d', 'date': '20220506' }
    ],
    '1f966': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f966', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f966', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f966', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f966', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f966', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f966', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f966', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f966', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f966', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f966', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f966', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f966', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f966', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f966', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f966', 'date': '20220506' }
    ],
    '1f952': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f952', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f952', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f952', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f952', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f952', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f952', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f952', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f952', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f952', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f952', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f952', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f952', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f952', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f952', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f952', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f952', 'date': '20220506' }
    ],
    '1fad1': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad1', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fad1', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad1', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fad1', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fad1', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fad1', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fad1', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fad1', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fad1', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fad1', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fad1', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fad1', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fad1', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad1', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad1', 'date': '20230301' }
    ],
    '1f9c4': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9c4', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9c4', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c4', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c4', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9c4', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9c4', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9c4', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9c4', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9c4', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9c4', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9c4', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9c4', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9c4', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9c4', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9c4', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9c4', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9c4', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9c4', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9c4', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9c4', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9c4', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9c4', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9c4', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9c4', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9c4', 'date': '20220506' }
    ],
    '1f954': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f954', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f954', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f954', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f954', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f954', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f954', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f954', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f954', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f954', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f954', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f954', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f954', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f954', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f954', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f954', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f954', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f954', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f954', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f954', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f954', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f954', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f954', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f954', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f954', 'date': '20230301' }
    ],
    '1fad8': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad8', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad8', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fad8', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fad8', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad8', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad8', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fad8', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fad8', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fad8', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fad8', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fad8', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fad8', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fad8', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fad8', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fad8', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fad8', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad8', 'date': '20220506' }
    ],
    '1f330': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f330', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f330', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f330', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f330', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f330', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f330', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f330', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f330', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f330', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f330', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f330', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f330', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f330', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f330', 'date': '20220506' }
    ],
    '1f95c': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f95c', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f95c', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f95c', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f95c', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f95c', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f95c', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f95c', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f95c', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f95c', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f95c', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f95c', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f95c', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f95c', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f95c', 'date': '20220506' }
    ],
    '1fad3': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad3', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fad3', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad3', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fad3', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fad3', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fad3', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fad3', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fad3', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fad3', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fad3', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fad3', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fad3', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fad3', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad3', 'date': '20220506' }
    ],
    '1f950': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f950', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f950', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f950', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f950', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f950', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f950', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f950', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f950', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f950', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f950', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f950', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f950', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f950', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f950', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f950', 'date': '20230301' }
    ],
    '1f96f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f96f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f96f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f96f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f96f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f96f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f96f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f96f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f96f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f96f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f96f', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f96f', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f96f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f96f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f96f', 'date': '20220506' }
    ],
    '1f95e': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f95e', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f95e', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f95e', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f95e', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f95e', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f95e', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f95e', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f95e', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f95e', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f95e', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f95e', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f95e', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f95e', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f95e', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f95e', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f95e', 'date': '20220506' }
    ],
    '1f969': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f969', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f969', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f969', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f969', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f969', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f969', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f969', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f969', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f969', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f969', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f969', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f969', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f969', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f969', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f969', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f969', 'date': '20230301' }
    ],
    '1f356': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f356', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f356', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f356', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f356', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f356', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f356', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f356', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f356', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f356', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f356', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f356', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f356', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f356', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f356', 'date': '20220506' }
    ],
    '1f354': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f354', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f354', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f354', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f354', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f354', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f354', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f354', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f354', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f354', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f354', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f354', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f354', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f354', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f354', 'date': '20220506' }
    ],
    '1f96a': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f96a', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f96a', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f96a', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f96a', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f96a', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f96a', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f96a', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f96a', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f96a', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f96a', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f96a', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f96a', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f96a', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f96a', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f96a', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f96a', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f96a', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f96a', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f96a', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f96a', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f96a', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f96a', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f96a', 'date': '20220506' }
    ],
    '1f968': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f968', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f968', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f968', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f968', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f968', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f968', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f968', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f968', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f968', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f968', 'date': '20230301' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f968', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f968', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f968', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f968', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f968', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f968', 'date': '20230301' }
    ],
    '1f35f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f35f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f35f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f35f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f35f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f35f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f35f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f35f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f35f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f35f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f35f', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f35f', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f35f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f35f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f35f', 'date': '20220506' }
    ],
    '1fad4': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fad4', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fad4', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad4', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad4', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fad4', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad4', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad4', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fad4', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fad4', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fad4', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fad4', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fad4', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fad4', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fad4', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fad4', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fad4', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fad4', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fad4', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fad4', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fad4', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fad4', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fad4', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fad4', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fad4', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad4', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad4', 'date': '20230301' }
    ],
    '1f32e': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f32e', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f32e', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f32e', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f32e', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f32e', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f32e', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f32e', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f32e', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f32e', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f32e', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f32e', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f32e', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f32e', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f32e', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f32e', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f32e', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f32e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f32e', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f32e', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f32e', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f32e', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f32e', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f32e', 'date': '20220506' }
    ],
    '1f32f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f32f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f32f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f32f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f32f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f32f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f32f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f32f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f32f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f32f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f32f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f32f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f32f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f32f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f32f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f32f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f32f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f32f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f32f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f32f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f32f', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f32f', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f32f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f32f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f32f', 'date': '20220506' }
    ],
    '1f959': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f959', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f959', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f959', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f959', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f959', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f959', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f959', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f959', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f959', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f959', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f959', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f959', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f959', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f959', 'date': '20220506' }
    ],
    '1f9c6': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c6', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9c6', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9c6', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9c6', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9c6', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9c6', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9c6', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9c6', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9c6', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9c6', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9c6', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9c6', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9c6', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9c6', 'date': '20220506' }
    ],
    '1f958': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f958', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f958', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f958', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f958', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f958', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f958', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f958', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f958', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f958', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f958', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f958', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f958', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f958', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f958', 'date': '20220506' }
    ],
    '1f35d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f35d', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f35d', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f35d', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f35d', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f35d', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f35d', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f35d', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f35d', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f35d', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f35d', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f35d', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f35d', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f35d', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f35d', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f35d', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f35d', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f35d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f35d', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f35d', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f35d', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f35d', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f35d', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f35d', 'date': '20220506' }
    ],
    '1f96b': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f96b', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f96b', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f96b', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f96b', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f96b', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f96b', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f96b', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f96b', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f96b', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f96b', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f96b', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f96b', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f96b', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f96b', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f96b', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f96b', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f96b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f96b', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f96b', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f96b', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f96b', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f96b', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f96b', 'date': '20220506' }
    ],
    '1fad5': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fad5', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fad5', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fad5', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fad5', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fad5', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fad5', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fad5', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fad5', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fad5', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fad5', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fad5', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fad5', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fad5', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad5', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fad5', 'date': '20230301' }
    ],
    '1f963': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f963', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f963', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f963', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f963', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f963', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f963', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f963', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f963', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f963', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f963', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f963', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f963', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f963', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f963', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f963', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f963', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f963', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f963', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f963', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f963', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f963', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f963', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f963', 'date': '20220506' }
    ],
    '1f957': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f957', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f957', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f957', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f957', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f957', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f957', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f957', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f957', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f957', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f957', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f957', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f957', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f957', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f957', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f957', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f957', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f957', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f957', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f957', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f957', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f957', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f957', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f957', 'date': '20220506' }
    ],
    '1f372': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f372', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f372', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f372', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f372', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f372', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f372', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f372', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f372', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f372', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f372', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f372', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f372', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f372', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f372', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f372', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f372', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f372', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f372', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f372', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f372', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f372', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f372', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f372', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f372', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f372', 'date': '20220506' }
    ],
    '1f35b': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f35b', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f35b', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f35b', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f35b', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f35b', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f35b', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f35b', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f35b', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f35b', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f35b', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f35b', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f35b', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f35b', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f35b', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f35b', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f35b', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f35b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f35b', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f35b', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f35b', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f35b', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f35b', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f35b', 'date': '20220506' }
    ],
    '1f35c': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f35c', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f35c', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f35c', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f35c', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f35c', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f35c', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f35c', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f35c', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f35c', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f35c', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f35c', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f35c', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f35c', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f35c', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f35c', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f35c', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f35c', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f35c', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f35c', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f35c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f35c', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f35c', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f35c', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f35c', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f35c', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f35c', 'date': '20220506' }
    ],
    '1f363': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f363', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f363', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f363', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f363', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f363', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f363', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f363', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f363', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f363', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f363', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f363', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f363', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f363', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f363', 'date': '20220506' }
    ],
    '1f364': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f364', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f364', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f364', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f364', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f364', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f364', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f364', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f364', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f364', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f364', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f364', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f364', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f364', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f364', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f364', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f364', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f364', 'date': '20230301' }
    ],
    '1f35a': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f35a', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f35a', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f35a', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f35a', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f35a', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f35a', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f35a', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f35a', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f35a', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f35a', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f35a', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f35a', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f35a', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f35a', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f35a', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f35a', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f35a', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f35a', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f35a', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f35a', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f35a', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f35a', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f35a', 'date': '20220506' }
    ],
    '1f371': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f371', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f371', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f371', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f371', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f371', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f371', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f371', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f371', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f371', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f371', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f371', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f371', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f371', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f371', 'date': '20220506' }
    ],
    '1f359': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f359', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f359', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f359', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f359', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f359', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f359', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f359', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f359', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f359', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f359', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f359', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f359', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f359', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f359', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f359', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f359', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f359', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f359', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f359', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f359', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f359', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f359', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f359', 'date': '20220506' }
    ],
    '1f358': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f358', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f358', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f358', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f358', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f358', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f358', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f358', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f358', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f358', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f358', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f358', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f358', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f358', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f358', 'date': '20220506' }
    ],
    '1f365': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f365', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f365', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f365', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f365', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f365', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f365', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f365', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f365', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f365', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f365', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f365', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f365', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f365', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f365', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f365', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f365', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f365', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f365', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f365', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f365', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f365', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f365', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f365', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f365', 'date': '20230301' }
    ],
    '1f960': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f960', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f960', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f960', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f960', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f960', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f960', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f960', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f960', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f960', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f960', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f960', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f960', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f960', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f960', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f960', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f960', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f960', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f960', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f960', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f960', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f960', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f960', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f960', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f960', 'date': '20230301' }
    ],
    '1f370': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f370', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f370', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f370', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f370', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f370', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f370', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f370', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f370', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f370', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f370', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f370', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f370', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f370', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f370', 'date': '20220506' }
    ],
    '1f36b': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f36b', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f36b', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f36b', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f36b', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f36b', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f36b', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f36b', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f36b', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f36b', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f36b', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f36b', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f36b', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f36b', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f36b', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f36b', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f36b', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f36b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f36b', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f36b', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f36b', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f36b', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f36b', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f36b', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f36b', 'date': '20230301' }
    ],
    '1f369': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f369', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f369', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f369', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f369', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f369', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f369', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f369', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f369', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f369', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f369', 'date': '20230301' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f369', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f369', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f369', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f369', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f369', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f369', 'date': '20230301' }
    ],
    '1f36a': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f36a', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f36a', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f36a', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f36a', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f36a', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f36a', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f36a', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f36a', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f36a', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f36a', 'date': '20230301' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f36a', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f36a', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f36a', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f36a', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f36a', 'date': '20220506' }
    ],
    '1f9c2': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c2', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9c2', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9c2', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9c2', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9c2', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9c2', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9c2', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9c2', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9c2', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9c2', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9c2', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9c2', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9c2', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9c2', 'date': '20220506' }
    ],
    '1f37f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f37f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f37f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f37f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f37f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f37f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f37f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f37f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f37f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f37f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f37f', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f37f', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f37f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f37f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f37f', 'date': '20220506' }
    ],
    '1f9cb': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9cb', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9cb', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9cb', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9cb', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9cb', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9cb', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9cb', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9cb', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9cb', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9cb', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9cb', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9cb', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9cb', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9cb', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9cb', 'date': '20220506' }
    ],
    '1f375': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f375', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f375', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f375', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f375', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f375', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f375', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f375', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f375', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f375', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f375', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f375', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f375', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f375', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f375', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f375', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f375', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f375', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f375', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f375', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f375', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f375', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f375', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f375', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f375', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f375', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f375', 'date': '20230301' }
    ],
    '1f9c9': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c9', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9c9', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9c9', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9c9', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9c9', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9c9', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9c9', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9c9', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9c9', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9c9', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9c9', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9c9', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9c9', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9c9', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9c9', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9c9', 'date': '20230301' }
    ],
    '1f6d1': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f6d1', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6d1', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6d1', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6d1', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6d1', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6d1', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6d1', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6d1', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f6d1', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f6d1', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f6d1', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f6d1', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f6d1', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f6d1', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f6d1', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f6d1', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f6d1', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f6d1', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f6d1', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f6d1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f6d1', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f6d1', 'date': '20221107' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f6d1', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f6d1', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f6d1', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f6d1', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6d1', 'date': '20220506' }
    ],
    '1f6a8': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f6a8', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6a8', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6a8', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6a8', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6a8', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f6a8', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f6a8', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f6a8', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f6a8', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f6a8', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f6a8', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f6a8', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f6a8', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f6a8', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f6a8', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f6a8', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f6a8', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f6a8', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f6a8', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f6a8', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f6a8', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f6a8', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6a8', 'date': '20220506' }
    ],
    '1f6df': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f6df', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6df', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6df', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6df', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6df', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f6df', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f6df', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f6df', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f6df', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f6df', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f6df', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f6df', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f6df', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f6df', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f6df', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f6df', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f6df', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f6df', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f6df', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f6df', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f6df', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f6df', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6df', 'date': '20220506' }
    ],
    '1f697': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f697', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f697', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f697', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f697', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f697', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f697', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f697', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f697', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f697', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f697', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f697', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f697', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f697', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f697', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f697', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f697', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f697', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f697', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f697', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f697', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f697', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f697', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f697', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f697', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f697', 'date': '20230301' }
    ],
    '1f3ce-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3ce-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3ce-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3ce-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3ce-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3ce-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3ce-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3ce-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3ce-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3ce-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3ce-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3ce-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3ce-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3ce-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3ce-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f3ce-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3ce-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3ce-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3ce-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f3ce-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3ce-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3ce-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f3ce-fe0f',
        'date': '20220203'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f3ce-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3ce-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3ce-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3ce-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3ce-fe0f', 'date': '20230301' }
    ],
    '1f695': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f695', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f695', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f695', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f695', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f695', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f695', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f695', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f695', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f695', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f695', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f695', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f695', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f695', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f695', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f695', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f695', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f695', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f695', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f695', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f695', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f695', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f695', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f695', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f695', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f695', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f695', 'date': '20230301' }
    ],
    '1f68c': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f68c', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f68c', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f68c', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f68c', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f68c', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f68c', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f68c', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f68c', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f68c', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f68c', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f68c', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f68c', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f68c', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f68c', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f68c', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f68c', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f68c', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f68c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f68c', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f68c', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f68c', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f68c', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f68c', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f68c', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f68c', 'date': '20230301' }
    ],
    '1f6f8': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f6f8', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6f8', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6f8', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6f8', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6f8', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6f8', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f6f8', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f6f8', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f6f8', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f6f8', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f6f8', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f6f8', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f6f8', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f6f8', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f6f8', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f6f8', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f6f8', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f6f8', 'date': '20230301' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f6f8', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f6f8', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f6f8', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f6f8', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f6f8', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f6f8', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6f8', 'date': '20220506' }
    ],
    '1f680': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f680', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f680', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f680', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f680', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f680', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f680', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f680', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f680', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f680', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f680', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f680', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f680', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f680', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f680', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f680', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f680', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f680', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f680', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f680', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f680', 'date': '20230301' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f680', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f680', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f680', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f680', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f680', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f680', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f680', 'date': '20220506' }
    ],
    '2708-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2708-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2708-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2708-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2708-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2708-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2708-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2708-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2708-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2708-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2708-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2708-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2708-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2708-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2708-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2708-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2708-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2708-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2708-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2708-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2708-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2708-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2708-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '2708-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2708-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2708-fe0f', 'date': '20220506' }
    ],
    '1f3aa': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3aa', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3aa', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3aa', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3aa', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3aa', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3aa', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3aa', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3aa', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3aa', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3aa', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3aa', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3aa', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3aa', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3aa', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3aa', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3aa', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3aa', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3aa', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3aa', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3aa', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3aa', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3aa', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3aa', 'date': '20220506' }
    ],
    '1f3e0': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3e0', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3e0', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3e0', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3e0', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3e0', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3e0', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3e0', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3e0', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3e0', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3e0', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3e0', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3e0', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3e0', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3e0', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3e0', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3e0', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3e0', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3e0', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3e0', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3e0', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3e0', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3e0', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3e0', 'date': '20220506' }
    ],
    '1f380': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f380', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f380', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f380', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f380', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f380', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f380', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f380', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f380', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f380', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f380', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f380', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f380', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f380', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f380', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f380', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f380', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f380', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f380', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f380', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f380', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f380', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f380', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f380', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f380', 'date': '20230301' }
    ],
    '1faa9': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1faa9', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1faa9', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1faa9', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1faa9', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1faa9', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1faa9', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1faa9', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1faa9', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1faa9', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1faa9', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1faa9', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1faa9', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1faa9', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1faa9', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1faa9', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1faa9', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1faa9', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1faa9', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1faa9', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1faa9', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1faa9', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1faa9', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1faa9', 'date': '20220506' }
    ],
    '1f947': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f947', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f947', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f947', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f947', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f947', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f947', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f947', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f947', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f947', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f947', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f947', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f947', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f947', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f947', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f947', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f947', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f947', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f947', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f947', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f947', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f947', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f947', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f947', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f947', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f947', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f947', 'date': '20220506' }
    ],
    '1f948': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f948', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f948', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f948', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f948', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f948', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f948', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f948', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f948', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f948', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f948', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f948', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f948', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f948', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f948', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f948', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f948', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f948', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f948', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f948', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f948', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f948', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f948', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f948', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f948', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f948', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f948', 'date': '20220506' }
    ],
    '1f949': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f949', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f949', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f949', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f949', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f949', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f949', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f949', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f949', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f949', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f949', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f949', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f949', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f949', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f949', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f949', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f949', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f949', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f949', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f949', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f949', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f949', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f949', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f949', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f949', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f949', 'date': '20220506' }
    ],
    '1f3c5': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3c5', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3c5', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3c5', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3c5', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3c5', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3c5', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3c5', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3c5', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3c5', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3c5', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3c5', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3c5', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3c5', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3c5', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3c5', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3c5', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3c5', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3c5', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3c5', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3c5', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3c5', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3c5', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3c5', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3c5', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3c5', 'date': '20220506' }
    ],
    '1f396-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f396-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f396-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f396-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f396-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f396-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f396-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f396-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f396-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f396-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f396-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f396-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f396-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f396-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f396-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f396-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f396-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f396-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f396-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f396-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f396-fe0f',
        'date': '20220203'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f396-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f396-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f396-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f396-fe0f', 'date': '20220506' }
    ],
    '26bd': [
      { 'leftEmoji': '26bd', 'rightEmoji': '1fa84', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f600', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f603', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f604', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f601', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f606', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f605', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f602', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f923', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f62d', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f609', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f617', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f619', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f61a', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f618', 'date': '20220406' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26bd', 'date': '20220203' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f970', 'date': '20220406' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26bd', 'date': '20220203' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f60d', 'date': '20220406' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26bd', 'date': '20220203' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f929', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f973', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f643', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f642', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f972', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f979', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f60b', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f61b', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f61d', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f61c', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f92a', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f607', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f60a', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '263a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f60f', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f60c', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f614', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f611', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f610', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f636', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1fae1', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f914', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f92b', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1fae2', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f92d', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f971', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f917', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1fae3', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f631', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f928', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f9d0', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f612', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f644', 'date': '20220406' },
      {
        'leftEmoji': '26bd',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20220406'
      },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f624', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f620', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f621', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f92c', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f97a', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f61f', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f625', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f622', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '2639-fe0f', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f641', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1fae4', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f615', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f910', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f630', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f628', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f627', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f626', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f62e', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f62f', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f632', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f633', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f92f', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f62c', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f613', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f61e', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f616', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f623', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f629', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f62b', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f635', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1fae5', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f634', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f62a', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f924', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f31b', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f31c', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f31e', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1fae0', 'date': '20220406' },
      {
        'leftEmoji': '26bd',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20220406'
      },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f974', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f975', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f976', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f922', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f92e', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f927', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f912', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f915', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f637', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f920', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f911', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f60e', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f913', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f978', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f925', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f921', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f47b', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f4a9', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f47d', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f916', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f383', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f608', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f47f', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f525', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f4ab', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '2b50', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f31f', 'date': '20220406' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26bd', 'date': '20220203' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f4af', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f573-fe0f', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f38a', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '2665-fe0f', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f498', 'date': '20220406' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26bd', 'date': '20220203' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f49d', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f496', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f497', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f493', 'date': '20220406' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26bd', 'date': '20220203' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f49e', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f495', 'date': '20220406' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26bd', 'date': '20220203' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f48c', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f49f', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '2763-fe0f', 'date': '20220406' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26bd', 'date': '20220203' },
      {
        'leftEmoji': '26bd',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20220406'
      },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f494', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f48b', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f9a0', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f480', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f441-fe0f', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1fae6', 'date': '20220406' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '26bd', 'date': '20220815' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f490', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f339', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f337', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f338', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f33c', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '26bd', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f335', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f332', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1fab5', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1faa8', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '26c4', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f32a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '26bd', 'rightEmoji': '2601-fe0f', 'date': '20220406' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '26bd', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '26bd', 'date': '20221101' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f308', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f30d', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f648', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f435', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f981', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f42f', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f431', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f436', 'date': '20220406' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '26bd', 'date': '20221101' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f43b', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f428', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f43c', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f42d', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f430', 'date': '20220406' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '26bd', 'date': '20221101' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f99d', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f437', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f984', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f422', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f429', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f410', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f98c', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f999', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f9a5', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f994', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f987', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f989', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f426', 'date': '20220406' },
      { 'leftEmoji': '1f414', 'rightEmoji': '26bd', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '26bd', 'date': '20221101' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f427', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f41f', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f419', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f982', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f577-fe0f', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f40c', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f41d', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f353', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f352', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f349', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f34a', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f34d', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f34c', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f34b', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f336-fe0f', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f951', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f35e', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f9c0', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f32d', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f382', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f9c1', 'date': '20220406' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '26bd', 'date': '20221101' },
      { 'leftEmoji': '26bd', 'rightEmoji': '2615', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f37d-fe0f', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f307', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f388', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f381', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f3c6', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '26bd', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '26bd', 'date': '20230126' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f3a7', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f451', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f48e', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f4f0', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f52e', 'date': '20220406' }
    ],
    '26be': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '26be', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '26be', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26be', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26be', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26be', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26be', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '26be', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '26be', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '26be', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26be', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '26be', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26be', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26be', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26be', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26be', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26be', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26be', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '26be', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '26be', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '26be', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '26be', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '26be', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '26be', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26be', 'date': '20220506' }
    ],
    '1f94e': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f94e', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f94e', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f94e', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f94e', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94e', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94e', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f94e', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f94e', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f94e', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f94e', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f94e', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f94e', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f94e', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f94e', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f94e', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f94e', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f94e', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f94e', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f94e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f94e', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f94e', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f94e', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f94e', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f94e', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f94e', 'date': '20220506' }
    ],
    '1f3c0': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3c0', 'date': '20220815' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f600', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f603', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f604', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f601', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f606', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f605', 'date': '20230126' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3c0', 'date': '20220815' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f602', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f923', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f62d', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f609', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f617', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f619', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f61a', 'date': '20230126' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3c0', 'date': '20220203' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f618', 'date': '20230126' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3c0', 'date': '20220203' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f970', 'date': '20230126' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3c0', 'date': '20220203' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f60d', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f929', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f973', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f643', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f642', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f972', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f979', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f60b', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f61b', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f61d', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f61c', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f92a', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f607', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f60a', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '263a-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f60f', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f60c', 'date': '20230126' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3c0', 'date': '20220815' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f611', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f610', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f636', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1fae1', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f914', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f92b', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1fae2', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f92d', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f971', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f917', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1fae3', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f631', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f928', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f9d0', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f612', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f644', 'date': '20230126' },
      {
        'leftEmoji': '1f3c0',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20230126'
      },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f624', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f620', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f621', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f92c', 'date': '20230126' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3c0', 'date': '20220815' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f61f', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f625', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f622', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '2639-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f641', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1fae4', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f615', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f910', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f630', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f628', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f627', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f626', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f62e', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f62f', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f632', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f633', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f92f', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f62c', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f613', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f61e', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f616', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f623', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f629', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f62b', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f635', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1fae5', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f634', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f62a', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f924', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f31b', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f31c', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f31e', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1fae0', 'date': '20230126' },
      {
        'leftEmoji': '1f3c0',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20230126'
      },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f974', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f975', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f976', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f922', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f92e', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f927', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f912', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f915', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f637', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f920', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f911', 'date': '20230126' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3c0', 'date': '20220815' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f913', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f978', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f925', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f921', 'date': '20230126' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3c0', 'date': '20220815' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f47b', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f4a9', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f47d', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f916', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f383', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f608', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f47f', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f525', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f4ab', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '2b50', 'date': '20230126' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3c0', 'date': '20220203' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f4af', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f573-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3c0', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3c0', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3c0', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3c0', 'date': '20220203' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f498', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f49d', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f496', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f497', 'date': '20230126' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3c0', 'date': '20220203' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f49e', 'date': '20230126' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3c0', 'date': '20220203' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f495', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f48c', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f49f', 'date': '20230126' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3c0', 'date': '20220203' },
      {
        'leftEmoji': '1f3c0',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20230126'
      },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f494', 'date': '20230126' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3c0', 'date': '20220815' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f9a0', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f480', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f441-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1fae6', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f44d', 'date': '20230126' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3c0', 'date': '20220815' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f490', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f339', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f337', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f338', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f33c', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f344', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f335', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f332', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1fab5', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1faa8', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '26c4', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f32a-fe0f', 'date': '20230126' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3c0', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3c0', 'date': '20221101' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f308', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f30d', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f648', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f435', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f981', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f42f', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f431', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f436', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f43a', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f43b', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f428', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f43c', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f42d', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f430', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f98a', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f99d', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f437', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f984', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f422', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f429', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f410', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f98c', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f999', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f9a5', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f994', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f987', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f989', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f426', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f414', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1fabf', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f427', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f41f', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f419', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f982', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f577-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f40c', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f41d', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f353', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f352', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f349', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f34a', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f34d', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f34c', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f34b', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f336-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f951', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f35e', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f9c0', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f32d', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f382', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f9c1', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f36c', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '2615', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f37d-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f307', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f388', 'date': '20230126' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3c0', 'date': '20220506' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f3c6', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '26bd', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f3c0', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3c0', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f3a7', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f451', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f48e', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f4f0', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3c0', 'date': '20220506' }
    ],
    '1f3d0': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3d0', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3d0', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d0', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d0', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d0', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3d0', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3d0', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3d0', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3d0', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3d0', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3d0', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3d0', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3d0', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3d0', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3d0', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3d0', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3d0', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3d0', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3d0', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3d0', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3d0', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3d0', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3d0', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3d0', 'date': '20220506' }
    ],
    '1f3c8': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3c8', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3c8', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3c8', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3c8', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3c8', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3c8', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3c8', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3c8', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3c8', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3c8', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3c8', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3c8', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3c8', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3c8', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3c8', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3c8', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3c8', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3c8', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3c8', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3c8', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3c8', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3c8', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3c8', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3c8', 'date': '20220506' }
    ],
    '1f3c9': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3c9', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3c9', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3c9', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3c9', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3c9', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3c9', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3c9', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3c9', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3c9', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3c9', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3c9', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3c9', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3c9', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3c9', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3c9', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3c9', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3c9', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3c9', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3c9', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3c9', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3c9', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3c9', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3c9', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3c9', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3c9', 'date': '20220506' }
    ],
    '1f3be': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3be', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3be', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3be', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3be', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3be', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3be', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3be', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3be', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3be', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3be', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3be', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3be', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3be', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3be', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3be', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3be', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3be', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3be', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3be', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3be', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3be', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3be', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3be', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3be', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3be', 'date': '20220506' }
    ],
    '1f945': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f945', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f945', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f945', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f945', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f945', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f945', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f945', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f945', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f945', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f945', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f945', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f945', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f945', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f945', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f945', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f945', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f945', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f945', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f945', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f945', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f945', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f945', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f945', 'date': '20220506' }
    ],
    '1f3f8': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3f8', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3f8', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3f8', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3f8', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3f8', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3f8', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3f8', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3f8', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3f8', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3f8', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3f8', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3f8', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3f8', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3f8', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3f8', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3f8', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3f8', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3f8', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3f8', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3f8', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3f8', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3f8', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3f8', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3f8', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3f8', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3f8', 'date': '20220506' }
    ],
    '1f94d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f94d', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f94d', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f94d', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f94d', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f94d', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f94d', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94d', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94d', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f94d', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f94d', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f94d', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f94d', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f94d', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f94d', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f94d', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f94d', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f94d', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f94d', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f94d', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f94d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f94d', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f94d', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f94d', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f94d', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f94d', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f94d', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f94d', 'date': '20230301' }
    ],
    '1f3cf': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3cf', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3cf', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3cf', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3cf', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3cf', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3cf', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3cf', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3cf', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3cf', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3cf', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3cf', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3cf', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3cf', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3cf', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3cf', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3cf', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3cf', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3cf', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3cf', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3cf', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3cf', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3cf', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3cf', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3cf', 'date': '20220506' }
    ],
    '1f3d1': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3d1', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3d1', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d1', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d1', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d1', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d1', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d1', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d1', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3d1', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3d1', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3d1', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3d1', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3d1', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3d1', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3d1', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3d1', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3d1', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3d1', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3d1', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3d1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3d1', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3d1', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3d1', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3d1', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3d1', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3d1', 'date': '20220506' }
    ],
    '1f3d2': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3d2', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3d2', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d2', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d2', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d2', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d2', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d2', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d2', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3d2', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3d2', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3d2', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3d2', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3d2', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3d2', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3d2', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3d2', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3d2', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3d2', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3d2', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3d2', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3d2', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3d2', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3d2', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3d2', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3d2', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3d2', 'date': '20220506' }
    ],
    '1f94c': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f94c', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f94c', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f94c', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f94c', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94c', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f94c', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f94c', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f94c', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f94c', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f94c', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f94c', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f94c', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f94c', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f94c', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f94c', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f94c', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f94c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f94c', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f94c', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f94c', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f94c', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f94c', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f94c', 'date': '20220506' }
    ],
    '1f6f7': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f6f7', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6f7', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6f7', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6f7', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6f7', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6f7', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f6f7', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f6f7', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f6f7', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f6f7', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f6f7', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f6f7', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f6f7', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f6f7', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f6f7', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f6f7', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f6f7', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f6f7', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f6f7', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f6f7', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f6f7', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f6f7', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f6f7', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6f7', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6f7', 'date': '20230301' }
    ],
    '1f3bf': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3bf', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3bf', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3bf', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3bf', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3bf', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3bf', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3bf', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3bf', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3bf', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3bf', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3bf', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3bf', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3bf', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3bf', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3bf', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3bf', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3bf', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3bf', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3bf', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3bf', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3bf', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3bf', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3bf', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3bf', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3bf', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3bf', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3bf', 'date': '20230301' }
    ],
    '26f8-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '26f8-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '26f8-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26f8-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26f8-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26f8-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26f8-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26f8-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '26f8-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '26f8-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '26f8-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26f8-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '26f8-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26f8-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26f8-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26f8-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26f8-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26f8-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '26f8-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '26f8-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '26f8-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '26f8-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '26f8-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '26f8-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26f8-fe0f', 'date': '20220506' }
    ],
    '1f6fc': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f6fc', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6fc', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6fc', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6fc', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6fc', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6fc', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f6fc', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f6fc', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f6fc', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f6fc', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f6fc', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f6fc', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f6fc', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f6fc', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f6fc', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f6fc', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f6fc', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f6fc', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f6fc', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f6fc', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f6fc', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f6fc', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f6fc', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6fc', 'date': '20220506' }
    ],
    '1fa70': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa70', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fa70', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa70', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa70', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa70', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa70', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa70', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fa70', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fa70', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fa70', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fa70', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fa70', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fa70', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fa70', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fa70', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fa70', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fa70', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fa70', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fa70', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa70', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fa70', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa70', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa70', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fa70', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa70', 'date': '20220506' }
    ],
    '1f6f9': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f6f9', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6f9', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6f9', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6f9', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6f9', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6f9', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6f9', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f6f9', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f6f9', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f6f9', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f6f9', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f6f9', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f6f9', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f6f9', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f6f9', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f6f9', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f6f9', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f6f9', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f6f9', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f6f9', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f6f9', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f6f9', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f6f9', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f6f9', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6f9', 'date': '20220506' }
    ],
    '26f3': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '26f3', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '26f3', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26f3', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26f3', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26f3', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26f3', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26f3', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '26f3', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '26f3', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '26f3', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26f3', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '26f3', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26f3', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26f3', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26f3', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26f3', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26f3', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '26f3', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '26f3', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '26f3', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '26f3', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '26f3', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '26f3', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26f3', 'date': '20220506' }
    ],
    '1f3af': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3af', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3af', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3af', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3af', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3af', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3af', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3af', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3af', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3af', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3af', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3af', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3af', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3af', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3af', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3af', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3af', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3af', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3af', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3af', 'date': '20230301' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3af', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3af', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3af', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3af', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3af', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3af', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3af', 'date': '20220506' }
    ],
    '1f3f9': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3f9', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3f9', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3f9', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3f9', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3f9', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3f9', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3f9', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3f9', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3f9', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3f9', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3f9', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3f9', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3f9', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3f9', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3f9', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3f9', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3f9', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3f9', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3f9', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3f9', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3f9', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3f9', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3f9', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3f9', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3f9', 'date': '20230301' }
    ],
    '1f94f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f94f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f94f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f94f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f94f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f94f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f94f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f94f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f94f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f94f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f94f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f94f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f94f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f94f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f94f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f94f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f94f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f94f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f94f', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f94f', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f94f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f94f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f94f', 'date': '20220506' }
    ],
    '1fa83': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa83', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fa83', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa83', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa83', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa83', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa83', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa83', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fa83', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fa83', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fa83', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fa83', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fa83', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fa83', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fa83', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fa83', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fa83', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fa83', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fa83', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fa83', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa83', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fa83', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa83', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa83', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fa83', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa83', 'date': '20220506' }
    ],
    '1fa81': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa81', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fa81', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa81', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa81', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa81', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fa81', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fa81', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fa81', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fa81', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fa81', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fa81', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fa81', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fa81', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fa81', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fa81', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fa81', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fa81', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa81', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fa81', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa81', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa81', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fa81', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa81', 'date': '20220506' }
    ],
    '1f93f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f93f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f93f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f93f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f93f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f93f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f93f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f93f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f93f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f93f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f93f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f93f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f93f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f93f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f93f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f93f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f93f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f93f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f93f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f93f', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f93f', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f93f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f93f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f93f', 'date': '20220506' }
    ],
    '1f3bd': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3bd', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3bd', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3bd', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3bd', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3bd', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3bd', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3bd', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3bd', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3bd', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3bd', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3bd', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3bd', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3bd', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3bd', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3bd', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3bd', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3bd', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3bd', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3bd', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3bd', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3bd', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3bd', 'date': '20220506' }
    ],
    '1f94b': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f94b', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f94b', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f94b', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f94b', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94b', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f94b', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f94b', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f94b', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f94b', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f94b', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f94b', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f94b', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f94b', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f94b', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f94b', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f94b', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f94b', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f94b', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f94b', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f94b', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f94b', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f94b', 'date': '20220506' }
    ],
    '1f94a': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f94a', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f94a', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f94a', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f94a', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94a', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f94a', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f94a', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f94a', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f94a', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f94a', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f94a', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f94a', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f94a', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f94a', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f94a', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f94a', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f94a', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f94a', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f94a', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f94a', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f94a', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f94a', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f94a', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f94a', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f94a', 'date': '20230301' }
    ],
    '1f3b1': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3b1', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3b1', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b1', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b1', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b1', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3b1', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3b1', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3b1', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3b1', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3b1', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3b1', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b1', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b1', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3b1', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3b1', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3b1', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3b1', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3b1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3b1', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3b1', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3b1', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3b1', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3b1', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b1', 'date': '20220506' }
    ],
    '1f3d3': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3d3', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3d3', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d3', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d3', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d3', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d3', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d3', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d3', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3d3', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3d3', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3d3', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3d3', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3d3', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3d3', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3d3', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3d3', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3d3', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3d3', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3d3', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3d3', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3d3', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3d3', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3d3', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3d3', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3d3', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3d3', 'date': '20220506' }
    ],
    '1f3b3': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3b3', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3b3', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b3', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b3', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b3', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b3', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b3', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3b3', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3b3', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3b3', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3b3', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3b3', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3b3', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b3', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3b3', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3b3', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3b3', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3b3', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3b3', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3b3', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3b3', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3b3', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3b3', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3b3', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b3', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b3', 'date': '20230301' }
    ],
    '265f-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '265f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '265f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '265f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '265f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '265f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '265f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '265f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '265f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '265f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '265f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '265f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '265f-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '265f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '265f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '265f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '265f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '265f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '265f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '265f-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '265f-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '265f-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '265f-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '265f-fe0f', 'date': '20220506' }
    ],
    '1fa80': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa80', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fa80', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa80', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa80', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa80', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fa80', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fa80', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fa80', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fa80', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fa80', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fa80', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fa80', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fa80', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fa80', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fa80', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fa80', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fa80', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa80', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fa80', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa80', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa80', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fa80', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa80', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa80', 'date': '20230301' }
    ],
    '1f9e9': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9e9', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9e9', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9e9', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9e9', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9e9', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9e9', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9e9', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9e9', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9e9', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9e9', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9e9', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9e9', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9e9', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9e9', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9e9', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9e9', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9e9', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9e9', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9e9', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9e9', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9e9', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9e9', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9e9', 'date': '20220506' }
    ],
    '1f3ae': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3ae', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3ae', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3ae', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3ae', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3ae', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3ae', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3ae', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3ae', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3ae', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3ae', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3ae', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3ae', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3ae', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3ae', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3ae', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3ae', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3ae', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3ae', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3ae', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3ae', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3ae', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3ae', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3ae', 'date': '20220506' }
    ],
    '1f3b2': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3b2', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3b2', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b2', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b2', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b2', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3b2', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3b2', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3b2', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3b2', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3b2', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3b2', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b2', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b2', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3b2', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3b2', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3b2', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3b2', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3b2', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3b2', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3b2', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3b2', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3b2', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3b2', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b2', 'date': '20220506' }
    ],
    '1f3b0': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3b0', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3b0', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b0', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b0', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b0', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b0', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b0', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b0', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3b0', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3b0', 'date': '20220815' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f31a', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f31d', 'date': '20230126' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3b0', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3b0', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3b0', 'date': '20220203' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4a5', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fae7', 'date': '20230126' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3b0', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b0', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3b0', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3b0', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3b0', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3b0', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3b0', 'date': '20220815' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9e0', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fac0', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fac1', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fa78', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9b7', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9b4', 'date': '20230126' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3b0', 'date': '20220815' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f33a', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4ae', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3f5-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f33b', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f341', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f331', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f33f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f343', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f340', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fab4', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f334', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f333', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fab9', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f30a', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f32c-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f300', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f30b', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3d6-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '26c5', 'date': '20230126' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3b0', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3b0', 'date': '20221101' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f329-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4a7', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2602-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '26a1', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2604-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fa90', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f54a-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9a9', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f99e', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f980', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fab8', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f41a', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f997', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fab2', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fab3', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f41e', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f98b', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f43e', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f96d', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f348', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f350', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f95d', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fad2', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fad0', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f347', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f965', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f345', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f955', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f360', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9c5', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f33d', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f966', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f952', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fad1', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9c4', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f954', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fad8', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f330', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f95c', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fad3', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f950', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f96f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f95e', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f373', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f969', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f356', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f354', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f96a', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f968', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f35f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fad4', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f32e', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f32f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f959', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9c6', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f958', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f35d', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f96b', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fad5', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f963', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f957', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f372', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f35b', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f35c', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f363', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f364', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f35a', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f371', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f359', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f358', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f365', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f960', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f367', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f368', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f366', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f370', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f36c', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f36b', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f369', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f36a', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9c2', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f37f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9cb', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f37c', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f375', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9c9', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f379', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f6d1', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f6a8', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f6df', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2693', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f697', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3ce-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f695', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f68c', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f682', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f6f8', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f680', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2708-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3a2', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3a1', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3aa', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3db-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3df-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3e0', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3d5-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3dd-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f380', 'date': '20230126' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3b0', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1faa9', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f947', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f948', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f949', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3c5', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f396-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '26be', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f94e', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3c0', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3d0', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3c8', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3c9', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3be', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f945', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3f8', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f94d', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3cf', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3d1', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3d2', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f94c', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f6f7', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3bf', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '26f8-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f6fc', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fa70', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f6f9', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '26f3', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3af', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3f9', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f94f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fa83', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fa81', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f93f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3bd', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f94b', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f94a', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3b1', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3d3', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3b3', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '265f-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fa80', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9e9', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3ae', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3b2', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3b0', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3b4', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f004', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f0cf', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4f7', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3a8', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f58c-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f58d-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1faa1', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9f5', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9f6', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3b9', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3b7', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3ba', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3b8', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fa95', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3bb', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fa98', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f941', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fa97', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3a4', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f399-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4fa', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f39e-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3ac', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3ad', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f39f-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4f1', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '260e-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f50b', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1faab', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4be', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4bf', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4b8', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2696-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4a1', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9fc', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9e6', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f6e0-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '26d3-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f5d1-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f58a-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2712-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '270f-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4da', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f5c3-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4e3', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f50e', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b0', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b0', 'date': '20230301' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9ff', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f5dd-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f512', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2648', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2649', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '264a', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '264b', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '264c', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '264d', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '264e', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '264f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2650', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2651', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2652', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2653', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '26ce', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2757', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2753', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2049-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f198', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4f4', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f508', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '26a0-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '267b-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2705', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f195', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f193', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f199', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f197', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f192', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f6ae', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '262e-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '262f-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '267e-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2716-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2795', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2796', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2797', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '27b0', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '27bf', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '3030-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2122-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2660-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f5ef-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4ac', 'date': '20230126' }
    ],
    '1f3b4': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3b4', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3b4', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b4', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b4', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b4', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b4', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3b4', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3b4', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3b4', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3b4', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3b4', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3b4', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b4', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3b4', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3b4', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3b4', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3b4', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3b4', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3b4', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3b4', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3b4', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3b4', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3b4', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b4', 'date': '20220506' }
    ],
    '1f004': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f004', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f004', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f004', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f004', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f004', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f004', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f004', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f004', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f004', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f004', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f004', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f004', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f004', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f004', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f004', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f004', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f004', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f004', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f004', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f004', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f004', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f004', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f004', 'date': '20220506' }
    ],
    '1f0cf': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f0cf', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f0cf', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f0cf', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f0cf', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f0cf', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f0cf', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f0cf', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f0cf', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f0cf', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f0cf', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f0cf', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f0cf', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f0cf', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f0cf', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f0cf', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f0cf', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f0cf', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f0cf', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f0cf', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f0cf', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f0cf', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f0cf', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f0cf', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f0cf', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f0cf', 'date': '20220506' }
    ],
    '1f4f7': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4f7', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4f7', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4f7', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4f7', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4f7', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4f7', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4f7', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4f7', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4f7', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4f7', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4f7', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4f7', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4f7', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4f7', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4f7', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4f7', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4f7', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4f7', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4f7', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4f7', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4f7', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4f7', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4f7', 'date': '20220506' }
    ],
    '1f3a8': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3a8', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3a8', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3a8', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3a8', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3a8', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3a8', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3a8', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3a8', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3a8', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3a8', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3a8', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3a8', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3a8', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3a8', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3a8', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3a8', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3a8', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3a8', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3a8', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3a8', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3a8', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3a8', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3a8', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3a8', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3a8', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3a8', 'date': '20220506' }
    ],
    '1f58c-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f58c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f58c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f58c-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f58c-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f58c-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f58c-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f58c-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f58c-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f58c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f58c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f58c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f58c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f58c-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f58c-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f58c-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f58c-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f58c-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f58c-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f58c-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f58c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f58c-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f58c-fe0f',
        'date': '20220203'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f58c-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f58c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f58c-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f58c-fe0f', 'date': '20220506' }
    ],
    '1f58d-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f58d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f58d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f58d-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f58d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f58d-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f58d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f58d-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f58d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f58d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f58d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f58d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f58d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f58d-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f58d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f58d-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f58d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f58d-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f58d-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f58d-fe0f', 'date': '20230301' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f58d-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f58d-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f58d-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f58d-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f58d-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f58d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f58d-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f58d-fe0f',
        'date': '20220203'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f58d-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f58d-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f58d-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f58d-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f58d-fe0f', 'date': '20230301' }
    ],
    '1faa1': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1faa1', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1faa1', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1faa1', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1faa1', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1faa1', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1faa1', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1faa1', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1faa1', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1faa1', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1faa1', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1faa1', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1faa1', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1faa1', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1faa1', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1faa1', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1faa1', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1faa1', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1faa1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1faa1', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1faa1', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1faa1', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1faa1', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1faa1', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1faa1', 'date': '20220506' }
    ],
    '1f9f5': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9f5', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9f5', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9f5', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9f5', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9f5', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9f5', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9f5', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9f5', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9f5', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9f5', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9f5', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9f5', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9f5', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9f5', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9f5', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9f5', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9f5', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9f5', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9f5', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9f5', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9f5', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9f5', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9f5', 'date': '20220506' }
    ],
    '1f9f6': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9f6', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9f6', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9f6', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9f6', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9f6', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9f6', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9f6', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9f6', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9f6', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9f6', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9f6', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9f6', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9f6', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9f6', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9f6', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9f6', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9f6', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9f6', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9f6', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9f6', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9f6', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9f6', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9f6', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9f6', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9f6', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9f6', 'date': '20220506' }
    ],
    '1f3b9': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3b9', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3b9', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b9', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b9', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b9', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3b9', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3b9', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3b9', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3b9', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3b9', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3b9', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b9', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3b9', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3b9', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3b9', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3b9', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3b9', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3b9', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3b9', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3b9', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3b9', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3b9', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b9', 'date': '20220506' }
    ],
    '1f3b7': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3b7', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3b7', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b7', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b7', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b7', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b7', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b7', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b7', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3b7', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3b7', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3b7', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3b7', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3b7', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3b7', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b7', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3b7', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3b7', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3b7', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3b7', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3b7', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3b7', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3b7', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3b7', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3b7', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3b7', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b7', 'date': '20220506' }
    ],
    '1f3ba': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3ba', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3ba', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3ba', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3ba', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3ba', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3ba', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3ba', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3ba', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3ba', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3ba', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3ba', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3ba', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3ba', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3ba', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3ba', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3ba', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3ba', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3ba', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3ba', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3ba', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3ba', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3ba', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3ba', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3ba', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3ba', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3ba', 'date': '20220506' }
    ],
    '1f3b8': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3b8', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3b8', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b8', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3b8', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3b8', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3b8', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3b8', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3b8', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3b8', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3b8', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3b8', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3b8', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3b8', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3b8', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3b8', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3b8', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3b8', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3b8', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3b8', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3b8', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3b8', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3b8', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3b8', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3b8', 'date': '20220506' }
    ],
    '1fa95': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa95', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fa95', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa95', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa95', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa95', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa95', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa95', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fa95', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fa95', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fa95', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fa95', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fa95', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fa95', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fa95', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fa95', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fa95', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fa95', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fa95', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fa95', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa95', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fa95', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa95', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa95', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fa95', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa95', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa95', 'date': '20230301' }
    ],
    '1f3bb': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3bb', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3bb', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3bb', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3bb', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3bb', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3bb', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3bb', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3bb', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3bb', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3bb', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3bb', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3bb', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3bb', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3bb', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3bb', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3bb', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3bb', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3bb', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3bb', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3bb', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3bb', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3bb', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3bb', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3bb', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3bb', 'date': '20220506' }
    ],
    '1fa98': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa98', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fa98', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa98', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa98', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa98', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa98', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa98', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fa98', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fa98', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fa98', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fa98', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fa98', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fa98', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fa98', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fa98', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fa98', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fa98', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fa98', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fa98', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa98', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fa98', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa98', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa98', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fa98', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa98', 'date': '20220506' }
    ],
    '1f941': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f941', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f941', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f941', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f941', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f941', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f941', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f941', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f941', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f941', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f941', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f941', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f941', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f941', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f941', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f941', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f941', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f941', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f941', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f941', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f941', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f941', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f941', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f941', 'date': '20220506' }
    ],
    '1fa97': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa97', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fa97', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa97', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa97', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa97', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fa97', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fa97', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fa97', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fa97', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fa97', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fa97', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fa97', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fa97', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fa97', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fa97', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fa97', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fa97', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa97', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fa97', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa97', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa97', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fa97', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa97', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa97', 'date': '20230301' }
    ],
    '1f3a4': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3a4', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3a4', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3a4', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3a4', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3a4', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3a4', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3a4', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3a4', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3a4', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3a4', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3a4', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3a4', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3a4', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3a4', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3a4', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3a4', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3a4', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3a4', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3a4', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3a4', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3a4', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3a4', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3a4', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3a4', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3a4', 'date': '20220506' }
    ],
    '1f399-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f399-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f399-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f399-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f399-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f399-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f399-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f399-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f399-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f399-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f399-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f399-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f399-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f399-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f399-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f399-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f399-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f399-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f399-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f399-fe0f',
        'date': '20220203'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f399-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f399-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f399-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f399-fe0f', 'date': '20220506' }
    ],
    '1f4fa': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4fa', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4fa', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4fa', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4fa', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4fa', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4fa', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4fa', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4fa', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4fa', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4fa', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4fa', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4fa', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4fa', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4fa', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4fa', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4fa', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4fa', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4fa', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4fa', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4fa', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4fa', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4fa', 'date': '20221107' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4fa', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4fa', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4fa', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4fa', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4fa', 'date': '20220506' }
    ],
    '1f39e-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f39e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f39e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f39e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f39e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f39e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f39e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f39e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f39e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f39e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f39e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f39e-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f39e-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f39e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f39e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f39e-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f39e-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f39e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f39e-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f39e-fe0f',
        'date': '20220203'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f39e-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f39e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f39e-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f39e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f39e-fe0f', 'date': '20230301' }
    ],
    '1f3ac': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3ac', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3ac', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3ac', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3ac', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3ac', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3ac', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3ac', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3ac', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3ac', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3ac', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3ac', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3ac', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3ac', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3ac', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3ac', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3ac', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3ac', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3ac', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3ac', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3ac', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3ac', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3ac', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3ac', 'date': '20220506' }
    ],
    '1f3ad': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3ad', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3ad', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3ad', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3ad', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3ad', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3ad', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3ad', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3ad', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3ad', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3ad', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3ad', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3ad', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3ad', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3ad', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3ad', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3ad', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3ad', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3ad', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3ad', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3ad', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3ad', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3ad', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3ad', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3ad', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3ad', 'date': '20220506' }
    ],
    '1f39f-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f39f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f39f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f39f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f39f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f39f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f39f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f39f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f39f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f39f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f39f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f39f-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f39f-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f39f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f39f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f39f-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f39f-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f39f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f39f-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f39f-fe0f',
        'date': '20220203'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f39f-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f39f-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f39f-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f39f-fe0f', 'date': '20220506' }
    ],
    '1f4f1': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4f1', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4f1', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4f1', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4f1', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4f1', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4f1', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4f1', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4f1', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4f1', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4f1', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4f1', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4f1', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4f1', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4f1', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4f1', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4f1', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4f1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4f1', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4f1', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4f1', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4f1', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4f1', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4f1', 'date': '20220506' }
    ],
    '260e-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '260e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '260e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '260e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '260e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '260e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '260e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '260e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '260e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '260e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '260e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '260e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '260e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '260e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '260e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '260e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '260e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '260e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '260e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '260e-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '260e-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '260e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '260e-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '260e-fe0f', 'date': '20220506' }
    ],
    '1f50b': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f50b', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f50b', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f50b', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f50b', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f50b', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f50b', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f50b', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f50b', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f50b', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f50b', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f50b', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f50b', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f50b', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f50b', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f50b', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f50b', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f50b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f50b', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f50b', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f50b', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f50b', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f50b', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f50b', 'date': '20220506' }
    ],
    '1faab': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1faab', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1faab', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1faab', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1faab', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1faab', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1faab', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1faab', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1faab', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1faab', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1faab', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1faab', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1faab', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1faab', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1faab', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1faab', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1faab', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1faab', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1faab', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1faab', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1faab', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1faab', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1faab', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1faab', 'date': '20220506' }
    ],
    '1f4bf': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4bf', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4bf', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4bf', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4bf', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4bf', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4bf', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4bf', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4bf', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4bf', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4bf', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4bf', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4bf', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4bf', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4bf', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4bf', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4bf', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4bf', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4bf', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4bf', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4bf', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4bf', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4bf', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4bf', 'date': '20220506' }
    ],
    '1f4b8': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4b8', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4b8', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4b8', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4b8', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4b8', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4b8', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4b8', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4b8', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4b8', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4b8', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4b8', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4b8', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4b8', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4b8', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4b8', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4b8', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4b8', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4b8', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4b8', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4b8', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4b8', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4b8', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4b8', 'date': '20220506' }
    ],
    '2696-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2696-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2696-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2696-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2696-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2696-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2696-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2696-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2696-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2696-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2696-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2696-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2696-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2696-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2696-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2696-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2696-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2696-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2696-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2696-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2696-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2696-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2696-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '2696-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2696-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2696-fe0f', 'date': '20220506' }
    ],
    '1f9e6': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9e6', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9e6', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9e6', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9e6', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9e6', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9e6', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9e6', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9e6', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9e6', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9e6', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9e6', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9e6', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9e6', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9e6', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9e6', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9e6', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9e6', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9e6', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9e6', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9e6', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9e6', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9e6', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9e6', 'date': '20220506' }
    ],
    '2602-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2602-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2602-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2602-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2602-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2602-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2602-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2602-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2602-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2602-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2602-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2602-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2602-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2602-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2602-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2602-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2602-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2602-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2602-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2602-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2602-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2602-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '2602-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2602-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2602-fe0f', 'date': '20220506' }
    ],
    '26d3-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '26d3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '26d3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26d3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26d3-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26d3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26d3-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26d3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26d3-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26d3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '26d3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '26d3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '26d3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26d3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '26d3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26d3-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26d3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26d3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26d3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26d3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '26d3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '26d3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '26d3-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '26d3-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '26d3-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '26d3-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26d3-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26d3-fe0f', 'date': '20230301' }
    ],
    '1f58a-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f58a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f58a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f58a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f58a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f58a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f58a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f58a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f58a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f58a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f58a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f58a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f58a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f58a-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f58a-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f58a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f58a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f58a-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f58a-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f58a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f58a-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f58a-fe0f',
        'date': '20220203'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f58a-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f58a-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f58a-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f58a-fe0f', 'date': '20220506' }
    ],
    '2712-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2712-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2712-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2712-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2712-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2712-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2712-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2712-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2712-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2712-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2712-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2712-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2712-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2712-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2712-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2712-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2712-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2712-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2712-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2712-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2712-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2712-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2712-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2712-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2712-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '2712-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2712-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2712-fe0f', 'date': '20220506' }
    ],
    '270f-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '270f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '270f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '270f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '270f-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '270f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '270f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '270f-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '270f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '270f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '270f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '270f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '270f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '270f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '270f-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '270f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '270f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '270f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '270f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '270f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '270f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '270f-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '270f-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '270f-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '270f-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '270f-fe0f', 'date': '20220506' }
    ],
    '1f4da': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4da', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4da', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4da', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4da', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4da', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4da', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4da', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4da', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4da', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4da', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4da', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4da', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4da', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4da', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4da', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4da', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4da', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4da', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4da', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4da', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4da', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4da', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4da', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4da', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4da', 'date': '20220506' }
    ],
    '1f5c3-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f5c3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f5c3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f5c3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f5c3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f5c3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f5c3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f5c3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f5c3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f5c3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f5c3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f5c3-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f5c3-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f5c3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f5c3-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f5c3-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f5c3-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f5c3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f5c3-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f5c3-fe0f',
        'date': '20220203'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f5c3-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f5c3-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f5c3-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f5c3-fe0f', 'date': '20220506' }
    ],
    '1f50e': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f50e', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f50e', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f50e', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f50e', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f50e', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f50e', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f50e', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f50e', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f50e', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f50e', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f50e', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f50e', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f50e', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f50e', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f50e', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f50e', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f50e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f50e', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f50e', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f50e', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f50e', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f50e', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f50e', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f50e', 'date': '20230301' }
    ],
    '1f5dd-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f5dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f5dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f5dd-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f5dd-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f5dd-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f5dd-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f5dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f5dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f5dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f5dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f5dd-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f5dd-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f5dd-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f5dd-fe0f',
        'date': '20230301'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f5dd-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f5dd-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f5dd-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f5dd-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f5dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f5dd-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f5dd-fe0f',
        'date': '20220203'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f5dd-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f5dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f5dd-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f5dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f5dd-fe0f', 'date': '20230301' }
    ],
    '1f512': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f512', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f512', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f512', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f512', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f512', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f512', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f512', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f512', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f512', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f512', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f512', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f512', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f512', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f512', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f512', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f512', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f512', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f512', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f512', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f512', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f512', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f512', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f512', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f512', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f512', 'date': '20230301' }
    ],
    '264a': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '264a', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '264a', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '264a', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '264a', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '264a', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '264a', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '264a', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '264a', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '264a', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '264a', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '264a', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264a', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264a', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '264a', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '264a', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '264a', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '264a', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '264a', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '264a', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '264a', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '264a', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '264a', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '264a', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '264a', 'date': '20220506' }
    ],
    '264b': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '264b', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '264b', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '264b', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '264b', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '264b', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '264b', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '264b', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '264b', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '264b', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '264b', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '264b', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264b', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264b', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '264b', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '264b', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '264b', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '264b', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '264b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '264b', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '264b', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '264b', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '264b', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '264b', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '264b', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '264b', 'date': '20230301' }
    ],
    '264c': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '264c', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '264c', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '264c', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '264c', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '264c', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '264c', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '264c', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '264c', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '264c', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '264c', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '264c', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264c', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264c', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '264c', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '264c', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '264c', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '264c', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '264c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '264c', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '264c', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '264c', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '264c', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '264c', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '264c', 'date': '20220506' }
    ],
    '264d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '264d', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '264d', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '264d', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '264d', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '264d', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '264d', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '264d', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '264d', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '264d', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '264d', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '264d', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264d', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264d', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '264d', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '264d', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '264d', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '264d', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '264d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '264d', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '264d', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '264d', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '264d', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '264d', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '264d', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '264d', 'date': '20230301' }
    ],
    '264e': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '264e', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '264e', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '264e', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '264e', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '264e', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '264e', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '264e', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '264e', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '264e', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '264e', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '264e', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264e', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264e', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '264e', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '264e', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '264e', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '264e', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '264e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '264e', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '264e', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '264e', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '264e', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '264e', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '264e', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '264e', 'date': '20230301' }
    ],
    '264f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '264f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '264f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '264f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '264f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '264f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '264f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '264f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '264f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '264f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '264f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '264f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '264f', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '264f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '264f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '264f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '264f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '264f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '264f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '264f', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '264f', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '264f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '264f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '264f', 'date': '20220506' }
    ],
    '26ce': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '26ce', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '26ce', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26ce', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26ce', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26ce', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26ce', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '26ce', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '26ce', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '26ce', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26ce', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '26ce', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26ce', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26ce', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26ce', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26ce', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26ce', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26ce', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '26ce', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '26ce', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '26ce', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '26ce', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '26ce', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '26ce', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26ce', 'date': '20220506' }
    ],
    '2049-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2049-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2049-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2049-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2049-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2049-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2049-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2049-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2049-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2049-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2049-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2049-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2049-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2049-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2049-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2049-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2049-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2049-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2049-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2049-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2049-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '2049-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2049-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2049-fe0f', 'date': '20220506' }
    ],
    '1f198': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f198', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f198', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f198', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f198', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f198', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f198', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f198', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f198', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f198', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f198', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f198', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f198', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f198', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f198', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f198', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f198', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f198', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f198', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f198', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f198', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f198', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f198', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f198', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f198', 'date': '20220506' }
    ],
    '1f4f4': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4f4', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4f4', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4f4', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4f4', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4f4', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4f4', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4f4', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4f4', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4f4', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4f4', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4f4', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4f4', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4f4', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4f4', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4f4', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4f4', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4f4', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4f4', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4f4', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4f4', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4f4', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4f4', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4f4', 'date': '20220506' }
    ],
    '1f508': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f508', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f508', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f508', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f508', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f508', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f508', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f508', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f508', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f508', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f508', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f508', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f508', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f508', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f508', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f508', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f508', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f508', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f508', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f508', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f508', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f508', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f508', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f508', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f508', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f508', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f508', 'date': '20230301' }
    ],
    '267b-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '267b-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '267b-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '267b-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '267b-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '267b-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '267b-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '267b-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '267b-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '267b-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '267b-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '267b-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '267b-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '267b-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '267b-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '267b-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '267b-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '267b-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '267b-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '267b-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '267b-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '267b-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '267b-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '267b-fe0f', 'date': '20220506' }
    ],
    '1f195': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f195', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f195', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f195', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f195', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f195', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f195', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f195', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f195', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f195', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f195', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f195', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f195', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f195', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f195', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f195', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f195', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f195', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f195', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f195', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f195', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f195', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f195', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f195', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f195', 'date': '20220506' }
    ],
    '1f193': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f193', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f193', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f193', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f193', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f193', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f193', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f193', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f193', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f193', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f193', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f193', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f193', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f193', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f193', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f193', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f193', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f193', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f193', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f193', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f193', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f193', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f193', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f193', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f193', 'date': '20220506' }
    ],
    '1f199': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f199', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f199', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f199', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f199', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f199', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f199', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f199', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f199', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f199', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f199', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f199', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f199', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f199', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f199', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f199', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f199', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f199', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f199', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f199', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f199', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f199', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f199', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f199', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f199', 'date': '20220506' }
    ],
    '1f197': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f197', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f197', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f197', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f197', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f197', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f197', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f197', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f197', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f197', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f197', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f197', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f197', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f197', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f197', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f197', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f197', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f197', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f197', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f197', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f197', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f197', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f197', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f197', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f197', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f197', 'date': '20230301' }
    ],
    '1f192': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f192', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f192', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f192', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f192', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f192', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f192', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f192', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f192', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f192', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f192', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f192', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f192', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f192', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f192', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f192', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f192', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f192', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f192', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f192', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f192', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f192', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f192', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f192', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f192', 'date': '20220506' }
    ],
    '1f6ae': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f6ae', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6ae', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6ae', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6ae', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6ae', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f6ae', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f6ae', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f6ae', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f6ae', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f6ae', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f6ae', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f6ae', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f6ae', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f6ae', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f6ae', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f6ae', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f6ae', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f6ae', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f6ae', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f6ae', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f6ae', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f6ae', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f6ae', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6ae', 'date': '20220506' }
    ],
    '262e-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '262e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '262e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '262e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '262e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '262e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '262e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '262e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '262e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '262e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '262e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '262e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '262e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '262e-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '262e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '262e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '262e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '262e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '262e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '262e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '262e-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '262e-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '262e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '262e-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '262e-fe0f', 'date': '20220506' }
    ],
    '262f-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '262f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '262f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '262f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '262f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '262f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '262f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '262f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '262f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '262f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '262f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '262f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '262f-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '262f-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '262f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '262f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '262f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '262f-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '262f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '262f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '262f-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '262f-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '262f-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '262f-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '262f-fe0f', 'date': '20220506' }
    ],
    '267e-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '267e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '267e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '267e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '267e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '267e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '267e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '267e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '267e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '267e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '267e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '267e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '267e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '267e-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '267e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '267e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '267e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '267e-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '267e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '267e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '267e-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '267e-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '267e-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '267e-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '267e-fe0f', 'date': '20220506' }
    ],
    '2716-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2716-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2716-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2716-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2716-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2716-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2716-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2716-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2716-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2716-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2716-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2716-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2716-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2716-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2716-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2716-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2716-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2716-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2716-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2716-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '2716-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2716-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2716-fe0f', 'date': '20220506' }
    ],
    '27b0': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '27b0', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '27b0', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '27b0', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '27b0', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '27b0', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '27b0', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '27b0', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '27b0', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '27b0', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '27b0', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '27b0', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '27b0', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '27b0', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '27b0', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '27b0', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '27b0', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '27b0', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '27b0', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '27b0', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '27b0', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '27b0', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '27b0', 'date': '20220506' }
    ],
    '27bf': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '27bf', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '27bf', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '27bf', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '27bf', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '27bf', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '27bf', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '27bf', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '27bf', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '27bf', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '27bf', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '27bf', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '27bf', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '27bf', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '27bf', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '27bf', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '27bf', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '27bf', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '27bf', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '27bf', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '27bf', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '27bf', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '27bf', 'date': '20220506' }
    ],
    '3030-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '3030-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '3030-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '3030-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '3030-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '3030-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '3030-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '3030-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '3030-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '3030-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '3030-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '3030-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '3030-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '3030-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '3030-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '3030-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '3030-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '3030-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '3030-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '3030-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '3030-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '3030-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '3030-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '3030-fe0f', 'date': '20220506' }
    ],
    '00a9-fe0f': [
      { 'leftEmoji': '1f618', 'rightEmoji': '00a9-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '00a9-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '00a9-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '00a9-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '00a9-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '00a9-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '00a9-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '00a9-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '00a9-fe0f', 'date': '20220203' }
    ],
    '00ae-fe0f': [
      { 'leftEmoji': '1f618', 'rightEmoji': '00ae-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '00ae-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '00ae-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '00ae-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f498', 'rightEmoji': '00ae-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '00ae-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '00ae-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '00ae-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '00ae-fe0f', 'date': '20220203' }
    ],
    '2122-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2122-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2122-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2122-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2122-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2122-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2122-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2122-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2122-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2122-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2122-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2122-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2122-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2122-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2122-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2122-fe0f', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2122-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2122-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2122-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2122-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2122-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '2122-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2122-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2122-fe0f', 'date': '20220506' }
    ],
    '1f5ef-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f5ef-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f5ef-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f5ef-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f5ef-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f5ef-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f5ef-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f5ef-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f5ef-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f5ef-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f5ef-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f5ef-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f5ef-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f5ef-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f5ef-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f5ef-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f5ef-fe0f',
        'date': '20230301'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f5ef-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f5ef-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f5ef-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f5ef-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f5ef-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f5ef-fe0f',
        'date': '20220203'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f5ef-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f5ef-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f5ef-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f5ef-fe0f', 'date': '20220506' }
    ],
    '1f4ac': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4ac', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4ac', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4ac', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4ac', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4ac', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4ac', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4ac', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4ac', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4ac', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4ac', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4ac', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4ac', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4ac', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4ac', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4ac', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4ac', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4ac', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4ac', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4ac', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4ac', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4ac', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4ac', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4ac', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4ac', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4ac', 'date': '20220506' }
    ],
    '1fae6': [
      { 'leftEmoji': '1fae6', 'rightEmoji': '1fa84', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f600', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f603', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f604', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f601', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f606', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f605', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f602', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f923', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f62d', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f609', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f617', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f619', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f61a', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f618', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f970', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f60d', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f929', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f973', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f643', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f642', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f972', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f979', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f60b', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f61b', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f61d', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f61c', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f92a', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f607', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f60a', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '263a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f60f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f60c', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f614', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f611', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f610', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f636', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1fae1', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f914', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f92b', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1fae2', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f92d', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f971', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f917', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1fae3', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f631', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f928', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f9d0', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f612', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f644', 'date': '20220203' },
      {
        'leftEmoji': '1fae6',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20220203'
      },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f624', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f620', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f621', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f92c', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f97a', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f61f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f625', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f622', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '2639-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f641', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1fae4', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f615', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f910', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f630', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f628', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f627', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f626', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f62e', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f62f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f632', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f633', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f92f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f62c', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f613', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f61e', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f616', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f623', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f629', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f62b', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f635', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1fae5', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f634', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f62a', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f924', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f31b', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f31c', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f31e', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1fae0', 'date': '20220203' },
      {
        'leftEmoji': '1fae6',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f974', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f975', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f976', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f922', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f92e', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f927', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f912', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f915', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f637', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f920', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f911', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f60e', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f913', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f978', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f925', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f921', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f47b', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f4a9', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f47d', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f916', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f383', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f608', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f47f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f525', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f4ab', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '2b50', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f31f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f4af', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f573-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f38a', 'date': '20220203' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1fae6', 'date': '20221101' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '2764-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f9e1', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f49b', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f49a', 'date': '20220203' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1fae6', 'date': '20221101' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f499', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f49c', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f90e', 'date': '20220203' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1fae6', 'date': '20221101' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f5a4', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f90d', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '2665-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f498', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f49d', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f496', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f497', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f493', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f49e', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f495', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f48c', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f49f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '2763-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '1fae6',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20220203'
      },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f494', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f48b', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f9a0', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f480', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f441-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1fae6', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f490', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f337', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f338', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f33c', 'date': '20220203' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1fae6', 'date': '20220406' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f335', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f332', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1fab5', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1faa8', 'date': '20220406' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '26c4', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f32a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '2601-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fae6', 'date': '20221101' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f308', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f30d', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f648', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f435', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f981', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f42f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f431', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f436', 'date': '20220203' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1fae6', 'date': '20221101' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f43b', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f428', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f43c', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f42d', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f430', 'date': '20220203' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1fae6', 'date': '20221101' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f99d', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f437', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f984', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f422', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f429', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f410', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f98c', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f999', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f9a5', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f994', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f987', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f989', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f426', 'date': '20220203' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fae6', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fae6', 'date': '20221101' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f427', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f41f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f419', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f982', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f577-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f40c', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f41d', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f353', 'date': '20220203' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1fae6', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1fae6', 'date': '20220406' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f34a', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f34d', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f34c', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f34b', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f336-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f951', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f35e', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f9c0', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f32d', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f382', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f9c1', 'date': '20220203' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1fae6', 'date': '20221101' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '2615', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f37d-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f307', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f388', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f381', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f3c6', 'date': '20220203' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1fae6', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1fae6', 'date': '20230126' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f3a7', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f451', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f48e', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f4f0', 'date': '20220203' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f52e', 'date': '20220203' }
    ],
    '1f352': [
      { 'leftEmoji': '1f352', 'rightEmoji': '1fa84', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f600', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f603', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f604', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f601', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f606', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f605', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f602', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f923', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f62d', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f609', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f617', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f619', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f61a', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f618', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f970', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f60d', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f929', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f973', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f643', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f642', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f972', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f979', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f60b', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f61b', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f61d', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f61c', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f92a', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f607', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f60a', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '263a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f60f', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f60c', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f614', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f611', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f610', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f636', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1fae1', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f914', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f92b', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1fae2', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f92d', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f971', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f917', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1fae3', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f631', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f928', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f9d0', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f612', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f644', 'date': '20220406' },
      {
        'leftEmoji': '1f352',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20220406'
      },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f624', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f620', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f621', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f92c', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f97a', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f61f', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f625', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f622', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '2639-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f641', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1fae4', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f615', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f910', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f630', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f628', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f627', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f626', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f62e', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f62f', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f632', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f633', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f92f', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f62c', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f613', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f61e', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f616', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f623', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f629', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f62b', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f635', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1fae5', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f634', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f62a', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f924', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f31b', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f31c', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f31e', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1fae0', 'date': '20220406' },
      {
        'leftEmoji': '1f352',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20220406'
      },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f974', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f975', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f976', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f922', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f92e', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f927', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f912', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f915', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f637', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f920', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f911', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f60e', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f913', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f978', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f925', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f921', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f47b', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f4a9', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f47d', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f916', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f383', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f608', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f47f', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f525', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f4ab', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '2b50', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f31f', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f4af', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f573-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f38a', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f49b', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '2665-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f498', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f49d', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f496', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f497', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f493', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f49e', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f495', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f48c', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f49f', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '2763-fe0f', 'date': '20220406' },
      {
        'leftEmoji': '1f352',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20220406'
      },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f494', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f48b', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f9a0', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f480', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f441-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1fae6', 'date': '20220406' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f352', 'date': '20220815' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f490', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f339', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f337', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f338', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f33c', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f352', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f335', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f332', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1fab5', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1faa8', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '26c4', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f32a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f352', 'rightEmoji': '2601-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f352', 'date': '20221101' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f308', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f30d', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f648', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f435', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f981', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f42f', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f431', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f436', 'date': '20220406' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f352', 'date': '20221101' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f43b', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f428', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f43c', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f42d', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f430', 'date': '20220406' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f352', 'date': '20221101' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f99d', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f437', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f984', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f422', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f429', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f410', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f98c', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f999', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f9a5', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f994', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f987', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f989', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f426', 'date': '20220406' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f352', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f352', 'date': '20221101' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f427', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f41f', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f419', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f982', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f577-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f40c', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f41d', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f353', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f352', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f349', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f34a', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f34d', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f34c', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f34b', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f336-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f951', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f35e', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f9c0', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f32d', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f382', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f9c1', 'date': '20220406' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f352', 'date': '20221101' },
      { 'leftEmoji': '1f352', 'rightEmoji': '2615', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f37d-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f307', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f388', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f381', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f3c6', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f352', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f352', 'date': '20230126' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f3a7', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f451', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f48e', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f4f0', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f52e', 'date': '20220406' }
    ],
    '1f349': [
      { 'leftEmoji': '1f349', 'rightEmoji': '1fa84', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f600', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f603', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f604', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f601', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f606', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f605', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f602', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f923', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f62d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f609', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f617', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f619', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f61a', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f618', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f970', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f60d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f929', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f973', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f643', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f642', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f972', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f979', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f60b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f61b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f61d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f61c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f92a', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f607', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f60a', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '263a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f60f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f60c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f614', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f611', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f610', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f636', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1fae1', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f914', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f92b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1fae2', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f92d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f971', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f917', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1fae3', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f631', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f928', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f9d0', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f612', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f644', 'date': '20220406' },
      {
        'leftEmoji': '1f349',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20220406'
      },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f624', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f620', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f621', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f92c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f97a', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f61f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f625', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f622', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '2639-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f641', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1fae4', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f615', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f910', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f630', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f628', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f627', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f626', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f62e', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f62f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f632', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f633', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f92f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f62c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f613', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f61e', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f616', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f623', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f629', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f62b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f635', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1fae5', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f634', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f62a', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f924', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f31b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f31c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f31e', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1fae0', 'date': '20220406' },
      {
        'leftEmoji': '1f349',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20220406'
      },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f974', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f975', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f976', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f922', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f92e', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f927', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f912', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f915', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f637', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f920', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f911', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f60e', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f913', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f978', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f925', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f921', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f47b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f4a9', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f47d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f916', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f383', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f608', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f47f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f525', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f4ab', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '2b50', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f31f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f4af', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f573-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f38a', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f49b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '2665-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f498', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f49d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f496', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f497', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f493', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f49e', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f495', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f48c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f49f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '2763-fe0f', 'date': '20220406' },
      {
        'leftEmoji': '1f349',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20220406'
      },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f494', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f48b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f9a0', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f480', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f441-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1fae6', 'date': '20220406' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f349', 'date': '20220815' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f490', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f339', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f337', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f338', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f33c', 'date': '20220406' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f349', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f335', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f332', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1fab5', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1faa8', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '26c4', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f32a-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '2601-fe0f', 'date': '20221107' },
      { 'leftEmoji': '1f349', 'rightEmoji': '2601-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f349', 'date': '20221101' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f308', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f30d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f648', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f435', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f981', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f42f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f431', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f436', 'date': '20220406' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f349', 'date': '20221101' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f43b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f428', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f43c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f42d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f430', 'date': '20220406' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f349', 'date': '20221101' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f99d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f437', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f984', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f422', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f429', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f410', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f98c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f999', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f9a5', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f994', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f987', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f989', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f426', 'date': '20220406' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f349', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f349', 'date': '20221101' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f427', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f41f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f419', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f982', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f577-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f40c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f41d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f353', 'date': '20220406' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f349', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f349', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f34a', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f34d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f34c', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f34b', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f336-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f951', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f35e', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f9c0', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f32d', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f382', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f9c1', 'date': '20220406' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f349', 'date': '20221101' },
      { 'leftEmoji': '1f349', 'rightEmoji': '2615', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f37d-fe0f', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f307', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f388', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f381', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f3c6', 'date': '20220406' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f349', 'date': '20220406' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f349', 'date': '20230126' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f3a7', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f451', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f48e', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f4f0', 'date': '20220406' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f52e', 'date': '20220406' }
    ],
    '1f31a': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f31a', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f31a', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f31a', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f31a', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f31a', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f31a', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f31a', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f31a', 'date': '20220506' }
    ],
    '1f31d': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f31d', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f31d', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f31d', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f31d', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f31d', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f31d', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f31d', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f31d', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f31d', 'date': '20220506' }
    ],
    '1f33b': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f33b', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f33b', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f33b', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f33b', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f33b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f33b', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f33b', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f33b', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f33b', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f33b', 'date': '20220506' }
    ],
    '1f331': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f331', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f331', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f331', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f331', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f331', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f331', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f331', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f331', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f331', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f331', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f331', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f331', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f331', 'date': '20220506' }
    ],
    '1f33f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f33f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f33f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f33f', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f33f', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f33f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f33f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f33f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f33f', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f33f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f33f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f33f', 'date': '20220506' }
    ],
    '1f343': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f343', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f343', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f343', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f343', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f343', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f343', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f343', 'date': '20220506' }
    ],
    '1f334': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f334', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f334', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f334', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f334', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f334', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f334', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f334', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f334', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f334', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f334', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f334', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f334', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f334', 'date': '20220506' }
    ],
    '1f333': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f333', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f333', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f333', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f333', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f333', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f333', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f333', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f333', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f333', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f333', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f333', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f333', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f333', 'date': '20220506' }
    ],
    '1fab9': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fab9', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fab9', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fab9', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fab9', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fab9', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fab9', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fab9', 'date': '20220506' }
    ],
    '1f30a': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f30a', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f30a', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f30a', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f30a', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f30a', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f30a', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f30a', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f30a', 'date': '20220506' }
    ],
    '1f32c-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f32c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f32c-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f32c-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f32c-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f32c-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f32c-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f32c-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f32c-fe0f', 'date': '20220506' }
    ],
    '1f30b': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f30b', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f30b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f30b', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f30b', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f30b', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f30b', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f30b', 'date': '20220506' }
    ],
    '1f3d6-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d6-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3d6-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f3d6-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f3d6-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3d6-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3d6-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f3d6-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f3d6-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3d6-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3d6-fe0f', 'date': '20220506' }
    ],
    '26c5': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '26c5', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26c5', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26c5', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '26c5', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '26c5', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '26c5', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '26c5', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '26c5', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '26c5', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26c5', 'date': '20220506' }
    ],
    '1f329-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f329-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f329-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f329-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f329-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f329-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f329-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f329-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f329-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f329-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f329-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f329-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f329-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f329-fe0f', 'date': '20220506' }
    ],
    '1f327-fe0f': [
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa84', 'date': '20221101' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f600', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f603', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f604', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f601', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f606', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f605', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f602', 'date': '20221101' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f923', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f62d', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f609', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f617', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f619', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f61a', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f618', 'date': '20221101' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f970', 'date': '20221101' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f60d', 'date': '20221101' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f929', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f973', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f643', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f642', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f972', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f979', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f60b', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f61b', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f61d', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f61c', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f92a', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f607', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f60a', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '263a-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f60f', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f60c', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f614', 'date': '20221101' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f611', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f610', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f636', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fae1', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f914', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f92b', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fae2', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f92d', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f971', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f917', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fae3', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f631', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f928', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9d0', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f612', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f644', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f624', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f620', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f621', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f92c', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f97a', 'date': '20221101' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f61f', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f625', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f622', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2639-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f641', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fae4', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f615', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f910', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f630', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f628', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f627', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f626', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f62e', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f62f', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f632', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f633', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f92f', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f62c', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f613', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f61e', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f616', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f623', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f629', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f62b', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f635', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fae5', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f634', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f62a', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f924', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f31b', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f31c', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f31a', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f31d', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f31e', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fae0', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f974', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f975', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f976', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f922', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f92e', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f927', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f912', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f915', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f637', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f920', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f911', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f60e', 'date': '20221101' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f327-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f913', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f978', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f925', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f921', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f47b', 'date': '20221101' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4a9', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f47d', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f916', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f383', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f608', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f47f', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f525', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4ab', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2b50', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f31f', 'date': '20221101' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4a5', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4af', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fae7', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f573-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f38a', 'date': '20221101' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2665-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f327-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f498', 'date': '20221101' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f49d', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f496', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f497', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f493', 'date': '20221101' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f49e', 'date': '20221101' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f48c', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f49f', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2763-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f327-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f494', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f48b', 'date': '20221101' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f327-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9e0', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fac0', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fac1', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa78', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9a0', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9b7', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9b4', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f480', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f441-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fae6', 'date': '20221101' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f327-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f327-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f490', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f339', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f33a', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f337', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f338', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4ae', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f3f5-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f33b', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f33c', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f341', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f344', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f331', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f33f', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f343', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f340', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fab4', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f335', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f334', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f333', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f332', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fab9', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fab5', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1faa8', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '26c4', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f30a', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f32c-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f300', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f32a-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f30b', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f3d6-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '26c5', 'date': '20221101' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f327-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f327-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f329-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4a7', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2602-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '26a1', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f308', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2604-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa90', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f30d', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f648', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f435', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f981', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f42f', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f431', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f436', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f43a', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f43b', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f428', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f43c', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f42d', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f430', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f98a', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f99d', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f437', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f984', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f422', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f429', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f410', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f98c', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f999', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9a5', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f994', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f987', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f989', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f426', 'date': '20221101' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f327-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f327-fe0f', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f54a-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9a9', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f427', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f41f', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f99e', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f980', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f419', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fab8', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f982', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f577-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f41a', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f40c', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f997', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fab2', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fab3', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f41d', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f41e', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f98b', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f43e', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f353', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f352', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f349', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f34a', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f96d', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f34d', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f34c', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f34b', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f348', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f350', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f95d', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fad2', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fad0', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f347', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f965', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f345', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f336-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f955', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f360', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9c5', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f33d', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f966', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f952', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fad1', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f951', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9c4', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f954', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fad8', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f330', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f95c', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f35e', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fad3', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f950', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f96f', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f95e', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f373', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9c0', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f969', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f356', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f354', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f32d', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f96a', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f968', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f35f', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fad4', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f32e', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f32f', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f959', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9c6', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f958', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f35d', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f96b', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fad5', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f963', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f957', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f372', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f35b', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f35c', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f363', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f364', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f35a', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f371', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f359', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f358', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f365', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f960', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f367', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f368', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f366', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f370', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f382', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9c1', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f327-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f36b', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f369', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f36a', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9c2', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f37f', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9cb', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f37c', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f375', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2615', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9c9', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f379', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f37d-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f6d1', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f6a8', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f6df', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2693', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f697', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f3ce-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f695', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f68c', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f682', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f6f8', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f680', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2708-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3a2', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3a1', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3aa', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f3db-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f3df-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3e0', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f3d5-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f307', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f3dd-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f388', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f380', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f381', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1faa9', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f947', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f948', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f949', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3c5', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f396-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3c6', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '26bd', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '26be', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f94e', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3c0', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3d0', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3c8', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3c9', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3be', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f945', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3f8', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f94d', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3cf', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3d1', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3d2', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f94c', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f6f7', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3bf', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '26f8-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f6fc', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa70', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f6f9', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '26f3', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3af', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3f9', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f94f', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa83', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa81', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f93f', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3bd', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f94b', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f94a', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3b1', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3d3', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3b3', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '265f-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa80', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9e9', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3ae', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3b2', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3b0', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3b4', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f004', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f0cf', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4f7', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3a8', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f58c-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f58d-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1faa1', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9f5', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9f6', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3b9', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3b7', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3ba', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3b8', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa95', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3bb', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa98', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f941', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa97', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3a4', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3a7', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f399-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4fa', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f39e-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3ac', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3ad', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f39f-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4f1', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '260e-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f50b', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1faab', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4be', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4bf', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4b8', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2696-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4a1', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9fc', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9e6', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f451', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f48e', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f6e0-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '26d3-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f5d1-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f58a-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2712-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '270f-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4da', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f5c3-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4f0', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4e3', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f50e', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f52e', 'date': '20221101' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f327-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9ff', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f5dd-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f512', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2648', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2649', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '264a', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '264b', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '264c', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '264d', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '264e', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '264f', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2650', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2651', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2652', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2653', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '26ce', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2757', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2753', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2049-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f198', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4f4', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f508', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '26a0-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '267b-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2705', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f195', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f193', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f199', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f197', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f192', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f6ae', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '262e-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '262f-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '267e-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2716-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2795', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2796', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '2797', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '27b0', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '27bf', 'date': '20221101' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '3030-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2122-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2660-fe0f',
        'date': '20221101'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f5ef-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4ac', 'date': '20221101' }
    ],
    '1f4a7': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4a7', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4a7', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f4a7', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4a7', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4a7', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4a7', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4a7', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4a7', 'date': '20220506' }
    ],
    '2604-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2604-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2604-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2604-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2604-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2604-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '2604-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2604-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2604-fe0f', 'date': '20220506' }
    ],
    '1fa90': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fa90', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fa90', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fa90', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fa90', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa90', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fa90', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fa90', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fa90', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fa90', 'date': '20220506' }
    ],
    '1f9a9': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9a9', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9a9', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9a9', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9a9', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9a9', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9a9', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9a9', 'date': '20230301' }
    ],
    '1f99e': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f99e', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f99e', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f99e', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f99e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f99e', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f99e', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f99e', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f99e', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f99e', 'date': '20230301' }
    ],
    '1f980': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f980', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f980', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f980', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f980', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f980', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f980', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f980', 'date': '20230301' }
    ],
    '1fab8': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fab8', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fab8', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fab8', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fab8', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fab8', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fab8', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fab8', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fab8', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fab8', 'date': '20230301' }
    ],
    '1f41a': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f41a', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f41a', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f41a', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f41a', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f41a', 'date': '20230301' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f41a', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f41a', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f41a', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f41a', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f41a', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f41a', 'date': '20230301' }
    ],
    '1f997': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f997', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f997', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f997', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f997', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f997', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f997', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f997', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f997', 'date': '20230301' }
    ],
    '1fab2': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fab2', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fab2', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fab2', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fab2', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fab2', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fab2', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fab2', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fab2', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fab2', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fab2', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fab2', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fab2', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fab2', 'date': '20230301' }
    ],
    '1fab3': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1fab3', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1fab3', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1fab3', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1fab3', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1fab3', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1fab3', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1fab3', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fab3', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1fab3', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1fab3', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1fab3', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1fab3', 'date': '20220506' }
    ],
    '1f41e': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f41e', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f41e', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f41e', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f41e', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f41e', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f41e', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f41e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f41e', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f41e', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f41e', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f41e', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f41e', 'date': '20220506' }
    ],
    '1f98b': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f98b', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f98b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f98b', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f98b', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f98b', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f98b', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f98b', 'date': '20220506' }
    ],
    '1f373': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f373', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f373', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f373', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f373', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f373', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f373', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f373', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f373', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f373', 'date': '20230301' }
    ],
    '1f367': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f367', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f367', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f367', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f367', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f367', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f367', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f367', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f367', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f367', 'date': '20220506' }
    ],
    '1f366': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f366', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f366', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f366', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f366', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f366', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f366', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f366', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f366', 'date': '20220506' }
    ],
    '1f37c': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f37c', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f37c', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f37c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f37c', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f37c', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f37c', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f37c', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f37c', 'date': '20220506' }
    ],
    '1f682': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f682', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f682', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f682', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f682', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f682', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f682', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f682', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f682', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f682', 'date': '20220506' }
    ],
    '1f3a2': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3a2', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3a2', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3a2', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3a2', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3a2', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3a2', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3a2', 'date': '20220506' }
    ],
    '1f3a1': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3a1', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3a1', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3a1', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3a1', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3a1', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3a1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3a1', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f3a1', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3a1', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3a1', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3a1', 'date': '20220506' }
    ],
    '1f3db-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3db-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f3db-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f3db-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3db-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3db-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f3db-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f3db-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3db-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3db-fe0f', 'date': '20220506' }
    ],
    '1f3df-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3df-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3df-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3df-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3df-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3df-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3df-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3df-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f3df-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f3df-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3df-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3df-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f3df-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f3df-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3df-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3df-fe0f', 'date': '20220506' }
    ],
    '1f3d5-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3d5-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3d5-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3d5-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3d5-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3d5-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3d5-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3d5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f3d5-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f3d5-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3d5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3d5-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f3d5-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f3d5-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3d5-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3d5-fe0f', 'date': '20220506' }
    ],
    '1f3dd-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f3dd-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f3dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f3dd-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f3dd-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f3dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3dd-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f3dd-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f3dd-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f3dd-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f3dd-fe0f', 'date': '20220506' }
    ],
    '1f4be': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4be', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4be', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4be', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4be', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4be', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4be', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4be', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4be', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4be', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4be', 'date': '20220506' }
    ],
    '1f4a1': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f4a1', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4a1', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4a1', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4a1', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f4a1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4a1', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4a1', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4a1', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4a1', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4a1', 'date': '20230301' }
    ],
    '1f9fc': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9fc', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9fc', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9fc', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9fc', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9fc', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f9fc', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9fc', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9fc', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9fc', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9fc', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9fc', 'date': '20220506' }
    ],
    '1f6e0-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f6e0-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f6e0-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f6e0-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f6e0-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f6e0-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f6e0-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f6e0-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f6e0-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f6e0-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f6e0-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f6e0-fe0f', 'date': '20220506' }
    ],
    '1f5d1-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f5d1-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f5d1-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f5d1-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f5d1-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f5d1-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f5d1-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f5d1-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f5d1-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f5d1-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f5d1-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f5d1-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f5d1-fe0f',
        'date': '20220506'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f5d1-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f5d1-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f5d1-fe0f', 'date': '20220506' }
    ],
    '1f9ff': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f9ff', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f9ff', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f9ff', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f9ff', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f9ff', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f9ff', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f9ff', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9ff', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f9ff', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f9ff', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f9ff', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f9ff', 'date': '20220506' }
    ],
    '26a0-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '26a0-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '26a0-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '26a0-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '26a0-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '26a0-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '26a0-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '26a0-fe0f', 'date': '20220506' }
    ],
    '2660-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '2660-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '2660-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '2660-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '2660-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '2660-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '2660-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2660-fe0f', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '2660-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '2660-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '2660-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '2660-fe0f', 'date': '20220506' }
    ],
    '1f368': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f368', 'date': '20230301' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f368', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f368', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f368', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f368', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f368', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f368', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f368', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f368', 'date': '20220506' }
    ],
    '1f379': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f379', 'date': '20230301' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f379', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f379', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f379', 'date': '20230301' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f379', 'date': '20230301' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f379', 'date': '20230301' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f379', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f379', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f379', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f379', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f379', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f379', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f379', 'date': '20220506' }
    ],
    '1f54a-fe0f': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f54a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f54a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f54a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f54a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f54a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f54a-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f54a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f54a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f54a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f54a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f54a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f54a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f54a-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2665-fe0f',
        'rightEmoji': '1f54a-fe0f',
        'date': '20220506'
      },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f54a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f54a-fe0f', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f54a-fe0f', 'date': '20220203' },
      {
        'leftEmoji': '2763-fe0f',
        'rightEmoji': '1f54a-fe0f',
        'date': '20220203'
      },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f54a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f54a-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '2601-fe0f',
        'rightEmoji': '1f54a-fe0f',
        'date': '20220203'
      },
      {
        'leftEmoji': '1f327-fe0f',
        'rightEmoji': '1f54a-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f54a-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f54a-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f54a-fe0f', 'date': '20220506' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f54a-fe0f', 'date': '20230301' }
    ],
    '1f341': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f618', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f341', 'date': '20230301' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f341', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f341', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f341', 'date': '20220815' }
    ],
    '1f36c': [
      { 'leftEmoji': '1f36c', 'rightEmoji': '1fa84', 'date': '20221101' },
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f600', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f603', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f604', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f601', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f606', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f605', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f602', 'date': '20221101' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f923', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f62d', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f609', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f617', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f619', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f61a', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f618', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f970', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f60d', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f929', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f973', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f643', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f642', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f972', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f979', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f60b', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f61b', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f61d', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f61c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f92a', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f607', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f60a', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '263a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f60f', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f60c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f614', 'date': '20221101' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f611', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f610', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f636', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1fae1', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f914', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f92b', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1fae2', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f92d', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f971', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f917', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1fae3', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f631', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f928', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f9d0', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f612', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f644', 'date': '20220815' },
      {
        'leftEmoji': '1f36c',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20220815'
      },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f624', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f620', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f621', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f92c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f97a', 'date': '20221101' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f61f', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f625', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f622', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '2639-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f641', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1fae4', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f615', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f910', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f630', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f628', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f627', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f626', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f62e', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f62f', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f632', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f633', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f92f', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f62c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f613', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f61e', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f616', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f623', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f629', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f62b', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f635', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1fae5', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f634', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f62a', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f924', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f31b', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f31c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f31e', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1fae0', 'date': '20220815' },
      {
        'leftEmoji': '1f36c',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20220815'
      },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f974', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f975', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f976', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f922', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f92e', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f927', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f912', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f915', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f637', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f920', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f911', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f60e', 'date': '20221101' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f913', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f978', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f925', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f921', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f47b', 'date': '20221101' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f4a9', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f47d', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f916', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f383', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f608', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f47f', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f525', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f4ab', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '2b50', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f31f', 'date': '20221101' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f4af', 'date': '20221101' },
      { 'leftEmoji': '1f4af', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f573-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f38a', 'date': '20221101' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f36c', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '2764-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f9e1', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f49b', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f49a', 'date': '20220815' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f36c', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f499', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f49c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f90e', 'date': '20220815' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f36c', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f5a4', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f90d', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '2665-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f498', 'date': '20221101' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f49d', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f496', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f497', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f493', 'date': '20221101' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f49e', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f495', 'date': '20221101' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f48c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f49f', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '2763-fe0f', 'date': '20221101' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f36c', 'date': '20220815' },
      {
        'leftEmoji': '1f36c',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20221101'
      },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f494', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f48b', 'date': '20221101' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f9a0', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f480', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f441-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1fae6', 'date': '20221101' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f937', 'date': '20221101' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f490', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f339', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f337', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f338', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f33c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f344', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f335', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f332', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1fab5', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1faa8', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '26c4', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f32a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '2601-fe0f', 'date': '20221101' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f327-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f308', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f30d', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f648', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f435', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f981', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f42f', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f431', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f436', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f43a', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f43b', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f428', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f43c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f42d', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f430', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f98a', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f99d', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f437', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f984', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f422', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f429', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f410', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f98c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f999', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f9a5', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f994', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f987', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f989', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f426', 'date': '20221101' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f36c', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f36c', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f427', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f41f', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f419', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f982', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f577-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f40c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f41d', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f353', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f352', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f349', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f34a', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f34d', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f34c', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f34b', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f336-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f951', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f35e', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f9c0', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f32d', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f382', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f9c1', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '2615', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f37d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f307', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f388', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f381', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f3c6', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '26bd', 'date': '20221101' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f36c', 'date': '20230126' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f36c', 'date': '20230126' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f3a7', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f451', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f48e', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f4f0', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f52e', 'date': '20221101' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f36c', 'date': '20220815' }
    ],
    '1f44d': [
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f31b', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f31c', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f31e', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f47b', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f4a9', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f47d', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f916', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f383', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f608', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f47f', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f525', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f4ab', 'date': '20221101' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '2b50', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f31f', 'date': '20221101' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f38a', 'date': '20221101' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f49f', 'date': '20221101' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '2763-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f48b', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f9a0', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f480', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f490', 'date': '20221101' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f339', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f337', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f338', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f33c', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f344', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f335', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f332', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fab5', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1faa8', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '26c4', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f32a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '2601-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f327-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f4a7', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f308', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fa90', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f30d', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f435', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f981', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f42f', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f431', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f436', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f43a', 'date': '20221101' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f43b', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f428', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f43c', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f42d', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f430', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f98a', 'date': '20221101' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f99d', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f437', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f984', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f422', 'date': '20220823' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f422', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f429', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f410', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f98c', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f999', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f9a5', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f994', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f987', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f989', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f426', 'date': '20220815' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f44d', 'date': '20230126' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fabf', 'date': '20221101' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f427', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f41f', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f419', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f982', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f577-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f40c', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f41d', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f353', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f352', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f349', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f34a', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f34d', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f34c', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f34b', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f336-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f951', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f35e', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f9c0', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f32d', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f367', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f382', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f9c1', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '2615', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f37d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f307', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f388', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f381', 'date': '20221101' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f3c6', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '26bd', 'date': '20220815' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f44d', 'date': '20230126' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f4a1', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f9fc', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f451', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f48e', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f4f0', 'date': '20220815' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f52e', 'date': '20220815' }
    ],
    '1f937': [
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa84', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f31b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f31c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f31e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f921', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f47b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4a9', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f47d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f916', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f383', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f608', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f47f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f525', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2b50', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f31f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4a5', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4af', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fae7', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f38a', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2764-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9e1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f49b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f49a', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f499', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f49c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f90e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f5a4', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f90d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2665-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f498', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f49d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f496', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f497', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f493', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f49e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f48c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f49f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2763-fe0f', 'date': '20220815' },
      {
        'leftEmoji': '1f937',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20220815'
      },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f494', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f48b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9e0', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fac0', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fac1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa78', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9a0', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9b7', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9b4', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f480', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f441-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1fae6', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f937', 'date': '20220823' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f490', 'date': '20220823' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f490', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f339', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f33a', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f337', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f338', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4ae', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3f5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f33b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f33c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f341', 'date': '20220815' },
      { 'leftEmoji': '1f344', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f331', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f33f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f343', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f340', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fab4', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f335', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f334', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f333', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f332', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fab9', 'date': '20220815' },
      { 'leftEmoji': '1fab5', 'rightEmoji': '1f937', 'date': '20230301' },
      { 'leftEmoji': '1faa8', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '26c4', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f30a', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f300', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f32a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f30b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3d6-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '26c5', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2601-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2601-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f327-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f329-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4a7', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2602-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '26a1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f308', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2604-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa90', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f30d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f648', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f435', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f981', 'date': '20220815' },
      { 'leftEmoji': '1f42f', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f431', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f436', 'date': '20220815' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f937', 'date': '20221101' },
      { 'leftEmoji': '1f43b', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f428', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f43c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f42d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f430', 'date': '20220815' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f937', 'date': '20221101' },
      { 'leftEmoji': '1f99d', 'rightEmoji': '1f937', 'date': '20230301' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f437', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f984', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f422', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f429', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f410', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f98c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f999', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9a5', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f994', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f987', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f989', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f426', 'date': '20220815' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f937', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f937', 'date': '20221101' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f54a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9a9', 'date': '20220815' },
      { 'leftEmoji': '1f41f', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f99e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f980', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f419', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fab8', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f982', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f577-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f41a', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f40c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f997', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fab2', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fab3', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f41d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f41e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f98b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f43e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f353', 'date': '20220815' },
      { 'leftEmoji': '1f352', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f349', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f96d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f34d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f34b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f348', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f350', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f95d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fad2', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fad0', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f347', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f965', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f345', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f336-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f955', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f360', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9c5', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f33d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f966', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f952', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fad1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f951', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9c4', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f954', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fad8', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f330', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f95c', 'date': '20220815' },
      { 'leftEmoji': '1f35e', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fad3', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f950', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f96f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f95e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f373', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9c0', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f969', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f356', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f354', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f32d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f96a', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f968', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f35f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fad4', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f32e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f32f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f959', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9c6', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f958', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f35d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f96b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fad5', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f963', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f957', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f372', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f35b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f35c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f363', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f364', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f35a', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f371', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f359', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f358', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f365', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f960', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f367', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f368', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f366', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f370', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f382', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9c1', 'date': '20220815' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f937', 'date': '20221101' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f36c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f36b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f369', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f36a', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9c2', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f37f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9cb', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f37c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f375', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2615', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9c9', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f379', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f37d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f6d1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f6a8', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f6df', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2693', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f697', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3ce-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f695', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f68c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f682', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f6f8', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f680', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2708-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3a2', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3a1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3aa', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3db-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3df-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3e0', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3d5-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f307', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f388', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f380', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1faa9', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f947', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f948', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f949', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3c5', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f396-fe0f', 'date': '20220815' },
      { 'leftEmoji': '26bd', 'rightEmoji': '1f937', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '26be', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f94e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3c0', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3d0', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3c8', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3c9', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3be', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f945', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3f8', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f94d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3cf', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3d1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3d2', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f94c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f6f7', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3bf', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '26f8-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f6fc', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa70', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f6f9', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '26f3', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3af', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3f9', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f94f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa83', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa81', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f94a', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3b1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3d3', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3b3', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '265f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa80', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9e9', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3ae', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3b2', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3b0', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3b4', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f004', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f0cf', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4f7', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3a8', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f58c-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f58d-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1faa1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9f5', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9f6', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3b9', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3b7', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3ba', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3b8', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa95', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3bb', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa98', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f941', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1fa97', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3a4', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f399-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4fa', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f39e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3ac', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f3ad', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f39f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4f1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '260e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f50b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1faab', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4be', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4bf', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4b8', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2696-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4a1', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9fc', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9e6', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f451', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f48e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f6e0-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '26d3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f5d1-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f58a-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2712-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '270f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4da', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f5c3-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4f0', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4e3', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f50e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f52e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f9ff', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f5dd-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f512', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2648', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2649', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '264a', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '264b', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '264c', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '264d', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '264e', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '264f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2650', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2651', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2652', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2653', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '26ce', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2757', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2753', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2049-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f198', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4f4', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f508', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '26a0-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '267b-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2705', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f195', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f193', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f199', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f197', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f192', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f6ae', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '262e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '262f-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '267e-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2122-fe0f', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '2660-fe0f', 'date': '20220815' }
    ],
    '1f4e3': [
      { 'leftEmoji': '1fa84', 'rightEmoji': '1f4e3', 'date': '20220815' },
      { 'leftEmoji': '1f602', 'rightEmoji': '1f4e3', 'date': '20220815' },
      { 'leftEmoji': '1f970', 'rightEmoji': '1f4e3', 'date': '20220203' },
      { 'leftEmoji': '1f60d', 'rightEmoji': '1f4e3', 'date': '20220203' },
      { 'leftEmoji': '1f614', 'rightEmoji': '1f4e3', 'date': '20220815' },
      { 'leftEmoji': '1f97a', 'rightEmoji': '1f4e3', 'date': '20220815' },
      { 'leftEmoji': '1f60e', 'rightEmoji': '1f4e3', 'date': '20220815' },
      { 'leftEmoji': '1f47b', 'rightEmoji': '1f4e3', 'date': '20220815' },
      { 'leftEmoji': '1f31f', 'rightEmoji': '1f4e3', 'date': '20220203' },
      { 'leftEmoji': '1f38a', 'rightEmoji': '1f4e3', 'date': '20220815' },
      { 'leftEmoji': '2665-fe0f', 'rightEmoji': '1f4e3', 'date': '20220506' },
      { 'leftEmoji': '1f498', 'rightEmoji': '1f4e3', 'date': '20220203' },
      { 'leftEmoji': '1f493', 'rightEmoji': '1f4e3', 'date': '20220203' },
      { 'leftEmoji': '1f495', 'rightEmoji': '1f4e3', 'date': '20220203' },
      { 'leftEmoji': '2763-fe0f', 'rightEmoji': '1f4e3', 'date': '20220203' },
      { 'leftEmoji': '1f48b', 'rightEmoji': '1f4e3', 'date': '20220815' },
      { 'leftEmoji': '1f937', 'rightEmoji': '1f4e3', 'date': '20220815' },
      { 'leftEmoji': '2601-fe0f', 'rightEmoji': '1f4e3', 'date': '20220203' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f4e3', 'date': '20221101' },
      { 'leftEmoji': '1f381', 'rightEmoji': '1f4e3', 'date': '20220506' },
      { 'leftEmoji': '1f3b0', 'rightEmoji': '1f4e3', 'date': '20230126' },
      { 'leftEmoji': '1f52e', 'rightEmoji': '1f4e3', 'date': '20220506' }
    ],
    '1f43a': [
      { 'leftEmoji': '1f43a', 'rightEmoji': '1fa84', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f600', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f603', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f604', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f601', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f606', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f605', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f602', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f923', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f62d', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f609', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f617', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f619', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f61a', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f618', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f970', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f60d', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f929', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f973', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f643', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f642', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f972', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f979', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f60b', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f61b', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f61d', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f61c', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f92a', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f607', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f60a', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '263a-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f60f', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f60c', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f614', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f611', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f610', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f636', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1fae1', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f914', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f92b', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1fae2', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f92d', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f971', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f917', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1fae3', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f631', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f928', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f9d0', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f612', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f644', 'date': '20221101' },
      {
        'leftEmoji': '1f43a',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20221101'
      },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f624', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f620', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f621', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f92c', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f97a', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f61f', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f625', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f622', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '2639-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f641', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1fae4', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f615', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f910', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f630', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f628', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f627', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f626', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f62e', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f62f', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f632', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f633', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f92f', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f62c', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f613', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f61e', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f616', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f623', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f629', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f62b', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f635', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1fae5', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f634', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f62a', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f924', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f31b', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f31c', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f31e', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1fae0', 'date': '20221101' },
      {
        'leftEmoji': '1f43a',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f974', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f975', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f976', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f922', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f92e', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f927', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f912', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f915', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f637', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f920', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f911', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f60e', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f913', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f978', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f925', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f921', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f47b', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f4a9', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f47d', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f916', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f383', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f608', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f47f', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f525', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f4ab', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '2b50', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f31f', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f4af', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f573-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f38a', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '2665-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f498', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f49d', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f496', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f497', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f493', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f49e', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f495', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f48c', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f49f', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '2763-fe0f', 'date': '20221101' },
      {
        'leftEmoji': '1f43a',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20221101'
      },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f494', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f48b', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f9a0', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f480', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f441-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1fae6', 'date': '20221101' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f43a', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f937', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f490', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f339', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f337', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f338', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f33c', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f335', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f332', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1fab5', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1faa8', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '26c4', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f32a-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '2601-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f43a', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f308', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f30d', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f648', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f435', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f981', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f42f', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f431', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f436', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f43a', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f43b', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f428', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f43c', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f42d', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f430', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f43a', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f99d', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f437', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f984', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f422', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f429', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f410', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f98c', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f999', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f9a5', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f994', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f987', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f989', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f426', 'date': '20221101' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f43a', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f43a', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f427', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f41f', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f419', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f982', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f577-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f40c', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f41d', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f353', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f352', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f349', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f34a', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f34d', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f34c', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f34b', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f336-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f951', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f35e', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f9c0', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f32d', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f382', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f9c1', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f43a', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '2615', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f37d-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f307', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f388', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f381', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f3c6', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '26bd', 'date': '20221101' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f43a', 'date': '20230126' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f3a7', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f451', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f48e', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f4f0', 'date': '20221101' },
      { 'leftEmoji': '1f43a', 'rightEmoji': '1f52e', 'date': '20221101' }
    ],
    '1f98a': [
      { 'leftEmoji': '1f98a', 'rightEmoji': '1fa84', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f600', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f603', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f604', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f601', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f606', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f605', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f602', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f923', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f62d', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f609', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f617', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f619', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f61a', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f618', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f970', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f60d', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f929', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f973', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f643', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f642', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f972', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f979', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f60b', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f61b', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f61d', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f61c', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f92a', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f607', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f60a', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '263a-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f60f', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f60c', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f614', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f611', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f610', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f636', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1fae1', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f914', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f92b', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1fae2', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f92d', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f971', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f917', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1fae3', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f631', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f928', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f9d0', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f612', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f644', 'date': '20221101' },
      {
        'leftEmoji': '1f98a',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20221101'
      },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f624', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f620', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f621', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f92c', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f97a', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f61f', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f625', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f622', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '2639-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f641', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1fae4', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f615', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f910', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f630', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f628', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f627', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f626', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f62e', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f62f', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f632', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f633', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f92f', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f62c', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f613', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f61e', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f616', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f623', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f629', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f62b', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f635', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1fae5', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f634', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f62a', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f924', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f31b', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f31c', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f31e', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1fae0', 'date': '20221101' },
      {
        'leftEmoji': '1f98a',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f974', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f975', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f976', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f922', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f92e', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f927', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f912', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f915', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f637', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f920', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f911', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f60e', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f913', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f978', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f925', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f921', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f47b', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f4a9', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f47d', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f916', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f383', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f608', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f47f', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f525', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f4ab', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '2b50', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f31f', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f4af', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f573-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f38a', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '2665-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f498', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f49d', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f496', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f497', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f493', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f49e', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f495', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f48c', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f49f', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '2763-fe0f', 'date': '20221101' },
      {
        'leftEmoji': '1f98a',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20221101'
      },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f494', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f48b', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f9a0', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f480', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f441-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1fae6', 'date': '20221101' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1f98a', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f937', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f490', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f339', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f337', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f338', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f33c', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f335', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f332', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1fab5', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1faa8', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '26c4', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f32a-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '2601-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f327-fe0f', 'rightEmoji': '1f98a', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f308', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f30d', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f648', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f435', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f981', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f42f', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f431', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f436', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f43a', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f43b', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f428', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f43c', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f42d', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f430', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f98a', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f99d', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f437', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f984', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f422', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f429', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f410', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f98c', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f999', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f9a5', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f994', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f987', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f989', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f426', 'date': '20221101' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f98a', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f98a', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f427', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f41f', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f419', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f982', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f577-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f40c', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f41d', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f353', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f352', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f349', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f34a', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f34d', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f34c', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f34b', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f336-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f951', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f35e', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f9c0', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f32d', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f382', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f9c1', 'date': '20221101' },
      { 'leftEmoji': '1f36c', 'rightEmoji': '1f98a', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '2615', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f37d-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f307', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f388', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f381', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f3c6', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '26bd', 'date': '20221101' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f98a', 'date': '20230126' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f3a7', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f451', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f48e', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f4f0', 'date': '20221101' },
      { 'leftEmoji': '1f98a', 'rightEmoji': '1f52e', 'date': '20221101' }
    ],
    '1fa75': [
      { 'leftEmoji': '1fa75', 'rightEmoji': '1fa84', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f618', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f970', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f60d', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f929', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f973', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f31b', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f31c', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f920', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f60e', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f913', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f921', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f525', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f4ab', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '2b50', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f31f', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f4af', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f573-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f38a', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1fa75', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '2764-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f9e1', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f49b', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f49a', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1fa75', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f499', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f49c', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f90e', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1fa75', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f5a4', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f5a4', 'date': '20230301' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f90d', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '2665-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '2665-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f498', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f49d', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f49d', 'date': '20230301' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f496', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f496', 'date': '20230301' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f497', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f497', 'date': '20230301' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f493', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f493', 'date': '20230301' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f49e', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f49e', 'date': '20230301' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f495', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f48c', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f49f', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '2763-fe0f', 'date': '20221101' },
      {
        'leftEmoji': '1fa75',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20221101'
      },
      {
        'leftEmoji': '1fa75',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20230301'
      },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f494', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f48b', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f48b', 'date': '20230301' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f440', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f440', 'date': '20230301' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f441-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1fae6', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f490', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f339', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f337', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f338', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f338', 'date': '20230301' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f33c', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f308', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f984', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f429', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f426', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f426', 'date': '20230301' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f382', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f9c1', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f36c', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '2615', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f388', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f381', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f381', 'date': '20230301' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f397-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f58d-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f451', 'date': '20221101' },
      { 'leftEmoji': '1fa75', 'rightEmoji': '1f48e', 'date': '20221101' }
    ],
    '1fa77': [
      { 'leftEmoji': '1fa77', 'rightEmoji': '1fa84', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f618', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f970', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f60d', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f929', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f973', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f31b', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f31c', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f920', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f60e', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f913', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f921', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f525', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f4ab', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f4ab', 'date': '20230301' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '2b50', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f31f', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f31f', 'date': '20230301' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f4af', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f573-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f38a', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f38a', 'date': '20230301' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1fa77', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '2764-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f9e1', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f49b', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f49a', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1fa75', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f499', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f49c', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f90e', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1fa77', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f5a4', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f90d', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '2665-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f498', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f49d', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f496', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f496', 'date': '20230301' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f497', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f493', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f49e', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f49e', 'date': '20230301' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f495', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f495', 'date': '20230301' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f48c', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f49f', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '2763-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '2763-fe0f', 'date': '20230301' },
      {
        'leftEmoji': '1fa77',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20221101'
      },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f494', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f48b', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f440', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f441-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f441-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1fae6', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f490', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f339', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f337', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f338', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f33c', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f308', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f984', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f984', 'date': '20230301' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f429', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f426', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f426', 'date': '20230301' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f382', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f9c1', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f36c', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '2615', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f388', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f381', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f397-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f58d-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f58d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f451', 'date': '20221101' },
      { 'leftEmoji': '1fa77', 'rightEmoji': '1f48e', 'date': '20221101' }
    ],
    '1fa76': [
      { 'leftEmoji': '1fa76', 'rightEmoji': '1fa84', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f618', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f970', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f60d', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f929', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f973', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f31b', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f31c', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f920', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f60e', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f913', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f921', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f525', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f525', 'date': '20230301' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f4ab', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f4ab', 'date': '20230301' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '2b50', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f31f', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f31f', 'date': '20230301' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f4af', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f573-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f38a', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f38a', 'date': '20230301' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1fa77', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '2764-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f9e1', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f49b', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f49a', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1fa75', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f499', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f49c', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f90e', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1fa76', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f5a4', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f5a4', 'date': '20230301' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f90d', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '2665-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f498', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f498', 'date': '20230301' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f49d', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f496', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f496', 'date': '20230301' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f497', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f493', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f49e', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f49e', 'date': '20230301' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f495', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f495', 'date': '20230301' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f48c', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f49f', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '2763-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '2763-fe0f', 'date': '20230301' },
      {
        'leftEmoji': '1fa76',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20221101'
      },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f494', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f48b', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f440', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f441-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f441-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1fae6', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f490', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f490', 'date': '20230301' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f339', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f337', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f338', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f33c', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f308', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f984', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f984', 'date': '20230301' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f429', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f426', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f426', 'date': '20230301' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f382', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f9c1', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f9c1', 'date': '20230301' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f36c', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '2615', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f388', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f381', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f397-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f58d-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f58d-fe0f', 'date': '20230301' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f451', 'date': '20221101' },
      { 'leftEmoji': '1fa76', 'rightEmoji': '1f48e', 'date': '20221101' }
    ],
    '1fabf': [
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fa84', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f600', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f603', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f604', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f601', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f606', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f605', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f602', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f923', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f62d', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f609', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f617', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f619', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f61a', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f618', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f970', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f60d', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f929', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f973', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f643', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f642', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f972', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f979', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f60b', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f61b', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f61d', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f61c', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f92a', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f607', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f60a', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '263a-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f60f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f60c', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f614', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f611', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f610', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f636', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fae1', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f914', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f92b', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fae2', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f92d', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f971', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f917', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fae3', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f631', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f928', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f9d0', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f612', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f644', 'date': '20221101' },
      {
        'leftEmoji': '1fabf',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20221101'
      },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f624', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f620', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f621', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f92c', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f97a', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f61f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f625', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f622', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '2639-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f641', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fae4', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f615', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f910', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f630', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f628', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f627', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f626', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f62e', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f62f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f632', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f633', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f92f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f62c', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f613', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f61e', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f616', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f623', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f629', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f62b', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f635', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fae5', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f634', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f62a', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f924', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f31b', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f31c', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f31e', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fae0', 'date': '20221101' },
      {
        'leftEmoji': '1fabf',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20221101'
      },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f974', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f975', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f976', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f922', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f92e', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f927', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f912', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f915', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f637', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f920', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f911', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f60e', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f913', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f978', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f925', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f921', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f47b', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f4a9', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f47d', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f916', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f383', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f608', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f47f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f525', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f4ab', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '2b50', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f31f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f4af', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f573-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f38a', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '2665-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f498', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f49d', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f496', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f497', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f493', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f49e', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f495', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f48c', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f49f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '2763-fe0f', 'date': '20221101' },
      {
        'leftEmoji': '1fabf',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20221101'
      },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f494', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f48b', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f9a0', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f480', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f441-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fae6', 'date': '20221101' },
      { 'leftEmoji': '1f44d', 'rightEmoji': '1fabf', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f937', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f490', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f339', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f337', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f338', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f33c', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f344', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f335', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f332', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fab5', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1faa8', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '26c4', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f32a-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '2601-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f327-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f308', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f30d', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f648', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f435', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f981', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f42f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f431', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f436', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f43a', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f43b', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f428', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f43c', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f42d', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f430', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f98a', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f99d', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f437', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f984', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f422', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f429', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f410', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f98c', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f999', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f9a5', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f994', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f987', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f989', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f426', 'date': '20221101' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fabf', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1fabf', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f427', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f41f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f419', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f982', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f577-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f40c', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f41d', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f353', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f352', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f349', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f34a', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f34d', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f34c', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f34b', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f336-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f951', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f35e', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f9c0', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f32d', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f382', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f9c1', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f36c', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '2615', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f37d-fe0f', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f307', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f388', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f381', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f3c6', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '26bd', 'date': '20221101' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1fabf', 'date': '20230126' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f3a7', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f451', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f48e', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f4f0', 'date': '20221101' },
      { 'leftEmoji': '1fabf', 'rightEmoji': '1f52e', 'date': '20221101' }
    ],
    '1f414': [
      { 'leftEmoji': '1f414', 'rightEmoji': '1fa84', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f600', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f603', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f604', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f601', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f606', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f605', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f602', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f923', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f62d', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f609', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f617', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f619', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f61a', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f618', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f970', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f60d', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f929', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f973', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f643', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f642', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f972', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f979', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f60b', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f61b', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f61d', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f61c', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f92a', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f607', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f60a', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '263a-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f60f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f60c', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f614', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f611', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f610', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f636', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fae1', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f914', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f92b', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fae2', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f92d', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f971', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f917', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fae3', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f631', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f928', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f9d0', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f612', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f644', 'date': '20230126' },
      {
        'leftEmoji': '1f414',
        'rightEmoji': '1f62e-200d-1f4a8',
        'date': '20230126'
      },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f624', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f620', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f621', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f92c', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f97a', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f61f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f625', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f622', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '2639-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f641', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fae4', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f615', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f910', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f630', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f628', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f627', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f626', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f62e', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f62f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f632', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f633', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f92f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f62c', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f613', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f61e', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f616', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f623', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f629', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f62b', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f635', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fae5', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f634', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f62a', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f924', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f31b', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f31c', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f31e', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fae0', 'date': '20230126' },
      {
        'leftEmoji': '1f414',
        'rightEmoji': '1f636-200d-1f32b-fe0f',
        'date': '20230126'
      },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f974', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f975', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f976', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f922', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f92e', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f927', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f912', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f915', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f637', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f920', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f911', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f60e', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f913', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f978', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f925', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f921', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f47b', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f4a9', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f47d', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f916', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f383', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f608', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f47f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f525', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f4ab', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '2b50', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f31f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f4af', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f573-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f38a', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '2665-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f498', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f49d', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f496', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f497', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f493', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f49e', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f495', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f48c', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f49f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '2763-fe0f', 'date': '20230126' },
      {
        'leftEmoji': '1f414',
        'rightEmoji': '2764-fe0f-200d-1fa79',
        'date': '20230126'
      },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f494', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f48b', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f9a0', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f480', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f441-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fae6', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f44d', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f937', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f490', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f339', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f337', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f338', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f33c', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f344', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f335', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f332', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fab5', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1faa8', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '26c4', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f32a-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '2601-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f327-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f308', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f30d', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f648', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f435', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f981', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f42f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f431', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f436', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f43a', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f43b', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f428', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f43c', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f42d', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f430', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f98a', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f99d', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f437', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f984', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f422', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f429', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f410', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f98c', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f999', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f9a5', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f994', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f987', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f989', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f426', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f414', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1fabf', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f427', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f41f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f419', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f982', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f577-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f40c', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f41d', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f353', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f352', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f349', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f34a', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f34d', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f34c', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f34b', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f336-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f951', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f35e', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f9c0', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f32d', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f382', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f9c1', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f36c', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '2615', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f37d-fe0f', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f307', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f388', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f381', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f3c6', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '26bd', 'date': '20230126' },
      { 'leftEmoji': '1f3c0', 'rightEmoji': '1f414', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f3a7', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f451', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f48e', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f4f0', 'date': '20230126' },
      { 'leftEmoji': '1f414', 'rightEmoji': '1f52e', 'date': '20230126' }
    ]
};

/**
 * `supportedEmojis` is an array of Unicode code point strings.
 * Each string in the array represents an emoji that is supported by your application.
 * 
 * For instance, '1fa84' represents the 🪄 emoji, '1f600' represents the 😀 emoji, and so on.
 * 
 * This list dictates which emojis are supported by Google Emoji Kitchen.
 * If an emoji's Unicode representation is in this list, that means it's supported and can be used.
 * If it's not in this list, then it's not supported and should not be used.
 */
const supportedEmojis = [
    '1fa84', // 🪄
    '1f600', // 😀
    '1f603', // 😃
    '1f604', // 😄
    '1f601', // 😁
    '1f606', // 😆
    '1f605', // 😅
    '1f602', // 😂
    '1f923', // 🤣
    '1f62d', // 😭
    '1f609', // 😉
    '1f617', // 😗
    '1f619', // 😙
    '1f61a', // 😚
    '1f618', // 😘
    '1f970', // 🥰
    '1f60d', // 😍
    '1f929', // 🤩
    '1f973', // 🥳
    '1f643', // 🙃
    '1f642', // 🙂
    '1f972', // 🥲
    '1f979', // 🥹
    '1f60b', // 😋
    '1f61b', // 😛
    '1f61d', // 😝
    '1f61c', // 😜
    '1f92a', // 🤪
    '1f607', // 😇
    '1f60a', // 😊
    '263a-fe0f', // ☺️
    '1f60f', // 😏
    '1f60c', // 😌
    '1f614', // 😔
    '1f611', // 😑
    '1f610', // 😐
    '1f636', // 😶
    '1fae1', // 🫡
    '1f914', // 🤔
    '1f92b', // 🤫
    '1fae2', // 🫢
    '1f92d', // 🤭
    '1f971', // 🥱
    '1f917', // 🤗
    '1fae3', // 🫣
    '1f631', // 😱
    '1f928', // 🤨
    '1f9d0', // 🧐
    '1f612', // 😒
    '1f644', // 🙄
    '1f62e-200d-1f4a8', // 😮‍💨
    '1f624', // 😤
    '1f620', // 😠
    '1f621', // 😡
    '1f92c', // 🤬
    '1f97a', // 🥺
    '1f61f', // 😟
    '1f625', // 😥
    '1f622', // 😢
    '2639-fe0f', // ☹️
    '1f641', // 🙁
    '1fae4', // 🫤
    '1f615', // 😕
    '1f910', // 🤐
    '1f630', // 😰
    '1f628', // 😨
    '1f627', // 😧
    '1f626', // 😦
    '1f62e', // 😮
    '1f62f', // 😯
    '1f632', // 😲
    '1f633', // 😳
    '1f92f', // 🤯
    '1f62c', // 😬
    '1f613', // 😓
    '1f61e', // 😞
    '1f616', // 😖
    '1f623', // 😣
    '1f629', // 😩
    '1f62b', // 😫
    '1f635', // 😵
    '1fae5', // 🫥
    '1f634', // 😴
    '1f62a', // 😪
    '1f924', // 🤤
    '1f31b', // 🌛
    '1f31c', // 🌜
    '1f31a', // 🌚
    '1f31d', // 🌝
    '1f31e', // 🌞
    '1fae0', // 🫠
    '1f636-200d-1f32b-fe0f', // 😶‍🌫️
    '1f974', // 🥴
    '1f975', // 🥵
    '1f976', // 🥶
    '1f922', // 🤢
    '1f92e', // 🤮
    '1f927', // 🤧
    '1f912', // 🤒
    '1f915', // 🤕
    '1f637', // 😷
    '1f920', // 🤠
    '1f911', // 🤑
    '1f60e', // 😎
    '1f913', // 🤓
    '1f978', // 🥸
    '1f925', // 🤥
    '1f921', // 🤡
    '1f47b', // 👻
    '1f4a9', // 💩
    '1f47d', // 👽
    '1f916', // 🤖
    '1f383', // 🎃
    '1f608', // 😈
    '1f47f', // 👿
    '1f525', // 🔥
    '1f4ab', // 💫
    '2b50', // ⭐
    '1f31f', // 🌟
    '1f4a5', // 💥
    '1f4af', // 💯
    '1fae7', // 🫧
    '1f573-fe0f', // 🕳️
    '1f38a', // 🎊
    '1fa77', // 🩷
    '2764-fe0f', // ❤️
    '1f9e1', // 🧡
    '1f49b', // 💛
    '1f49a', // 💚
    '1fa75', // 🩵
    '1f499', // 💙
    '1f49c', // 💜
    '1f90e', // 🤎
    '1fa76', // 🩶
    '1f5a4', // 🖤
    '1f90d', // 🤍
    '2665-fe0f', // ♥️
    '1f498', // 💘
    '1f49d', // 💝
    '1f496', // 💖
    '1f497', // 💗
    '1f493', // 💓
    '1f49e', // 💞
    '1f495', // 💕
    '1f48c', // 💌
    '1f49f', // 💟
    '2763-fe0f', // ❣️
    '2764-fe0f-200d-1fa79', // ❤️‍🩹
    '1f494', // 💔
    '1f48b', // 💋
    '1f9e0', // 🧠
    '1fac0', // 🫀
    '1fac1', // 🫁
    '1fa78', // 🩸
    '1f9a0', // 🦠
    '1f9b7', // 🦷
    '1f9b4', // 🦴
    '1f480', // 💀
    '1f440', // 👀
    '1f441-fe0f', // 👁️
    '1fae6', // 🫦
    '1f44d', // 👍
    '1f937', // 🤷
    '1f490', // 💐
    '1f339', // 🌹
    '1f33a', // 🌺
    '1f337', // 🌷
    '1f338', // 🌸
    '1f4ae', // 💮
    '1f3f5-fe0f', // 🏵️
    '1f33b', // 🌻
    '1f33c', // 🌼
    '1f341', // 🍁
    '1f344', // 🍄
    '1f331', // 🌱
    '1f33f', // 🌿
    '1f343', // 🍃
    '1f340', // 🍀
    '1fab4', // 🪴
    '1f335', // 🌵
    '1f334', // 🌴
    '1f333', // 🌳
    '1f332', // 🌲
    '1fab9', // 🪹
    '1fab5', // 🪵
    '1faa8', // 🪨
    '26c4', // ⛄
    '1f30a', // 🌊
    '1f32c-fe0f', // 🌬️
    '1f300', // 🌀
    '1f32a-fe0f', // 🌪️
    '1f30b', // 🌋
    '1f3d6-fe0f', // 🏖️
    '26c5', // ⛅
    '2601-fe0f', // ☁️
    '1f327-fe0f', // 🌧️
    '1f329-fe0f', // 🌩️
    '1f4a7', // 💧
    '2602-fe0f', // ☂️
    '26a1', // ⚡
    '1f308', // 🌈
    '2604-fe0f', // ☄️
    '1fa90', // 🪐
    '1f30d', // 🌍
    '1f648', // 🙈
    '1f435', // 🐵
    '1f981', // 🦁
    '1f42f', // 🐯
    '1f431', // 🐱
    '1f436', // 🐶
    '1f43a', // 🐺
    '1f43b', // 🐻
    '1f428', // 🐨
    '1f43c', // 🐼
    '1f42d', // 🐭
    '1f430', // 🐰
    '1f98a', // 🦊
    '1f99d', // 🦝
    '1f437', // 🐷
    '1f984', // 🦄
    '1f422', // 🐢
    '1f429', // 🐩
    '1f410', // 🐐
    '1f98c', // 🦌
    '1f999', // 🦙
    '1f9a5', // 🦥
    '1f994', // 🦔
    '1f987', // 🦇
    '1f989', // 🦉
    '1f426', // 🐦
    '1f414', // 🐔
    '1fabf', // 🪿
    '1f54a-fe0f', // 🕊️
    '1f9a9', // 🦩
    '1f427', // 🐧
    '1f41f', // 🐟
    '1f99e', // 🦞
    '1f980', // 🦀
    '1f419', // 🐙
    '1fab8', // 🪸
    '1f982', // 🦂
    '1f577-fe0f', // 🕷️
    '1f41a', // 🐚
    '1f40c', // 🐌
    '1f997', // 🦗
    '1fab2', // 🪲
    '1fab3', // 🪳
    '1f41d', // 🐝
    '1f41e', // 🐞
    '1f98b', // 🦋
    '1f43e', // 🐾
    '1f353', // 🍓
    '1f352', // 🍒
    '1f349', // 🍉
    '1f34a', // 🍊
    '1f96d', // 🥭
    '1f34d', // 🍍
    '1f34c', // 🍌
    '1f34b', // 🍋
    '1f348', // 🍈
    '1f350', // 🍐
    '1f95d', // 🥝
    '1fad2', // 🫒
    '1fad0', // 🫐
    '1f347', // 🍇
    '1f965', // 🥥
    '1f345', // 🍅
    '1f336-fe0f', // 🌶️
    '1f955', // 🥕
    '1f360', // 🍠
    '1f9c5', // 🧅
    '1f33d', // 🌽
    '1f966', // 🥦
    '1f952', // 🥒
    '1fad1', // 🫑
    '1f951', // 🥑
    '1f9c4', // 🧄
    '1f954', // 🥔
    '1fad8', // 🫘
    '1f330', // 🌰
    '1f95c', // 🥜
    '1f35e', // 🍞
    '1fad3', // 🫓
    '1f950', // 🥐
    '1f96f', // 🥯
    '1f95e', // 🥞
    '1f373', // 🍳
    '1f9c0', // 🧀
    '1f969', // 🥩
    '1f356', // 🍖
    '1f354', // 🍔
    '1f32d', // 🌭
    '1f96a', // 🥪
    '1f968', // 🥨
    '1f35f', // 🍟
    '1fad4', // 🫔
    '1f32e', // 🌮
    '1f32f', // 🌯
    '1f959', // 🥙
    '1f9c6', // 🧆
    '1f958', // 🥘
    '1f35d', // 🍝
    '1f96b', // 🥫
    '1fad5', // 🫕
    '1f963', // 🥣
    '1f957', // 🥗
    '1f372', // 🍲
    '1f35b', // 🍛
    '1f35c', // 🍜
    '1f363', // 🍣
    '1f364', // 🍤
    '1f35a', // 🍚
    '1f371', // 🍱
    '1f359', // 🍙
    '1f358', // 🍘
    '1f365', // 🍥
    '1f960', // 🥠
    '1f367', // 🍧
    '1f368', // 🍨
    '1f366', // 🍦
    '1f370', // 🍰
    '1f382', // 🎂
    '1f9c1', // 🧁
    '1f36c', // 🍬
    '1f36b', // 🍫
    '1f369', // 🍩
    '1f36a', // 🍪
    '1f9c2', // 🧂
    '1f37f', // 🍿
    '1f9cb', // 🧋
    '1f37c', // 🍼
    '1f375', // 🍵
    '2615', // ☕
    '1f9c9', // 🧉
    '1f379', // 🍹
    '1f37d-fe0f', // 🍽️
    '1f6d1', // 🛑
    '1f6a8', // 🚨
    '1f6df', // 🛟
    '2693', // ⚓
    '1f697', // 🚗
    '1f3ce-fe0f', // 🏎️
    '1f695', // 🚕
    '1f68c', // 🚌
    '1f682', // 🚂
    '1f6f8', // 🛸
    '1f680', // 🚀
    '2708-fe0f', // ✈️
    '1f3a2', // 🎢
    '1f3a1', // 🎡
    '1f3aa', // 🎪
    '1f3db-fe0f', // 🏛️
    '1f3df-fe0f', // 🏟️
    '1f3e0', // 🏠
    '1f3d5-fe0f', // 🏕️
    '1f307', // 🌇
    '1f3dd-fe0f', // 🏝️
    '1f388', // 🎈
    '1f380', // 🎀
    '1f381', // 🎁
    '1faa9', // 🪩
    '1f397-fe0f', // 🎗️
    '1f947', // 🥇
    '1f948', // 🥈
    '1f949', // 🥉
    '1f3c5', // 🏅
    '1f396-fe0f', // 🎖️
    '1f3c6', // 🏆
    '26bd', // ⚽
    '26be', // ⚾
    '1f94e', // 🥎
    '1f3c0', // 🏀
    '1f3d0', // 🏐
    '1f3c8', // 🏈
    '1f3c9', // 🏉
    '1f3be', // 🎾
    '1f945', // 🥅
    '1f3f8', // 🏸
    '1f94d', // 🥍
    '1f3cf', // 🏏
    '1f3d1', // 🏑
    '1f3d2', // 🏒
    '1f94c', // 🥌
    '1f6f7', // 🛷
    '1f3bf', // 🎿
    '26f8-fe0f', // ⛸️
    '1f6fc', // 🛼
    '1fa70', // 🩰
    '1f6f9', // 🛹
    '26f3', // ⛳
    '1f3af', // 🎯
    '1f3f9', // 🏹
    '1f94f', // 🥏
    '1fa83', // 🪃
    '1fa81', // 🪁
    '1f93f', // 🤿
    '1f3bd', // 🎽
    '1f94b', // 🥋
    '1f94a', // 🥊
    '1f3b1', // 🎱
    '1f3d3', // 🏓
    '1f3b3', // 🎳
    '265f-fe0f', // ♟️
    '1fa80', // 🪀
    '1f9e9', // 🧩
    '1f3ae', // 🎮
    '1f3b2', // 🎲
    '1f3b0', // 🎰
    '1f3b4', // 🎴
    '1f004', // 🀄
    '1f0cf', // 🃏
    '1f4f7', // 📷
    '1f3a8', // 🎨
    '1f58c-fe0f', // 🖌️
    '1f58d-fe0f', // 🖍️
    '1faa1', // 🪡
    '1f9f5', // 🧵
    '1f9f6', // 🧶
    '1f3b9', // 🎹
    '1f3b7', // 🎷
    '1f3ba', // 🎺
    '1f3b8', // 🎸
    '1fa95', // 🪕
    '1f3bb', // 🎻
    '1fa98', // 🪘
    '1f941', // 🥁
    '1fa97', // 🪗
    '1f3a4', // 🎤
    '1f3a7', // 🎧
    '1f399-fe0f', // 🎙️
    '1f4fa', // 📺
    '1f39e-fe0f', // 🎞️
    '1f3ac', // 🎬
    '1f3ad', // 🎭
    '1f39f-fe0f', // 🎟️
    '1f4f1', // 📱
    '260e-fe0f', // ☎️
    '1f50b', // 🔋
    '1faab', // 🪫
    '1f4be', // 💾
    '1f4bf', // 💿
    '1f4b8', // 💸
    '2696-fe0f', // ⚖️
    '1f4a1', // 💡
    '1f9fc', // 🧼
    '1f9e6', // 🧦
    '1f451', // 👑
    '1f48e', // 💎
    '1f6e0-fe0f', // 🛠️
    '26d3-fe0f', // ⛓️
    '1f5d1-fe0f', // 🗑️
    '1f58a-fe0f', // 🖊️
    '2712-fe0f', // ✒️
    '270f-fe0f', // ✏️
    '1f4da', // 📚
    '1f5c3-fe0f', // 🗃️
    '1f4f0', // 📰
    '1f4e3', // 📣
    '1f50e', // 🔎
    '1f52e', // 🔮
    '1f9ff', // 🧿
    '1f5dd-fe0f', // 🗝️
    '1f512', // 🔒
    '2648', // ♈
    '2649', // ♉
    '264a', // ♊
    '264b', // ♋
    '264c', // ♌
    '264d', // ♍
    '264e', // ♎
    '264f', // ♏
    '2650', // ♐
    '2651', // ♑
    '2652', // ♒
    '2653', // ♓
    '26ce', // ⛎
    '2757', // ❗
    '2753', // ❓
    '2049-fe0f', // ⁉️
    '1f198', // 🆘
    '1f4f4', // 📴
    '1f508', // 🔈
    '26a0-fe0f', // ⚠️
    '267b-fe0f', // ♻️
    '2705', // ✅
    '1f195', // 🆕
    '1f193', // 🆓
    '1f199', // 🆙
    '1f197', // 🆗
    '1f192', // 🆒
    '1f6ae', // 🚮
    '262e-fe0f', // ☮️
    '262f-fe0f', // ☯️
    '267e-fe0f', // ♾️
    '2716-fe0f', // ✖️
    '2795', // ➕
    '2796', // ➖
    '2797', // ➗
    '27b0', // ➰
    '27bf', // ➿
    '3030-fe0f', // 〰️
    '00a9-fe0f', // ©️
    '00ae-fe0f', // ®️
    '2122-fe0f', // ™️
    '2660-fe0f', // ♠️
    '1f5ef-fe0f', // 🗯️
    '1f4ac', // 💬
];

/**
 * The base URL for fetching emoji images from Google's Android Emoji Kitchen.
 */
const baseUrl = 'https://www.gstatic.com/android/keyboard/emojikitchen';

/**
 * Transforms an emoji Unicode representation for inclusion in a URL. 
 * This includes prefixing each Unicode part with 'u' and joining the parts with hyphens.
 *
 * @param {string} emoji - The emoji's Unicode representation.
 * @returns {string} The transformed Unicode representation suitable for inclusion in a URL.
 */
const googleRequestUrlEmojiPart = emoji =>  {
    return emoji.split('-').map(part => `u${part.toLowerCase()}`).join('-');
}

/**
 * Validates and transforms an input into a Unicode representation. 
 * If the input is already a Unicode representation, it returns the lowercased version. 
 * If the input is a valid emoji, it converts it to Unicode. 
 * If the input is not a valid emoji or Unicode representation, it returns undefined.
 *
 * @param {string} input - The input string to convert.
 * @returns {string} The Unicode representation of the input, or undefined if the input is not valid.
 */
const toUnicode = input => {
    if (/^[\da-f]+$/i.test(input)) // Input is already a Unicode representation
        return input.toLowerCase();
    else if (emojiRegex().test(input)) // Input is an emoji, convert it to Unicode
        return input.codePointAt(0).toString(16);
    else
        return undefined;
}

/**
 * Checks if an emoji is supported by looking it up in the emojiCompatibilityData object.
 *
 * @param {string} emoji - An emoji or its Unicode representation as a string.
 * @returns {(Object[]|null)} If the emoji is supported, it returns the array of
 * emoji data associated with this emoji; otherwise, it returns null.
 *
 * @example
 * const isSupported = checkSupported('😀');
 * // Outputs the corresponding emoji data array if emoji is supported, otherwise outputs null
 * console.log(isSupported); 
 */
const checkSupported = emoji => {
  // Convert the input emoji to its Unicode representation
  emoji = toUnicode(emoji);

  // If the conversion was unsuccessful, return null
  if (!emoji)
      return null;

  // If the emoji is supported, return its data; otherwise, return null
  return emojiCompatibilityData[emoji] ?? null;
}

/**
 * Generates a URL for fetching an emoji combination image from Google's Android Emoji Kitchen.
 * The URL includes the base URL, the date, and the transformed Unicode representations of the left and right emojis.
 *
 * @param {string} leftEmoji - The Unicode representation of the left emoji.
 * @param {string} rightEmoji - The Unicode representation of the right emoji.
 * @param {string} date - The date in YYYYMMDD format.
 * @returns {string} The URL for fetching the emoji combination image.
 */
const googleRequestUrl = emojiMixData => {
    const { leftEmoji, rightEmoji, date } = emojiMixData

    const leftEmojiUrlPart = googleRequestUrlEmojiPart(leftEmoji)
    const rightEmojiUrlPart = googleRequestUrlEmojiPart(rightEmoji)

    return `${
        baseUrl
    }/${
        date
    }/${
        leftEmojiUrlPart
    }/${
        leftEmojiUrlPart
    }_${
        rightEmojiUrlPart
    }.png`;
}

/**
 * This function is used to find a matching emoji combination from the global emojiCompatibilityData object. 
 * The function will search for a matching pair of emojis (leftEmoji and rightEmoji) in both 
 * possible orders. If multiple matches are found, it sorts them in descending order by date
 * and returns the most recent match.
 *
 * @param {string} leftEmoji - The Unicode representation of the left emoji as a string. 
 * @param {string} rightEmoji - The Unicode representation of the right emoji as a string.
 * @returns {Object} If a matching emoji combination is found, it returns an object containing 
 * information about the matched emoji combination. The object includes properties for the 
 * left emoji, the right emoji, and the date when the emoji combination was added. 
 * If no matching emoji combination is found, it returns undefined.
 *
 * @example
 * let emojiCombo = getEmojiCombo("1f600", "2615");
*/
const getEmojiCombo = (leftEmoji, rightEmoji) => {
    // Filter the emojiCompatibilityData for the specified rightEmoji to find matching combinations
    let matchingEmojis = emojiCompatibilityData[rightEmoji]
        .filter(emoji => 
            // Look for combinations where either the leftEmoji matches leftEmoji and the rightEmoji matches rightEmoji
            // or where the leftEmoji matches rightEmoji and the rightEmoji matches leftEmoji
            (emoji.leftEmoji === leftEmoji && emoji.rightEmoji === rightEmoji) || 
            (emoji.leftEmoji === rightEmoji && emoji.rightEmoji === leftEmoji)
        ).sort((a, b) => a.date > b.date ? -1 : 1); // Sort the matching combinations by date in descending order

    // If there's at least one matching combination, return the most recent (first in the sorted list)
    if (matchingEmojis.length > 0)
        return matchingEmojis[0];
    else
        return undefined;
}

/**
 * This function generates a URL for an emoji mix image from Google's Android Emoji Kitchen 
 * based on the unicode representation of two input emojis. 
 * It validates the input emojis, checks for compatibility, 
 * and throws an error with additional information if the emojis are not valid or not compatible.
 *
 * @param {string} leftEmoji - The unicode representation of the first emoji.
 * @param {string} rightEmoji - The unicode representation of the second emoji.
 * @param {boolean} detailedErrors - Optional parameter. 
 * If set to true, in case of an error, instead of undefined
 * a detailed explanation of what went wrong will be thrown instead.
 *
 * @returns {string | null | undefined} - The URL for fetching the mixed emoji image, 
 * undefined if something went wrong or null if no suitable image is available. 
 * If detailedErrors is enabled it throws a detailed error message instead.
 *
 * @throws Should detailedErrors be set to true, will throw detailed errors regarding what 
 * went wrong during execution.
 */
const getEmojiMixUrl = (leftEmoji, rightEmoji, detailedErrors = false) => {
  leftEmoji = toUnicode(leftEmoji);
  rightEmoji = toUnicode(rightEmoji);

  // Left emoji argument is incompatible.
  if (!leftEmoji && !detailedErrors)
      return undefined;
  else if (!leftEmoji && detailedErrors)
      throw new Error(`${leftEmoji} [leftEmoji] argument is not a valid unicode emoji.`);

  // Right emoji argument is incompatible.
  if (!rightEmoji && !detailedErrors)
      return undefined;
  if (!rightEmoji && detailedErrors)
      throw new Error(`${rightEmoji} [rightEmoji] argument is not a valid unicode emoji.`);

  // Left emoji isn't supported by Google Emoji Kitchen
  if (!supportedEmojis.includes(leftEmoji) && !detailedErrors)
      return undefined;
  else if (!supportedEmojis.includes(leftEmoji) && detailedErrors)
      throw new Error(`${leftEmoji} [leftEmoji] argument is not a supported emoji.`);

  // Right emoji isn't supported by Google Emoji Kitchen
  if (!supportedEmojis.includes(rightEmoji) && !detailedErrors)
      return undefined;
  else if (!supportedEmojis.includes(rightEmoji) && detailedErrors)
      throw new Error(`${rightEmoji} [rightEmoji] argument is not a supported emoji.`);

  const emojiMixData = getEmojiCombo(leftEmoji, rightEmoji);
  // This emoji combination isn't supported by Google Emoji Kitchen
  if (emojiMixData === undefined && !detailedErrors)
      return undefined
  else if (emojiMixData === undefined && detailedErrors)
      throw new Error(`'${leftEmoji}' is not compatible with '${rightEmoji}'. Here are all emojis compatible with '${rightEmoji}':\n[${
          emojiCompatibilityData[rightEmoji].map(e => {
              return e.leftEmoji
          }).join(', ')
      }]`)

  return googleRequestUrl(emojiMixData) ?? null;
}

/**
 * Exporting the functions defined in this module.
 * 
 * The main function of this module is `getEmojiMixUrl`, which combines two emojis into a unique 
 * emoji mix using Google's Android Emoji Kitchen, and generates the appropriate URL for fetching 
 * this emoji mix image. This function takes in two emojis (in unicode representation) and an optional 
 * boolean flag indicating whether to show compatibility information in case of errors. 
 * 
 * Apart from the main function, this module also provides a number of helper functions:
 * 
 * `toUnicode` - This function validates a given string input and transforms it into a Unicode representation. 
 * If the input string is a valid emoji, it is converted to Unicode. If the input string is 
 * not a valid emoji or Unicode representation, the function returns undefined.
 * 
 * `getEmojiCombo` - This function searches the global `emojiCompatibilityData` object for a matching pair 
 * of emojis and returns information about the matched emoji combination if one is found. 
 * If no match is found, it returns undefined.
 * 
 * `checkSupported` - This function checks if a given emoji is supported by looking it up in 
 * the `emojiCompatibilityData` object. If the emoji is supported, it returns an array of emoji data associated 
 * with this emoji; otherwise, it returns null.
 * 
 * `googleRequestUrl` - This function generates a URL for fetching an emoji combination image 
 * from Google's Android Emoji Kitchen. The URL includes the base URL, the date, and the transformed 
 * Unicode representations of the left and right emojis.
 * 
 * `googleRequestUrlEmojiPart` - This function transforms an emoji Unicode representation for 
 * inclusion in a URL. This includes prefixing each Unicode part with 'u' and joining the parts 
 * with hyphens.
 */ // Main function
export default getEmojiMixUrl; 

// Helper functions
export {
  toUnicode,
  getEmojiCombo,
  checkSupported,
  googleRequestUrl,
  googleRequestUrlEmojiPart
};